/**
 * CollateralMapModal — SaaS Production-Grade Collateral Boundary Map
 * Uses Leaflet 1.9.4 + @geoman-io/leaflet-geoman-free
 *
 * [COLLATERAL MAP] Added 2026-07-14
 * [MAP v10] 2026-07-15 — Post-Claude-debate production improvements:
 *   NEW-2: localStorage base layer memory (default satellite)
 *   GAP-4: Enhanced snapping (snapDistance:15, snapMiddle, snapGuidelines)
 *   GAP-5: "Reset to Saved" button (replaces full undo/redo — Geoman state safe)
 *   NEW-1: Long-press 500ms → lat/lng popup + copy (cancel if moved >10px)
 *   GAP-7: "ໄປຫາພິກັດ" panel — text+inputmode decimal, Laos bounds, temp marker
 *   GAP-8: 2-click distance measurement mode (Geoman-safe, disables draw on enter)
 * [MAP v9] 2026-07-15 — Production-grade fixes:
 *   BUG-C: segment labels + area card persist after drawing completes
 *   BUG-D: MapUnsavedDialog gets "Save and Close" as primary action
 *   BUG-E: desktop modal expands to min(1400px, 96vw)
 *   BUG-A UX: auto-activate edit mode after polygon creation
 *   GAP-1: auto-save after polygon creation (background)
 *   GAP-2: Enter key finishes drawing (finishOn: enter)
 *   GAP-3: vertex count in drawing guide tooltip
 *   GAP-6: default area unit = rai (Lao context)
 * [SaaS UPGRADE v6] 2026-07-14 — Reference app mobile UX upgrade:
 *   - Green polygon fill + white outline (reference app style)
 *   - Geoman draw style: green templine + hintline
 *   - Area + Perimeter floating card on map (always visible)
 *   - Drawing guide tooltip (top-center, step-aware)
 *   - Haptic feedback (navigator.vibrate 30ms per vertex)
 *   - Floating undo button on map during drawing
 *   - Leaflet scale bar (metric, bottom-right)
 * [SaaS UPGRADE v5] 2026-07-14 — Claude deep-dive improvements:
 *   - Segment distance labels on each polygon edge (SegmentLabelManager)
 *   - Offset drag crosshair for fat-finger fix on mobile (OffsetDragIndicator)
 *   - Perimeter display alongside area
 *   - Improved mobile UX: compact toolbar, drawing guide
 *   - pm:markerdragstart/end integration for offset indicator
 *   - pm:edit triggers segment label update
 */

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AnchoredSelectPopover } from "./ui/AnchoredSelectPopover";
import {
  Save,
  Trash2,
  FileDown,
  FileText,
  X,
  Layers,
  MapPin,
  MapPinned,
  Ruler,
  AlertTriangle,
  Loader2,
  RotateCcw,
  RotateCw,
  Undo2,
  Crosshair,
  Navigation,
  Hexagon,
  Maximize,
  Pencil,
  Hand,
  Eraser,
  History,
  Clock,
  Redo2,
  Plus,
  List,
  Tag,
  Eye,
  EyeOff,
  Check,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { MapLoadingSkeleton } from "./MapLoadingSkeleton";
import { MapUnsavedDialog } from "./MapUnsavedDialog";
import { getCollateralMapEvidenceCapability } from "../lib/collateralMapPolicy";
import { getCollateralStaffReference } from "../lib/collateralStaffReference";
import { MapConfirmDialog, type MapConfirmState } from "./MapConfirmDialog";
import { MapLayerSelectorPopover, type MapLayerSelectorTrace } from "./crs/MapLayerSelectorPopover";
import { useCollateralMap, type MapTileLayer, type CollateralMapPDFInfo } from "../hooks/useCollateralMap";
import { buildFeatureCollection, type GeoJsonPolygon } from "../lib/geoJsonUtils";
import { COLLATERAL_MAP } from "../../../shared/constants/collateralMap";
import {
  createStreetViewCoverageRequest,
  getStreetViewOffsetMeters,
  isCurrentStreetViewRequest,
  isValidStreetViewCoordinate,
  resolveStreetViewCoverageState,
  type StreetViewSessionState,
} from "../lib/streetViewSession";
import { SegmentLabelManager, extractOuterRing, computeAreaPerimeter } from "../lib/segmentLabels";
import { buildCenterLabelHtml, CENTER_LABEL_ICON_CLASS, updateCenterLabelContent } from "../lib/mapCenterLabel";
import { getManagedPolygonSource, removeManagedPolygon } from "../lib/mapPolygonSource";
import { resolveMapLayerSelection } from "../lib/mapLayerSwitchPolicy";
import { shouldEnableMapLayerTrace } from "../lib/mapLayerTraceAccess";
import { isCurrentLiveMidpointFrame } from "../lib/drawMidpointProjection";
import { buildLiveDrawSegment } from "../lib/drawLiveSegment";
import { shouldRunDrawLabelReanchor } from "../lib/drawLabelReanchor";
import {
  beginDrawGesture,
  consumeSyntheticClickSuppression,
  finishDrawGesture,
  idleDrawGesture,
  shouldAcceptDrawingVertex,
  shouldClearTransientDrawingFeedback,
  updateDrawGesture,
} from "../lib/drawGestureState";
import { OffsetDragIndicator } from "../lib/offsetDragIndicator";
import { calcPerimeterFromLatLngs, formatPerimeter } from "../lib/areaUnitConversion";
import CollateralMapPDFTemplate, { type CollateralMapPDFTemplateHandle } from "./CollateralMapPDFTemplate";
import { trpc } from "../lib/trpc";
import html2canvas from "html2canvas";
import { toast as sonnerToast } from "sonner";
import GoogleMutantClass from "leaflet.gridlayer.googlemutant/src/Leaflet.GoogleMutant.mjs";
import { LandmarkProximityPanel } from "./crs/LandmarkProximityPanel";
import {
  buildLandmarkLabelHtml,
  buildLandmarkTooltipText,
  LANDMARK_APP_FONT_STACK,
  resolveLandmarkLabelLayout,
  type LandmarkLabelPlacement,
} from "../lib/landmarkPresentation";

// ============================================================
// Types
// ============================================================

interface Coords {
  lat: number;
  lng: number;
}

/**
 * V402 — Presentation-only source of truth for a single audited Landmark.
 * This deliberately contains no Leaflet layers or persisted state: one
 * immutable evidence record yields exactly one origin, endpoint, two-point
 * line, and mathematical midpoint for every visual part of the relationship.
 */
interface LandmarkRenderGeometry {
  key: string;
  evidenceId: number;
  revision: number;
  origin: [number, number];
  landmark: [number, number];
  midpoint: [number, number];
  meters: number;
}

function buildLandmarkRenderGeometry(item: any): LandmarkRenderGeometry | null {
  const originLat = Number(item.originLatitude);
  const originLng = Number(item.originLongitude);
  const landmarkLat = Number(item.latitude);
  const landmarkLng = Number(item.longitude);
  const meters = Number(item.straightLineMeters);
  const evidenceId = Number(item.id);
  const revision = Number(item.revision ?? 0);
  if (![originLat, originLng, landmarkLat, landmarkLng, meters, evidenceId, revision].every(Number.isFinite) || meters < 0 || meters > 1_000_000) {
    return null;
  }
  return {
    key: `${evidenceId}@r${revision}`,
    evidenceId,
    revision,
    origin: [originLat, originLng],
    landmark: [landmarkLat, landmarkLng],
    midpoint: [(originLat + landmarkLat) / 2, (originLng + landmarkLng) / 2],
    meters,
  };
}

/** v377 legacy surface signature: type MapSurface = 'none' | 'layers' | 'edit-tools' | 'street-view'; */
/** v377/v390: map controls may present only one transient surface at a time. */
type MapSurface = 'none' | 'layers' | 'edit-tools' | 'street-view' | 'landmarks' | 'measure';
type MapLayerChooserValue = MapTileLayer | 'google:roadmap' | 'google:hybrid' | 'google:terrain';

export function StreetViewOverlayCloseButton({
  onClose,
  children,
  ariaLabel,
  className,
}: {
  onClose: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
  className: string;
}) {
  return (
    <button type="button" onClick={onClose} aria-label={ariaLabel} className={className}>
      {children}
    </button>
  );
}

// ============================================================
// Tile configurations
// ============================================================

// V498: Keep the public OSM provider informed of the application origin without
// disclosing a loan/collateral path or query string. This is intentionally scoped
// to OSM image requests; Helmet's document-level security defaults stay unchanged.
const OSM_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_TILE_OPTIONS = {
  referrerPolicy: "strict-origin-when-cross-origin" as ReferrerPolicy,
};

function createOsmTileLayer(
  leaflet: any,
  options: Record<string, unknown>,
  onTileError: () => void,
  onTileLoad: () => void,
) {
  const layer = leaflet.tileLayer(OSM_TILE_URL, {
    ...options,
    ...OSM_TILE_OPTIONS,
  });
  layer.on("tileerror", onTileError);
  layer.on("tileload", onTileLoad);
  return layer;
}

const TILE_CONFIGS: Record<Exclude<MapTileLayer, "google">, { url: string; attribution: string; maxZoom: number; maxNativeZoom: number }> = {
  streets: {
    url: OSM_TILE_URL,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxNativeZoom: 19,
    maxZoom: 22,
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
    // ESRI World Imagery native tiles go up to zoom 19; allow overzoom to 22
    maxNativeZoom: 19,
    maxZoom: 22,
  },
  hybrid: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
    maxNativeZoom: 19,
    maxZoom: 22,
  },
  // Sprint v168.8: MapTiler Satellite — maxNativeZoom 22 (same as Google Maps)
  // URL is a placeholder; actual key is injected at runtime via getTileConfig()
  maptiler: {
    url: "https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=MAPTILER_KEY_PLACEHOLDER",
    attribution: '<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>',
    maxNativeZoom: 22,
    maxZoom: 22,
  },
};

/** Sprint v171: Load Google Maps JS API script once (idempotent)
 * Uses callback= parameter so google.maps is guaranteed ready when promise resolves.
 * loading=async removed because it conflicts with callback= pattern.
 */
function loadGoogleMapsScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).google?.maps) { resolve(); return; }
    // If script already injected, poll until google.maps is ready
    const existing = document.getElementById("google-maps-script");
    if (existing) {
      const poll = setInterval(() => {
        if ((window as any).google?.maps) { clearInterval(poll); resolve(); }
      }, 100);
      setTimeout(() => { clearInterval(poll); reject(new Error("Google Maps timeout")); }, 10000);
      return;
    }
    // Use callback= so google.maps is fully initialized before resolve
    const callbackName = "__gmapsReady_" + Date.now();
    (window as any)[callbackName] = () => {
      delete (window as any)[callbackName];
      resolve();
    };
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      delete (window as any)[callbackName];
      reject(new Error("Failed to load Google Maps"));
    };
    document.head.appendChild(script);
  });
}

/** Sprint v168.8: Return tile config with MapTiler key injected at runtime */
function getTileConfig(layer: Exclude<MapTileLayer, "google">, maptilerKey: string) {
  const cfg = TILE_CONFIGS[layer];
  if (layer === "maptiler" && maptilerKey) {
    return { ...cfg, url: `https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${maptilerKey}` };
  }
  return cfg;
}

const LABEL_OVERLAY_URL = OSM_TILE_URL;

// ============================================================
// Helpers
// ============================================================

function formatCoords(lat: number, lng: number): string {
  const latDir = lat >= 0 ? "N" : "S";
  const lngDir = lng >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(5)}°${latDir}  ${Math.abs(lng).toFixed(5)}°${lngDir}`;
}

function calcAreaFromLatLngs(latLngs: any[]): number {
  if (latLngs.length < 3) return 0;
  const R = 6371000;
  let area = 0;
  const n = latLngs.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const lat1 = (latLngs[i].lat * Math.PI) / 180;
    const lat2 = (latLngs[j].lat * Math.PI) / 180;
    const dLng = ((latLngs[j].lng - latLngs[i].lng) * Math.PI) / 180;
    area += (dLng * (2 + Math.sin(lat1) + Math.sin(lat2)));
  }
  return Math.abs((area * R * R) / 2);
}

// Sprint v145: compute visual centroid of a polygon (average of vertices)
function calcPolygonCentroid(latLngs: any[]): { lat: number; lng: number } {
  if (!latLngs || latLngs.length === 0) return { lat: 0, lng: 0 };
  const flat = Array.isArray(latLngs[0]) ? latLngs[0] : latLngs;
  const sumLat = flat.reduce((s: number, p: any) => s + (p.lat ?? p[0] ?? 0), 0);
  const sumLng = flat.reduce((s: number, p: any) => s + (p.lng ?? p[1] ?? 0), 0);
  return { lat: sumLat / flat.length, lng: sumLng / flat.length };
}

type AreaUnit = "sqm" | "rai" | "hectare";

function formatAreaByUnit(sqm: number, unit: AreaUnit): string {
  if (sqm <= 0) return "—";
  switch (unit) {
    case "sqm":
      return `${Math.round(sqm).toLocaleString("en-US")} m²`;
    case "rai": {
      const rai = Math.floor(sqm / 1600);
      const ngan = Math.floor((sqm % 1600) / 400);
      const wa = Math.floor((sqm % 400) / 4);
      const parts: string[] = [];
      if (rai > 0) parts.push(`${rai} ໄຮ`);
      if (ngan > 0) parts.push(`${ngan} ງານ`);
      if (wa > 0 || parts.length === 0) parts.push(`${wa} ຕ.ວ.`);
      return parts.join(" ");
    }
    case "hectare":
      return `${(sqm / 10000).toFixed(4)} ເຮັກຕ້າ`;
    default:
      return `${Math.round(sqm).toLocaleString("en-US")} m²`;
  }
}

function formatMeasureDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters <= 0) return "0 m";
  return meters >= 1_000
    ? `${(meters / 1_000).toFixed(meters >= 10_000 ? 1 : 2)} km`
    : `${Math.round(meters)} m`;
}

// Field routes commonly need five manually placed points. Keep every segment
// label readable through that workflow; density reduction begins only after
// six segments, never immediately after a completed drag at point four/five.
const SMART_MEASURE_FULL_LABEL_SEGMENTS = 5;

function shouldShowSmartMeasureSegmentLabel(segmentIndex: number, segmentCount: number, activePointIndex: number | null) {
  if (segmentCount <= SMART_MEASURE_FULL_LABEL_SEGMENTS) return true;
  const isActiveSegment = activePointIndex !== null && (segmentIndex === activePointIndex || segmentIndex === activePointIndex - 1);
  return segmentIndex === 0 || segmentIndex === segmentCount - 1 || isActiveSegment;
}

function createSmartMeasureSegmentIcon(meters: number, segmentIndex: number, visible = true) {
  const labelOffsetY = segmentIndex % 2 === 0 ? -18 : 18;
  return L.divIcon({
    className: 'crs-smart-measure-segment',
    html: `<span data-smart-measure-segment-label="true" data-smart-measure-segment-index="${segmentIndex}" aria-label="ໄລຍະຊ່ວງວັດ ${segmentIndex + 1}: ${formatMeasureDistance(meters)}" style="display:${visible ? 'block' : 'none'};min-width:44px;text-align:center;white-space:nowrap;border:1px solid rgba(255,255,255,.45);border-radius:999px;background:#0f172a;color:#fff;padding:5px 10px;font:700 13px/1 system-ui;letter-spacing:.01em;box-shadow:0 2px 8px rgba(0,0,0,.38);font-variant-numeric:tabular-nums;transform:translateY(${labelOffsetY}px)">${formatMeasureDistance(meters)}</span>`,
    iconAnchor: [42, 15],
  });
}

const MAX_SMART_MEASURE_POINTS = 20;
const MIN_SMART_MEASURE_POINT_METERS = 2;

function getManagedPolygonOrigin(feature: any): [number, number] | null {
  const geometry = feature?.geometry;
  const ring = geometry?.type === 'Polygon'
    ? geometry.coordinates?.[0]
    : geometry?.type === 'MultiPolygon'
      ? geometry.coordinates?.[0]?.[0]
      : null;
  if (!Array.isArray(ring) || ring.length < 3) return null;
  const vertices = ring
    .map((coordinate: any) => ({ lat: Number(coordinate?.[1]), lng: Number(coordinate?.[0]) }))
    .filter((coordinate: any) => Number.isFinite(coordinate.lat) && Number.isFinite(coordinate.lng));
  if (vertices.length < 3) return null;
  const centroid = calcPolygonCentroid(vertices);
  return Number.isFinite(centroid.lat) && Number.isFinite(centroid.lng)
    ? [centroid.lat, centroid.lng]
    : null;
}

// ============================================================
// Sprint v109: Module-level getSafeLatLngs helper
// Handles both L.Polygon (no getLayers) and L.geoJSON wrapper
// Used by pm:rotateend, handleUndo, handleRedo, and any code outside loadSavedPolygon scope
// ============================================================
function getSafeLatLngsModule(l: any): any[] {
  if (typeof l.getLatLngs === 'function') {
    const raw = l.getLatLngs();
    return Array.isArray(raw[0]) ? raw[0] : raw;
  }
  if (typeof l.getLayers === 'function') {
    const sub = l.getLayers()[0];
    if (sub && typeof sub.getLatLngs === 'function') {
      const raw = sub.getLatLngs();
      return Array.isArray(raw[0]) ? raw[0] : raw;
    }
  }
  return [];
}

// ============================================================
// Sprint v115 Fix #1: Single polygon color palette — used everywhere (load, undo, redo, polygon list)
// Previously 3 separate arrays caused colors to change on undo/redo
// ============================================================
const POLYGON_COLORS = ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];
// Sprint v131: extended palette for custom color picker (8 colors)
const POLYGON_COLOR_PALETTE = [
  "#22c55e", // ຂຽວ
  "#3b82f6", // ຟ້າ
  "#f59e0b", // ເຫຼືອງ
  "#ef4444", // ແດງ
  "#8b5cf6", // ມ່ວງ
  "#06b6d4", // cyan
  "#f97316", // ສົ້ມ
  "#ec4899", // ບົວ
];

// Module-scoped because collateral switch options are derived before JSX render
// helpers. Keeping this map out of component TDZ guarantees the selector can
// render on both mobile and desktop before late UI helpers are initialized.
const collateralTypeMap: Record<string, string> = {
  land: "ທີ່ດິນ", building: "ເຮືອນ/ອາຄານ", vehicle: "ລົດ", deposit: "ເງິນຝາກ", other: "ອື່ນໆ",
};

// ============================================================
// Lazy Leaflet ref
// ============================================================

let L: any = null;

// ============================================================
// Props
// ============================================================

/** Sprint v163: Lightweight collateral info for switcher + ghost layers */
export interface CollateralSwitcherItem {
  id: number;
  collateralType: string;
  buildingSubType?: string | null;
  ownerName?: string | null;
  landTitleNumber?: string | null;
  documentNumber?: string | null;
  gpsLatitude?: string | number | null;
  gpsLongitude?: string | number | null;
}

interface CollateralMapModalProps {
  open: boolean;
  onClose: () => void;
  collateralId: number;
  loanApplicationId: number;
  collateralType?: string;
  collateralSubType?: string | null;
  ownerName?: string;
  landTitleNumber?: string;
  documentNumber?: string;
  gpsLatitude?: string | number;
  gpsLongitude?: string | number;
  canEdit: boolean;
  maptilerKey: string;
  /** Sprint v163: All collaterals in the same loan — enables switcher + ghost layers */
  allCollaterals?: CollateralSwitcherItem[];
  /** Sprint v163: Callback when user switches to a different collateral */
  onSwitchCollateral?: (collateralId: number) => void;
}

// ============================================================
// Component
// ============================================================

export function CollateralMapModal({
  open,
  onClose,
  collateralId,
  loanApplicationId,
  collateralType,
  collateralSubType,
  ownerName,
  landTitleNumber,
  documentNumber,
  gpsLatitude,
  gpsLongitude,
  canEdit,
  allCollaterals,
  onSwitchCollateral,
  maptilerKey = "",
}: CollateralMapModalProps) {
  const activeCollateral = allCollaterals?.find((col) => col.id === collateralId);
  const activeMapEvidence = getCollateralMapEvidenceCapability({
    collateralType: activeCollateral?.collateralType ?? collateralType,
    buildingSubType: activeCollateral?.buildingSubType ?? collateralSubType,
    landTitleNumber: activeCollateral?.landTitleNumber ?? landTitleNumber,
    documentNumber: activeCollateral?.documentNumber ?? documentNumber,
  });
  const canEditMapBoundary = canEdit && activeMapEvidence.allowsBoundary;
  const mapFallbackGoogleUrl = Number.isFinite(Number(gpsLatitude)) && Number.isFinite(Number(gpsLongitude))
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${Number(gpsLatitude)},${Number(gpsLongitude)}`)}`
    : null;

  // Refs
  // v375: The portal target must be React state, not ref.current. DialogContent
  // attaches refs after the first render; passing ref.current then would leave a
  // selector menu portalled under document.body, where Radix disables pointers.
  const [mapDialogPortalElement, setMapDialogPortalElement] = useState<HTMLDivElement | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<any>(null);
  const mobileLandmarkTriggerRef = useRef<HTMLButtonElement | null>(null);
  const tileLayerRef = useRef<any>(null);
  // Every selection increments this generation. A late Google script promise from a
  // superseded selection must never overwrite the user's newer base layer choice.
  const tileLayerSwitchGenerationRef = useRef(0);
  // Google sublayers use a second generation so a late type selection cannot
  // replace a newer one while the optional provider is still initializing.
  const googleSubLayerSwitchGenerationRef = useRef(0);
  const labelLayerRef = useRef<any>(null);
  // V498: tile errors arrive outside React. Keep the threshold and one-time
  // notice session-scoped so repeated failed images cannot spam staff.
  const osmTileErrorCountRef = useRef(0);
  const osmTileErrorReportedRef = useRef(false);
  // Landmark evidence is presentation-only and intentionally never joins the
  // editable polygon/measure layers or GeoJSON draft lifecycle.
  const landmarkEvidenceLayerRef = useRef<any>(null);
  // V400: maintain ownership of every presentation group explicitly. A delayed
  // Leaflet cleanup may remove only the group it created; it must never leave a
  // previous evidence group painting a stale marker, line, or distance badge.
  const landmarkEvidenceGroupsRef = useRef<Set<any>>(new Set());
  const landmarkTraceEnabledRef = useRef(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1',
  );
  const [landmarkTraceSummary, setLandmarkTraceSummary] = useState('');
  // A selected-but-unsaved landmark owns a separate presentation layer. It must
  // never join evidence, measure, or editable geometry layers.
  const landmarkPreviewLayerRef = useRef<any>(null);
  // G9: polygonLayerRef = active (last drawn/edited) layer; polygonLayersRef = all layers
  const polygonLayerRef = useRef<any>(null);
  const polygonLayersRef = useRef<any[]>([]);  // G9: all polygon layers on map
  const draftGeoJsonRef = useRef<any>(null);  // G7+G9: always-current draftGeoJson for undo snapshots
  // v274: block stale saved-boundary hydration and duplicate Geoman callbacks
  // while a manager delete is persisting its authoritative next boundary.
  const isBoundaryDeletionRef = useRef(false);
  // Sprint v145: center label markers (name + area) shown at polygon centroid
  const centerLabelMarkersRef = useRef<any[]>([]);
  // Keep the last committed visibility state available to map callbacks that run
  // outside React's render cycle (Leaflet events, delayed redraws, and overlays).
  const hiddenPolygonsRef = useRef<Set<number>>(new Set());
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const segmentLabelMgrRef = useRef<SegmentLabelManager | null>(null);
  const offsetDragRef = useRef<OffsetDragIndicator | null>(null);
  // O1 v42: PDF template ref — fonts pre-loaded in DOM
  const pdfTemplateRef = useRef<CollateralMapPDFTemplateHandle | null>(null);
  const initCalledRef = useRef(false);
  const isDrawingRef = useRef(false);
  const drawingVerticesRef = useRef<any[]>([]);
  // Stable refs to break initMap dependency chain (prevents timer cancel on re-render)
  const canEditRef = useRef(canEdit);
  const gpsLatRef = useRef(gpsLatitude);
  const gpsLngRef = useRef(gpsLongitude);
  canEditRef.current = canEdit;
  gpsLatRef.current = gpsLatitude;
  gpsLngRef.current = gpsLongitude;
  // NEW: track drawMode in ref so loadSavedPolygon can restore edit mode after refetch
  const drawModeRef = useRef<"polygon" | "rectangle" | "edit" | "drag" | "delete" | null>(null);

  // v377: Map controls use one compact, touch-safe composition below the desktop
  // dock breakpoint. This deliberately includes tablets (768–1023px) so they do
  // not receive the former overlapping desktop rails.
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

  // Sprint v146: renderCenterLabels defined EARLY (before any useEffect that references it)
  // Root cause of crash: was defined at line ~3655 but used in useEffect dep at line ~535
  // In production build, const useCallback is NOT hoisted → temporal dead zone → crash
  const renderCenterLabels = useCallback((hiddenIndexes: ReadonlySet<number> = hiddenPolygonsRef.current) => {
    const map = leafletMapRef.current;
    if (!map || !L) return;
    // Clear old markers
    for (const m of centerLabelMarkersRef.current) { try { map.removeLayer(m); } catch {} }
    centerLabelMarkersRef.current = [];
    const draft = draftGeoJsonRef.current as any;
    if (!draft?.features?.length) return;
    draft.features.forEach((feat: any, idx: number) => {
      try {
        // A hidden polygon must never leave its passive centre label on the map.
        if (hiddenIndexes.has(idx)) return;
        const lls = polygonLayersRef.current[idx]?.getLatLngs?.();
        if (!lls) return;
        const flat = Array.isArray(lls[0]) ? lls[0] : lls;
        const centroid = calcPolygonCentroid(flat);
        const label = feat.properties?.label || `ຜືນທີ ${idx + 1}`;
        const areaSqM = feat.properties?.areaSqM ?? 0;
        const icon = L.divIcon({
          // Passive content label: it must never inherit an edit-marker surface.
          className: CENTER_LABEL_ICON_CLASS,
          iconSize: [0, 0],
          html: buildCenterLabelHtml(label, areaSqM),
          iconAnchor: [0, 0],
        });
        const marker = L.marker([centroid.lat, centroid.lng], { icon, interactive: false, zIndexOffset: 500 });
        marker.addTo(map);
        centerLabelMarkersRef.current.push(marker);
      } catch {}
    });
  }, []);

  // Sprint v93 B1: shared helper for enableGlobalEditMode options (component-level, stable)
  // Defined outside useEffect so attachMobileEditHandler and handleEditMode can use it.
  // Sprint v108 Fix 1: Remove markerStyle from _editModeOpts.
  // Root cause: markerStyle applies to ALL Geoman edit markers (vertex + midpoint).
  // Passing midpoint icon here caused vertex markers to render at midpoint size (12px)
  // making vertex and midpoint visually indistinguishable.
  // The Geoman prototype monkey-patches (_createMarker, _createMiddleMarker) at lines ~509-540
  // already correctly assign vertex=marker-icon (20px) and midpoint=marker-icon-middle (12px).
  // So markerStyle is not needed — removing it lets the monkey-patches work correctly.
  const _editModeOpts = useCallback(() => {
    return {
      allowSelfIntersection: false,
      // Sprint v108: NO markerStyle here — monkey-patches handle vertex vs midpoint icons
    };
  }, []);

  // Sprint v73 STEP C: mapMode state machine
  const [mapMode, setMapMode] = useState<'view' | 'draw' | 'edit'>('view');
  // v377: Layer picker, contextual edit tools, and Street View share one
  // presentation state. They are visual-only surfaces and never participate in
  // geometry draft/dirty tracking.
  const [activeSurface, setActiveSurface] = useState<MapSurface>('none');
  const showMobileMoreSheet = activeSurface === 'edit-tools';
  const setShowMobileMoreSheet = useCallback((visible: boolean) => {
    setActiveSurface(visible ? 'edit-tools' : 'none');
  }, []);
  // Layer/provider changes are presentation actions, never geometry actions.
  // Lock them only while a confirmed handle drag owns the active pointer.
  const [isMapControlInteractionLocked, setIsMapControlInteractionLocked] = useState(false);
  const setMapControlInteractionLocked = useCallback((locked: boolean) => {
    setIsMapControlInteractionLocked(previous => previous === locked ? previous : locked);
  }, []);
  const [showMobileDeleteConfirm, setShowMobileDeleteConfirm] = useState(false);
  const [showMobilePolySheet, setShowMobilePolySheet] = useState(false); // Sprint v135: mobile polygon management bottom sheet
  // V395: deleting one polygon is a local draft operation. Persistence remains
  // exclusively behind the existing green Save action / explicit clear confirm.
  const [pendingManagedPolygonDeleteIndex, setPendingManagedPolygonDeleteIndex] = useState<number | null>(null);
  // Sprint v73 STEP E: draw vertex count (state so JSX re-renders)
  const [drawVertexCount, setDrawVertexCount] = useState(0);
  const mapModeRef = useRef<'view' | 'draw' | 'edit'>('view');
  // Keep ref in sync with state
  useEffect(() => { mapModeRef.current = mapMode; }, [mapMode]);
  // Sprint v73 STEP C: ref to attachMobileEditHandler (declared later as useCallback)
  // Using ref pattern so it can be called inside the map useEffect without stale closure
  const attachMobileEditHandlerRef = useRef<(layer: any) => void>(() => {});
  // Sprint v73 STEP H: DOM ref for live measurement text in edit strip (no React re-render during drag)
  const liveMeasureRef = useRef<HTMLSpanElement | null>(null);
  // Sprint v96: DOM refs for desktop info bar live measure during vertex drag (zero React re-renders)
  const desktopAreaRef = useRef<HTMLSpanElement | null>(null);
  const desktopPerimRef = useRef<HTMLSpanElement | null>(null);
  // Sprint v80: DOM ref for live measurement text in draw strip (no React re-render during vertex add)
  const drawLiveMeasureRef = useRef<HTMLSpanElement | null>(null);
  // Sprint v83 Fix B: ref to store the latest projected pan measure text
  // Updated imperatively during pan (no re-render). useLayoutEffect prefers this over state.
  const projectedMeasureRef = useRef<string>('');
  // Sprint v73 STEP H: stable ref for areaUnit (avoids stale closure in attachLiveMeasure)
  const areaUnitRef = useRef<AreaUnit>("rai");
  // Sprint v73 STEP H: ref to attachLiveMeasure (declared later as useCallback)
  const attachLiveMeasureRef = useRef<(layer: any) => void>(() => {});
  // Sprint v120: ref to _buildDrawMidpoints (declared inside pm:drawstart closure)
  // Pattern mirrors attachLiveMeasureRef — allows handleUndoVertex (component scope) to call it
  const buildDrawMidpointsRef = useRef<(verts: any[]) => void>(() => {});
  // Sprint v110 M2: ref to peek bar for ResizeObserver (attribution fix)
  const peekBarRef = useRef<HTMLDivElement | null>(null);
  const mobileMapHeaderRef = useRef<HTMLDivElement | null>(null);
  const mobileNavigationDockRef = useRef<HTMLDivElement | null>(null);
  // v379: the layer trigger lives inside the fixed mobile pill. The dialog-owned
  // popover must open in the remaining map rectangle instead of covering any
  // fixed mobile control or the browser gesture-safe edge.
  const getMobileLayerPopoverAvoidRects = useCallback(() => {
    const rectFor = (element: HTMLElement | null) => {
      const rect = element?.getBoundingClientRect();
      return rect && rect.width > 0 && rect.height > 0
        ? { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width }
        : null;
    };
    const visualViewport = window.visualViewport;
    const viewportTop = visualViewport?.offsetTop ?? 0;
    const viewportLeft = visualViewport?.offsetLeft ?? 0;
    const viewportWidth = visualViewport?.width ?? window.innerWidth;
    const viewportHeight = visualViewport?.height ?? window.innerHeight;
    const gestureInset = 16;
    const gestureSafeEdge = {
      top: viewportTop + viewportHeight - gestureInset,
      left: viewportLeft,
      right: viewportLeft + viewportWidth,
      bottom: viewportTop + viewportHeight,
      width: viewportWidth,
    };
    return [
      rectFor(mobileMapHeaderRef.current),
      rectFor(mobileNavigationDockRef.current),
      rectFor(peekBarRef.current),
      gestureSafeEdge,
    ].filter((rect): rect is NonNullable<typeof rect> => rect !== null);
  }, []);

  // State
  const [mapReady, setMapReady] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingCollateralSwitchId, setPendingCollateralSwitchId] = useState<number | null>(null);
  // Sprint v115 Fix #4: branded confirm dialog state (replaces window.confirm)
  const [confirmState, setConfirmState] = useState<MapConfirmState | null>(null);
  const [hoverCoords, setHoverCoords] = useState<Coords | null>(null);
  const [liveArea, setLiveArea] = useState<number>(0);
  const [livePerimeter, setLivePerimeter] = useState<number>(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [canUndoVertex, setCanUndoVertex] = useState(false);
  const [showLabelOverlay, setShowLabelOverlay] = useState(false);
  const [osmTilesUnavailable, setOsmTilesUnavailable] = useState(false);
  const [savedPerimeter, setSavedPerimeter] = useState<number>(0);
  // Custom drawing toolbar mode state
  const [drawMode, setDrawMode] = useState<"polygon" | "rectangle" | "edit" | "drag" | "delete" | null>(null);
  // Keep drawModeRef in sync (direct assignment in render is safe for refs)
  drawModeRef.current = drawMode;
  // Android needs browser panning suppressed while a Geoman edit handle owns the
  // gesture, but this must be strictly session-scoped. The cleanup runs when the
  // mode changes, the dialog closes/unmounts, or the selected collateral changes.
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!isMobile || !map) return;
    const container = map.getContainer();
    container.style.touchAction = drawMode === "edit" ? "none" : "";
    return () => {
      container.style.touchAction = "";
    };
  }, [drawMode, isMobile, open, collateralId]);
  // Area unit selector
  // GAP-6: default to Lao rai/ngan/taowa (target users are Lao loan officers)
  const [areaUnit, setAreaUnit] = useState<AreaUnit>(() => {
    // Sprint v110 S5: persist area unit preference across sessions
    // Sprint v110.1: fixed validator — was accepting 'sqwa'/'ngan' (not valid AreaUnit) and rejecting 'hectare' (valid)
    // Sprint v143: default changed from 'rai' → 'sqm' (Lao users prefer m²/ha; rai still selectable)
    try { const s = localStorage.getItem('crs_map_areaUnit'); if (s === 'rai' || s === 'sqm' || s === 'hectare') return s as AreaUnit; } catch {}
    return 'sqm';
  });
  // Sprint v73 STEP H: keep areaUnitRef in sync (stable ref for use in Leaflet event handlers)
  areaUnitRef.current = areaUnit;
  // Sprint v110 S5: persist area unit on change
  useEffect(() => { try { localStorage.setItem('crs_map_areaUnit', areaUnit); } catch {} }, [areaUnit]);
  // BUG-A UX: show edit hint after polygon creation
  const [showEditHint, setShowEditHint] = useState(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Sprint v100 B9: store _drawMeasureInterval in a ref so modal-close cleanup can clear it
  // even if the modal closes mid-draw (local variable inside pm:drawstart would be lost)
  const _drawMeasureIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // GAP-7: Coordinate navigation panel
  const [showCoordPanel, setShowCoordPanel] = useState(false);
  // v377: Compact anchored Layer Chooser state (derived from activeSurface).
  const showTilePanel = activeSurface === 'layers';
  const [coordLat, setCoordLat] = useState("");
  const [coordLng, setCoordLng] = useState("");
  const [coordError, setCoordError] = useState("");
  const [coordMapsLink, setCoordMapsLink] = useState("");
  const tempMarkerRef = useRef<any>(null);

  const clearOsmTileFailure = useCallback(() => {
    osmTileErrorCountRef.current = 0;
    osmTileErrorReportedRef.current = false;
    setOsmTilesUnavailable(false);
    sonnerToast.dismiss("collateral-osm-tiles-unavailable");
  }, []);

  const handleOsmTileError = useCallback(() => {
    osmTileErrorCountRef.current += 1;
    if (osmTileErrorCountRef.current < 3 || osmTileErrorReportedRef.current) return;
    osmTileErrorReportedRef.current = true;
    setOsmTilesUnavailable(true);
    sonnerToast.warning("ໂຫຼດແຜນທີ່ OpenStreetMap ບໍ່ສຳເລັດ", {
      id: "collateral-osm-tiles-unavailable",
      description: "ພິກັດ GPS ແລະຂໍ້ມູນຫຼັກຊັບຍັງຄົງຢູ່. ສະຫຼັບແຜນທີ່ ຫຼື ເປີດ Google Maps ໄດ້.",
    });
  }, []);

  const handleOsmTileLoad = useCallback(() => {
    osmTileErrorCountRef.current = 0;
    if (!osmTileErrorReportedRef.current) return;
    osmTileErrorReportedRef.current = false;
    setOsmTilesUnavailable(false);
    sonnerToast.dismiss("collateral-osm-tiles-unavailable");
  }, []);

  // GAP-8 / V407: Smart Field Measure has explicit temporary presentation states.
  // `measuring` owns map taps; `review` retains the local-only route but releases
  // map interaction; `idle` owns no temporary measure presentation.
  const [measureMode, setMeasureMode] = useState(false);
  const [measureStage, setMeasureStage] = useState<'idle' | 'measuring' | 'review'>('idle');
  const measureStageRef = useRef<'idle' | 'measuring' | 'review'>('idle');
  measureStageRef.current = measureStage;
  const measureLayerRef = useRef<any>(null);
  const [landmarkCaptureMode, setLandmarkCaptureMode] = useState(false);
  const landmarkCaptureModeRef = useRef(false);
  landmarkCaptureModeRef.current = landmarkCaptureMode;
  const [landmarkPickedCoordinate, setLandmarkPickedCoordinate] = useState<{ lat: number; lng: number } | null>(null);
  // V394: local presentation context only. This never joins Landmark persistence,
  // map geometry, drafts, calculations, or workflow state.
  const [focusedLandmarkId, setFocusedLandmarkId] = useState<number | null>(null);
  // Presentation-only revision. Re-layout happens after a zoom, never while
  // panning or editing, so labels cannot produce map draft or geometry changes.
  const [landmarkLabelLayoutRevision, setLandmarkLabelLayoutRevision] = useState(0);
  // V400: one presentation-only frame per collateral/evidence revision. This
  // prevents an asynchronously hydrated Landmark endpoint from living outside
  // the initial Leaflet viewport without changing saved GPS, GeoJSON, drafts,
  // or the user's subsequent manual map position.
  const landmarkInitialFrameKeyRef = useRef<string | null>(null);
  const landmarkInitialFrameStatusRef = useRef('pending');
  const { data: savedLandmarkEvidence = [] } = trpc.collaterals.getLandmarkProximities.useQuery(
    { collateralId },
    { enabled: Boolean(collateralId) },
  );
  useEffect(() => {
    if (!open) setFocusedLandmarkId(null);
  }, [open]);
  useEffect(() => {
    setFocusedLandmarkId(null);
  }, [collateralId]);
  useEffect(() => {
    setFocusedLandmarkId(previous => previous !== null && !(savedLandmarkEvidence as any[]).some((item) => Number(item.id) === previous) ? null : previous);
  }, [savedLandmarkEvidence]);
  useEffect(() => {
    if (activeSurface !== 'none' && activeSurface !== 'landmarks') setFocusedLandmarkId(null);
  }, [activeSurface]);
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !mapReady) return;
    const refreshLabelLayout = () => setLandmarkLabelLayoutRevision((revision) => revision + 1);
    map.on('zoomend', refreshLabelLayout);
    return () => { map.off('zoomend', refreshLabelLayout); };
  }, [mapReady]);
  const handleStartLandmarkMapPick = useCallback(() => {
    const map = leafletMapRef.current;
    if (!map) return;
    map.pm.disableDraw();
    map.pm.disableGlobalEditMode();
    map.pm.disableGlobalDragMode();
    map.pm.disableGlobalRemovalMode();
      (map as any)._measureModeActive = false;
      measurePointsRef.current = [];
      measureLayerRef.current?.clearLayers?.();
    setDrawMode(null);
    setMapMode('view');
    setMeasureMode(false);
    setMeasureDistance(null);
    setMeasurePointCount(0);
    setShowMeasureManualEntry(false);
    setMeasureManualError('');
    setLandmarkPickedCoordinate(null);
    landmarkPreviewLayerRef.current?.clearLayers?.();
    setLandmarkCaptureMode(true);
    map.getContainer().style.cursor = 'crosshair';
  }, [setMapMode]);
  const handleCancelLandmarkMapPick = useCallback(() => {
    setLandmarkCaptureMode(false);
    if (leafletMapRef.current) leafletMapRef.current.getContainer().style.cursor = '';
  }, []);

  useEffect(() => {
    handleCancelLandmarkMapPick();
    setLandmarkPickedCoordinate(null);
    landmarkEvidenceLayerRef.current?.clearLayers?.();
    landmarkPreviewLayerRef.current?.clearLayers?.();
    landmarkInitialFrameKeyRef.current = null;
    landmarkInitialFrameStatusRef.current = 'pending';
  }, [collateralId, handleCancelLandmarkMapPick]);

  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !L) return;

    landmarkPreviewLayerRef.current?.clearLayers?.();
    landmarkPreviewLayerRef.current?.remove?.();
    if (activeSurface !== 'landmarks' || !landmarkPickedCoordinate) return;

    const group = L.layerGroup().addTo(map);
    landmarkPreviewLayerRef.current = group;
    const selectedIcon = L.divIcon({
      className: 'crs-landmark-pick-preview-pin',
      html: '<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;background:#f59e0b;border:3px solid #fff;transform:rotate(-45deg);box-shadow:0 2px 10px rgba(0,0,0,.38)"></div>',
      iconSize: [28, 28],
      iconAnchor: [14, 28],
    });
    L.marker([landmarkPickedCoordinate.lat, landmarkPickedCoordinate.lng], { icon: selectedIcon, interactive: false })
      .bindTooltip('ຈຸດທີ່ເລືອກ — ລໍຖ້າຢືນຢັນ', { direction: 'top', permanent: true, className: 'crs-landmark-pick-preview-label' })
      .addTo(group);

    return () => {
      group.clearLayers();
      map.removeLayer(group);
    };
  }, [activeSurface, landmarkPickedCoordinate]);

  // V399/V400: data can resolve before Leaflet assigns its map ref. Include
  // mapReady so saved evidence hydrates automatically, and keep each render
  // group independently owned so a delayed cleanup cannot paint stale UI.
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !L || !mapReady) return;

    // V403: Landmark evidence is read-only review data, never polygon geometry.
    // Keep every Landmark visual in its own non-interactive pane beneath Geoman
    // edit handles so edit mode cannot visually or behaviorally claim ownership.
    const landmarkEvidencePane = 'crs-landmark-evidence-pane';
    const pane = map.getPane(landmarkEvidencePane) ?? map.createPane(landmarkEvidencePane);
    pane.classList.add(landmarkEvidencePane);
    pane.style.zIndex = mapMode === 'edit' ? '370' : '430';
    pane.style.pointerEvents = 'none';

    landmarkEvidenceGroupsRef.current.forEach((previousGroup) => {
      try { previousGroup.clearLayers?.(); map.removeLayer(previousGroup); } catch {}
    });
    landmarkEvidenceGroupsRef.current.clear();
    const group = L.layerGroup().addTo(map);
    landmarkEvidenceGroupsRef.current.add(group);
    landmarkEvidenceLayerRef.current = group;
    const evidenceIdentity: string[] = [];
    const evidenceEndpoints: Array<[number, number]> = [];
    const renderedEvidence: Array<{
      geometry: LandmarkRenderGeometry;
      originMarker: any;
      landmarkMarker: any;
      labelMarker: any | null;
      label: LandmarkLabelPlacement;
      line: any;
      badgeMarker: any;
    }> = [];

    const labelCandidates = (savedLandmarkEvidence as any[])
      .map((item) => ({ item, geometry: buildLandmarkRenderGeometry(item) }))
      .filter((candidate): candidate is { item: any; geometry: LandmarkRenderGeometry } => candidate.geometry !== null)
      .map(({ geometry }) => {
        const point = map.latLngToContainerPoint(L.latLng(geometry.landmark[0], geometry.landmark[1]));
        return { id: geometry.evidenceId, x: point.x, y: point.y, meters: geometry.meters, isFocused: geometry.evidenceId === focusedLandmarkId };
      });
    const labelLayoutById = resolveLandmarkLabelLayout(labelCandidates, {
      isMobile,
      mapWidth: map.getSize().x,
      reservedRight: !isMobile && activeSurface === 'landmarks' ? 384 : 12,
    });

    (savedLandmarkEvidence as any[]).forEach((item) => {
      const geometry = buildLandmarkRenderGeometry(item);
      if (!geometry) return;
      evidenceIdentity.push(geometry.key);
      evidenceEndpoints.push(geometry.origin, geometry.landmark);
      const originIcon = L.divIcon({
        className: "crs-landmark-origin-pin",
        html: '<div style="width:22px;height:22px;border-radius:50%;background:#002975;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>',
        iconSize: [22, 22], iconAnchor: [11, 11],
      });
      const isFocused = geometry.evidenceId === focusedLandmarkId;
      const landmarkMarkerSize = isMobile ? 18 : 24;
      const landmarkIcon = L.divIcon({
        className: `crs-landmark-evidence-pin${isFocused ? ' crs-landmark-evidence-pin-focused' : ''}`,
        html: `<div data-landmark-marker-visual="true" style="width:${landmarkMarkerSize}px;height:${landmarkMarkerSize}px;border-radius:50% 50% 50% 0;background:#0f9d58;border:3px solid #fff;transform:rotate(-45deg);box-sizing:border-box;box-shadow:${isFocused ? '0 0 0 4px rgba(0,41,117,.38),0 2px 8px rgba(0,0,0,.42)' : '0 2px 7px rgba(0,0,0,.35)'}"></div>`,
        iconSize: [landmarkMarkerSize, landmarkMarkerSize], iconAnchor: [landmarkMarkerSize / 2, landmarkMarkerSize],
      });
      const evidenceOpacity = mapMode === 'edit' ? 0.68 : 0.9;
      const line = L.polyline([geometry.origin, geometry.landmark], { pane: landmarkEvidencePane, className: 'crs-landmark-evidence-line', color: "#0f9d58", weight: 3, dashArray: "7 8", opacity: evidenceOpacity, interactive: false, pmIgnore: true }).addTo(group);
      const originMarker = L.marker(geometry.origin, { pane: landmarkEvidencePane, icon: originIcon, interactive: false, keyboard: false, pmIgnore: true }).bindTooltip("ຫຼັກຊັບ", { direction: "top", pane: landmarkEvidencePane }).addTo(group);
      const landmarkMarker = L.marker(geometry.landmark, { pane: landmarkEvidencePane, icon: landmarkIcon, interactive: false, keyboard: false, pmIgnore: true }).bindTooltip(String(item.name || "ຈຸດອ້າງອີງ"), { direction: "top", pane: landmarkEvidencePane }).addTo(group);
      const label = labelLayoutById.get(geometry.evidenceId) ?? { id: geometry.evidenceId, variant: "hidden", side: "right", width: 0, height: 0 } as LandmarkLabelPlacement;
      const labelMarker = label.variant === "hidden" ? null : L.marker(geometry.landmark, {
        pane: landmarkEvidencePane,
        interactive: false,
        keyboard: false,
        pmIgnore: true,
        icon: L.divIcon({
          className: `crs-landmark-location-label${isFocused ? ' crs-landmark-location-label-focused' : ''}`,
          html: buildLandmarkLabelHtml(item, label.variant, isFocused),
          iconSize: [label.width, label.height],
          iconAnchor: [label.side === "right" ? -12 : label.width + 12, label.height + landmarkMarkerSize + 6],
        }),
      }).addTo(group);
      landmarkMarker.bindTooltip(buildLandmarkTooltipText(item), { direction: "top", pane: landmarkEvidencePane });
      const midpoint = L.latLng(geometry.midpoint[0], geometry.midpoint[1]);
      const distance = geometry.meters < 1_000 ? `${Math.round(geometry.meters)} m` : `${(geometry.meters / 1_000).toFixed(2)} km`;
      const badgeMarker = L.marker(midpoint, {
        interactive: false,
        icon: L.divIcon({
          className: "crs-landmark-distance-badge",
          html: `<div data-landmark-distance-shell="true" style="position:relative;width:188px;height:42px;pointer-events:none"><span style="position:absolute;left:0;top:0;display:flex;width:188px;min-height:30px;align-items:center;justify-content:center;white-space:nowrap;border-radius:999px;background:#002975;color:#fff;padding:4px 8px;font-family:${LANDMARK_APP_FONT_STACK};font-size:12px;font-weight:600;line-height:1.25;letter-spacing:0;box-sizing:border-box;box-shadow:0 2px 7px rgba(0,0,0,.3);font-variant-numeric:tabular-nums" aria-label="ໄລຍະທາງກົງ ${distance}">${distance}</span><span data-landmark-distance-leader="true" aria-hidden="true" style="position:absolute;left:50%;top:30px;width:2px;height:7px;transform:translateX(-50%);background:#0f9d58"></span><span data-landmark-midpoint-anchor="true" aria-hidden="true" style="position:absolute;left:50%;top:34px;width:10px;height:10px;transform:translateX(-50%);border-radius:50%;box-sizing:border-box;background:#0f9d58;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.28)"></span></div>`,
          iconSize: [188, 42],
          iconAnchor: [94, 39],
        }),
        pane: landmarkEvidencePane,
        keyboard: false,
        pmIgnore: true,
      }).addTo(group);
      renderedEvidence.push({ geometry, originMarker, landmarkMarker, labelMarker, label, line, badgeMarker });
    });

    // Production trace confirmed that all evidence layers were owned by the
    // active group, but an endpoint could still be above the initial viewport.
    // Fit only once for an unseen active evidence set, only when needed, and
    // only while the map is not in a draw/edit session. This is view state, not
    // map data: it never marks the collateral dirty or invokes persistence.
    const initialFrameKey = `${collateralId}:${evidenceIdentity.join(',')}`;
    let initialFrame: number | null = null;
    if (evidenceEndpoints.length > 0 && mapMode === 'view' && landmarkInitialFrameKeyRef.current !== initialFrameKey) {
      initialFrame = window.requestAnimationFrame(() => {
        if (!map.hasLayer(group) || landmarkInitialFrameKeyRef.current === initialFrameKey) return;
        const bounds = L.latLngBounds(evidenceEndpoints);
        if (!bounds.isValid()) return;
        landmarkInitialFrameKeyRef.current = initialFrameKey;
        if (map.getBounds().contains(bounds)) {
          landmarkInitialFrameStatusRef.current = 'not-needed';
          return;
        }
        landmarkInitialFrameStatusRef.current = 'applied';
        map.fitBounds(bounds, {
          animate: false,
          maxZoom: 17,
          paddingTopLeft: isMobile ? [18, 92] : [96, 72],
          paddingBottomRight: isMobile ? [18, 350] : [420, 72],
        });
      });
    }

    let traceFrame: number | null = null;
    if (landmarkTraceEnabledRef.current && typeof window !== 'undefined') {
      traceFrame = window.requestAnimationFrame(() => {
        const container = map.getContainer();
        const containerRect = container.getBoundingClientRect();
        const describePoint = (point: any) => `${Math.round(point.x)},${Math.round(point.y)}`;
        const renderManifest = renderedEvidence.map(({ geometry, originMarker, landmarkMarker, labelMarker, label, line, badgeMarker }) => {
          const midpointPoint = map.latLngToContainerPoint(L.latLng(geometry.midpoint[0], geometry.midpoint[1]));
          const badgeRect = badgeMarker.getElement?.()?.getBoundingClientRect?.();
          const midpointAnchor = badgeMarker.getElement?.()?.querySelector?.('[data-landmark-midpoint-anchor="true"]') as HTMLElement | null;
          const anchorRect = midpointAnchor?.getBoundingClientRect?.();
          const anchorCenter = anchorRect
            ? { x: anchorRect.left - containerRect.left + anchorRect.width / 2, y: anchorRect.top - containerRect.top + anchorRect.height / 2 }
            : null;
          const deltaPx = anchorCenter ? Math.round(Math.hypot(anchorCenter.x - midpointPoint.x, anchorCenter.y - midpointPoint.y)) : -1;
          const linePoints = (line.getLatLngs?.() ?? []).length;
          const originVisible = Boolean(originMarker.getElement?.() && container.contains(originMarker.getElement()));
          const landmarkVisible = Boolean(landmarkMarker.getElement?.() && container.contains(landmarkMarker.getElement()));
          const labelVisible = Boolean(labelMarker?.getElement?.() && container.contains(labelMarker.getElement()));
          return `${geometry.key}:pts${linePoints}/o${originVisible ? '1' : '0'}/p${landmarkVisible ? '1' : '0'}/label${label.variant}:${labelVisible ? '1' : '0'}/mid${describePoint(midpointPoint)}/anchor${anchorCenter ? describePoint(anchorCenter) : 'none'}/d${deltaPx}`;
        }).join(';') || 'none';
        const originCount = container.querySelectorAll('.crs-landmark-origin-pin').length;
        const pinCount = container.querySelectorAll('.crs-landmark-evidence-pin').length;
        const lineCount = container.querySelectorAll('.crs-landmark-evidence-line').length;
        const badges = Array.from(container.querySelectorAll<HTMLElement>('.crs-landmark-distance-badge'));
        const badgeBounds = badges.map((badge) => {
          const rect = badge.getBoundingClientRect();
          return `${Math.round(rect.width)}x${Math.round(rect.height)}`;
        }).join(',') || 'none';
        let geomanTempLayers = 0;
        map.eachLayer((layer: any) => {
          if (layer?._pmTempLayer || layer?._pmSnapLine) geomanTempLayers += 1;
        });
        const geomanDomArtifacts = container.querySelectorAll('[class*="leaflet-pm-"]').length;
        const summary = `LM render ids=${evidenceIdentity.join(',') || 'none'} group=${group._leaflet_id ?? 'new'} map=${map.hasLayer(group) ? 'on' : 'off'} layers=${group.getLayers().length} dom=o${originCount}/p${pinCount}/l${lineCount}/b${badges.length} badge=${badgeBounds} frame=${landmarkInitialFrameStatusRef.current} geoman=tmp${geomanTempLayers}/dom${geomanDomArtifacts} records=${renderManifest}`;
        setLandmarkTraceSummary(summary);
        console.info('[CRS landmark-render trace]', summary);
      });
    }

    return () => {
      if (initialFrame !== null) window.cancelAnimationFrame(initialFrame);
      if (traceFrame !== null) window.cancelAnimationFrame(traceFrame);
      landmarkEvidenceGroupsRef.current.delete(group);
      if (landmarkEvidenceLayerRef.current === group) landmarkEvidenceLayerRef.current = null;
      try { group.clearLayers(); map.removeLayer(group); } catch {}
    };
  }, [activeSurface, collateralId, focusedLandmarkId, isMobile, landmarkLabelLayoutRevision, mapMode, mapReady, savedLandmarkEvidence]);
  // V394: focus saved evidence in the existing presentation layer. This is a
  // view-only operation: it never modifies landmark rows, map draft, or dirty state.
  const focusLandmarkOnMap = useCallback((proximityId: number) => {
    const item = (savedLandmarkEvidence as any[]).find((candidate) => Number(candidate.id) === proximityId);
    const map = leafletMapRef.current;
    if (!item || !map || !L) {
      sonnerToast.error('ບໍ່ພ້ອມເບິ່ງຈຸດໃນແຜນທີ່', { description: 'ກະລຸນາລໍຖ້າໃຫ້ແຜນທີ່ໂຫຼດແລ້ວລອງໃໝ່' });
      return;
    }
    const originLat = Number(item.originLatitude);
    const originLng = Number(item.originLongitude);
    const landmarkLat = Number(item.latitude);
    const landmarkLng = Number(item.longitude);
    if (![originLat, originLng, landmarkLat, landmarkLng].every(Number.isFinite)) {
      sonnerToast.error('ພິກັດຈຸດສຳຄັນບໍ່ສົມບູນ');
      return;
    }
    const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    setFocusedLandmarkId(proximityId);
    map.flyToBounds(L.latLngBounds([[originLat, originLng], [landmarkLat, landmarkLng]]), {
      animate: !reduceMotion,
      duration: reduceMotion ? 0 : 0.45,
      maxZoom: 17,
      paddingTopLeft: isMobile ? [18, 92] : [96, 72],
      paddingBottomRight: isMobile ? [18, 350] : [420, 72],
    });
    if (isMobile) {
      setActiveSurface('none');
      window.setTimeout(() => mobileLandmarkTriggerRef.current?.focus({ preventScroll: true }), 0);
    }
  }, [isMobile, savedLandmarkEvidence]);
  // Sprint v119: Draw-mode midpoint marker layer (re-implemented without _replayDrawWithVertices)
  // v119-R129 grep: grep -c 'Sprint v119' client/src/components/CollateralMapModal.tsx >= 1
  const drawMidLayerRef = useRef<any>(null);
  // BUG-v280: A dedicated presentation-only layer used while a custom midpoint is
  // actively dragged. It prevents Geoman's stale working path from visually
  // diverging from the pointer-driven midpoint marker before drag end.
  const drawLiveSegmentLayerRef = useRef<any>(null);
  // v281: settled map movement re-anchors drawing labels from the canonical
  // drawing snapshot, not from Geoman's asynchronously repainted working ring.
  const drawLabelReanchorRafRef = useRef<number | null>(null);
  const drawLabelReanchorGenerationRef = useRef(0);
  const isLiveMidpointDragRef = useRef(false);
  const measurePointsRef = useRef<[number, number][]>([]);
  // V406: Stable presentation references permit same-frame drag updates without
  // tearing down the actively dragged handle or the rest of the temporary route.
  const measurePolylineRef = useRef<any>(null);
  const measureSegmentLabelRefs = useRef<any[]>([]);
  const measureDragFrameRef = useRef<number | null>(null);
  const measureActiveDragIndexRef = useRef<number | null>(null);
  const [measureDistance, setMeasureDistance] = useState<number | null>(null);
  const [measurePointCount, setMeasurePointCount] = useState(0);
  const [showMeasureManualEntry, setShowMeasureManualEntry] = useState(false);
  const [measureManualLat, setMeasureManualLat] = useState("");
  const [measureManualLng, setMeasureManualLng] = useState("");
  const [measureManualError, setMeasureManualError] = useState("");
  const [measureTilePickerOpen, setMeasureTilePickerOpen] = useState(false);
  useEffect(() => {
    if (!measureMode) setMeasureTilePickerOpen(false);
  }, [measureMode]);
  // G11: Polygon rotation mode
  const [isRotating, setIsRotating] = useState(false);
  // G8: Snap-to-angle toggle (45°/90° snapping during drawing)
  const [snapAngle, setSnapAngle] = useState(false);
  const snapAngleRef = useRef(false);
  snapAngleRef.current = snapAngle;
  // G7: Undo/Redo history stack (for completed polygon edits)
  const undoStackRef = useRef<any[]>([]);  // G9: stores GeoJsonFeatureCollection snapshots
  const redoStackRef = useRef<any[]>([]);  // G9: stores GeoJsonFeatureCollection snapshots
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  // G3: Zoom level indicator
  const [currentZoom, setCurrentZoom] = useState<number>(COLLATERAL_MAP.DEFAULT_ZOOM);

  // G4: Live GPS tracking
  const [liveGps, setLiveGps] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const gpsWatchIdRef = useRef<number | null>(null);
  const liveGpsMarkerRef = useRef<any>(null);
  const liveGpsCircleRef = useRef<any>(null);
  // Sprint v168.7: track stored GPS pin so NAV-1 effect can remove+re-add it on collateral switch
  const storedGpsPinRef = useRef<any>(null);
  // Sprint v171: Google Mutant layer ref + Street View state
  const googleMutantRef = useRef<any>(null);
  const googleMapsKey = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
  const showStreetView = activeSurface === 'street-view';
  const setShowStreetView = useCallback((visible: boolean) => {
    setActiveSurface(visible ? 'street-view' : 'none');
  }, []);
  const [streetViewSessionState, setStreetViewSessionState] = useState<StreetViewSessionState>('idle');
  const [streetViewPosition, setStreetViewPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [streetViewOffsetMeters, setStreetViewOffsetMeters] = useState<number | null>(null);
  const streetViewRequestIdRef = useRef(0);
  const returnToDrawingToolsAfterStreetViewRef = useRef(false);
  const streetViewPanoramaRef = useRef<any>(null);
  const streetViewPanoramaElementRef = useRef<HTMLDivElement | null>(null);
  const streetViewLaunchButtonRef = useRef<HTMLButtonElement | null>(null);
  // BUG-SV-1 fix: stable Street View URL — only set when user OPENS Street View, not on every liveGps update
  // This prevents iframe re-mount (flicker) and freeze caused by GPS watchPosition firing every few seconds
  const [stableStreetViewUrl, setStableStreetViewUrl] = useState<string | null>(null);
  // Sprint v172: Google sub-layer state (satellite | roadmap | hybrid | terrain)
  const [googleSubLayer, setGoogleSubLayer] = useState<"satellite"|"roadmap"|"hybrid"|"terrain">("satellite");
  const googleSubLayerRef = useRef<"satellite"|"roadmap"|"hybrid"|"terrain">("satellite");

  const closeMobileStreetView = useCallback(() => {
    streetViewRequestIdRef.current += 1;
    streetViewPanoramaRef.current = null;
    const returnToDrawingTools = returnToDrawingToolsAfterStreetViewRef.current;
    returnToDrawingToolsAfterStreetViewRef.current = false;
    setShowStreetView(false);
    setStreetViewSessionState('idle');
    setStreetViewPosition(null);
    setStreetViewOffsetMeters(null);
    setStableStreetViewUrl(null);
    if (returnToDrawingTools) {
      setShowMobileMoreSheet(true);
    }
    window.setTimeout(() => streetViewLaunchButtonRef.current?.focus(), 0);
  }, [setShowMobileMoreSheet, setShowStreetView]);

  const openMobileStreetView = useCallback(async () => {
    const lat = liveGps?.lat ?? Number(gpsLatitude);
    const lng = liveGps?.lng ?? Number(gpsLongitude);
    const requestId = streetViewRequestIdRef.current + 1;
    streetViewRequestIdRef.current = requestId;

    if (!googleMapsKey || !isValidStreetViewCoordinate(lat, lng)) {
      setShowStreetView(true);
      setStreetViewSessionState('error');
      setStreetViewPosition(null);
      setStreetViewOffsetMeters(null);
      setStableStreetViewUrl(null);
      return;
    }

    // Capture the requested GPS point once for both the mobile preview and the
    // desktop side pane. The URL must not follow live device-GPS updates while
    // the user is reviewing the same collateral location.
    setStableStreetViewUrl(`https://www.google.com/maps/embed/v1/streetview?key=${googleMapsKey}&location=${lat},${lng}&heading=0&pitch=0&fov=90`);
    setShowStreetView(true);
    setStreetViewSessionState('checking');
    setStreetViewPosition(null);
    setStreetViewOffsetMeters(null);

    try {
      await loadGoogleMapsScript(googleMapsKey);
      if (!isCurrentStreetViewRequest(requestId, streetViewRequestIdRef.current)) return;

      const googleMaps = (window as any).google?.maps;
      if (!googleMaps?.StreetViewService) {
        setStreetViewSessionState('error');
        return;
      }

      const service = new googleMaps.StreetViewService();
      const coverageRequest = createStreetViewCoverageRequest({ lat, lng });
      service.getPanorama({
        ...coverageRequest,
        preference: googleMaps.StreetViewPreference?.NEAREST ?? coverageRequest.preference,
      }, (data: any, status: string) => {
        if (!isCurrentStreetViewRequest(requestId, streetViewRequestIdRef.current)) return;
        const nextState = resolveStreetViewCoverageState(status);
        setStreetViewSessionState(nextState);
        if (nextState === 'available') {
          const resolvedLatLng = data?.location?.latLng;
          const resolvedPosition = {
            lat: typeof resolvedLatLng?.lat === 'function' ? resolvedLatLng.lat() : lat,
            lng: typeof resolvedLatLng?.lng === 'function' ? resolvedLatLng.lng() : lng,
          };
          setStreetViewPosition(resolvedPosition);
          setStreetViewOffsetMeters(getStreetViewOffsetMeters({ lat, lng }, resolvedPosition));
        }
      });
    } catch {
      if (isCurrentStreetViewRequest(requestId, streetViewRequestIdRef.current)) {
        setStreetViewSessionState('error');
      }
    }
  }, [googleMapsKey, gpsLatitude, gpsLongitude, liveGps, setShowStreetView]);

  const openStreetViewFromMobileTools = useCallback(() => {
    // Street View is a read-only preview. Preserve every active drawing ref
    // and return the user to the same drawing tools after closing it.
    returnToDrawingToolsAfterStreetViewRef.current = isDrawing;
    setShowMobileMoreSheet(false);
    void openMobileStreetView();
  }, [isDrawing, openMobileStreetView, setShowMobileMoreSheet]);

  useEffect(() => {
    if (!isMobile || !showStreetView || streetViewSessionState !== 'available' || !streetViewPosition || !streetViewPanoramaElementRef.current) {
      return;
    }
    const googleMaps = (window as any).google?.maps;
    if (!googleMaps?.StreetViewPanorama) {
      setStreetViewSessionState('error');
      return;
    }

    const panoramaElement = streetViewPanoramaElementRef.current;
    streetViewPanoramaRef.current = new googleMaps.StreetViewPanorama(panoramaElement, {
      position: streetViewPosition,
      pov: { heading: 0, pitch: 0 },
      zoom: 0,
      addressControl: false,
      fullscreenControl: true,
      motionTracking: false,
    });

    return () => {
      streetViewPanoramaRef.current = null;
      panoramaElement.innerHTML = '';
    };
  }, [isMobile, showStreetView, streetViewPosition, streetViewSessionState]);

  // V65: Map rotation (leaflet-rotate)
  const [mapBearing, setMapBearing] = useState<number>(0);
  const [followDeviceCompass, setFollowDeviceCompass] = useState(false);
  // Sprint v158: Compass overlay toggle — persisted in localStorage, default ON
  const [showCompassOverlay, setShowCompassOverlay] = useState<boolean>(() => {
    try { return localStorage.getItem('crs-compass-overlay') !== '0'; } catch { return true; }
  });
  const deviceOrientationListenerRef = useRef<((e: DeviceOrientationEvent) => void) | null>(null);
  // NEW-1: Long-press coords
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressStartPosRef = useRef<{ x: number; y: number } | null>(null);

  // Sprint v170: F2 — save device GPS coordinates to collateral record in DB
  const saveGpsMutation = trpc.collaterals.update.useMutation();

  // GAP-7: tRPC mutation for Google Maps link resolution
  const resolveLinkMutation = trpc.utils.resolveGoogleMapsLink.useMutation({
    onSuccess: (data) => {
      setCoordLat(String(data.lat));
      setCoordLng(String(data.lon));
      setCoordError("");
      setCoordMapsLink("");
    },
    onError: (err) => {
      setCoordError(err.message || "ບໍ່ສາມາດ parse ພິກັດໄດ້");
    },
  });

  // G5: History panel state
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);

  // Hook
  // G9: active polygon index (for multi-polygon selection)
  const [activePolygonIndex, setActivePolygonIndex] = useState<number>(0);
  const activePolygonIndexRef = useRef<number>(0); // A1 v28: stable ref for use inside event handlers
  activePolygonIndexRef.current = activePolygonIndex; // keep in sync
  const [showPolygonList, setShowPolygonList] = useState(false);
  // Sprint v131: Polygon visibility toggle (client-side only, resets on modal close)
  const [hiddenPolygons, setHiddenPolygons] = useState<Set<number>>(new Set());

  // v377: A primary surface always takes visual precedence. Auxiliary panels are
  // intentionally closed rather than layered underneath a chooser, edit dock, or
  // Street View pane, preventing the former overlap and pointer ambiguity.
  useEffect(() => {
    if (activeSurface === 'none') return;
    setShowCoordPanel(false);
    setShowHistoryPanel(false);
    if (activeSurface !== 'edit-tools') setShowPolygonList(false);
  }, [activeSurface]);
  useEffect(() => {
    hiddenPolygonsRef.current = hiddenPolygons;
  }, [hiddenPolygons]);
  // Sprint v131: Color picker — which polygon index is showing the palette
  const [colorPickerIndex, setColorPickerIndex] = useState<number | null>(null);
  // Sprint v131: Inline label editing
  const [editingLabelIndex, setEditingLabelIndex] = useState<number | null>(null);
  const [editingLabelValue, setEditingLabelValue] = useState<string>("");
  // H2: Segment distance label toggle
  const [showSegmentLabels, setShowSegmentLabels] = useState(true);
  const showSegmentLabelsRef = useRef(true);
  showSegmentLabelsRef.current = showSegmentLabels;
  const refreshVisibleSegmentLabels = useCallback((hiddenIndexes: ReadonlySet<number> = hiddenPolygonsRef.current) => {
    const manager = segmentLabelMgrRef.current;
    if (!manager) return;

    // Clear first even when segment labels are disabled, because a hidden
    // polygon must never leave a stale distance overlay behind.
    manager.clearLabels();
    if (!showSegmentLabelsRef.current) return;

    const map = leafletMapRef.current;
    polygonLayersRef.current.forEach((layer: any, index: number) => {
      if (hiddenIndexes.has(index)) return;
      if (map?.hasLayer && !map.hasLayer(layer)) return;
      manager.appendForLayer(layer);
    });
  }, []);
  // O1 v42: PDF info state — updated just before capture so template renders with latest data
  const [pdfInfo, setPdfInfo] = useState<CollateralMapPDFInfo | null>(null);

  // ── DEBUG PANEL (Sprint v89) ──────────────────────────────────────────────
  // Activate: long-press compass button 1 second. Shows real-time event log on screen.
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const debugLogsRef = useRef<string[]>([]);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const debugCompassLongPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // v293: explicit ?debug=1 trace. It records only interaction/provider state,
  // never customer data, coordinates, or GeoJSON.
  const mapLayerTraceEnabledRef = useRef(false);
  const [mapLayerTraceEnabled, setMapLayerTraceEnabled] = useState(false);
  const [mapLayerTraceEntries, setMapLayerTraceEntries] = useState<string[]>([]);
  const dbg = useCallback((msg: string) => {
    const ts = new Date().toISOString().slice(11, 23);
    const line = `${ts} ${msg}`;
    debugLogsRef.current = [line, ...debugLogsRef.current.slice(0, 99)];
    setDebugLogs([...debugLogsRef.current]);
  }, []);
  const traceMapLayer = useCallback((entry: MapLayerSelectorTrace | { phase: string; option?: string; detail?: string }) => {
    if (!mapLayerTraceEnabledRef.current) return;
    const parts = [
      entry.phase,
      entry.option ? `option=${entry.option}` : null,
      'target' in entry && entry.target ? `target=${entry.target}` : null,
      'pointerType' in entry && entry.pointerType ? `pointer=${entry.pointerType}` : null,
      'containment' in entry && entry.containment ? `within=${entry.containment}` : null,
      'detail' in entry && entry.detail ? entry.detail : null,
    ].filter(Boolean);
    const line = `${new Date().toISOString().slice(11, 23)} ${parts.join(' | ')}`;
    setMapLayerTraceEntries(entries => [...entries.slice(-11), line]);
    console.info('[CRS map-layer trace]', line);
  }, []);
  // ── END DEBUG PANEL ───────────────────────────────────────────────────────

  // v379: A trace is a diagnostic aid, never normal map-editing content. It is
  // deliberately opt-in so an administrator's role cannot push drawing actions
  // below the mobile sheet's first viewport.
  useEffect(() => {
    if (open && typeof window !== 'undefined') {
      try {
        const params = new URLSearchParams(window.location.search);
        const traceEnabled = shouldEnableMapLayerTrace(params.get('debug') === '1');
        mapLayerTraceEnabledRef.current = traceEnabled;
        setMapLayerTraceEnabled(traceEnabled);
        setMapLayerTraceEntries([]);
        if (traceEnabled && import.meta.env.DEV && params.get('debugPanel') === '1') {
          setDebugPanelOpen(true);
          dbg('🔧 Debug panel auto-opened via ?debug=1&debugPanel=1 URL params (Sprint v115.8)');
        }
      } catch {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const {
    geoBoundaryJson,
    areaSqM,
    areaDisplay,
    updatedAt,
    isLoading,
    isSaving,
    isExporting,
    draftGeoJson,
    hasUnsavedChanges,
    tileLayer,
    setTileLayer,
    setDraftGeoJson,
    hydrateSavedGeoJson,
    addPolygonToDraft,
    removePolygonFromDraft,
    saveBoundary,
    clearBoundary,
    exportPDF,
    boundaryHistory,
    isLoadingHistory,
    refetchHistory,
  } = useCollateralMap({ collateralId, loanApplicationId, canEdit: canEditMapBoundary });

  const [isAssessmentReportExporting, setIsAssessmentReportExporting] = useState(false);
  const handleExportCollateralAssessmentReport = useCallback(async () => {
    if (hasUnsavedChanges) {
      sonnerToast.warning("ກະລຸນາບັນທຶກ ຫຼື ຍົກເລີກການແກ້ໄຂຂອບເຂດກ່ອນສ້າງບົດປະເມີນ");
      return;
    }
    setIsAssessmentReportExporting(true);
    const toastId = sonnerToast.loading("ກຳລັງສ້າງບົດປະເມີນຫຼັກຊັບ...");
    try {
      const response = await fetch(`/api/pdf/collateral-assessment/${collateralId}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-idempotency-key": typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `car-${Date.now()}-${collateralId}`,
        },
        body: JSON.stringify({}),
      });
      const payload = await response.json().catch(() => null) as { message?: string; downloadUrl?: string; reportNumber?: string } | null;
      if (!response.ok || !payload?.downloadUrl) throw new Error(payload?.message || "REPORT_EXPORT_FAILED");
      window.open(payload.downloadUrl, "_blank", "noopener,noreferrer");
      sonnerToast.success(`ສ້າງບົດປະເມີນ ${payload.reportNumber || "PDF"} ສຳເລັດ`, { id: toastId });
    } catch (error) {
      sonnerToast.error(error instanceof Error ? error.message : "ບໍ່ສາມາດສ້າງບົດປະເມີນໄດ້", { id: toastId });
    } finally {
      setIsAssessmentReportExporting(false);
    }
  }, [collateralId, hasUnsavedChanges]);

  // One display source for both desktop/mobile polygon managers. During a save
  // transition the committed draft remains preferred; when a modal first opens,
  // the persisted boundary supplies the list until a draft is hydrated.
  const managedPolygonGeoJson = getManagedPolygonSource(draftGeoJson, geoBoundaryJson);
  const measureOriginOptions = ((managedPolygonGeoJson as any)?.features ?? [])
    .map((feature: any, index: number) => ({
      value: String(index),
      label: feature?.properties?.label || `ຫຼັກຊັບ Polygon ${index + 1}`,
      searchableText: `polygon ${index + 1} ${feature?.properties?.label ?? ''}`,
    }));
  const activeCollateralReference = getCollateralStaffReference({
    id: collateralId,
    collateralType: activeCollateral?.collateralType ?? collateralType,
    buildingSubType: activeCollateral?.buildingSubType ?? collateralSubType,
    ownerName: activeCollateral?.ownerName ?? ownerName,
    landTitleNumber: activeCollateral?.landTitleNumber ?? landTitleNumber,
    documentNumber: activeCollateral?.documentNumber ?? documentNumber,
  });
  const collateralSwitchOptions = (allCollaterals ?? []).map((col) => {
    const reference = getCollateralStaffReference(col);
    return {
      value: col.id,
      label: `${reference.primaryLabel} · #${col.id}`,
      searchableText: reference.searchableText,
    };
  });
  const mapTypePolicy = activeMapEvidence;
  const mobileMapEditAvailabilityMessage = !canEdit
    ? "ໜ້ານີ້ເປັນໂໝດດູຂໍ້ມູນ; ຈຶ່ງບໍ່ສາມາດແຕ້ມ ຫຼືແກ້ໄຂແຜນທີ່ໄດ້"
    : canEditMapBoundary
      ? "ທ່ານສາມາດແຕ້ມ ຫຼືແກ້ໄຂຂອບເຂດຕອນດິນໄດ້"
      : mapTypePolicy.guidance;
  const mobileMapEditAvailabilityLabel = !canEdit
    ? "ດູຢ່າງດຽວ · ແຕ້ມບໍ່ໄດ້"
    : canEditMapBoundary
      ? "ແຕ້ມຂອບເຂດໄດ້"
      : "ປະເພດ/ເອກະສານນີ້ບໍ່ຮອງຮັບການແຕ້ມ";
  const desktopMapEditAvailabilityLabel = !canEdit
    ? "ດູຢ່າງດຽວ · ຂັ້ນຕອນນີ້ລັອກການແກ້ໄຂ"
    : canEditMapBoundary
      ? "ແຕ້ມຂອບເຂດໄດ້"
      : "ປະເພດ/ເອກະສານນີ້ບໍ່ຮອງຮັບການແຕ້ມ";

  // G7+G9: Keep draftGeoJsonRef in sync with draftGeoJson state for undo snapshots
  useEffect(() => {
    draftGeoJsonRef.current = draftGeoJson;
    // Sprint v145: re-render center labels whenever draft changes (rename, color, undo, etc.)
    // Sprint v151: when draftGeoJson is null (after save), clear all labels (was skipped → stale ghost labels)
    setTimeout(() => renderCenterLabels(), 50);
  }, [draftGeoJson, renderCenterLabels]);

  // ============================================================
  // Sprint v163: Ghost Layers — sibling collateral boundaries
  // ============================================================
  // Ref to hold all ghost polygon layers on the Leaflet map
  const ghostLayersRef = useRef<any[]>([]);

  // Derive sibling IDs: all collaterals in the same loan EXCEPT the active one
  const siblingCollateralIds = (allCollaterals || [])
    .filter((c) => c.id !== collateralId)
    .map((c) => c.id);

  // Fetch geoBoundary for ALL siblings in parallel (each query is individually cached)
  // trpc.useQueries is available in @trpc/react-query v11 on the proxy object
  const siblingBoundaryQueries = trpc.useQueries((t) =>
    siblingCollateralIds.map((sibId) =>
      t.collaterals.getGeoBoundary({ collateralId: sibId }, { enabled: open && sibId > 0, staleTime: 60_000 })
    )
  );

  // Render ghost layers whenever sibling data or map readiness changes
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !L || !mapReady) return;

    // Remove old ghost layers
    for (const gl of ghostLayersRef.current) {
      try { map.removeLayer(gl); } catch {}
    }
    ghostLayersRef.current = [];

    // Render each sibling boundary as a semi-transparent gray ghost polygon
    // parseGeoJson is already imported at module scope via useCollateralMap
    for (const query of siblingBoundaryQueries) {
      const data = (query as any)?.data;
      if (!data?.geoBoundaryJson) continue;
      try {
        // Use L.geoJSON directly — geoBoundaryJson is already a valid GeoJSON object from the server
        const rawGeo = data.geoBoundaryJson;
        if (!rawGeo) continue;
        const ghostLayer = L.geoJSON(rawGeo as any, {
          style: () => ({
            color: '#9ca3af',
            fillColor: '#9ca3af',
            fillOpacity: 0.15,
            weight: 1.5,
            dashArray: '4 4',
          }),
          interactive: false,
        });
        ghostLayer.addTo(map);
        ghostLayersRef.current.push(ghostLayer);
      } catch {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, siblingBoundaryQueries.map((q: any) => q?.data?.geoBoundaryJson).join('|')]);

  // ============================================================
  // Map initialization
  // ============================================================

  const initMap = useCallback(async () => {
    if (!mapRef.current || leafletMapRef.current || initCalledRef.current) return;
    initCalledRef.current = true;
    clearOsmTileFailure();

    const container = mapRef.current;
    if (container.clientWidth === 0 || container.clientHeight === 0) {
      initCalledRef.current = false;
      setTimeout(() => { initMap(); }, 100);
      return;
    }

    // Read stable refs (not closure values) to avoid stale data
    const currentCanEdit = canEditRef.current;
    const currentGpsLat = gpsLatRef.current;
    const currentGpsLng = gpsLngRef.current;

    // Import Leaflet AND Geoman BEFORE creating the map instance
    // Geoman must be imported before L.map() so it can attach .pm to the map prototype
    const leaflet = await import("leaflet");
    L = leaflet.default || leaflet;

    // V65: leaflet-rotate must be imported AFTER leaflet (patches L directly)
    await import("leaflet-rotate");

    // Pre-load Geoman so it patches Leaflet.Map.prototype BEFORE map creation
    await import("@geoman-io/leaflet-geoman-free");
    await import("@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css");
    await import("../styles/geoman-overrides.css");
    // =====================================================================
    // SPRINT v95: Monkey-patch Geoman Edit.Line prototype to inject correct
    // iconSize + iconAnchor on ALL edit-mode vertex/midpoint markers.
    //
    // Root cause (confirmed via Leaflet source + StackOverflow):
    //   Geoman hardcodes L.divIcon({ className: "marker-icon" }) with NO
    //   iconSize/iconAnchor. Leaflet DivIcon default iconSize=[12,12] writes
    //   margin-left:-6px; margin-top:-6px as plain inline style. CSS
    //   !important cannot override inline style. MutationObserver (Sprint
    //   v83-v94) had timing races on slow mobile CPUs.
    //
    // Fix: patch L.PM.Edit.Line.prototype._createMarker so every vertex
    //   marker is created with iconSize/iconAnchor from the start. Also
    //   patch _createMiddleMarker, _onMiddleMarkerMoveEnd, _onMiddleMarkerClick
    //   which call setIcon(L.divIcon({})) without size.
    //   L.PM.Edit.Polygon and Rectangle inherit from Line — one patch covers all.
    //
    // v95-R46 grep: grep -c 'v95-R46' client/src/components/CollateralMapModal.tsx >= 1
    // =====================================================================
    {
      // Sprint v110 A1: idempotency guard — prototype patch runs once per page load
      // (survives Vite HMR; module-level var would not)
      const _pmEditLine = (L as any).PM?.Edit?.Line;
      if (_pmEditLine?.prototype && !(_pmEditLine.prototype as any).__v95Patched) {
        (_pmEditLine.prototype as any).__v95Patched = true;
        const _getMarkerSz = () => (typeof window !== 'undefined' && window.matchMedia('(max-width:767px)').matches) ? 20 : 14;
        // Sprint v99: midpoint must be visually smaller than vertex (vertex=20px, midpoint=12px on mobile)
        const _getMidSz = () => (typeof window !== 'undefined' && window.matchMedia('(max-width:767px)').matches) ? 12 : 10;
        // Sprint v115 Bug2 / Sprint v115.3: edit-mode midpoints use 32px transparent hit area (same class as draw-mode hit markers)
        // The visible dot is rendered as an html child div (same pattern as draw-mode midpoints at line ~1249).
        // CSS .marker-icon-middle-hit = transparent container; child div = visible dot.
        const _getEditMidHitSz = () => (typeof window !== 'undefined' && window.matchMedia('(max-width:767px)').matches) ? 44 : 32;
        // Sprint v115 Bug2: reduce hide threshold from 50px → 28px.
        // 50px was too aggressive — midpoints disappeared at reasonable zoom levels.
        // 28px = 2 × 14px vertex radius, so midpoint hides only when it would overlap a vertex.
        const _MID_MIN_SCREEN_PX = 28;
        // Patch _createMarker: inject iconSize/iconAnchor
        const _origCM = _pmEditLine.prototype._createMarker;
        _pmEditLine.prototype._createMarker = function(latlng: any) {
          const result = _origCM.call(this, latlng);
          if (result) {
            const sz = _getMarkerSz();
            try { result.setIcon(L.divIcon({ className: 'marker-icon', iconSize: [sz, sz], iconAnchor: [sz/2, sz/2] })); } catch {}
          }
          return result;
        };
        // Patch _createMiddleMarker: Sprint v99 — smaller icon + hide when edge too short
        const _origMM = _pmEditLine.prototype._createMiddleMarker;
        if (_origMM) {
          _pmEditLine.prototype._createMiddleMarker = function(leftM: any, rightM: any) {
            const result = _origMM.call(this, leftM, rightM);
            if (result) {
              // Sprint v115.3 Bug2: add html child dot (transparent hit area + visible dot inside).
              // Without the html child, the transparent .marker-icon-middle-hit makes midpoints invisible.
              const sz = _getEditMidHitSz();
              const dotSz = _getMidSz();
              const isMob = typeof window !== 'undefined' && window.matchMedia('(max-width:767px)').matches;
              try { result.setIcon(L.divIcon({
                className: 'marker-icon marker-icon-middle marker-icon-middle-hit',
                iconSize: [sz, sz],
                iconAnchor: [sz/2, sz/2],
                html: `<div class="midpoint-dot" style="width:${dotSz}px;height:${dotSz}px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);border-radius:50%;background:rgba(255,255,255,0.7);border:${isMob ? 2 : 1.5}px solid #22c55e;box-shadow:0 1px 3px rgba(0,0,0,0.4);opacity:0.85;pointer-events:none"></div>`,
              })); } catch {}
              // Sprint v118 Bug2 Fix: set touch-action:none on midpoint element so Samsung Chrome
              // allows Leaflet Draggable to receive touch events without browser pan interception.
              try {
                result.on('add', () => {
                  const el = result.getElement?.();
                  if (el) el.style.setProperty('touch-action', 'none', 'important');
                });
                // Also set immediately if already added
                const el = result.getElement?.();
                if (el) el.style.setProperty('touch-action', 'none', 'important');
              } catch {}
              // Sprint v99: hide midpoint if edge screen distance < 50px (prevents overlap with vertex)
              try {
                const map = this._map;
                if (map && leftM && rightM) {
                  const pA = map.latLngToContainerPoint(leftM.getLatLng());
                  const pB = map.latLngToContainerPoint(rightM.getLatLng());
                  const dx = pA.x - pB.x; const dy = pA.y - pB.y;
                  if (Math.sqrt(dx * dx + dy * dy) < _MID_MIN_SCREEN_PX) {
                    result.setOpacity(0);
                    (result as any)._pmHidden = true;
                  }
                }
              } catch {}
            }
            return result;
          };
        }
        // Patch _onMiddleMarkerMoveEnd: Geoman resets to L.divIcon({className:"marker-icon"}) without size
        const _origMME = _pmEditLine.prototype._onMiddleMarkerMoveEnd;
        if (_origMME) {
          _pmEditLine.prototype._onMiddleMarkerMoveEnd = function(e: any) {
            // Sprint v115.4 G4: push undo snapshot BEFORE insert
            // (midpoint drag does not fire pm:markerdragstart → no snapshot pushed otherwise)
            try { (this._map as any).__pushUndoSnapshot?.(); } catch {}
            _origMME.call(this, e);
            const sz = _getMarkerSz();
            // KEEP setIcon: promotes old midpoint marker object to vertex size (marker-icon 20/14px).
            // Sizes the marker in the 0ms gap before setTimeout fires (prevents flash).
            // Re-init below also sizes via _createMarker patch, but setIcon runs first.
            try { e.target?.setIcon(L.divIcon({ className: 'marker-icon', iconSize: [sz, sz], iconAnchor: [sz/2, sz/2] })); } catch {}
            // Sprint v115.4 Bug1: Path A — force Geoman to re-init edit markers on the layer.
            // Manual DOM sweep rejected: Geoman 2.x stores midpoints on _middleMarkerNext/Prev (not in
            // _markers array) → sweep would delete ALL live midpoints, not just the ghost.
            // disable+enable rebuilds vertex + midpoint markers cleanly via existing _createMarker /
            // _createMiddleMarker patches → correct sizes, no ghost DOM.
            const _m = this._map;
            const _layer = this._layer;
            // Sprint v115.5 Bug B: use global edit mode re-init instead of layer-level re-init.
            // Root cause: _layer.pm.enable() on a globally-managed layer causes Geoman state desync
            // (all vertex/midpoint markers become invisible). Use disableGlobalEditMode +
            // enableGlobalEditMode when map is in global edit mode (confirmed Geoman 2.20.0 API);
            // fall back to layer-level for non-global sessions.
            setTimeout(() => {
              try {
                if (_m?.pm?.globalEditModeEnabled?.()) {
                  _m.pm.disableGlobalEditMode();
                  _m.pm.enableGlobalEditMode({ allowSelfIntersection: false });
                } else if (_layer?.pm?.enabled?.()) {
                  _layer.pm.disable();
                  _layer.pm.enable({ allowSelfIntersection: false });
                }
              } catch {}
              // Rebuild segment labels with fresh coordinates after re-init
              try { _m?.fire('pm:edit'); } catch {}
            }, 0);
          };
        }
        // Patch _onMiddleMarkerClick: Geoman resets to L.divIcon({className:"marker-icon"}) without size
        const _origMMC = _pmEditLine.prototype._onMiddleMarkerClick;
        if (_origMMC) {
          _pmEditLine.prototype._onMiddleMarkerClick = function(e: any) {
            // Sprint v115.4 G4: push undo snapshot BEFORE insert
            // (midpoint click does not fire pm:markerdragstart → no snapshot pushed otherwise)
            try { (this._map as any).__pushUndoSnapshot?.(); } catch {}
            _origMMC.call(this, e);
            const sz = _getMarkerSz();
            // KEEP setIcon: promotes old midpoint marker object to vertex size.
            try { e.target?.setIcon(L.divIcon({ className: 'marker-icon', iconSize: [sz, sz], iconAnchor: [sz/2, sz/2] })); } catch {}
            // Sprint v115.4 Bug2: defer pm:edit so Geoman commits new vertex coordinates before
            // label rebuild. Without setTimeout(0), pm:edit fires synchronously with stale coords
            // → segment label shows "1 m" instead of correct distance.
            // Same deferred pattern as pm:markerdragend (~line 2244).
            const _m = this._map;
            setTimeout(() => { try { _m?.fire('pm:edit'); } catch {} }, 0);
          };
        }
        dbg('\u2705 v95: Geoman Edit.Line prototype patched — iconSize/iconAnchor injected on all edit markers');
      } else {
        dbg('\u26a0\ufe0f v95: L.PM.Edit.Line not found — monkey-patch skipped, MutationObserver fallback active');
      }
    }

    // Fix default icons
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
      iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
      shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    });

    const defaultLat = currentGpsLat ? Number(currentGpsLat) : COLLATERAL_MAP.DEFAULT_CENTER.lat;
    const defaultLng = currentGpsLng ? Number(currentGpsLng) : COLLATERAL_MAP.DEFAULT_CENTER.lng;

    const map = L.map(container, {
      center: [defaultLat, defaultLng],
      zoom: COLLATERAL_MAP.DEFAULT_ZOOM,
      maxZoom: 22,
      // The built-in Leaflet control occupies the same top-left geometry as the
      // SaaS View Dock. Zoom is rendered by the dialog-owned React navigation
      // docks below, so there is one clear, pointer-reachable control owner.
      zoomControl: false,
      attributionControl: true,
      // V65: leaflet-rotate options
      rotate: true,
      bearing: 0,
      touchRotate: true,        // 📱 2-finger rotate on mobile
      shiftKeyRotate: true,     // 🖥️ Shift + mouse wheel on desktop
      rotateControl: false, // Sprint v73 STEP B: disable built-in control — use custom React compass only
    });

    // V65: sync bearing state to React so compass button updates
    map.on('rotate', () => {
      try { setMapBearing(map.getBearing ? map.getBearing() : 0); } catch {}
    });

    leafletMapRef.current = map;

    // NEW-2: Restore last-used tile layer from localStorage (default satellite)
    // Sprint v171 fix: 'google' cannot be loaded via getTileConfig (different API) — fallback to satellite on init, then switchTileLayer after mapReady
    const savedLayer = (localStorage.getItem("collateralMap.baseLayer") as MapTileLayer) || "satellite";
    const initLayer: Exclude<MapTileLayer, "google"> = savedLayer === "google" ? "satellite" : (savedLayer as Exclude<MapTileLayer, "google">);
    const cfg = getTileConfig(initLayer, maptilerKey);
    const baseOptions = {
      maxNativeZoom: cfg.maxNativeZoom,
      maxZoom: cfg.maxZoom,
      attribution: cfg.attribution,
    };
    tileLayerRef.current = (cfg.url === OSM_TILE_URL
      ? createOsmTileLayer(L, baseOptions, handleOsmTileError, handleOsmTileLoad)
      : L.tileLayer(cfg.url, baseOptions)
    ).addTo(map);
    setTileLayer(initLayer);

    // GPS marker
    if (currentGpsLat && currentGpsLng) {
      const gpsIcon = L.divIcon({
        html: `<div style="width:14px;height:14px;background:#002975;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.5);"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
        className: "crs-map-stored-gps-pin",
      });
      storedGpsPinRef.current = L.marker([Number(currentGpsLat), Number(currentGpsLng)], { icon: gpsIcon })
        .bindTooltip("📍 GPS ຈຸດ", { permanent: false, direction: "top" })
        .addTo(map);
    }

    // SegmentLabelManager
    segmentLabelMgrRef.current = new SegmentLabelManager(L, map);

    // OffsetDragIndicator (mobile fat-finger fix)
    offsetDragRef.current = new OffsetDragIndicator(container);

    // Scale bar (bottom-right, metric)
    L.control.scale({ position: "bottomright", imperial: false, maxWidth: 100 }).addTo(map);

    // Coordinate display on mouse move
    map.on("mousemove", (e: any) => {
      setHoverCoords({ lat: e.latlng.lat, lng: e.latlng.lng });
    });
    map.on("mouseout", () => setHoverCoords(null));
    map.on("click", (e: any) => {
      setHoverCoords({ lat: e.latlng.lat, lng: e.latlng.lng });
      if (landmarkCaptureModeRef.current) {
        setLandmarkPickedCoordinate({ lat: e.latlng.lat, lng: e.latlng.lng });
        setLandmarkCaptureMode(false);
        map.getContainer().style.cursor = "";
      }
    });

    // NEW-1: Long-press on map → show lat/lng popup + copy button
    // Cancel if finger/mouse moves > 10px (prevent trigger during pan)
    map.on("mousedown touchstart", (e: any) => {
      const point = e.containerPoint;
      if (!point) return;
      // Sprint v109 Fix 6: guard long-press against firing during draw mode
      if (isDrawingRef.current) return;
      longPressStartPosRef.current = { x: point.x, y: point.y };
      longPressTimerRef.current = setTimeout(() => {
        if (!leafletMapRef.current || !L) return;
        // Double-check: abort if drawing started during the 500ms hold
        if (isDrawingRef.current) return;
        const { lat, lng } = e.latlng;
        const latStr = lat.toFixed(6);
        const lngStr = lng.toFixed(6);
        L.popup({ closeButton: true, className: "coord-popup" })
          .setLatLng(e.latlng)
          .setContent(
            `<div style="text-align:center;font-family:monospace;font-size:12px;padding:4px">
              <div style="font-weight:bold;margin-bottom:6px">📍 ພິກັດ</div>
              <div>${latStr}, ${lngStr}</div>
              <button onclick="navigator.clipboard.writeText('${latStr}, ${lngStr}').then(()=>this.textContent='✓ Copied!').catch(()=>{})" style="margin-top:6px;padding:3px 10px;background:#002975;color:white;border:none;border-radius:4px;cursor:pointer;font-size:11px">📋 Copy</button>
            </div>`
          )
          .openOn(leafletMapRef.current);
      }, 500);
    });
    map.on("mousemove touchmove", (e: any) => {
      if (longPressStartPosRef.current && longPressTimerRef.current) {
        const point = e.containerPoint;
        if (!point) return;
        const dist = Math.hypot(
          point.x - longPressStartPosRef.current.x,
          point.y - longPressStartPosRef.current.y
        );
        if (dist > 10) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
          longPressStartPosRef.current = null;
        }
      }
    });
    map.on("mouseup touchend", () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      longPressStartPosRef.current = null;
    });

    // V390: Smart Field Measure map-click handler. Measurement remains temporary,
    // presentation-only, and intentionally outside GeoJSON/save/valuation state.
    map.on("click", (e: any) => {
      if (!(map as any)._measureModeActive) return;
      const { lat, lng } = e.latlng;
      if (measurePointsRef.current.length >= MAX_SMART_MEASURE_POINTS) {
        sonnerToast.error(`ວັດແທກໄດ້ສູງສຸດ ${MAX_SMART_MEASURE_POINTS} ຈຸດ; ກະລຸນາຖອຍ ຫຼື ລ້າງຈຸດເກົ່າ`);
        return;
      }
      const previous = measurePointsRef.current.at(-1);
      if (previous && L.latLng(previous[0], previous[1]).distanceTo(L.latLng(lat, lng)) < MIN_SMART_MEASURE_POINT_METERS) {
        sonnerToast.message('ຈຸດໃໝ່ໃກ້ຈຸດກ່ອນເກີນໄປ');
        return;
      }
      const nextPoints = [...measurePointsRef.current, [lat, lng] as [number, number]];
      measurePointsRef.current = nextPoints;
      renderSmartMeasureSessionRef.current(nextPoints);
    });

    // v281: Coalesce map-settle events. While drawing, labels are rebuilt only
    // from a cloned drawingVerticesRef snapshot after Leaflet has settled. This
    // avoids stale marker transforms without touching geometry or persistence.
    const cancelDrawingLabelReanchor = () => {
      drawLabelReanchorGenerationRef.current += 1;
      if (drawLabelReanchorRafRef.current !== null) {
        cancelAnimationFrame(drawLabelReanchorRafRef.current);
        drawLabelReanchorRafRef.current = null;
      }
    };
    const scheduleDrawingLabelReanchor = () => {
      const generation = drawLabelReanchorGenerationRef.current + 1;
      cancelDrawingLabelReanchor();
      drawLabelReanchorGenerationRef.current = generation;
      drawLabelReanchorRafRef.current = requestAnimationFrame(() => {
        drawLabelReanchorRafRef.current = null;
        const vertices = drawingVerticesRef.current.map((point: any) => L.latLng(point.lat, point.lng));
        if (!shouldRunDrawLabelReanchor({
          requestGeneration: generation,
          currentGeneration: drawLabelReanchorGenerationRef.current,
          isDrawing: isDrawingRef.current,
          hasActiveMidpointDrag: isLiveMidpointDragRef.current,
          areSegmentLabelsVisible: showSegmentLabelsRef.current,
          vertexCount: vertices.length,
        })) return;
        segmentLabelMgrRef.current?.updateForDrawingVertices(vertices);
      });
    };
    (map as any).__cancelDrawingLabelReanchor = cancelDrawingLabelReanchor;

    // Update segment labels on zoom/move/resize (for label repositioning)
    map.on("zoomend moveend viewreset resize", () => {
      // G3: Update zoom level indicator
      setCurrentZoom(map.getZoom());
      if (isDrawingRef.current) {
        scheduleDrawingLabelReanchor();
        return;
      }
      // F2 v33: render labels for ALL polygons on zoom/pan
      // updateForLayer(activeSublayer) clears all labels then shows only active polygon
      // — with multi-polygon boundaries, other polygon labels disappear PERMANENTLY after zoom
      // (no safety-net fires after zoomend/moveend unlike pm:create)
      if (segmentLabelMgrRef.current) {
        // J1 v37: respect segment label toggle — if OFF, clear only (no re-render)
        if (!showSegmentLabelsRef.current) {
          segmentLabelMgrRef.current.clearLabels();
        } else if (polygonLayersRef.current.length > 0) {
          segmentLabelMgrRef.current.clearLabels();
          polygonLayersRef.current.forEach((l: any) => {
            segmentLabelMgrRef.current!.appendForLayer(l);
          });
        } else if (polygonLayerRef.current) {
          segmentLabelMgrRef.current.updateForLayer(polygonLayerRef.current);
        }
      }
      // Sprint v179 BUG-DRIFT-1 FIX: re-render center labels on every pan/zoom.
      // renderCenterLabels() was never called here — center label (name + area at polygon centroid)
      // stayed at stale screen position after pan until user clicked or refreshed.
      // renderCenterLabels has deps:[] and reads draftGeoJsonRef.current (ref, not stale) — safe to call here.
      renderCenterLabels();
    });

    // Always disable native Geoman toolbar — we use custom React toolbar buttons instead
    map.pm.addControls({
      position: "topleft",
      drawMarker: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawCircle: false,
      drawText: false,
      drawRectangle: false,
      drawPolygon: false,
      editMode: false,
      dragMode: false,
      cutPolygon: false,
      removalMode: false,
      rotateMode: false, // G11: hide native Geoman rotate button — we use custom FAB instead
    });

    if (!currentCanEdit) {
      map.pm.disableDraw();
      map.pm.disableGlobalEditMode();
      map.pm.disableGlobalDragMode();
      map.pm.disableGlobalRemovalMode();
    }

    if (currentCanEdit) {

      // GAP-4: Enhanced snapping — snapDistance mobile:40/desktop:15, snapMiddle + snapGuidelines
      // v75.2 BUG-2 FIX: snapDistance 15 too small for finger tap on mobile → increase to 40px
      // This allows closing polygon by tapping near (not exactly on) the first vertex
      const _isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
      map.pm.setGlobalOptions({
        snappable: true,
        snapDistance: _isMobile ? 40 : 15,
        snapMiddle: true,
        snapGuidelines: true,
        allowSelfIntersection: false,
        tooltips: false, // BUG-V3 FIX: disable English Geoman tooltips — use custom Lao guide instead
        templineStyle: { color: "#22c55e", weight: 3 },
        hintlineStyle: { color: "#22c55e", weight: 2, dashArray: "5,5" },
        pathOptions: { color: "#ffffff", fillColor: "#22c55e", fillOpacity: 0.35, weight: 3 },
        // GAP-2: Enter key finishes drawing
        finishOn: "enter",
      });

      // =====================================================================
      // SPRINT v83 Fix A: MutationObserver for midpoint marker inline styles
      // Root cause: Geoman recreates middle markers on EVERY vertex drag via
      // _updateMiddleMarkers → _createMiddleMarker → new L.divIcon → Leaflet's
      // _setIconStyles writes inline width/margin from default iconSize=[12,12].
      // Sprint v82 event-based fix (pm:globaleditmodetoggled + setTimeout) missed
      // recreation windows (drag, movestart, etc.) — markers re-created after timeout.
      // Fix: MutationObserver watches childList + attribute changes on map container.
      // Catches EVERY new .marker-icon-middle node the moment it's inserted OR when
      // Leaflet's setIcon rewrites the style attribute on an existing element.
      // setProperty(..., 'important') beats Leaflet's plain inline style always.
      // =====================================================================
      {
        const _isMobileVP = () => window.matchMedia('(max-width: 767px)').matches;
        const _fixMidpointEl = (el: HTMLElement) => {
          // Sprint v91 Fix 1: Leaflet DivIcon default iconSize=[12,12] → _setIconStyles writes
          // margin-left:-6px; margin-top:-6px as INLINE style AFTER CSS loads.
          // CSS !important on margin doesn't help because inline style wins over CSS !important
          // for properties set via element.style (not setProperty).
          // Fix: use setProperty with 'important' on ALL 4 properties so they beat Leaflet inline.
          // Sprint v101 Fix 1: Use MIDPOINT sizes (12px mobile / 10px desktop), NOT vertex sizes.
          // Previous code used sz=20/14 (vertex size) which made midpoints same size as vertices
          // AND wrote margin=-10/-7px instead of correct -6/-5px → midpoints floated off the edge.
          // Must match v99 monkey-patch: _getMidSz() = mobile ? 12 : 10.
          const mobile = _isMobileVP();
          const sz = mobile ? 12 : 10; // Sprint v101: midpoint size (smaller than vertex 20/14)
          const mg = -(sz / 2);        // Correct anchor: -6px mobile, -5px desktop
          el.style.setProperty('width', `${sz}px`, 'important');
          el.style.setProperty('height', `${sz}px`, 'important');
          el.style.setProperty('margin-left', `${mg}px`, 'important');
          el.style.setProperty('margin-top', `${mg}px`, 'important');
          el.style.setProperty('border-radius', '50%', 'important');
          el.style.setProperty('background', 'rgba(255,255,255,0.7)', 'important');
          el.style.setProperty('border', `${mobile ? 2 : 1.5}px solid #22c55e`, 'important');
          el.style.setProperty('opacity', '0.85', 'important');
          el.style.setProperty('box-shadow', '0 1px 3px rgba(0,0,0,0.4)', 'important');
        };
        // Sprint v91 Fix 2: Also fix vertex markers (marker-icon) — same Leaflet inline style problem
        const _fixVertexEl = (el: HTMLElement) => {
          if (el.classList.contains('cursor-marker')) return; // skip cursor/hint marker
          const mobile = _isMobileVP();
          const sz = mobile ? 20 : 14;
          const mg = -(sz / 2);
          el.style.setProperty('width', `${sz}px`, 'important');
          el.style.setProperty('height', `${sz}px`, 'important');
          el.style.setProperty('margin-left', `${mg}px`, 'important');
          el.style.setProperty('margin-top', `${mg}px`, 'important');
          el.style.setProperty('border-radius', '50%', 'important');
          el.style.setProperty('background', 'white', 'important');
          el.style.setProperty('border', `2px solid #22c55e`, 'important');
          el.style.setProperty('box-shadow', '0 1px 4px rgba(0,0,0,0.5)', 'important');
          // Sprint v118 Bug2 Fix: touch-action:none allows Leaflet Draggable to work on Samsung Chrome
          el.style.setProperty('touch-action', 'none', 'important');
          // Sprint v118.3 Bug3 Fix: draw-mode vertex markers have draggable:false → no leaflet-interactive
          // class → Leaflet CSS sets pointer-events:none → Samsung touch.target = map container (not marker)
          // → _onDrawTouchStart vertex detection fails → _dragState never set → vertex appears frozen.
          // Fix: force pointer-events:auto so touch.target correctly returns the marker element.
          el.style.setProperty('pointer-events', 'auto', 'important');
        };
        const _scanAndFixMarkers = (root: ParentNode) => {
          if (root instanceof HTMLElement) {
            // Sprint v114.1 Fix H-real: skip marker-icon-middle-hit (draw-mode midpoints with 44px hit area)
            if (root.classList?.contains('marker-icon-middle') && !root.classList?.contains('marker-icon-middle-hit')) _fixMidpointEl(root);
            else if (root.classList?.contains('marker-icon') && !root.classList?.contains('marker-icon-middle')) _fixVertexEl(root);
          }
          // Sprint v114.1 Fix H-real: exclude marker-icon-middle-hit from _fixMidpointEl (12px override)
          root.querySelectorAll<HTMLElement>('.marker-icon-middle:not(.marker-icon-middle-hit)').forEach(_fixMidpointEl);
          root.querySelectorAll<HTMLElement>('.marker-icon:not(.marker-icon-middle)').forEach(_fixVertexEl);
        };
        const mapContainerEl = map.getContainer();
        const midpointObserver = new MutationObserver((mutations) => {
          for (const m of mutations) {
            // New nodes added (markers being created or re-created)
            m.addedNodes.forEach(n => {
              if (n.nodeType === 1) _scanAndFixMarkers(n as HTMLElement);
            });
            // Attribute changes — Leaflet's setIcon rewrites style on same element
            if (m.type === 'attributes' && m.target instanceof HTMLElement) {
              const t = m.target;
              // Sprint v114.1 Fix H-real: skip marker-icon-middle-hit (44px draw-mode hit area)
              if (t.classList.contains('marker-icon-middle') && !t.classList.contains('marker-icon-middle-hit')) _fixMidpointEl(t);
              else if (t.classList.contains('marker-icon') && !t.classList.contains('cursor-marker')) _fixVertexEl(t);
            }
          }
        });
        midpointObserver.observe(mapContainerEl, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class'], // watch style rewrites by Leaflet
        });
        // Store for cleanup on unmount
        (map as any).__midpointObserver = midpointObserver;
        // Initial pass for any markers already present
        _scanAndFixMarkers(mapContainerEl);
      }
      // Drawing start — disable doubleClickZoom to prevent map zoom on finish
      // V65: save bearing before draw, restore after (Geoman safety)
      const savedBearingRef = { current: 0 };


      map.on("pm:drawstart", (e: any) => {
        dbg(`▶ pm:drawstart shape=${e.shape}`);
        // Sprint v110 A2: single cleanup array for all DOM listeners in this draw session
        const _drawCleanups: Array<() => void> = [];
        (map as any).__drawSessionCleanups = _drawCleanups;
        // Sprint v114 Fix D: drag guard — prevents stale drawingVerticesRef during vertex drag
        // pm:vertexadded fires during drag on some Geoman builds; this flag blocks it.
        const _isDraggingVertexRef = { current: false };
        // Sprint v277: source-of-truth for one active touch gesture. A pan may move the
        // map but must never become a Geoman vertex through a delayed compatibility click.
        let _drawGestureState = idleDrawGesture();
        let _postDragReconcileRaf: number | null = null;
        // Sprint v278: one live midpoint frame at a time. The custom midpoint becomes an
        // injected vertex on dragstart, so its visible marker and the working line must be
        // synchronized from the same current ring before each paint.
        let _liveMidpointRaf: number | null = null;
        let _liveMidpointSessionId = 0;
        let _activeLiveMidpointMarker: any = null;
        const _cancelLiveMidpointProjection = () => {
          if (_liveMidpointRaf !== null) cancelAnimationFrame(_liveMidpointRaf);
          _liveMidpointRaf = null;
          _liveMidpointSessionId += 1;
          _activeLiveMidpointMarker = null;
        };
        // Sprint v116 Fix 2: Midpoint-drag mutex — prevents Geoman from interpreting
        // the midpoint touchstart (which bubbles to Geoman after Fix B removed stopPropagation)
        // as a new vertex tap → _createVertex / _finishShape → all lines disappear.
        // Set true at touchstart on midpoint, clear on dragend + 400ms timeout.
        map.once('pm:drawend', () => {
          try { (map as any).__cancelDrawingLabelReanchor?.(); } catch {}
          isLiveMidpointDragRef.current = false;
          segmentLabelMgrRef.current?.setDrawingHandleDragging(false);
          while (_drawCleanups.length) { try { _drawCleanups.pop()!(); } catch {} }
          (map as any).__drawSessionCleanups = null;
        });
        map.doubleClickZoom.disable();
        isDrawingRef.current = true;
        drawingVerticesRef.current = [];
        setIsDrawing(true);
        setMapMode('draw'); // Sprint v73 STEP C
        setLiveArea(0);
        setLivePerimeter(0);
        setCanUndoVertex(false);
        setDrawVertexCount(0); // Sprint v73 STEP E
        // Sprint v80: reset draw header live measure text
        // Sprint v83 Fix B: also reset projectedMeasureRef so useLayoutEffect starts clean
        projectedMeasureRef.current = '';
        if (drawLiveMeasureRef.current) {
          drawLiveMeasureRef.current.textContent = 'ກຳລັງແຕ້ມ... ແຕະ ✓ ເພື່ອຈົບ';
        }
        // SPRINT v85: Radical fix — setInterval fallback guarantees live measure updates
        // every 100ms regardless of map events (bypasses all race conditions, event issues).
        // map.on('move') may not fire reliably on all mobile browsers/environments.
        // setInterval is the nuclear option: always works, no event dependency.
        // Sprint v100 B9: also store in ref so modal-close cleanup can clear it
        const _drawMeasureInterval = setInterval(() => {
          try {
            if (!isDrawingRef.current) return;
            const verts = drawingVerticesRef.current;
            const el = drawLiveMeasureRef.current;
            if (!el) return;
            let text = 'ກຳລັງແຕ້ມ... ແຕະ ✓ ເພື່ອຈົບ';
            let hasProjected = false; // Sprint v87 Fix B: only set projectedMeasureRef when we have real projected data
            if (verts.length >= 1) {
              const centerLatLng = map.getCenter();
              const projectedVerts = [...verts, centerLatLng];
              if (projectedVerts.length >= 3) {
                const projArea = calcAreaFromLatLngs(projectedVerts);
                const projPerim = calcPerimeterFromLatLngs(projectedVerts);
                if (projArea > 0) {
                  text = `${formatAreaByUnit(projArea, areaUnitRef.current)} · ${formatPerimeter(projPerim)}`;
                  hasProjected = true;
                }
              } else if (projectedVerts.length === 2) {
                const projPerim = calcPerimeterFromLatLngs(projectedVerts);
                text = formatPerimeter(projPerim);
                hasProjected = true;
              }
            }
            // Sprint v87 Fix B: ONLY update projectedMeasureRef when we have real projected data.
            // When verts=0, projectedMeasureRef stays '' so useLayoutEffect early-return is NOT triggered
            // and it correctly falls back to state (committed area). This prevents the stale defaultText
            // from blocking the early-return check after vertex add.
            if (hasProjected) {
              projectedMeasureRef.current = text;
            }
            el.textContent = text;
            // Sprint v97: also update desktop info bar DOM refs (zero React re-renders)
            if (hasProjected && verts.length >= 2) {
              try {
                const projectedVerts2 = [...verts, map.getCenter()];
                if (projectedVerts2.length >= 3) {
                  const pA = calcAreaFromLatLngs(projectedVerts2);
                  const pP = calcPerimeterFromLatLngs(projectedVerts2);
                  if (pA > 0) {
                    if (desktopAreaRef.current) desktopAreaRef.current.textContent = formatAreaByUnit(pA, areaUnitRef.current);
                    if (desktopPerimRef.current) desktopPerimRef.current.textContent = formatPerimeter(pP);
                  }
                }
              } catch {}
            }
          } catch {}
        }, 100);
        // Sprint v100 B9: store in ref for modal-close cleanup
        _drawMeasureIntervalRef.current = _drawMeasureInterval;
        // Clear interval when drawing ends (registered once per drawstart)
        map.once('pm:drawend', () => {
          clearInterval(_drawMeasureInterval);
          _drawMeasureIntervalRef.current = null;
        });
        // V65: lock bearing to 0 during drawing to avoid Geoman vertex offset bugs
        try {
          savedBearingRef.current = map.getBearing ? map.getBearing() : 0;
          if (savedBearingRef.current !== 0) {
            if (map.setBearing) map.setBearing(0);
            if (map.touchRotate && map.touchRotate.disable) map.touchRotate.disable();
          }
        } catch {}

        // =====================================================================
        // Sprint v120: Draw-mode midpoint drag — mimics Geoman Edit _addMarker.
        // KEY CHANGES vs v119:
        //   1. draggable:true → Leaflet native drag handles touch+mouse (no custom listeners)
        //   2. _latlngInfo splice included (THE TRAP v119 missed)
        //   3. buildDrawMidpointsRef bridge (component-scope ref) replaces __drawMidBuild
        //   4. _assertDrawSync verifies all 4 arrays stay parallel
        // =====================================================================

        // 3a. Helper: create a Geoman-compatible draw vertex marker for injected vertex
        // Sprint v128: DEFINITIVE FIX — restore v120 exact approach with correct analysis.
        //
        // PROBLEM HISTORY:
        //   v119/v120: addLayer to di._layerGroup → reported as 'ghost dots' by v121
        //   v121: MISDIAGNOSIS — removed addLayer entirely → invisible vertex markers
        //         (v121 claimed setLatLngs draws vertex DOTS — WRONG: setLatLngs draws
        //          polygon LINE only; vertex dots require markers in _layerGroup)
        //         v121 also claimed the dot was a 'ghost' — WRONG: it was the CORRECT
        //          vertex dot (the only visual representation of the injected vertex)
        //   v126: restored addLayer + cleanup from _layerGroup → removed vertex dots
        //   v127: added to map directly → large white circles (wrong CSS scope)
        //
        // CORRECT APPROACH (v128):
        //   1. Add injected marker to di._layerGroup permanently (same as v120)
        //      → correct visual rendering with .marker-icon CSS styling
        //      → injected marker IS the permanent vertex dot (no Geoman marker for it)
        //   2. NO cleanup needed — the injected marker should stay in _layerGroup
        //      permanently as the visual representation of the new vertex
        //   3. At pm:drawend, Geoman calls _map.removeLayer(_layerGroup) which removes
        //      ALL markers including injected ones — no cleanup needed
        //   4. The 'ghost' v121 saw was actually the CORRECT vertex dot being visible
        //      at the correct position — this is expected behavior

        const _makeDrawVertexMarker = (latlng: any, di: any) => {
          // Sprint v128: exact v120 approach — same icon as Geoman's _createMarker
          const m = L.marker(latlng, {
            pane: 'markerPane',
            draggable: false,
            icon: L.divIcon({ className: 'marker-icon' }),
          });
          (m as any)._pmTempLayer = true;
          (m as any)._isInjectedVtx = true;
          // Sprint v128: add to di._layerGroup (same as Geoman's _createMarker)
          // This ensures the marker is rendered with correct .marker-icon CSS styling
          try { (di._layerGroup as any)?.addLayer(m); } catch { /* ignore */ }
          return m;
        };

        // 3b. Sync assertion: checks 4 arrays stay parallel during draw-mode midpoint insert
        const _assertDrawSync = (label: string): boolean => {
          try {
            const di = (map.pm?.Draw as any)?.[drawModeRef.current === 'rectangle' ? 'Rectangle' : 'Polygon'];
            if (!di) return true;
            const verts = drawingVerticesRef.current || [];
            const lls = di._layer?.getLatLngs?.() ?? [];
            const ring = Array.isArray(lls[0]) ? lls[0] : lls;
            const markers = Array.isArray(di._markers) ? di._markers : [];
            const info = Array.isArray(di._layer?._latlngInfo) ? di._layer._latlngInfo : [];
            const nV = verts.length, nR = ring.length, nM = markers.length, nI = info.length;
            const lenOK = (nV === nR && nR === nM && nM === nI);
            const EPS = 1e-7;
            let driftIdx = -1;
            if (lenOK) {
              for (let i = 0; i < nV; i++) {
                const v = verts[i], r = ring[i], inf = info[i]?.latlng, mk = markers[i]?.getLatLng?.();
                const bad = (p: any) => !p || Math.abs(p.lat - v.lat) > EPS || Math.abs(p.lng - v.lng) > EPS;
                if (bad(r) || bad(inf) || bad(mk)) { driftIdx = i; break; }
              }
            }
            if (lenOK && driftIdx === -1) {
              dbg(`✅ SYNC @${label}  v=${nV} ring=${nR} mk=${nM} info=${nI}`);
              return true;
            }
            if (!lenOK) {
              dbg(`🔴 DESYNC-LEN @${label}  v=${nV} ring=${nR} mk=${nM} info=${nI}  ← lengths differ`);
            } else {
              const r = ring[driftIdx], inf = info[driftIdx]?.latlng, mk = markers[driftIdx]?.getLatLng?.(), v = verts[driftIdx];
              dbg(`🔴 DESYNC-DRIFT @${label} idx=${driftIdx}`);
              dbg(`   v=(${v?.lat?.toFixed(6)},${v?.lng?.toFixed(6)}) ring=(${(r as any)?.lat?.toFixed(6)},${(r as any)?.lng?.toFixed(6)})`);
              dbg(`   info=(${inf?.lat?.toFixed(6)},${inf?.lng?.toFixed(6)}) mk=(${mk?.lat?.toFixed(6)},${mk?.lng?.toFixed(6)})`);
            }
            return false;
          } catch (err) {
            dbg(`⚠️ _assertDrawSync error @${label}: ${err}`);
            return false;
          }
        };

        // 3c. _buildDrawMidpoints v120: draggable:true + 4-array splice
        // v120-R129 grep: grep -c '_buildDrawMidpoints' client/src/components/CollateralMapModal.tsx >= 1
        const _buildDrawMidpoints = (verts: any[]) => {
          if (!drawMidLayerRef.current) return;
          drawMidLayerRef.current.eachLayer((m: any) => { try { m.off(); } catch {} });
          drawMidLayerRef.current.clearLayers();
          if (verts.length < 2) return;
          const di = (map.pm?.Draw as any)?.[drawModeRef.current === 'rectangle' ? 'Rectangle' : 'Polygon'];
          if (!di) return;
          // Sprint v128: NO cleanup of injected vertex markers before rebuild.
          // Injected markers stay in di._layerGroup permanently as vertex dots.
          // They are the ONLY visual representation of midpoint-drag-inserted vertices.
          const _mob = window.matchMedia('(max-width:767px)').matches;
          const _hit = _mob ? 44 : 32, _dot = _mob ? 12 : 10;

          for (let i = 0; i < verts.length - 1; i++) {
            const a = verts[i], b = verts[i + 1];
            // Use Geoman's projected midpoint if available, else simple average
            const mid = (L as any).PM?.Utils?.calcMiddleLatLng
              ? (L as any).PM.Utils.calcMiddleLatLng(map, a, b)
              : L.latLng((a.lat + b.lat) / 2, (a.lng + b.lng) / 2);
            // Hide midpoint if segment is too short on screen (< 50px)
            try {
              const pa = map.latLngToContainerPoint(a), pb = map.latLngToContainerPoint(b);
              if (Math.hypot(pa.x - pb.x, pa.y - pb.y) < 50) continue;
            } catch {}

            const midMkr = L.marker(mid, {
              // Sprint v125 Fix: use markerPane + zIndexOffset:-100 so Geoman vertex markers
              // (which use zIndexOffset:0 default, rendered at higher z by Leaflet's
              // _updateZIndex logic) always appear ON TOP of midpoint dots.
              // Sprint v123 shadowPane caused vertex markers to be hidden behind the pane
              // because Leaflet renders shadowPane (z=500) markers as a flat layer that
              // covers markerPane (z=600) elements when the shadow layer repaints.
              pane: 'markerPane',
              zIndexOffset: -100,
              draggable: true,
              interactive: true,
              icon: L.divIcon({
                className: 'marker-icon marker-icon-middle marker-icon-middle-hit',
                iconSize: [_hit, _hit], iconAnchor: [_hit/2, _hit/2],
                // Sprint v123 Fix 1 kept: opacity 0.45 so labels are readable through midpoint dot
                html: `<div style="width:${_dot}px;height:${_dot}px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);border-radius:50%;background:rgba(255,255,255,0.55);border:${_mob?2:1.5}px solid #22c55e;box-shadow:0 1px 3px rgba(0,0,0,.3);opacity:.45;pointer-events:none"></div>`,
              }),
            });
            const _segIdx = i;
            let _ins: { idx: number; vtx: any } | null = null;
            let _midpointDragSessionId = 0;
            let _workingLayerWasNested = false;
            let _workingLayerStyle: { opacity?: number; fillOpacity?: number } | null = null;
            let _liveSegmentOverlay: any = null;

            const _clearLiveSegmentOverlay = () => {
              try { _liveSegmentOverlay?.remove?.(); } catch {}
              _liveSegmentOverlay = null;
              try { drawLiveSegmentLayerRef.current?.clearLayers?.(); } catch {}
            };

            const _renderLiveSegmentOverlay = (liveVertices: any[]) => {
              if (!_liveSegmentOverlay) {
                _liveSegmentOverlay = L.polyline(liveVertices, {
                  pane: 'overlayPane',
                  color: '#16a34a',
                  weight: 4,
                  opacity: 1,
                  interactive: false,
                  lineCap: 'round',
                  lineJoin: 'round',
                }).addTo(drawLiveSegmentLayerRef.current);
              } else {
                _liveSegmentOverlay.setLatLngs(liveVertices);
              }
            };

            const _restoreWorkingLayerSurface = () => {
              _clearLiveSegmentOverlay();
              if (_workingLayerStyle) {
                try { di._layer?.setStyle?.(_workingLayerStyle); } catch {}
              }
              _workingLayerStyle = null;
            };

            const _scheduleLiveMidpointProjection = (pointerLatLng: any) => {
              if (!_ins) return;
              const frameSessionId = _midpointDragSessionId;
              const frameMarker = midMkr;
              const livePointer = L.latLng(pointerLatLng.lat, pointerLatLng.lng);
              if (_liveMidpointRaf !== null) cancelAnimationFrame(_liveMidpointRaf);
              _liveMidpointRaf = requestAnimationFrame(() => {
                _liveMidpointRaf = null;
                // BUG-LIVE-MIDPOINT v278: reject delayed frames after another marker,
                // undo/cancel, drawend, or Geoman layer rebuild takes ownership.
                if (!isCurrentLiveMidpointFrame(frameSessionId, _liveMidpointSessionId, frameMarker, _activeLiveMidpointMarker) || !_ins) return;
                try {
                  // BUG-LIVE-MIDPOINT v280: Geoman can repaint the old working path while
                  // a custom midpoint marker follows the finger. The temporary overlay and
                  // the marker therefore use this one captured pointer coordinate. Geoman
                  // receives the accepted full geometry only at drag end.
                  const currentVertices = [...drawingVerticesRef.current];
                  const activeSegment = buildLiveDrawSegment(
                    currentVertices[_ins.idx - 1],
                    livePointer,
                    currentVertices[_ins.idx + 1],
                  );
                  if (!activeSegment) return;
                  currentVertices[_ins.idx] = livePointer;
                  _ins.vtx?.setLatLng?.(livePointer);
                  midMkr.setLatLng(livePointer);
                  if (di._layer._latlngInfo?.[_ins.idx]) di._layer._latlngInfo[_ins.idx].latlng = livePointer;
                  drawingVerticesRef.current = currentVertices;
                  _renderLiveSegmentOverlay(currentVertices);
                  segmentLabelMgrRef.current?.updateForDrawingVertices(currentVertices);
                } catch {}
              });
            };

            // Guard: click on midpoint should not place a new draw vertex
            midMkr.on('click', (ev: any) => { try { (L as any).DomEvent.stop(ev); } catch {} });

            midMkr.on('dragstart', () => {
              try { map.dragging.disable(); } catch {}
              _isDraggingVertexRef.current = true;
              isLiveMidpointDragRef.current = true;
              segmentLabelMgrRef.current?.setDrawingHandleDragging(true);
              try { (map as any).__cancelDrawingLabelReanchor?.(); } catch {}
              _cancelLiveMidpointProjection();
              _midpointDragSessionId = ++_liveMidpointSessionId;
              _activeLiveMidpointMarker = midMkr;
              try { (map as any).__liveDrawSegmentCleanup?.(); } catch {}
              // Sprint v122 Fix: clear hint label on midpoint dragstart
              // Root cause: _hintMarker (separate from edge labels) keeps updating via
              // touchmove/mousemove while isDrawingRef=true, leaving a ghost label after drag.
              // clearHintLabel() removes it immediately; it will reappear on next finger move.
              if (segmentLabelMgrRef.current?.clearHintLabel) segmentLabelMgrRef.current.clearHintLabel();
              const ll = midMkr.getLatLng();
              const insIdx = _segIdx + 1;
              // 1. Splice into Geoman layer ring
              const lls = di._layer.getLatLngs();
              const ring = Array.isArray(lls[0]) ? lls[0] : lls;
              _workingLayerWasNested = Array.isArray(lls[0]);
              _workingLayerStyle = {
                opacity: di._layer?.options?.opacity,
                fillOpacity: di._layer?.options?.fillOpacity,
              };
              ring.splice(insIdx, 0, ll);
              di._layer.setLatLngs(Array.isArray(lls[0]) ? [ring] : ring);
              // Hide Geoman's competing working surface while the overlay owns the
              // live drag frame. Vertex markers stay visible in their own layer group.
              try { di._layer.setStyle?.({ opacity: 0, fillOpacity: 0 }); } catch {}
              // 2. Splice into di._markers
              if (Array.isArray(di._markers)) {
                const vtx = _makeDrawVertexMarker(ll, di);
                di._markers.splice(insIdx, 0, vtx);
                _ins = { idx: insIdx, vtx };
              }
              // 3. Splice into _latlngInfo (THE TRAP v119 missed)
              if (!Array.isArray(di._layer._latlngInfo)) di._layer._latlngInfo = [];
              di._layer._latlngInfo.splice(insIdx, 0, { latlng: ll, snapInfo: null });
              // 4. Splice into drawingVerticesRef
              drawingVerticesRef.current.splice(insIdx, 0, ll);
              setDrawVertexCount(drawingVerticesRef.current.length);
              (map as any).__liveDrawSegmentCleanup = _restoreWorkingLayerSurface;
              _assertDrawSync('midpoint-insert-start');
            });

            midMkr.on('drag', () => {
              if (!_ins) return;
              const ll = midMkr.getLatLng();
              // The marker is dragged by Leaflet already. The following shared pipeline
              // renders its matching segment in the next frame without touching Geoman's
              // working path, which may otherwise repaint stale coordinates mid-drag.
              const nextLatLng = L.latLng(ll.lat, ll.lng);
              drawingVerticesRef.current[_ins.idx] = nextLatLng;
              // Paint synchronously in this drag event, then use RAF only to reconcile
              // associated marker metadata and labels. This prevents even a one-frame
              // visible separation between the finger-following marker and live segment.
              _renderLiveSegmentOverlay([...drawingVerticesRef.current]);
              _scheduleLiveMidpointProjection(nextLatLng);
            });

            midMkr.on('dragend', () => {
              try { map.dragging.enable(); } catch {}
              _isDraggingVertexRef.current = false;
              isLiveMidpointDragRef.current = false;
              segmentLabelMgrRef.current?.setDrawingHandleDragging(false);
              _cancelLiveMidpointProjection();
              const flat = [...drawingVerticesRef.current];
              // Commit the accepted geometry once after the active overlay has stopped.
              // This is the sole Geoman handoff point for a custom midpoint drag.
              try { di._layer.setLatLngs(_workingLayerWasNested ? [flat] : flat); } catch {}
              _restoreWorkingLayerSurface();
              try { delete (map as any).__liveDrawSegmentCleanup; } catch {}
              // Sprint v122 Fix: clear hint label on dragend (belt-and-suspenders)
              // In case a stale touchmove fired during drag and left a hint label at wrong position.
              if (segmentLabelMgrRef.current?.clearHintLabel) segmentLabelMgrRef.current.clearHintLabel();
              const area = calcAreaFromLatLngs(flat), perim = calcPerimeterFromLatLngs(flat);
              setLiveArea(area); setLivePerimeter(perim);
              setDrawVertexCount(flat.length);
              setCanUndoVertex(flat.length >= 1);
              if (desktopAreaRef.current) desktopAreaRef.current.textContent = formatAreaByUnit(area, areaUnitRef.current);
              if (desktopPerimRef.current) desktopPerimRef.current.textContent = formatPerimeter(perim);
              if (segmentLabelMgrRef.current) segmentLabelMgrRef.current.updateForDrawingVertices(flat);
              _assertDrawSync('midpoint-drag-end');
              _ins = null;
              requestAnimationFrame(() => { try { _buildDrawMidpoints(flat); } catch {} });
            });

            midMkr.addTo(drawMidLayerRef.current);
          }
        };

        // Phase 4: expose via ref so handleUndoVertex (component scope) can call it
        buildDrawMidpointsRef.current = _buildDrawMidpoints;
        _drawCleanups.push(() => {
          // Sprint v128: injected vertex markers are cleaned up automatically by Geoman
          // via _map.removeLayer(_layerGroup) in disable() — no manual cleanup needed.
          drawMidLayerRef.current?.clearLayers();
          try { (map as any).__liveDrawSegmentCleanup?.(); } catch {}
          try { delete (map as any).__liveDrawSegmentCleanup; } catch {}
          drawLiveSegmentLayerRef.current?.clearLayers();
          _cancelLiveMidpointProjection();
          buildDrawMidpointsRef.current = () => {}; // no-op after drawend
        });

        // Sprint v115.5 Bug A: extracted helper to rebuild draw-mode midpoint markers.
        e.workingLayer?.on("pm:vertexadded", (ve: any) => {
          // Sprint v277: a mobile pan can emit a delayed Geoman compatibility click.
          // Reject it before it enters drawingVerticesRef and immediately restore the
          // working layer/marker arrays from the last accepted geometry snapshot.
          const _vertexMap = leafletMapRef.current;
          let _panOriginatedVertex = !shouldAcceptDrawingVertex(_drawGestureState);
          if (!_panOriginatedVertex && _vertexMap && ve.latlng) {
            try {
              const _point = _vertexMap.latLngToContainerPoint(ve.latlng);
              const _rect = _vertexMap.getContainer().getBoundingClientRect();
              const _consumed = consumeSyntheticClickSuppression(_drawGestureState, {
                now: performance.now(),
                clientX: _rect.left + _point.x,
                clientY: _rect.top + _point.y,
              });
              _drawGestureState = _consumed.state;
              _panOriginatedVertex = _consumed.suppressed;
            } catch {}
          }
          if (_panOriginatedVertex) {
            const _accepted = [...drawingVerticesRef.current];
            try {
              const _workingLatLngs = e.workingLayer?.getLatLngs?.() ?? [];
              e.workingLayer?.setLatLngs?.(Array.isArray(_workingLatLngs[0]) ? [_accepted] : _accepted);
              const _drawInstance = (map.pm?.Draw as any)?.[drawModeRef.current === 'rectangle' ? 'Rectangle' : 'Polygon'];
              if (Array.isArray(_drawInstance?._markers) && _drawInstance._markers.length > _accepted.length) {
                const _extras = _drawInstance._markers.splice(_accepted.length);
                _extras.forEach((marker: any) => { try { _drawInstance._layerGroup?.removeLayer(marker); } catch {} });
              }
              if (Array.isArray(_drawInstance?._layer?._latlngInfo)) {
                _drawInstance._layer._latlngInfo.splice(_accepted.length);
              }
              segmentLabelMgrRef.current?.clearHintLabel?.();
              if (_postDragReconcileRaf) cancelAnimationFrame(_postDragReconcileRaf);
              _postDragReconcileRaf = requestAnimationFrame(() => {
                _postDragReconcileRaf = null;
                try { _buildDrawMidpoints(_accepted); } catch {}
              });
            } catch {}
            dbg('🟡 v277: rejected pan-originated pm:vertexadded');
            return;
          }
          // Sprint v114 Fix D: reject pm:vertexadded during vertex drag (prevents stale drawingVerticesRef)
          if (_isDraggingVertexRef.current) {
            dbg('⚠️ Sprint v114 Fix D: pm:vertexadded rejected during drag');
            return;
          }
          if (ve.latlng) {
            // STEP2-R24: Screen-pixel duplicate guard (replaces meter-based guard)
            // Exempt: first vertex (no prev), and close-shape tap (snap to first vertex)
            // Guard: if new vertex is within 25 screen-pixels of the PREVIOUS vertex
            // (not first vertex), reject as accidental double-tap.
            // 25px: Mapbox GL Draw touchBuffer default (docs/API.md) — validated by real-user testing.
            const map = leafletMapRef.current;
            if (map && drawingVerticesRef.current.length >= 1) {
              const prevLatLng = drawingVerticesRef.current[drawingVerticesRef.current.length - 1];
              const prevPx = map.latLngToContainerPoint(prevLatLng);
              const newPx = map.latLngToContainerPoint(ve.latlng);
              const dx = prevPx.x - newPx.x;
              const dy = prevPx.y - newPx.y;
              const distPx = Math.sqrt(dx * dx + dy * dy);
              // Exempt close-shape tap: if new vertex is within snapDistance of FIRST vertex,
              // Geoman will close the shape — allow it through regardless of prev-vertex distance.
              const firstLatLng = drawingVerticesRef.current[0];
              const firstPx = map.latLngToContainerPoint(firstLatLng);
              const dxFirst = firstPx.x - newPx.x;
              const dyFirst = firstPx.y - newPx.y;
              const distFirstPx = Math.sqrt(dxFirst * dxFirst + dyFirst * dyFirst);
              const snapDist = 40; // matches mobile snapDistance in setGlobalOptions
              const isCloseShapeTap = distFirstPx <= snapDist;
              if (distPx < 25 && !isCloseShapeTap) {
                // Duplicate tap — reject silently
                return;
              }
            }
            // G8: Snap-to-angle — snap to nearest 45° from previous segment
            let snappedLatlng = ve.latlng;
            // Sprint v114 Fix B: use projected pixel-space for 45° snap (raw lat/lng is non-uniform at
            // high latitudes — dx/dy in degrees gives wrong angles because 1° lng ≠ 1° lat in metres)
            if (snapAngleRef.current && drawingVerticesRef.current.length >= 1) {
              try {
                const prev = drawingVerticesRef.current[drawingVerticesRef.current.length - 1];
                const _snapMap = leafletMapRef.current;
                if (_snapMap) {
                  const prevPx = _snapMap.latLngToContainerPoint(prev);
                  const newPx = _snapMap.latLngToContainerPoint(ve.latlng);
                  const dxPx = newPx.x - prevPx.x;
                  const dyPx = -(newPx.y - prevPx.y); // invert Y: screen-down = map-south
                  const rawAngle = Math.atan2(dyPx, dxPx) * (180 / Math.PI);
                  const snappedAngle = Math.round(rawAngle / 45) * 45;
                  const distPx = Math.sqrt(dxPx * dxPx + dyPx * dyPx);
                  const rad = snappedAngle * (Math.PI / 180);
                  const snappedPx = _snapMap.containerPointToLatLng({
                    x: prevPx.x + distPx * Math.cos(rad),
                    y: prevPx.y - distPx * Math.sin(rad), // re-invert Y back to screen-space
                  } as any);
                  const L = (window as any).L;
                  if (L) snappedLatlng = L.latLng(snappedPx.lat, snappedPx.lng);
                }
              } catch {}
            }
            // Sprint v109 Fix 3: Write snapped latlng back to Geoman workingLayer
            // Previously: snappedLatlng was pushed to drawingVerticesRef but the Geoman layer
            // kept the unsnapped vertex — causing geometry/measurement divergence with Snap ON.
            if (snapAngleRef.current && snappedLatlng !== ve.latlng) {
              try {
                const wl = e.workingLayer;
                if (wl && wl.getLatLngs) {
                  const lls = wl.getLatLngs() as any[];
                  const ring = Array.isArray(lls[0]) ? lls[0] : lls;
                  if (ring.length > 0) {
                    ring[ring.length - 1] = snappedLatlng;
                    wl.setLatLngs(Array.isArray(lls[0]) ? [ring] : ring);
                  }
                }
              } catch {}
            }
            drawingVerticesRef.current.push(snappedLatlng);
            // Sprint v109 Fix 2b: Haptic feedback moved here from removed duplicate map-level handler
            if (typeof navigator !== 'undefined' && navigator.vibrate) {
              try { navigator.vibrate(30); } catch {}
            }
            // v17 FIX: active from first vertex (>= 1) so user can undo immediately
            setCanUndoVertex(drawingVerticesRef.current.length >= 1);
            setDrawVertexCount(drawingVerticesRef.current.length);
            const area = calcAreaFromLatLngs(drawingVerticesRef.current);
            const perim = calcPerimeterFromLatLngs(drawingVerticesRef.current);
            setLiveArea(area);
            setLivePerimeter(perim);
            // Sprint v80: update draw mode header live measure via DOM ref (zero re-renders)
            if (drawLiveMeasureRef.current && area > 0) {
              drawLiveMeasureRef.current.textContent =
                `${formatAreaByUnit(area, areaUnitRef.current)} · ${formatPerimeter(perim)}`;
            }
            // Sprint v97: also update desktop info bar DOM refs on vertex commit
            if (area > 0) {
              if (desktopAreaRef.current) desktopAreaRef.current.textContent = formatAreaByUnit(area, areaUnitRef.current);
              if (desktopPerimRef.current) desktopPerimRef.current.textContent = formatPerimeter(perim);
            }
            // Sprint v124: Reverted Sprint v123 Fix 2 (rAF defer broke vertex rendering
            // because requestAnimationFrame raced with Geoman's internal state update,
            // causing di._markers to be out of sync with drawingVerticesRef).
            // Restore synchronous label update (v120 pattern).
            if (segmentLabelMgrRef.current) {
              segmentLabelMgrRef.current.updateForDrawingVertices(drawingVerticesRef.current);
            }
            // Sprint v120: rebuild draw-mode midpoint markers after each vertex
            setTimeout(() => _buildDrawMidpoints([...drawingVerticesRef.current]), 0);
            requestAnimationFrame(() => { _assertDrawSync('vertex-added'); });
          }
        });

        // =====================================================================
        // SPRINT v78: LIVE HINTLINE MEASURE (Bug 2 Fix)
        // Claude Opus: live measure only updated on pm:vertexadded (after tap).
        // Fix: listen to map 'move' (pan) + DOM 'touchmove' to update distance
        // from last vertex to current cursor position in real-time.
        {
          let _hintLastTouchX = 0;
          let _hintLastTouchY = 0;
          let _hintRafId: number | null = null;
          // Sprint v79 Fix: accept optional overrideLatLng (map.getCenter() during pan)
          const _updateHintlineMeasure = (overrideLatLng?: any) => {
            try {
              const verts = drawingVerticesRef.current;
              if (verts.length === 0) return;
              const lastVertex = verts[verts.length - 1];
              // Sprint v79: use overrideLatLng (map center on pan) or touch position
              let cursorLatLng: any;
              if (overrideLatLng) {
                cursorLatLng = overrideLatLng;
              } else {
                const rect = map.getContainer().getBoundingClientRect();
                const cursorContainerPt = (window as any).L?.point
                  ? (window as any).L.point(_hintLastTouchX - rect.left, _hintLastTouchY - rect.top)
                  : { x: _hintLastTouchX - rect.left, y: _hintLastTouchY - rect.top };
                cursorLatLng = map.containerPointToLatLng(cursorContainerPt);
              }
              // Haversine distance from last vertex to cursor
              const R = 6371000;
              const lat1 = lastVertex.lat * Math.PI / 180;
              const lat2 = cursorLatLng.lat * Math.PI / 180;
              const dLat = (cursorLatLng.lat - lastVertex.lat) * Math.PI / 180;
              const dLng = (cursorLatLng.lng - lastVertex.lng) * Math.PI / 180;
              const a = Math.sin(dLat/2)**2 + Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLng/2)**2;
              const distM = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
              // Update hintline segment label (midpoint between last vertex and cursor)
              // Sprint v106 Fix 3: Revert to arithmetic average for hintline label midpoint.
              const midLatLng = { lat: (lastVertex.lat + cursorLatLng.lat) / 2, lng: (lastVertex.lng + cursorLatLng.lng) / 2 };
              if (segmentLabelMgrRef.current?.updateHintLabel) {
                segmentLabelMgrRef.current.updateHintLabel(midLatLng, distM);
              }
            } catch {}
          };
          // Sprint v83 Fix B: helper to update draw header live measure with projected area
          // Uses drawingVerticesRef + map.getCenter() to compute area of polygon-in-progress
          // SPRINT v84 Fix: Claude diagnosis — RAF in _onHintMapMove caused race conditions.
          // Leaflet 'move' event is already throttled internally; direct DOM update is safe.
          // Timestamp throttle (16ms ≈ 60fps) prevents excessive computation.
          // projectedMeasureRef stores latest pan text; useLayoutEffect prefers it over state.
          let _lastDrawHeaderUpdate = 0;
          const _updateDrawHeaderLiveMeasure = () => {
            try {
              if (!drawLiveMeasureRef.current) return;
              // Throttle to ~60fps (16ms) to avoid excessive area calculations
              const now = performance.now();
              if (now - _lastDrawHeaderUpdate < 16) return;
              _lastDrawHeaderUpdate = now;
              const verts = drawingVerticesRef.current;
              let text = 'ກຳລັງແຕ້ມ... ແຕະ ✓ ເພື່ອຈົບ';
              if (verts.length >= 1) {
                // Project area: polygon with current vertices + map center as next vertex
                // Works from 1 vertex (shows projected line/area as user pans)
                const centerLatLng = map.getCenter();
                const projectedVerts = [...verts, centerLatLng];
                if (projectedVerts.length >= 3) {
                  const projArea = calcAreaFromLatLngs(projectedVerts);
                  const projPerim = calcPerimeterFromLatLngs(projectedVerts);
                  if (projArea > 0) {
                    text = `${formatAreaByUnit(projArea, areaUnitRef.current)} · ${formatPerimeter(projPerim)}`;
                  }
                } else if (projectedVerts.length === 2) {
                  const projPerim = calcPerimeterFromLatLngs(projectedVerts);
                  text = formatPerimeter(projPerim);
                }
              }
              // CRITICAL: Update both ref AND DOM directly
              projectedMeasureRef.current = text;
              drawLiveMeasureRef.current.textContent = text;
              // Sprint v97: also update desktop info bar DOM refs during pan/move
              if (verts.length >= 2) {
                const pVerts = [...verts, map.getCenter()];
                if (pVerts.length >= 3) {
                  const pA = calcAreaFromLatLngs(pVerts);
                  const pP = calcPerimeterFromLatLngs(pVerts);
                  if (pA > 0) {
                    if (desktopAreaRef.current) desktopAreaRef.current.textContent = formatAreaByUnit(pA, areaUnitRef.current);
                    if (desktopPerimRef.current) desktopPerimRef.current.textContent = formatPerimeter(pP);
                  }
                }
              }
            } catch {}
          };
          const _onHintTouchMove = (ev: TouchEvent) => {
            // Sprint v80 Fix: hintline label update on touchmove (originally used map.getCenter())
            // Sprint v94 B3: use actual touch position (not map.getCenter()) for hintline label
            // Root cause: map.getCenter() doesn't change when user moves finger (map doesn't pan)
            // so label was stuck at map center instead of following the cursor.
            // Fix: convert touch coords to latlng directly — label follows finger in real-time.
            if (!isDrawingRef.current || ev.touches.length === 0) return;
            const _gestureTouch = Array.from(ev.touches).find((touch) => touch.identifier === _drawGestureState.touchId);
            if (_gestureTouch) {
              _drawGestureState = updateDrawGesture(_drawGestureState, {
                touchId: _gestureTouch.identifier,
                clientX: _gestureTouch.clientX,
                clientY: _gestureTouch.clientY,
                now: performance.now(),
              });
              if (shouldClearTransientDrawingFeedback(_drawGestureState)) {
                segmentLabelMgrRef.current?.clearHintLabel?.();
                return;
              }
            }
            _hintLastTouchX = ev.touches[0].clientX;
            _hintLastTouchY = ev.touches[0].clientY;
            if (_hintRafId) cancelAnimationFrame(_hintRafId);
            _hintRafId = requestAnimationFrame(() => {
              // Pass undefined so _updateHintlineMeasure uses _hintLastTouchX/Y (actual cursor)
              _updateHintlineMeasure(undefined);
              // Sprint v84: direct call (no RAF wrapper) for draw header update
              _updateDrawHeaderLiveMeasure();
            });
          };
          const _onHintMapMove = () => {
            // Sprint v84 Fix: REMOVE RAF wrapper — Leaflet 'move' is already throttled.
            // RAF caused race: if cancelled by _onDrawTouchMove's RAF cancel, update was lost.
            // Direct call is safe and ensures update fires on every Leaflet move event.
            if (!isDrawingRef.current) return;
            if (shouldClearTransientDrawingFeedback(_drawGestureState)) {
              segmentLabelMgrRef.current?.clearHintLabel?.();
              return;
            }
            const centerLatLng = map.getCenter();
            // Update hintline label (still uses RAF for smooth animation)
            if (_hintRafId) cancelAnimationFrame(_hintRafId);
            _hintRafId = requestAnimationFrame(() => {
              _updateHintlineMeasure(centerLatLng);
            });
            // Sprint v84: draw header update is DIRECT (no RAF) — guaranteed to fire
            _updateDrawHeaderLiveMeasure();
          };
          // Sprint v111 Fix 3: Add mousemove listener for desktop hint label
          // Root cause: no mousemove listener existed — hint label only updated on pan/touch.
          // On desktop, moving mouse fires neither 'move' nor 'touchmove' — label never appeared.
          const _onHintMouseMove = (ev: MouseEvent) => {
            if (!isDrawingRef.current) return;
            const rect = map.getContainer().getBoundingClientRect();
            const pt = (window as any).L?.point
              ? (window as any).L.point(ev.clientX - rect.left, ev.clientY - rect.top)
              : { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
            const cursorLatLng = map.containerPointToLatLng(pt);
            if (_hintRafId) cancelAnimationFrame(_hintRafId);
            _hintRafId = requestAnimationFrame(() => {
              _hintRafId = null;
              _updateHintlineMeasure(cursorLatLng);
              _updateDrawHeaderLiveMeasure();
            });
          };
          const mapContainer = map.getContainer();
          mapContainer.addEventListener('touchmove', _onHintTouchMove, { passive: true });
          mapContainer.addEventListener('mousemove', _onHintMouseMove);
          // Sprint v84: listen to 'move' (fires on every pan frame in Leaflet)
          // 'drag' and 'zoom' are also included for completeness
          map.on('move drag zoom', _onHintMapMove);
          // Sprint v110 A2: register cleanup via _drawCleanups (no capture flag on touchmove)
          _drawCleanups.push(() => {
            mapContainer.removeEventListener('touchmove', _onHintTouchMove);
            mapContainer.removeEventListener('mousemove', _onHintMouseMove);
            map.off('move drag zoom', _onHintMapMove);
            if (_hintRafId) { cancelAnimationFrame(_hintRafId); _hintRafId = null; }
            if (segmentLabelMgrRef.current?.clearHintLabel) segmentLabelMgrRef.current.clearHintLabel();
          });
        }
        // =====================================================================
        // SPRINT v79: OVERRIDE _syncHintMarker TO USE map.getCenter() IN DRAW MODE
        // Root cause: Geoman _syncHintMarker uses e.latlng (touch position) → cursor at finger
        // Fix: replace with map.getCenter() so hintMarker always stays at map center during pan
        // This ensures the dashed hintline always points to map center (where vertex will be placed)
        // Sprint v111 Fix 3: Gate to mobile only — on desktop, mouse cursor IS the hint target;
        // overriding to map.getCenter() on desktop caused hint to freeze at center, not follow cursor.
        if (isMobile) {
          const di = (map.pm?.Draw as any)?.['Polygon'] || (map.pm?.Draw as any)?.['Rectangle'];
          if (di && di._syncHintMarker) {
            const _originalSyncHintMarker = di._syncHintMarker.bind(di);
            di._syncHintMarker = function(e: any) {
              // Sprint v79: always use map center (not touch position) for hintMarker
              const centerLatLng = map.getCenter();
              if (this._hintMarker) {
                this._hintMarker.setLatLng(centerLatLng);
                // Still call snap logic by passing a synthetic event with map center
                const syntheticE = { ...e, latlng: centerLatLng };
                // Update hintline (templine to cursor) via _change
                try {
                  const latlngs = this._layer?._defaultShape?.()?.slice?.() || [];
                  latlngs.push(centerLatLng);
                  if (typeof this._change === 'function') this._change(latlngs);
                } catch {}
              }
            };
            // Restore on drawend
            map.once('pm:drawend', () => {
              if (di) di._syncHintMarker = _originalSyncHintMarker;
            });
          }
        }
        // =====================================================================
        // =====================================================================
        // SPRINT v75.2: DRAW-MODE VERTEX DRAG (v75-R20)
        // v75.2 FIX: Use LAZY di access inside touchstart handler (not captured in closure)
        // Root cause of v75 failure: di was captured at pm:drawstart time when _markers=[]
        // Now: di is re-fetched inside each handler call so _markers is always current.
        // Also: target detection uses both classList.contains AND closest() for robustness.
        // v75-R20 grep: grep -c 'v75-R20' client/src/components/CollateralMapModal.tsx >= 1
        // =====================================================================
        {
          // Store shape name for lazy di access (e.shape is available at pm:drawstart time)
          const _drawShape: string = (e as any).shape || 'Polygon';

          let _dragState: {
            marker: any;
            markerIndex: number;
            startTouchId: number;
            active: boolean;
            di: any;
            _origSync: ((...args: any[]) => any) | null;
          } | null = null;
          // Sprint v98: rAF handle for draw-mode vertex drag label update
          let _drawDragRaf: number | null = null;

          const _onDrawTouchStart = (ev: TouchEvent) => {
            if (!isDrawingRef.current) return;
            // Sprint v114 Fix P: reject multi-touch (pinch-zoom) — ev.touches.length > 1 means
            // user is pinching/zooming, not tapping to add a vertex. Without this guard,
            // a two-finger pinch fires 2 touchstart events → 2 vertices added simultaneously.
            if (ev.touches.length > 1) {
              const firstTouch = ev.touches[0];
              if (firstTouch) {
                _drawGestureState = beginDrawGesture({
                  touchId: firstTouch.identifier,
                  target: "canvas",
                  clientX: firstTouch.clientX,
                  clientY: firstTouch.clientY,
                  now: performance.now(),
                  isMultiTouch: true,
                });
              }
              segmentLabelMgrRef.current?.clearHintLabel?.();
              return;
            }
            const touch = ev.touches[0];
            if (!touch) return;
            const _initialTarget = touch.target as HTMLElement;
            _drawGestureState = beginDrawGesture({
              touchId: touch.identifier,
              target: _initialTarget.closest?.('.leaflet-control, button, [role="button"]')
                ? "control"
                : _initialTarget.closest?.('.crs-map-center-label-icon, .segment-label')
                  ? "passive"
                  : "canvas",
              clientX: touch.clientX,
              clientY: touch.clientY,
              now: performance.now(),
            });
            dbg(`👆 touchStart x=${touch.clientX.toFixed(0)} y=${touch.clientY.toFixed(0)} verts=${drawingVerticesRef.current.length}`);

            // =====================================================================
            // Sprint v113.1 Fix 1: midpoint-icon guard — MUST BE FIRST CHECK
            // Claude Code audit: original v113 guard was placed AFTER close-shape check.
            // When first segment < ~120px on screen, midpoint is inside 60px close-shape
            // radius → close-shape check fires first → exits draw mode.
            // Fix: midpoint guard runs BEFORE any other check so it always wins.
            // Belt-and-braces: keep stopPropagation (capture phase) + preventDefault
            // (suppresses compatibility click that Geoman turns into spurious vertex).
            // Marker-level click stop added in midpoint creation loop (Fix 1b).
            // =====================================================================
            const _touchTarget = touch.target as HTMLElement;

            // ===================================================================
            // Sprint v77 Bug B Fix: close-polygon proximity check
            // Sprint v90 Bug2 Fix: use mapContainer.getBoundingClientRect() for correct
            //   touch-to-container-point conversion (mouseEventToContainerPoint fails for TouchEvent)
            // 60px snap radius (> Apple 44px minimum), vibrate on close
            // Must run BEFORE marker-drag detection so close-tap wins over drag
            // ===================================================================
            if (drawingVerticesRef.current.length >= 3) {
              try {
                const firstLatLng = drawingVerticesRef.current[0];
                const firstPx = map.latLngToContainerPoint(firstLatLng);
                // Sprint v90 Bug2 Fix: get container offset from DOM rect (not mouseEventToContainerPoint)
                const mapContainer = map.getContainer();
                const rect = mapContainer.getBoundingClientRect();
                const touchContainerX = touch.clientX - rect.left;
                const touchContainerY = touch.clientY - rect.top;
                const dx = firstPx.x - touchContainerX;
                const dy = firstPx.y - touchContainerY;
                const distPx = Math.sqrt(dx * dx + dy * dy);
                dbg(`close-check: firstPx=(${firstPx.x.toFixed(0)},${firstPx.y.toFixed(0)}) touchPx=(${touchContainerX.toFixed(0)},${touchContainerY.toFixed(0)}) dist=${distPx.toFixed(1)}px`);
                if (distPx <= 60) {
                  ev.stopPropagation();
                  ev.preventDefault();
                  dbg(`close-polygon triggered at dist=${distPx.toFixed(1)}px`);
                  // Magnetic snap: finish shape programmatically
                  // Sprint v114 Fix A: use _drawShape (not hardcoded 'Polygon') to support Rectangle
                  const di = (map.pm?.Draw as any)?.[_drawShape];
                  if (di?._finishShape) {
                    di._finishShape();
                  } else if (di?.disable) {
                    // Fallback: disable draw (triggers pm:drawend with current vertices)
                    di.disable();
                  }
                  if (navigator.vibrate) navigator.vibrate([30, 50, 30]);
                  return;
                }
              } catch {}
            }
            // === End Bug B Fix ===

            // Sprint v113.2: removed dead v113 guard block (was unreachable after v113.1 moved guard to first check)
            // _touchTarget already holds touch.target from the first-check block above
            // Detect vertex marker: target itself OR its parent has .marker-icon (but not cursor-marker)
            // Sprint v118.3 Bug3 Fix: belt-and-suspenders — also try elementFromPoint.
            // On Samsung Chrome, draw-mode vertex markers had pointer-events:none (no leaflet-interactive
            // class) → touch.target returned the map container, not the marker. The _fixVertexEl now
            // sets pointer-events:auto, but elementFromPoint is a reliable fallback for older renders.
            let _resolvedTarget: HTMLElement = _touchTarget;
            if (!_touchTarget.classList?.contains('marker-icon') && !_touchTarget.closest?.('.marker-icon')) {
              const elFromPt = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null;
              if (elFromPt && (elFromPt.classList?.contains('marker-icon') || elFromPt.closest?.('.marker-icon'))) {
                _resolvedTarget = elFromPt;
              }
            }
            const markerEl = (
              _resolvedTarget.classList?.contains('marker-icon') ? _resolvedTarget :
              _resolvedTarget.closest?.('.marker-icon')
            ) as HTMLElement | null;
            if (!markerEl || markerEl.classList.contains('cursor-marker')) return;
            _drawGestureState = beginDrawGesture({
              touchId: touch.identifier,
              target: markerEl.classList.contains('marker-icon-middle') ? "midpoint" : "vertex",
              clientX: touch.clientX,
              clientY: touch.clientY,
              now: performance.now(),
            });
            segmentLabelMgrRef.current?.clearHintLabel?.();
            try {
              // LAZY ACCESS: get draw instance at touchstart time (markers already exist)
              const di = (map.pm?.Draw as any)?.[_drawShape];
              if (!di || !Array.isArray(di._markers) || di._markers.length === 0) return;
              const markers: any[] = di._markers;
              let foundMarker: any = null;
              let foundIdx = -1;
              for (let i = 0; i < markers.length; i++) {
                const m = markers[i];
                // Check: m._icon is the marker div (class: leaflet-marker-icon marker-icon)
                if (m._icon === markerEl || m._icon === _touchTarget ||
                    m._icon?.contains(_touchTarget) || markerEl.contains(m._icon)) {
                  foundMarker = m; foundIdx = i; break;
                }
              }
              if (!foundMarker || foundIdx < 0) return;
              ev.stopPropagation();
              ev.preventDefault();
              map.dragging.disable();
              // Sprint v78 Bug 1 Fix: disable Geoman _syncHintMarker to prevent override of 60px offset
              const _origSync = typeof di._syncHintMarker === 'function' ? di._syncHintMarker.bind(di) : null;
              if (_origSync) di._syncHintMarker = () => {}; // no-op during drag
              _dragState = { marker: foundMarker, markerIndex: foundIdx, startTouchId: touch.identifier, active: true, di, _origSync };
              _isDraggingVertexRef.current = true; // Sprint v114 Fix D: block pm:vertexadded during drag
              segmentLabelMgrRef.current?.setDrawingHandleDragging(true);
              setMapControlInteractionLocked(true);
            } catch {}
          };

          const _onDrawTouchMove = (ev: TouchEvent) => {
            const _gestureTouch = Array.from(ev.touches).find((touch) => touch.identifier === _drawGestureState.touchId);
            if (_gestureTouch) {
              _drawGestureState = updateDrawGesture(_drawGestureState, {
                touchId: _gestureTouch.identifier,
                clientX: _gestureTouch.clientX,
                clientY: _gestureTouch.clientY,
                now: performance.now(),
              });
              if (shouldClearTransientDrawingFeedback(_drawGestureState)) {
                segmentLabelMgrRef.current?.clearHintLabel?.();
              }
            }
            if (!_dragState?.active) return;
            let touch: Touch | null = null;
            for (let i = 0; i < ev.touches.length; i++) {
              if (ev.touches[i].identifier === _dragState.startTouchId) { touch = ev.touches[i]; break; }
            }
            if (!touch) return;
            ev.stopPropagation();
            ev.preventDefault();
            try {
              // Sprint v78 Bug 1 Fix: use clientX/clientY directly (Touch object, not MouseEvent)
              // Claude Opus: mouseEventToContainerPoint(touch) may misread Touch — use rect-based calc
              // _syncHintMarker is disabled during drag so setLatLng persists
              const rect = map.getContainer().getBoundingClientRect();
              const rawX = touch.clientX - rect.left;
              const rawY = touch.clientY - rect.top;
              // Sprint v100 B6: adaptive Y-offset — 60px on normal screens, scales with container height
              // Clamp between 40px (small screen) and 80px (large screen) for fat-finger clearance
              const _containerH = rect.height || 400;
              const _yOffset = Math.max(40, Math.min(80, Math.round(_containerH * 0.10)));
              const L_ref = (window as any).L;
              const offsetPoint = L_ref?.point ? L_ref.point(rawX, rawY - _yOffset) : { x: rawX, y: rawY - _yOffset };
              const newLatLng = map.containerPointToLatLng(offsetPoint);
              _dragState.marker.setLatLng(newLatLng);
              const di = _dragState.di;
              if (!di?._layer) return;
              const latlngs = di._layer.getLatLngs();
              const flat: any[] = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
              if (flat[_dragState.markerIndex] !== undefined) {
                flat[_dragState.markerIndex] = newLatLng;
                di._layer.setLatLngs(Array.isArray(latlngs[0]) ? [flat] : flat);
              }
                            if (di._hintline && _dragState.markerIndex === (di._markers?.length ?? 0) - 1) {
                di._hintline.setLatLngs([newLatLng, newLatLng]);
              }
              // Sprint v98: live segment label update during draw-mode vertex drag
              // rAF-throttled (skip frame if previous rAF still pending) — same pattern as edit mode
              if (!_drawDragRaf && segmentLabelMgrRef.current) {
                _drawDragRaf = requestAnimationFrame(() => {
                  _drawDragRaf = null;
                  try {
                    const di2 = _dragState?.di;
                    if (!di2?._layer) return;
                    const ll2 = di2._layer.getLatLngs();
                    const f2: any[] = Array.isArray(ll2[0]) ? ll2[0] : ll2;
                    if (f2.length >= 2) {
                      segmentLabelMgrRef.current?.updateForDrawingVertices(f2);
                    }
                  } catch {}
                });
              }
            } catch {}
          };
          const _onDrawTouchEnd = (_ev: TouchEvent, cancelled = false) => {
            const _gestureTouch = Array.from(_ev.changedTouches).find((touch) => touch.identifier === _drawGestureState.touchId);
            if (_gestureTouch) {
              const _wasPan = _drawGestureState.phase === "panning" || _drawGestureState.phase === "cancelled";
              _drawGestureState = finishDrawGesture(_drawGestureState, {
                touchId: _gestureTouch.identifier,
                clientX: _gestureTouch.clientX,
                clientY: _gestureTouch.clientY,
                now: performance.now(),
                cancelled,
              });
              if (_wasPan || cancelled) {
                try { _ev.preventDefault(); } catch {}
                segmentLabelMgrRef.current?.clearHintLabel?.();
              }
            }
            if (!_dragState?.active) return;
            // Sprint v78 Bug 1 Fix: restore Geoman _syncHintMarker after drag ends
            if (_dragState._origSync && _dragState.di) {
              _dragState.di._syncHintMarker = _dragState._origSync;
            }
            _dragState.active = false;
            map.dragging.enable();
            segmentLabelMgrRef.current?.setDrawingHandleDragging(false);
            setMapControlInteractionLocked(false);
            // Sprint v98: cancel any pending rAF from _onDrawTouchMove
            if (_drawDragRaf) { cancelAnimationFrame(_drawDragRaf); _drawDragRaf = null; }
            try {
              const di = _dragState.di;
              if (di?._layer) {
                const latlngs = di._layer.getLatLngs();
                const flat: any[] = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
                drawingVerticesRef.current = [...flat];
                const area = calcAreaFromLatLngs(flat);
                const perim = calcPerimeterFromLatLngs(flat);
                setLiveArea(area);
                setLivePerimeter(perim);
                if (segmentLabelMgrRef.current) segmentLabelMgrRef.current.updateForDrawingVertices(flat);
                // Sprint v115.7 rAF: consolidate di._markers sync + midpoint rebuild into single rAF.
                // T2 verdict: capture _di locally BEFORE _dragState = null (fires after rAF schedule).
                // T1 finding: Geoman may call _syncMarkers() after touchend → override synchronous
                // setLatLng. Moving into rAF ensures our sync runs AFTER Geoman's internal sync.
                const _di = di; // Sprint v115.7: capture before _dragState = null
                const _flatSnap = [...flat];
                // Sprint v115.7 GHOST-DEBUG: log marker counts before rAF
                dbg(`🟡 touchEnd: verts=${_flatSnap.length} di._markers=${Array.isArray(_di?._markers) ? _di._markers.length : 'N/A'}`);
                if (_postDragReconcileRaf) cancelAnimationFrame(_postDragReconcileRaf);
                _postDragReconcileRaf = requestAnimationFrame(() => {
                  _postDragReconcileRaf = null;
                  try {
                    // Sync all vertex markers after Geoman internal _syncMarkers completes
                    if (Array.isArray(_di?._markers)) {
                      _di._markers.forEach((m: any, i: number) => {
                        if (m && _flatSnap[i]) { try { m.setLatLng(_flatSnap[i]); } catch {} }
                      });
                    }
                    // v277: exactly one post-Geoman reconciliation from the final layer snapshot.
                    // Rebuilding midpoint markers here removes any old midpoint that survived a drag.
                    const _finalLatLngs = _di?._layer?.getLatLngs?.() ?? _flatSnap;
                    const _finalFlat: any[] = Array.isArray(_finalLatLngs[0]) ? _finalLatLngs[0] : _finalLatLngs;
                    drawingVerticesRef.current = [..._finalFlat];
                    segmentLabelMgrRef.current?.updateForDrawingVertices(_finalFlat);
                    _buildDrawMidpoints(_finalFlat);
                    segmentLabelMgrRef.current?.clearHintLabel?.();
                    dbg(`✅ rAF done: markers synced=${Array.isArray(_di?._markers) ? _di._markers.length : 0}`);
                  } catch (e: any) { dbg(`❌ rAF error: ${e?.message}`); }
                });
              }
            } catch {}
            _dragState = null;
            _isDraggingVertexRef.current = false; // Sprint v114 Fix D: unblock pm:vertexadded after drag
          };

          const mapContainer = map.getContainer();
          mapContainer.addEventListener('touchstart', _onDrawTouchStart, { capture: true, passive: false });
          mapContainer.addEventListener('touchmove', _onDrawTouchMove, { capture: true, passive: false });
          mapContainer.addEventListener('touchend', _onDrawTouchEnd, { capture: true, passive: false });
          const _onDrawTouchCancel = (event: TouchEvent) => _onDrawTouchEnd(event, true);
          // Sprint v114.1 Fix D-touchcancel: OS can cancel a touch mid-drag (incoming notification,
          // system gesture, palm rejection). Without touchcancel, _isDraggingVertexRef stays true
          // and _onDrawTouchEnd never fires → every subsequent pm:vertexadded is rejected →
          // user cannot add vertices until draw is restarted. Reuse _onDrawTouchEnd as handler
          // (it clears _isDraggingVertexRef.current and re-enables map.dragging).
          mapContainer.addEventListener('touchcancel', _onDrawTouchCancel, { capture: true, passive: false });

          const _onDrawCompatibilityClick = (event: MouseEvent) => {
            const result = consumeSyntheticClickSuppression(_drawGestureState, {
              now: performance.now(),
              clientX: event.clientX,
              clientY: event.clientY,
            });
            _drawGestureState = result.state;
            if (!result.suppressed) return;
            event.preventDefault();
            event.stopPropagation();
            try { event.stopImmediatePropagation(); } catch {}
            segmentLabelMgrRef.current?.clearHintLabel?.();
            dbg('🟡 v277: consumed pan compatibility click');
          };
          mapContainer.addEventListener('click', _onDrawCompatibilityClick, { capture: true });

          // Sprint v118.2 Bug1 Fix: hide hintline on touchend to prevent ghost dashed-line
          // staying at map.getCenter() after finger lifts. Show again on next touchstart.
          // Root cause: _syncHintMarker (Sprint v79) points hintline to map.getCenter().
          // After touchend, hintline stays at old center until map pans → ghost dashed line.
          // Fix: hide hintline element on touchend, restore on touchstart.
          const _onHintLineHide = () => {
            if (!isDrawingRef.current || _dragState?.active) return;
            try {
              const di = (map.pm?.Draw as any)?.[_drawShape];
              const hl = di?._hintline;
              if (hl) {
                const el = hl.getElement?.() || hl._path;
                if (el) el.style.opacity = '0';
              }
            } catch {}
          };
          const _onHintLineShow = () => {
            if (!isDrawingRef.current) return;
            try {
              const di = (map.pm?.Draw as any)?.[_drawShape];
              const hl = di?._hintline;
              if (hl) {
                const el = hl.getElement?.() || hl._path;
                if (el) el.style.opacity = '';
              }
            } catch {}
          };
          mapContainer.addEventListener('touchend', _onHintLineHide, { capture: false, passive: true });
          mapContainer.addEventListener('touchcancel', _onHintLineHide, { capture: false, passive: true });
          mapContainer.addEventListener('touchstart', _onHintLineShow, { capture: false, passive: true });
          // Sprint v110 A2: register cleanup via _drawCleanups (capture:true trio)
          _drawCleanups.push(() => {
            mapContainer.removeEventListener('touchstart', _onDrawTouchStart, true);
            mapContainer.removeEventListener('touchmove', _onDrawTouchMove, true);
            mapContainer.removeEventListener('touchend', _onDrawTouchEnd, true);
            // Sprint v114.1 Fix D-touchcancel: remove touchcancel alongside touchend
            mapContainer.removeEventListener('touchcancel', _onDrawTouchCancel, true);
            mapContainer.removeEventListener('click', _onDrawCompatibilityClick, true);
            mapContainer.removeEventListener('touchend', _onHintLineHide, false);
            mapContainer.removeEventListener('touchcancel', _onHintLineHide, false);
            mapContainer.removeEventListener('touchstart', _onHintLineShow, false);
            if (_dragState?.active) { map.dragging.enable(); _dragState = null; }
            segmentLabelMgrRef.current?.setDrawingHandleDragging(false);
            if (_postDragReconcileRaf) { cancelAnimationFrame(_postDragReconcileRaf); _postDragReconcileRaf = null; }
            _drawGestureState = idleDrawGesture();
            // Sprint v115 Bug1a: belt-and-braces — clear pending candidate across sessions
            // (in case mousedown fired but drawend cancelled before mouseup)
            // Note: _pending is declared in the if (!isMobile) block below; this comment
            // documents the intent. The actual _pending = null is in _onDrawMouseDown reset.
          });

          // =====================================================================
          // SPRINT v112 Fix 2: DESKTOP MOUSE VERTEX DRAG (draw mode)
          // Geoman has no vertex drag in draw mode — touch-only v75.2 left desktop broken.
          // Two nuances vs touch: (1) no Y-offset (mouse is precise), (2) click suppression
          // after mouseup to prevent Geoman adding a vertex at drag-release point.
          // Gate: if (!isMobile) — avoids double-handling synthetic mouse events on touch devices.
          // v112-F2 grep: grep -c 'v112-F2' client/src/components/CollateralMapModal.tsx >= 1
          // =====================================================================
          if (!isMobile) {
            let _mouseDrag: { marker: any; idx: number; di: any; _origSync: any } | null = null;
            // Sprint v115 Bug1a: a mousedown on a vertex is NOT yet a drag. It becomes a drag
            // only after the mouse moves > threshold; a stationary press+release is a CLICK and
            // must reach Geoman (so clicking the first vertex closes the polygon). Without this,
            // every click on a vertex was swallowed → desktop users could not finish the draw.
            let _pending: { marker: any; idx: number; di: any; startX: number; startY: number } | null = null;
            const _DRAG_THRESHOLD_PX = 4;
            let _suppressNextClick = false;
            let _mouseDrawDragRaf: number | null = null;

            const _onDrawMouseDown = (ev: MouseEvent) => {
              _suppressNextClick = false;
              _pending = null;
              if (!isDrawingRef.current || ev.button !== 0) return;
              const t = ev.target as HTMLElement;
              const el = (t.classList?.contains('marker-icon') ? t : t.closest?.('.marker-icon')) as HTMLElement | null;
              if (!el || el.classList.contains('cursor-marker')) return;
              const di = (map.pm?.Draw as any)?.[_drawShape];
              if (!di?._markers?.length) return;
              const idx = (di._markers as any[]).findIndex((m: any) => m._icon === el || m._icon?.contains(t));
              if (idx < 0) return;
              // Sprint v115 Bug1a: record candidate ONLY — do not disable dragging, do not
              // stopPropagation, do not activate drag. Let the click reach Geoman if the user
              // does not move (needed to close the polygon on the first vertex).
              _pending = { marker: di._markers[idx], idx, di, startX: ev.clientX, startY: ev.clientY };
            };

            const _onDrawMouseMove = (ev: MouseEvent) => {
              // Sprint v115 Bug1a: promote pending → active drag once movement exceeds threshold
              if (_pending && !_mouseDrag) {
                const ddx = ev.clientX - _pending.startX;
                const ddy = ev.clientY - _pending.startY;
                if (Math.sqrt(ddx * ddx + ddy * ddy) < _DRAG_THRESHOLD_PX) return; // still a potential click
                const di = _pending.di;
                map.dragging.disable();
                const _origSync = typeof di._syncHintMarker === 'function' ? di._syncHintMarker.bind(di) : null;
                if (_origSync) di._syncHintMarker = () => {}; // no-op during drag
                _mouseDrag = { marker: _pending.marker, idx: _pending.idx, di, _origSync }; // v112-F2
                segmentLabelMgrRef.current?.setDrawingHandleDragging(true);
                _pending = null;
              }
              if (!_mouseDrag) return;
              ev.stopPropagation(); ev.preventDefault();
              try {
                const rect = map.getContainer().getBoundingClientRect();
                const ll = map.containerPointToLatLng((window as any).L?.point
                  ? (window as any).L.point(ev.clientX - rect.left, ev.clientY - rect.top)
                  : { x: ev.clientX - rect.left, y: ev.clientY - rect.top });
                _mouseDrag.marker.setLatLng(ll);
                const di = _mouseDrag.di;
                if (!di?._layer) return;
                const lls = di._layer.getLatLngs();
                const flat: any[] = Array.isArray(lls[0]) ? lls[0] : lls;
                if (flat[_mouseDrag.idx] !== undefined) {
                  flat[_mouseDrag.idx] = ll;
                  di._layer.setLatLngs(Array.isArray(lls[0]) ? [flat] : flat);
                }
                if (!_mouseDrawDragRaf && segmentLabelMgrRef.current) {
                  _mouseDrawDragRaf = requestAnimationFrame(() => {
                    _mouseDrawDragRaf = null;
                    try {
                      const di2 = _mouseDrag?.di;
                      if (!di2?._layer) return;
                      const ll2 = di2._layer.getLatLngs();
                      const f2: any[] = Array.isArray(ll2[0]) ? ll2[0] : ll2;
                      if (f2.length >= 2) segmentLabelMgrRef.current?.updateForDrawingVertices(f2);
                    } catch {}
                  });
                }
              } catch {}
            };

            const _onDrawMouseUp = (ev: MouseEvent) => {
              // Sprint v115 Bug1a: stationary press (never crossed threshold) = click → let it pass
              // so Geoman closes the polygon on the first vertex. Only suppress after a real drag.
              if (_pending && !_mouseDrag) { _pending = null; return; }
              if (!_mouseDrag) return;
              ev.stopPropagation();
              if (_mouseDrawDragRaf) { cancelAnimationFrame(_mouseDrawDragRaf); _mouseDrawDragRaf = null; }
              if (_mouseDrag._origSync) _mouseDrag.di._syncHintMarker = _mouseDrag._origSync;
              map.dragging.enable();
              segmentLabelMgrRef.current?.setDrawingHandleDragging(false);
              try {
                const di = _mouseDrag.di;
                if (di?._layer) {
                  const lls = di._layer.getLatLngs();
                  const flat: any[] = Array.isArray(lls[0]) ? lls[0] : lls;
                  drawingVerticesRef.current = [...flat];
                  const area = calcAreaFromLatLngs(flat);
                  const perim = calcPerimeterFromLatLngs(flat);
                  setLiveArea(area); setLivePerimeter(perim);
                  if (segmentLabelMgrRef.current) segmentLabelMgrRef.current.updateForDrawingVertices(flat);
                  // Sprint v115.7 rAF: consolidate di._markers sync + midpoint rebuild into single rAF.
                  // T2 verdict: capture _di2 locally BEFORE _mouseDrag = null (fires after rAF schedule).
                  const _di2 = di; // Sprint v115.7: capture before _mouseDrag = null
                  const _flatSnap2 = [...flat];
                  requestAnimationFrame(() => {
                    try {
                      if (Array.isArray(_di2?._markers)) {
                        _di2._markers.forEach((m: any, i: number) => {
                          if (m && _flatSnap2[i]) { try { m.setLatLng(_flatSnap2[i]); } catch {} }
                        });
                      }
                      } catch {}
                  });
                }
              } catch {}
              _suppressNextClick = true; // consumed in _onDrawClickCapture
              _mouseDrag = null;
            };

            const _onDrawClickCapture = (ev: MouseEvent) => {
              if (_suppressNextClick) {
                // Sprint v114 Fix C: consume flag here (exactly one click suppressed per drag)
                _suppressNextClick = false;
                ev.stopPropagation();
                ev.preventDefault();
              }
            };

            mapContainer.addEventListener('mousedown', _onDrawMouseDown, { capture: true });
            mapContainer.addEventListener('mousemove', _onDrawMouseMove, { capture: true });
            mapContainer.addEventListener('mouseup', _onDrawMouseUp, { capture: true });
            mapContainer.addEventListener('click', _onDrawClickCapture, { capture: true });

            _drawCleanups.push(() => {
              mapContainer.removeEventListener('mousedown', _onDrawMouseDown, true);
              mapContainer.removeEventListener('mousemove', _onDrawMouseMove, true);
              mapContainer.removeEventListener('mouseup', _onDrawMouseUp, true);
              mapContainer.removeEventListener('click', _onDrawClickCapture, true);
              if (_mouseDrag) { map.dragging.enable(); _mouseDrag = null; }
              if (_mouseDrawDragRaf) { cancelAnimationFrame(_mouseDrawDragRaf); _mouseDrawDragRaf = null; }
            });
          }
        }

        // BUG-7 FIX: Rectangle uses drag (not vertexadded) — listen to pm:change for live area
        e.workingLayer?.on("pm:change", () => {
          try {
            const lls = e.workingLayer.getLatLngs ? e.workingLayer.getLatLngs() : [];
            const flat = Array.isArray(lls[0]) ? lls[0] : lls;
            if (flat.length >= 3) {
              const area = calcAreaFromLatLngs(flat);
              const perim = calcPerimeterFromLatLngs(flat);
              if (area > 0) { setLiveArea(area); setLivePerimeter(perim); }
            }
          } catch {}
        });
      });

      // Sprint v109 Fix 2: Removed duplicate map-level pm:vertexadded handler.
      // The workingLayer handler (registered in pm:drawstart) already tracks all vertices,
      // applies 45° snap, and updates live measurements. A second map-level handler caused:
      //   a) double-push of every vertex into drawingVerticesRef (doubling area/perimeter)
      //   b) haptic vibrate never fired (workingLayer handler ran first, map handler was dead code)
      // Haptic feedback has been moved into the workingLayer handler above.

      // Drawing end — re-enable doubleClickZoom after drawing finishes
      map.on("pm:drawend", () => {
        // Small delay to let Geoman fully clean up before re-enabling dblclick zoom
        dbg(`■ pm:drawend verts=${drawingVerticesRef.current.length}`);
        setTimeout(() => { map.doubleClickZoom.enable(); }, 300);
        // Sprint v119: clear draw-mode midpoint markers on draw end
        drawMidLayerRef.current?.clearLayers();
        isDrawingRef.current = false;
        setIsDrawing(false);
        setCanUndoVertex(false);
        drawingVerticesRef.current = [];
        setDrawVertexCount(0); // Sprint v110 A4: reset drawVertexCount on draw end
        // Sprint v83 Fix B: reset projectedMeasureRef on draw end
        projectedMeasureRef.current = '';
        setDrawMode(null);
        setMapMode('view'); // Sprint v73 STEP C
        // Sprint v114 Fix U: remove Geoman snap guidelines and hint markers on draw end
        // Geoman leaves .leaflet-pm-snapline and .leaflet-pm-snap-marker elements in the DOM
        // after pm:drawend. These are invisible but accumulate and can cause layout/event issues.
        try {
          const _container = map.getContainer();
          _container.querySelectorAll(
            '.leaflet-pm-snapline, .leaflet-pm-snap-marker, .leaflet-pm-draw-marker, .leaflet-pm-tooltip'
          ).forEach((el: Element) => {
            try { (el as HTMLElement).remove(); } catch {}
          });
          // Also remove any Geoman temp layers from the map
          map.eachLayer((layer: any) => {
            if (layer._pmTempLayer || layer._pmSnapLine) {
              try { map.removeLayer(layer); } catch {}
            }
          });
        } catch {}
        // V65: restore bearing + touchRotate after drawing ends
        try {
          if (savedBearingRef.current !== 0 && map.setBearing) {
            map.setBearing(savedBearingRef.current);
          }
          if (map.touchRotate && map.touchRotate.enable) map.touchRotate.enable();
        } catch {}
        // Sprint v113 Fix 2: Clear drawing-phase labels atomically on draw cancel.
        // v17 FIX note: pm:drawend fires BEFORE pm:create for a completed polygon.
        // We use a one-shot pm:create listener: if pm:create fires within 500ms, it owns
        // label lifecycle. If NOT (user cancelled / midpoint-drag bug exit), we clearLabels
        // immediately so ghost labels don't persist on the map.
        let _drawCreated = false;
        map.once('pm:create', () => { _drawCreated = true; });
        setTimeout(() => {
          if (!_drawCreated && segmentLabelMgrRef.current) {
            dbg('🧹 Sprint v113 Fix 2: draw cancelled — clearing ghost labels');
            segmentLabelMgrRef.current.clearLabels();
            if (segmentLabelMgrRef.current.clearHintLabel) {
              segmentLabelMgrRef.current.clearHintLabel();
            }
            // Re-render saved polygon labels if any exist
            if (showSegmentLabelsRef.current && polygonLayersRef.current.length > 0) {
              polygonLayersRef.current.forEach((l: any) => {
                segmentLabelMgrRef.current!.appendForLayer(l);
              });
            }
          }
        }, 500);
        // pm:create is the SOLE owner of label lifecycle after draw completes.
        // Sprint v87 Fix C: Remove stray gray dot via CSS class query (more reliable than _pmTempLayer flag).
        // Geoman may leave hint/cursor markers with class 'leaflet-pm-draw-marker' or 'leaflet-pm-tooltip'
        // after pm:drawend. Remove them directly from the DOM.
        setTimeout(() => {
          try {
            const container = map.getContainer();
            // Remove any Geoman draw markers (hint cursor, temp vertices)
            container.querySelectorAll('.leaflet-pm-draw-marker, .leaflet-pm-tooltip, .leaflet-pm-draw-tooltip').forEach((el: Element) => {
              try { (el as HTMLElement).style.display = 'none'; } catch {}
            });
            // Also remove via layer check with broader criteria
            map.eachLayer((layer: any) => {
              if (!layer._pmTempLayer) return;
              try { layer.remove(); } catch {};
            });
          } catch {}
        }, 100);
      });

      // G7: Helper to push current polygon state to undo stack
      // G9 FIX: snapshot the full draftGeoJson FeatureCollection (not just single layer)
      const pushUndoSnapshot = (_geoJson?: GeoJsonPolygon) => {
        // Use the current draftGeoJson from the ref (FeatureCollection)
        let snapshot = draftGeoJsonRef.current;
        // Sprint v115.4 G4b: fall back to current on-map geometry when no draft exists yet.
        // loadSavedPolygon does not call setDraftGeoJson → draftGeoJson is null until first pm:edit.
        // Without this, pushUndoSnapshot returns early → Ctrl+Z after first insert does nothing.
        // Sprint v133 Bug2: this fallback path also needs to preserve properties (color, label).
        // l.toGeoJSON() returns empty properties — use draftGeoJsonRef (always-current) as source.
        // Note: geoBoundaryJson is NOT in initMap closure scope; draftGeoJsonRef IS.
        if (!snapshot && polygonLayersRef.current.length > 0) {
          try {
            // draftGeoJsonRef.current may also be null here (first snapshot before any pm:edit).
            // In that case we have no properties to preserve — just use raw toGeoJSON().
            const _fbFeatures = (draftGeoJsonRef.current as any)?.features || [];
            const features = polygonLayersRef.current.map((l: any, idx: number) => {
              const geo = l.toGeoJSON();
              const fbProps = _fbFeatures[idx]?.properties || {};
              return { ...geo, properties: { ...fbProps, ...geo.properties } };
            });
            snapshot = { type: "FeatureCollection", features, properties: { totalAreaSqM: 0 } } as any;
          } catch {}
        }
        if (!snapshot) return;
        // Sprint v115.4 G4c: dedup guard — skip if snapshot equals top-of-stack.
        // Prevents double-push when midpoint drag causes both our patch AND pm:markerdragstart
        // to call pushUndoSnapshot for the same pre-insert state → user would need Ctrl+Z twice.
        const _json = JSON.stringify(snapshot);
        const _top = undoStackRef.current[undoStackRef.current.length - 1];
        if (_top && JSON.stringify(_top) === _json) return;
        undoStackRef.current.push(JSON.parse(_json));
        if (undoStackRef.current.length > 20) undoStackRef.current.shift(); // LRU cap
        redoStackRef.current = []; // new edit clears redo
        setCanUndo(undoStackRef.current.length > 0);
        setCanRedo(false);
      };
      // Sprint v115.4 G4: expose for Geoman prototype patches (_onMiddleMarkerMoveEnd, _onMiddleMarkerClick).
      // Patches run in Geoman prototype context (no React closure) — map instance bridge required.
      // Placement: outside __v95Patched guard → re-bound on every modal open (no stale map ref).
      // Verified: __pushUndoSnapshot was NOT previously exposed anywhere in this file.
      (map as any).__pushUndoSnapshot = pushUndoSnapshot;

      // Shape created
      map.on("pm:create", (e: any) => {
        const layer = e.layer;
        dbg(`✓ pm:create polygons=${polygonLayersRef.current.length + 1}`);
        // G9: DO NOT remove existing polygons — add new one to collection
        // polygonLayerRef tracks the ACTIVE (last drawn) layer for edit/drag/delete ops
        polygonLayerRef.current = layer;
        polygonLayersRef.current.push(layer); // G9: track all layers
        // Sprint v73 STEP C: mobile tap-to-edit on newly created polygon
        attachMobileEditHandlerRef.current(layer);
        // Sprint v73 STEP H: live measurement during vertex drag on newly created polygon
        attachLiveMeasureRef.current(layer);
        // Sprint v179 BUG-DRIFT-2 FIX: refresh labels after whole-polygon drag ends (newly drawn polygon)
        layer.on("pm:dragend", () => {
          if (segmentLabelMgrRef.current && showSegmentLabelsRef.current) {
            segmentLabelMgrRef.current.clearLabels();
            polygonLayersRef.current.forEach((l: any) => {
              segmentLabelMgrRef.current!.appendForLayer(l);
            });
          }
          renderCenterLabels();
        });
        const createdGeoJson = layer.toGeoJSON() as GeoJsonPolygon;
        // G9: calculate area for this polygon
        let polyAreaSqM = 0;
        try {
          const lls = layer.getLatLngs ? layer.getLatLngs() : [];
          const flat = Array.isArray(lls[0]) ? lls[0] : lls;
          polyAreaSqM = calcAreaFromLatLngs(flat);
        } catch {}
        addPolygonToDraft(createdGeoJson, polyAreaSqM);
        setActivePolygonIndex((polygonLayersRef.current.length - 1));
        // G7: Clear undo/redo on new polygon creation
        undoStackRef.current = [];
        redoStackRef.current = [];
        setCanUndo(false);
        setCanRedo(false);
        map.pm.disableDraw();
        isDrawingRef.current = false;
        setIsDrawing(false);
        setCanUndoVertex(false);
        // Sprint v114 Fix J: sync drawModeRef immediately (React setState is async —
        // drawModeRef.current may still read 'polygon'/'rectangle' in callbacks fired
        // between setDrawMode(null) and the next render)
        drawModeRef.current = null;
        setDrawMode(null);
        drawingVerticesRef.current = [];

        // Apply POLYGON_COLORS to all layers (including newly created one) so multi-polygon
        // colors are correct. Sprint v130 GAP-MULTI: previously hardcoded #22c55e for new polygon
        // which is correct only for polygon 0. Now uses index-based color for all.
        // Sprint v134 Bug A: preserve custom colors from draftGeoJsonRef — pm:create must NOT wipe
        // user-set colors on existing polygons when a new polygon is added.
        try {
          const _draftFeatures = (draftGeoJsonRef.current as any)?.features || [];
          polygonLayersRef.current.forEach((l: any, idx: number) => {
            const savedColor = (_draftFeatures[idx] as any)?.properties?.color as string | null;
            const fillColor = savedColor || POLYGON_COLORS[idx % POLYGON_COLORS.length];
            if (l.setStyle) {
              l.setStyle({ color: "#ffffff", fillColor, fillOpacity: 0.35, weight: 3 });
            } else {
              l.eachLayer?.((sub: any) => sub.setStyle?.({ color: "#ffffff", fillColor, fillOpacity: 0.35, weight: 3 }));
            }
          });
        } catch {}

        // F1 v33: render labels for ALL polygons on polygon creation
        // updateForLayer(layer) clears all labels then shows only the new polygon
        // — with multi-polygon boundaries, previously drawn polygon labels disappear
        // J1 v37: respect segment label toggle
        if (segmentLabelMgrRef.current && showSegmentLabelsRef.current) {
          segmentLabelMgrRef.current.clearLabels();
          polygonLayersRef.current.forEach((l: any) => {
            segmentLabelMgrRef.current!.appendForLayer(l);
          });
        }

        // Calculate and show perimeter + area (BUG-C: keep liveArea visible after creation)
        try {
          const lls = layer.getLatLngs ? layer.getLatLngs() : [];
          const flat = Array.isArray(lls[0]) ? lls[0] : lls;
          const perim = calcPerimeterFromLatLngs(flat);
          setSavedPerimeter(perim);
          // BUG-C FIX: set liveArea so area card stays visible until auto-save refetch updates areaSqM
          const area = calcAreaFromLatLngs(flat);
          if (area > 0) setLiveArea(area);
        } catch {}

        // BUG-A UX: auto-activate edit mode so user can immediately adjust vertices.
        // A stale/read-only modal must never expose a false editable state.
        if (!canEditRef.current) {
          mapModeRef.current = 'view';
          setMapMode('view');
          setDrawMode(null);
          return;
        }
        // Sprint v105: 3-step sequence to fix markers offset after polygon completion.
        // Root cause (confirmed by 3 videos):
        //   1. Draw strip (48px absolute overlay) disappears when mapMode changes 'draw'→'view'
        //   2. React re-renders (setIsDrawing, setLiveArea, setMapMode) cause layout shifts
        //   3. Leaflet pixel↔LatLng cache becomes stale relative to new layout
        //   4. enableGlobalEditMode places markers at stale pixel positions → offset down-right
        // Fix: invalidateSize first, THEN disable+re-enable edit mode so Geoman recalculates
        // all marker positions from the fresh pixel cache.
        // Step 1 (0ms): invalidateSize immediately after React re-renders settle
        setTimeout(() => {
          if (!leafletMapRef.current) return;
          try { leafletMapRef.current.invalidateSize(true); } catch {}
        }, 0);
        // Step 2 (350ms): enable edit mode after layout has fully settled
        // 350ms > typical React commit + CSS transition duration
        setTimeout(() => {
          if (!leafletMapRef.current) return;
          // Refresh pixel cache one more time right before Geoman places markers
          try { leafletMapRef.current.invalidateSize(true); } catch {}
          try {
            leafletMapRef.current.pm.enableGlobalEditMode(_editModeOpts());
            if (isMobile) leafletMapRef.current.getContainer().style.touchAction = 'none';
            setDrawMode("edit");
            mapModeRef.current = 'edit'; // [GAP-9 FIX v252x] sync update before async setMapMode
            setMapMode('edit'); // Sprint v73 STEP C
            setShowEditHint(true);
            setTimeout(() => setShowEditHint(false), 4000);
          } catch {}
          // F7 v33: re-render labels for ALL polygons after edit mode is enabled
          // J1 v37: respect segment label toggle
          if (segmentLabelMgrRef.current && polygonLayersRef.current.length > 0 && showSegmentLabelsRef.current) {
            segmentLabelMgrRef.current.clearLabels();
            polygonLayersRef.current.forEach((l: any) => {
              segmentLabelMgrRef.current!.appendForLayer(l);
            });
          }
          // Step 3 (350+100ms): disable+re-enable so Geoman repositions markers from fresh cache
          setTimeout(() => {
            if (!leafletMapRef.current) return;
            try {
              leafletMapRef.current.pm.disableGlobalEditMode();
              leafletMapRef.current.invalidateSize(true);
              leafletMapRef.current.pm.enableGlobalEditMode(_editModeOpts());
            } catch {}
          }, 100);
        }, 350);

        // v21 FIX: Multiple safety-net timers to ensure labels survive all async events:
        // - 200ms: after Geoman internal cleanup settles
        // - 800ms: after enableGlobalEditMode + any triggered zoomend/moveend
        // - 1500ms: after auto-save refetch (700ms save + ~500ms refetch = ~1300ms total)
        // - 2500ms: final safety net in case refetch is slow
        // C24 v30: render labels for ALL polygons on each safety-net timer
        // updateForLayer(activeLayer) only shows labels for the active polygon;
        // if user has drawn multiple polygons, other polygons lose labels on each timer.
        [200, 800, 1500, 2500].forEach((delay) => {
          setTimeout(() => {
            if (!segmentLabelMgrRef.current) return;
            // J1 v37: respect segment label toggle
            if (!showSegmentLabelsRef.current) return;
            segmentLabelMgrRef.current.clearLabels();
            polygonLayersRef.current.forEach((l: any) => {
              segmentLabelMgrRef.current!.appendForLayer(l);
            });
          }, delay);
        });

        // Sprint v145: render center labels after polygon creation
        setTimeout(() => renderCenterLabels(), 500);

        // GAP-1: auto-save after polygon creation (background, 700ms delay for React state settle)
        if (canEditRef.current) {
          if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = setTimeout(async () => {
            // saveBoundary reads draftGeoJson from hook closure — by 700ms it will be set
            await saveBoundary();
            autoSaveTimerRef.current = null;
          }, 700);
        }
      });

      // Shape edited — update labels + perimeter + area
      // N1 v41: Safe helper — handles both L.Polygon (no getLayers) and L.geoJSON wrapper
      // L.Polygon.getLatLngs() = [[LatLng,...]], L.geoJSON.getLayers()[0].getLatLngs() = [[LatLng,...]]
      const getSafeLatLngs = (l: any): any[] => {
        if (typeof l.getLatLngs === "function") {
          // L.Polygon — direct
          const raw = l.getLatLngs();
          return Array.isArray(raw[0]) ? raw[0] : raw;
        }
        if (typeof l.getLayers === "function") {
          // L.geoJSON wrapper
          const sub = l.getLayers()[0];
          if (sub && typeof sub.getLatLngs === "function") {
            const raw = sub.getLatLngs();
            return Array.isArray(raw[0]) ? raw[0] : raw;
          }
        }
        return [];
      };

      map.on("pm:edit", () => {
        setMapMode('edit'); // Sprint v73 STEP C
        if (polygonLayerRef.current) {
          // G9 FIX: rebuild FeatureCollection from all polygon layers after edit
          // Sprint v133 Bug1: merge existing draftGeoJson properties (color, label) into new features.
          // l.toGeoJSON() returns { properties: {} } — color/label are NOT on the Leaflet layer.
          // Without this merge, every vertex drag wipes the user's custom color and label.
          const _existingFeatures = (draftGeoJsonRef.current as any)?.features || [];
          const allFeatures = polygonLayersRef.current.map((l: any, idx: number) => {
            const geo = l.toGeoJSON();
            const existingProps = _existingFeatures[idx]?.properties || {};
            // BUG-STALE-1 FIX (Sprint v178): inject fresh per-polygon areaSqM so
            // renderCenterLabels(), polygon list panel, and mobile sheet all show
            // the updated area immediately after vertex drag (not stale old value).
            const flat = getSafeLatLngs(l);
            const freshArea = calcAreaFromLatLngs(flat);
            return { ...geo, properties: { ...existingProps, ...geo.properties, areaSqM: Math.round(freshArea) } };
          });
          const fc = { type: "FeatureCollection" as const, features: allFeatures, properties: { totalAreaSqM: 0 } };
          let totalArea = 0;
          polygonLayersRef.current.forEach((l: any) => {
            // N1 v41: use safe helper instead of l.getLayers()[0]?.getLatLngs?.()
            // which crashes for L.Polygon (no getLayers method)
            const flat = getSafeLatLngs(l);
            totalArea += calcAreaFromLatLngs(flat);
          });
          fc.properties.totalAreaSqM = totalArea;
          setDraftGeoJson(fc as any);
          // F3 v33: render labels for ALL polygons after edit
          // updateForLayer(editActiveSub) clears all labels then shows only edited polygon
          // — with multi-polygon boundaries, other polygon labels disappear during edit
          // J1 v37: respect segment label toggle
          if (segmentLabelMgrRef.current && showSegmentLabelsRef.current) {
            segmentLabelMgrRef.current.clearLabels();
            polygonLayersRef.current.forEach((l: any) => {
              segmentLabelMgrRef.current!.appendForLayer(l);
            });
          }
          // D12 v31: use active sub-layer for perimeter/area (not stale polygonLayerRef.current)
          // polygonLayerRef.current = last drawn polygon; for multi-polygon, editing polygon 1
          // would show polygon N's perimeter. Use activePolygonIndexRef for correct layer.
          try {
            const perimSub = polygonLayersRef.current[activePolygonIndexRef.current] || polygonLayerRef.current;
            // N1 v41: use safe helper (getSafeLatLngs) — handles both L.Polygon and L.geoJSON wrapper
            const flat = getSafeLatLngs(perimSub);
            const perim = calcPerimeterFromLatLngs(flat);
            setSavedPerimeter(perim);
            // BUG-6 FIX: Also update area so live display reflects edited polygon
            const area = calcAreaFromLatLngs(flat);
            if (area > 0) setLiveArea(area);
          } catch {}
        }
      });
      // Vertex drag — offset indicator
      // [BUG-CENTER-LIVE-V8 FIX v252x] Use touchmove/mousemove on map container for guaranteed center label live update
      // Root cause analysis: pm:markerdragstart/pm:markerdrag Geoman events are unreliable in Chrome mobile.
      // The Geoman event chain fires on subLayer → geoJSONWrapper → map via getAllParentGroups,
      // but in practice Chrome mobile does not always propagate to map.on() listener.
      // Fix: listen to touchmove/mousemove on the map container DOM element directly.
      // These are native browser events — guaranteed to fire for ALL drag types (vertex, midpoint, whole polygon).
      // rAF-throttled to avoid layout thrash. Only active in edit mode (mapModeRef.current === 'edit').
      let _centerMoveRaf: number | null = null;
      const _updateCenterLabelsFromMove = () => {
        _centerMoveRaf = null;
        if (mapModeRef.current !== 'edit') return;
        try {
          const _layers = polygonLayersRef.current;
          const _cms = centerLabelMarkersRef.current;
          const _m = leafletMapRef.current;
          if (_m && _layers.length > 0) { // [GAP-10 FIX v252x] removed _cms.length guard — _cm check inside forEach is sufficient
            _layers.forEach((_layer, _idx) => {
              const _cm = _cms[_idx];
              if (!_cm || !_layer) return;
              try {
                const _lls = extractOuterRing(_layer);
                if (_lls.length < 3) return;
                const _c = calcPolygonCentroid(_lls);
                const _newLatLng = L.latLng(_c.lat, _c.lng);
                _cm.setLatLng(_newLatLng);
                const _pt = _m.latLngToLayerPoint(_newLatLng);
                const _el = _cm.getElement?.();
                if (_el) L.DomUtil.setTransform(_el, _pt, 1);
                // [BUG-AREA-LIVE FIX v252y] also update area text in icon HTML during drag
                // draftGeoJson.areaSqM is stale during drag — compute live area from current latLngs
                try {
                  const { areaM2 } = computeAreaPerimeter(_lls);
                  const _draft = draftGeoJsonRef.current as any;
                  const _feat = _draft?.features?.[_idx];
                  const _label = _feat?.properties?.label || `ຜືນທີ ${_idx + 1}`;
                  updateCenterLabelContent(_el, _label, areaM2);
                } catch {}
              } catch {}
            });
          }
        } catch {}
      };
      const _onMapContainerMove = () => {
        if (_centerMoveRaf) return;
        _centerMoveRaf = requestAnimationFrame(_updateCenterLabelsFromMove);
      };
      const _mapContainer = map.getContainer();
      _mapContainer.addEventListener('touchmove', _onMapContainerMove, { passive: true, capture: true });
      _mapContainer.addEventListener('mousemove', _onMapContainerMove, { capture: true });
      map.on("pm:markerdragstart", (e: any) => {
        dbg(`✎ pm:markerdragstart`);
        if (offsetDragRef.current) offsetDragRef.current.startDrag();
        // G7: Push snapshot BEFORE drag so undo restores pre-drag state
        if (polygonLayerRef.current) {
          try { pushUndoSnapshot(); } catch {}
        }
        // [BUG-CENTER-LIVE-V8] touchmove/mousemove handles live update — no polling needed here
        // v74-R14: add .am-dragging for teardrop CSS indicator (mobile edit mode only)
        if (isMobile) {
          const el = (e as any).markerEvent?.target?.getElement?.();
          el?.classList.add('am-dragging');
          // Sprint v90 Bug3 Fix: disable map dragging when vertex drag starts
          // Root cause: Leaflet map.dragging was still enabled during Geoman edit mode,
          // so touch drag on vertex was interpreted as map pan instead of vertex move.
          try { map.dragging.disable(); } catch {}
          // Sprint v77 Bug A Fix v2 (edit-mode): SUBTRACT 60px from _startPos.y
          // Sprint v115 M3: directional offset — vertex near top (y < 120) → offset DOWN (+60)
          // so marker stays visible below the edit strip; elsewhere → offset UP (-60) as before.
          try {
            const marker = (e as any).markerEvent?.target;
            const draggable = marker?.dragging?._draggable;
            if (draggable && draggable._startPos) {
              const _mp = marker?.getLatLng ? map.latLngToContainerPoint(marker.getLatLng()) : null;
              const _oy = (_mp && _mp.y < 120) ? -60 : 60; // near top → +60 (down); else -60 (up)
              draggable._startPos = draggable._startPos.subtract
                ? draggable._startPos.subtract({ x: 0, y: _oy })
                : { x: draggable._startPos.x, y: draggable._startPos.y - _oy };
            }
          } catch {}
        }
      });
      // [BUG-CENTER-LIVE-V6] map.on pm:markerdrag for MIDPOINT drag live center label update
      // Root cause: pm:markerdragstart does NOT fire for midpoint drag
      // pm:markerdrag fires for midpoint drag → use it to update center labels directly
      let _midpointDragRaf: number | null = null;
      map.on("pm:markerdrag", (e: any) => {
        if (_midpointDragRaf) return;
        _midpointDragRaf = requestAnimationFrame(() => {
          _midpointDragRaf = null;
          try {
            const _dragLayer = (e as any).layer;
            if (!_dragLayer) return;
            const _m = leafletMapRef.current;
            if (!_m) return;
            const _layerIdx = polygonLayersRef.current.indexOf(_dragLayer);
            if (_layerIdx < 0) return;
            const _cm = centerLabelMarkersRef.current[_layerIdx];
            if (!_cm) return;
            const _lls = extractOuterRing(_dragLayer);
            if (_lls.length < 3) return;
            const _c = calcPolygonCentroid(_lls);
            const _newLatLng = L.latLng(_c.lat, _c.lng);
            _cm.setLatLng(_newLatLng);
            const _pt = _m.latLngToLayerPoint(_newLatLng);
            const _el = _cm.getElement?.();
            if (_el) {
              L.DomUtil.setTransform(_el, _pt, 1);
              // [BUG-AREA-LIVE-V6 FIX v252z] update area text in center label during midpoint drag
              try {
                const { areaM2 } = computeAreaPerimeter(_lls);
                const _draft = draftGeoJsonRef.current as any;
                const _feat = _draft?.features?.[_layerIdx];
                const _label = _feat?.properties?.label || `ຜືນທີ ${_layerIdx + 1}`;
                  updateCenterLabelContent(_el, _label, areaM2);
              } catch {}
            }
          } catch {}
        });
      });
      map.on("pm:markerdragend", (e: any) => {
        dbg(`✎ pm:markerdragend`);
        if (offsetDragRef.current) offsetDragRef.current.endDrag();
        // [BUG-CENTER-LIVE-V8] touchmove/mousemove stops automatically (no polling to stop)
        // v74-R14: remove .am-dragging on drag end
        if (isMobile) {
          const el = (e as any).markerEvent?.target?.getElement?.();
          el?.classList.remove('am-dragging');
          // Sprint v90 Bug3 Fix: restore map dragging after vertex drag ends
          try { map.dragging.enable(); } catch {}
        }
        // A1 v28: use active sub-layer (not L.geoJSON wrapper) for correct getLatLngs()
        // F4 v33: render labels for ALL polygons after vertex drag
        // N2 v41: use setTimeout(0) to let Geoman commit coordinates before reading them
        // pm:markerdragend fires BEFORE Geoman updates polygon coordinates
        // → without delay, appendForLayer reads stale coordinates
        // J1 v37: respect segment label toggle
        if (segmentLabelMgrRef.current && showSegmentLabelsRef.current) {
          setTimeout(() => {
            if (!segmentLabelMgrRef.current || !showSegmentLabelsRef.current) return;
            segmentLabelMgrRef.current.clearLabels();
            polygonLayersRef.current.forEach((l: any) => {
              segmentLabelMgrRef.current!.appendForLayer(l);
            });
            // [BUG-CENTER-STALE-DRAGEND FIX v252m] update center label after vertex drag
            renderCenterLabels();
          }, 0);
        }
        // [BUG-CENTER-STALE-DRAGEND FIX v252n] center label must update even when segment labels are OFF
        // Root cause: renderCenterLabels() was inside the showSegmentLabelsRef guard → stale label when toggle=off
        // Fix: call renderCenterLabels() unconditionally in a separate setTimeout(0) when showSegmentLabels=false
        if (!showSegmentLabelsRef.current) {
          setTimeout(() => { renderCenterLabels(); }, 0);
        }
      });

      // Sprint v93 B7: early touchstart guard for edit-mode vertex drag
      // Root cause: pm:markerdragstart fires AFTER Geoman detects drag threshold (50-100ms)
      // → map pans for first 50-100ms before dragging is disabled.
      // Fix: detect .marker-icon touchstart immediately (capture phase) → disable map drag right away.
      const _onEditTouchStart = (ev: TouchEvent) => {
        if (mapModeRef.current !== 'edit') return;
        const t = ev.touches[0];
        if (!t) return;
        const el = document.elementFromPoint(t.clientX, t.clientY) as HTMLElement | null;
        if (!el) return;
        const isVertex = el.classList.contains('marker-icon') || !!(el.closest?.('.marker-icon'));
        const isCursor = el.classList.contains('cursor-marker') || !!(el.closest?.('.cursor-marker'));
        if (isVertex && !isCursor) {
          try { map.dragging.disable(); } catch {}
          // The edit session sets `touch-action: none` before the gesture begins.
          // Do not cancel this touchstart: Android Chrome may otherwise cancel the
          // Leaflet/Geoman draggable sequence and leave a visible-but-frozen vertex.
          dbg('🔒 mobile vertex touchstart → map drag disabled; Geoman receives the drag');
        }
      };
      const _onEditTouchEnd = () => {
        // Re-enable dragging after touch ends (pm:markerdragend also re-enables, this is a safety net)
        if (mapModeRef.current === 'edit') {
          try { map.dragging.enable(); } catch {}
          // Sprint v115 M4: clear stuck teardrop if pm:markerdragend didn't fire (touchcancel)
          // OS notifications, palm rejection, or system gestures can cancel touch mid-drag
          // leaving .am-dragging on the marker element until the user exits edit mode.
          document.querySelectorAll('.am-dragging').forEach(el => el.classList.remove('am-dragging'));
        }
      };
      const mapContainer = map.getContainer();
      // The map drag guard is capture-only; CSS touch-action prevents browser panning
      // without cancelling the marker's own draggable touch sequence.
      mapContainer.addEventListener('touchstart', _onEditTouchStart, { capture: true, passive: true });
      mapContainer.addEventListener('touchend', _onEditTouchEnd, { capture: true, passive: true });
      mapContainer.addEventListener('touchcancel', _onEditTouchEnd, { capture: true, passive: true });
      // Sprint v93 B7: store refs on map instance so cleanup block can removeEventListener
      (map as any).__b7TouchContainer = mapContainer;
      (map as any).__b7OnEditTouchStart = _onEditTouchStart;
      (map as any).__b7OnEditTouchEnd = _onEditTouchEnd;

      // Shape removed
      map.on("pm:remove", (e: any) => {
        // v274: manager deletion owns the optimistic local update and server write.
        // A duplicate Geoman event must not schedule stale label rendering or a
        // second draft mutation while the authoritative write is in flight.
        if (isBoundaryDeletionRef.current) {
          segmentLabelMgrRef.current?.clearLabels();
          return;
        }
        // G9: find which index was removed and call removePolygonFromDraft
        // CRITICAL FIX v27: e.layer may be a sub-layer of an L.geoJSON wrapper;
        // use sub-layer check + coordinate comparison, not just indexOf
        const removedIdx = polygonLayersRef.current.findIndex((wrapperLayer: any) => {
          if (wrapperLayer === e.layer) return true;
          try {
            // Check if e.layer is a direct child of the wrapper
            const subLayers: any[] = [];
            if (wrapperLayer.eachLayer) wrapperLayer.eachLayer((sl: any) => subLayers.push(sl));
            if (subLayers.includes(e.layer)) return true;
            // Fallback: coordinate comparison
            const removedCoords = JSON.stringify(e.layer.toGeoJSON?.()?.geometry?.coordinates);
            const wrapperGeo = wrapperLayer.toGeoJSON?.();
            const wrapperCoords = JSON.stringify(
              wrapperGeo?.geometry?.coordinates ||
              wrapperGeo?.features?.[0]?.geometry?.coordinates
            );
            return !!(removedCoords && removedCoords === wrapperCoords);
          } catch { return false; }
        });
        // [BUG-STALE-LABEL-FIX v203] ALWAYS clear ALL labels first on any pm:remove event.
        // Root cause: when removedIdx === -1 AND polygonLayerRef.current !== e.layer,
        // neither branch called clearLabels() -> stale edge labels remained on screen.
        if (segmentLabelMgrRef.current) {
          segmentLabelMgrRef.current.clearLabels();
        }
        if (removedIdx !== -1) {
          polygonLayersRef.current.splice(removedIdx, 1);
          removePolygonFromDraft(removedIdx);
          // If active layer removed, switch to last remaining
          if (polygonLayerRef.current === e.layer) {
            polygonLayerRef.current = polygonLayersRef.current[polygonLayersRef.current.length - 1] || null;
            setActivePolygonIndex(Math.max(0, polygonLayersRef.current.length - 1));
          }
          // A8 v28: clear ALL labels then re-render for each remaining polygon
          // (prevents ghost labels from the deleted polygon staying on screen)
          if (segmentLabelMgrRef.current) {
            segmentLabelMgrRef.current.clearLabels();
            if (polygonLayersRef.current.length === 0) {
              setSavedPerimeter(0);
            } else if (showSegmentLabelsRef.current) {
              // J1 v37: only re-render if labels toggle is ON
              polygonLayersRef.current.forEach((remainingLayer: any) => {
                segmentLabelMgrRef.current!.appendForLayer(remainingLayer);
              });
            }
          } else if (polygonLayersRef.current.length === 0) {
            setSavedPerimeter(0);
          }
        } else if (polygonLayerRef.current === e.layer) {
          // Legacy fallback
          polygonLayerRef.current = null;
          setDraftGeoJson(null);
          setSavedPerimeter(0);
          // clearLabels() already called unconditionally above
        } else {
          // [BUG-STALE-LABEL-FIX v203] Unknown layer removed - re-render for all known layers
          if (segmentLabelMgrRef.current && showSegmentLabelsRef.current && polygonLayersRef.current.length > 0) {
            polygonLayersRef.current.forEach((remainingLayer: any) => {
              segmentLabelMgrRef.current!.appendForLayer(remainingLayer);
            });
          }
        }
        // [BUG-STALE-LABEL-FIX v203] Also refresh center labels after any delete
        // [BUG-1 FIX v252x] renderCenterLabels uses draftGeoJsonRef which may be stale after splice
        // Fix: use longer timeout to let setDraftGeoJson state update propagate
        setTimeout(() => renderCenterLabels(), 150);
      });

      // G11+G7: Rotation start — push snapshot BEFORE rotate so undo restores pre-rotate state
      map.on("pm:rotatestart", () => {
        if (polygonLayerRef.current) {
          try { pushUndoSnapshot(); } catch {}
        }
      });

      // G11: Rotation complete — update draftGeoJson + recalculate area + update labels
      map.on("pm:rotateend", (e: any) => {
        const layer = e.layer || polygonLayerRef.current;
        if (!layer) return;
        // G9 FIX: update the rotated layer in polygonLayersRef, rebuild FeatureCollection
        const layerIdx = polygonLayersRef.current.indexOf(layer);
        if (layerIdx >= 0) polygonLayersRef.current[layerIdx] = layer;
        try {
          // Sprint v133 Bug1b: preserve color/label from existing draftGeoJson (same fix as pm:edit)
          const _rotExistingFeatures = (draftGeoJsonRef.current as any)?.features || [];
          const allFeatures = polygonLayersRef.current.map((l: any, idx: number) => {
            const geo = l.toGeoJSON();
            const existingProps = _rotExistingFeatures[idx]?.properties || {};
            return { ...geo, properties: { ...existingProps, ...geo.properties } };
          });
          const fc = { type: "FeatureCollection" as const, features: allFeatures, properties: { totalAreaSqM: 0 } };
          let totalArea = 0;
          // Sprint v109 Fix 1: use getSafeLatLngsModule instead of l.getLayers()[0] which throws
          // TypeError for L.Polygon layers (no getLayers method) loaded by loadSavedPolygon
          polygonLayersRef.current.forEach((l: any) => {
            const flat2 = getSafeLatLngsModule(l);
            totalArea += calcAreaFromLatLngs(flat2);
          });
          fc.properties.totalAreaSqM = totalArea;
          const geoJson = layer.toGeoJSON() as GeoJsonPolygon;
          setDraftGeoJson(fc as any);
          const lls = layer.getLatLngs ? layer.getLatLngs() : [];
          const flat = Array.isArray(lls[0]) ? lls[0] : lls;
          const area = calcAreaFromLatLngs(flat);
          const perim = calcPerimeterFromLatLngs(flat);
          if (area > 0) setLiveArea(area);
          setSavedPerimeter(perim);
          if (segmentLabelMgrRef.current) {
            // G7 v34: replace updateForLayer(layer) + 300ms safety-net with immediate loop
            // updateForLayer(layer) showed only rotated polygon for 300ms — flash in multi-polygon
            // D28 v31 safety-net already proved the correct pattern; now apply it immediately
            // J1 v37: respect segment label toggle
            if (showSegmentLabelsRef.current) {
              segmentLabelMgrRef.current.clearLabels();
              polygonLayersRef.current.forEach((l: any) => {
                segmentLabelMgrRef.current!.appendForLayer(l);
              });
            }
            // Safety-net: re-render after 300ms in case Geoman finishes repositioning vertices
            setTimeout(() => {
              if (!segmentLabelMgrRef.current) return;
              // J1 v37: respect segment label toggle
              if (!showSegmentLabelsRef.current) return;
              segmentLabelMgrRef.current.clearLabels();
              polygonLayersRef.current.forEach((l: any) => {
                segmentLabelMgrRef.current!.appendForLayer(l);
              });
            }, 300);
          }
        } catch {}
      });
    }

    // ResizeObserver
    // Sprint v110.1 M2+S1: ResizeObserver handles both container resize and peek bar height.
    // When peek bar resizes, set --peek-bar-h CSS var on map container parent so the map div
    // (bottom: var(--peek-bar-h, 0px)) shrinks and OSM attribution becomes visible (license req).
    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === peekBarRef.current) {
          // Sprint v110.2 M2 fix: use offsetHeight (border-box) instead of contentRect.height.
          // contentRect excludes padding — peek bar has paddingBottom: calc(8px + env(safe-area-inset-bottom))
          // which means the map bottom edge was ~8px (more on gesture-nav devices) below the bar top,
          // partially covering the OSM attribution. offsetHeight includes padding + border.
          const h = (entry.target as HTMLElement).offsetHeight;
          mapRef.current?.parentElement?.style.setProperty('--peek-bar-h', h + 'px');
        }
      }
      if (leafletMapRef.current) leafletMapRef.current.invalidateSize();
    });
    resizeObserverRef.current.observe(container);
    // Sprint v110 S1 + Sprint v110.1 M2+S1: also observe peek bar to track its height for CSS var
    if (peekBarRef.current) resizeObserverRef.current.observe(peekBarRef.current);

    // GAP-8: Initialize measure layer group
    measureLayerRef.current = L.layerGroup().addTo(map);
    // Sprint v119: Initialize draw-mode midpoint layer
    drawMidLayerRef.current = L.layerGroup().addTo(map);
    // BUG-v280: Presentation-only temporary path for the active midpoint drag.
    drawLiveSegmentLayerRef.current = L.layerGroup().addTo(map);

    setMapReady(true);

    // Force invalidateSize
    [0, 100, 300, 600].forEach((delay) => {
      setTimeout(() => {
        if (leafletMapRef.current) leafletMapRef.current.invalidateSize(true);
      }, delay);
    });

    // Fallback: ensure mapReady is true even if something above caused a re-render race
    setTimeout(() => setMapReady(true), 800);

    // Sprint v93 B7: listeners are stored on map instance via __b7* keys for cleanup on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDraftGeoJson, setTileLayer, _editModeOpts]);

  // ============================================================
  // Load saved polygon
  // ============================================================

  const loadSavedPolygon = useCallback(() => {
    if (isBoundaryDeletionRef.current || !leafletMapRef.current || !L || !geoBoundaryJson) return;
    const map = leafletMapRef.current;
    // NEW: preserve edit mode across refetch (e.g. after auto-save triggers loadSavedPolygon)
    const wasInEditMode = drawModeRef.current === "edit" && !!(map.pm?.globalEditModeEnabled?.());
    // G9: Remove ALL existing polygon layers
    try { if (map.pm?.globalEditModeEnabled?.()) map.pm.disableGlobalEditMode(); } catch {}
    for (const oldLayer of polygonLayersRef.current) {
      try { map.removeLayer(oldLayer); } catch {}
    }
    polygonLayersRef.current = [];
    polygonLayerRef.current = null;
    try {
      // G9: geoBoundaryJson is now FeatureCollection — render all features with distinct colors
      // Sprint v115 Fix #1: uses module-level POLYGON_COLORS (single source of truth)
      const layer = L.geoJSON(geoBoundaryJson, {
        style: (feature: any) => {
          const idx = feature?.properties?.polygonIndex ?? 0;
          // Sprint v131: use custom color if saved, else fall back to POLYGON_COLORS[idx]
          const savedColor = feature?.properties?.color as string | null;
          return {
            color: "#ffffff",
            fillColor: savedColor || POLYGON_COLORS[idx % POLYGON_COLORS.length],
            fillOpacity: 0.35,
            weight: 3,
            dashArray: undefined,
          };
        },
      });
      layer.addTo(map);
      // G9: track each sub-layer individually
      layer.eachLayer((subLayer: any) => {
        polygonLayersRef.current.push(subLayer);
        // N2 v41: attach layer-level pm:markerdrag for live label updates during vertex drag
        // Geoman fires pm:markerdrag on the LAYER (not map) during edit
        // This gives real-time label updates as user drags a vertex
        subLayer.on("pm:markerdrag", () => {
          // Sprint v73 STEP H: live label updates handled by attachLiveMeasure
          // (rAF-throttled, DOM mutation only — see attachLiveMeasure)
          // This handler intentionally left empty.
        });
        // Sprint v179 BUG-DRIFT-2 FIX: refresh ALL labels after whole-polygon drag ends.
        // Geoman fires pm:dragend on the LAYER (not map) when user drags entire polygon in GlobalDragMode.
        // Without this, segment + center labels stay at old position until user zooms or pans.
        // Note: pm:drag (during drag) is NOT throttled here — Leaflet already repositions L.marker
        // via CSS transform on every frame; only the label manager's internal lat/lng record is stale.
        // We only need a full rebuild on pm:dragend (drag release).
        subLayer.on("pm:dragend", () => {
          if (segmentLabelMgrRef.current && showSegmentLabelsRef.current) {
            segmentLabelMgrRef.current.clearLabels();
            polygonLayersRef.current.forEach((l: any) => {
              segmentLabelMgrRef.current!.appendForLayer(l);
            });
          }
          renderCenterLabels();
        });
        // Sprint v73 STEP C: mobile tap-to-edit
        attachMobileEditHandlerRef.current(subLayer);
        // Sprint v73 STEP H: live measurement during vertex drag
        attachLiveMeasureRef.current(subLayer);
      });
      polygonLayerRef.current = layer; // L.geoJSON wrapper as active ref
      // Use animate:false to avoid fitBounds animation race:
      // animated fitBounds fires zoomend mid-animation where latLngToLayerPoint
      // returns transitional screen coords → edges appear < 30px → labels skipped.
      map.fitBounds(layer.getBounds(), { padding: [40, 40], animate: false });

      // v21 FIX: Show segment labels on saved polygon — immediate + multiple safety nets
      // fitBounds(animate:false) still triggers zoomend which can race with label render.
      // Multiple timers ensure labels survive any async tile/resize/zoomend events.
      // B2 v29: use appendForLayer() per sub-layer (not updateForLayer on L.geoJSON wrapper)
      // so multi-polygon saved boundaries show labels for ALL polygons, not just the first.
      if (segmentLabelMgrRef.current) {
        // J1 v37: respect segment label toggle on load
        if (showSegmentLabelsRef.current) {
          segmentLabelMgrRef.current.clearLabels();
          polygonLayersRef.current.forEach((subLayer: any) => {
            segmentLabelMgrRef.current!.appendForLayer(subLayer);
          });
        }
        [150, 400, 800, 1500].forEach((delay) => {
          setTimeout(() => {
            if (!segmentLabelMgrRef.current) return;
            // J1 v37: respect segment label toggle
            if (!showSegmentLabelsRef.current) return;
            // B2 v29: re-render all sub-layers on each safety-net timer
            segmentLabelMgrRef.current.clearLabels();
            polygonLayersRef.current.forEach((subLayer: any) => {
              segmentLabelMgrRef.current!.appendForLayer(subLayer);
            });
          }, delay);
        });
      }

      // Calculate perimeter from saved polygon
      try {
        let perim = 0;
        layer.eachLayer((l: any) => {
          if (l.getLatLngs) {
            const lls = l.getLatLngs();
            const flat = Array.isArray(lls[0]) ? lls[0] : lls;
            perim += calcPerimeterFromLatLngs(flat);
          }
        });
        setSavedPerimeter(perim);
      } catch {}

      // Sprint v101 Fix 2: Only enable per-layer pm if user was actively in edit mode before refetch.
      // Previously this ran for ALL canEdit users, re-attaching edit handles even in view mode.
      // This caused edit-mode vertex/midpoint markers to appear on the map after save/refetch
      // even though the user had returned to view mode. Now only restore if wasInEditMode.
      if (canEdit && wasInEditMode) {
        layer.eachLayer((l: any) => {
          if (l.pm) l.pm.enable({ allowSelfIntersection: false });
        });
      }

      // NEW: restore edit mode if user was editing before refetch (e.g. after auto-save)
      if (wasInEditMode && canEdit) {
        setTimeout(() => {
          try {
            if (leafletMapRef.current && !leafletMapRef.current.pm.globalEditModeEnabled()) {
              leafletMapRef.current.pm.enableGlobalEditMode(_editModeOpts());
            }
          } catch {}
        }, 100);
      }
      // Seed the draft-backed polygon manager from confirmed saved geometry without
      // marking it as an unsaved edit. A map opened on an existing house must still
      // allow the user to switch to its land collateral without a false draft dialog.
      hydrateSavedGeoJson(geoBoundaryJson as any);
      // Sprint v145: render center labels after polygons are loaded
      setTimeout(() => renderCenterLabels(), 200);
    } catch (err) {
      console.error("[CollateralMapModal] Failed to load polygon:", err);
    }
  }, [geoBoundaryJson, canEdit, hydrateSavedGeoJson, renderCenterLabels]);

  // ============================================================
  // Tile switching
  // ============================================================

  const switchTileLayer = useCallback((requestedLayer: MapTileLayer) => {
    const map = leafletMapRef.current;
    if (!map || !L) {
      traceMapLayer({ phase: 'transaction-guard', option: requestedLayer, detail: `map=${Boolean(map)} leaflet=${Boolean(L)}` });
      return;
    }

    traceMapLayer({ phase: 'transaction-start', option: requestedLayer, detail: `active-before=${tileLayer}` });

    const selection = resolveMapLayerSelection(requestedLayer, {
      hasGoogleMapsKey: Boolean(googleMapsKey),
      hasMapTilerKey: Boolean(maptilerKey),
    });
    if (selection.kind === "unavailable") {
      traceMapLayer({ phase: 'provider-unavailable', option: requestedLayer, detail: 'missing-google-key' });
      sonnerToast.error("Google Maps API key ຍັງບໍ່ໄດ້ຕັ້ງຄ່າ");
      return;
    }

    const resolvedLayer = selection.resolved as MapTileLayer;
    traceMapLayer({ phase: 'provider-resolved', option: requestedLayer, detail: `resolved=${resolvedLayer} fallback=${selection.usedFallback}` });
    const requestGeneration = ++tileLayerSwitchGenerationRef.current;
    const commitLayer = (nextBaseLayer: any) => {
      if (requestGeneration !== tileLayerSwitchGenerationRef.current || !leafletMapRef.current) return;
      traceMapLayer({ phase: 'transaction-commit', option: requestedLayer, detail: `active-after=${resolvedLayer}` });

      // v285: The former callback referenced `_centerMoveRaf`, a variable scoped
      // inside initMap. That ReferenceError aborted every selection before state or
      // storage changed. Do not remove map-motion listeners while merely switching
      // tiles; they are required for live center labels and are cleaned on teardown.
      const mountedBaseLayers = new Set([tileLayerRef.current, googleMutantRef.current].filter(Boolean));
      mountedBaseLayers.forEach((layer) => {
        try { map.removeLayer(layer); } catch {}
      });
      if (labelLayerRef.current) {
        try { map.removeLayer(labelLayerRef.current); } catch {}
        labelLayerRef.current = null;
      }
      tileLayerRef.current = null;
      googleMutantRef.current = null;

      nextBaseLayer.addTo(map);
      tileLayerRef.current = nextBaseLayer;
      if (resolvedLayer === "google") googleMutantRef.current = nextBaseLayer;
      if (resolvedLayer === "hybrid" || showLabelOverlay) {
        labelLayerRef.current = createOsmTileLayer(
          L,
          { maxZoom: 19, opacity: 0.7, attribution: "" },
          handleOsmTileError,
          handleOsmTileLoad,
        ).addTo(map);
      }
      if (resolvedLayer !== "streets" && resolvedLayer !== "hybrid" && !showLabelOverlay) {
        clearOsmTileFailure();
      }

      setTileLayer(resolvedLayer);
      try { localStorage.setItem("collateralMap.baseLayer", resolvedLayer); } catch {}
      if (selection.usedFallback) sonnerToast.info("HD ບໍ່ພ້ອມ — ສະແດງດາວທຽມແທນ");
    };

    if (resolvedLayer === "google") {
      loadGoogleMapsScript(googleMapsKey).then(() => {
        if (requestGeneration !== tileLayerSwitchGenerationRef.current) {
          traceMapLayer({ phase: 'stale-provider-result', option: requestedLayer, detail: 'google-script' });
          return;
        }
        try {
          const mutant = new (GoogleMutantClass as any)({ type: googleSubLayerRef.current, maxZoom: 22 });
          commitLayer(mutant);
        } catch (mutantErr) {
          traceMapLayer({ phase: 'provider-error', option: requestedLayer, detail: 'google-mutant-create' });
          console.error("[v285] googleMutant creation error:", mutantErr);
          sonnerToast.error("ໂຫຼດ Google Maps ບໍ່ສຳເລັດ — ຮັກສາແຜນທີ່ເດີມໄວ້");
        }
      }).catch((scriptErr) => {
        traceMapLayer({ phase: 'provider-error', option: requestedLayer, detail: 'google-script-load' });
        console.error("[v285] loadGoogleMapsScript error:", scriptErr);
        sonnerToast.error("ໂຫຼດ Google Maps ບໍ່ສຳເລັດ — ຮັກສາແຜນທີ່ເດີມໄວ້");
      });
      return;
    }

    const cfg = getTileConfig(resolvedLayer as Exclude<MapTileLayer, "google">, maptilerKey);
    const baseOptions = { maxNativeZoom: cfg.maxNativeZoom, maxZoom: cfg.maxZoom, attribution: cfg.attribution };
    commitLayer(
      cfg.url === OSM_TILE_URL
        ? createOsmTileLayer(L, baseOptions, handleOsmTileError, handleOsmTileLoad)
        : L.tileLayer(cfg.url, baseOptions),
    );
  }, [setTileLayer, showLabelOverlay, googleMapsKey, maptilerKey, tileLayer, traceMapLayer, clearOsmTileFailure, handleOsmTileError, handleOsmTileLoad]);

  const toggleLabelOverlay = useCallback(() => {
    if (!leafletMapRef.current || !L) return;
    const map = leafletMapRef.current;
    if (labelLayerRef.current) {
      map.removeLayer(labelLayerRef.current);
      labelLayerRef.current = null;
      setShowLabelOverlay(false);
      if (tileLayer !== "streets" && tileLayer !== "hybrid") clearOsmTileFailure();
    } else {
      labelLayerRef.current = createOsmTileLayer(
        L,
        { maxZoom: 19, opacity: 0.7, attribution: "" },
        handleOsmTileError,
        handleOsmTileLoad,
      ).addTo(map);
      setShowLabelOverlay(true);
    }
  }, [tileLayer, clearOsmTileFailure, handleOsmTileError, handleOsmTileLoad]);

  // Sprint v172: Switch Google sub-layer (satellite | roadmap | hybrid | terrain)
  const switchGoogleSubLayer = useCallback((type: "satellite"|"roadmap"|"hybrid"|"terrain") => {
    if (!leafletMapRef.current) return;
    const requestGeneration = ++googleSubLayerSwitchGenerationRef.current;
    // Keep the requested Google view ready before the base provider resolves.
    // This is presentation-only and lets the unified v377 layer chooser select a
    // Google subtype in the same click that activates Google as the base layer.
    googleSubLayerRef.current = type;
    setGoogleSubLayer(type);
    try { localStorage.setItem("collateralMap.googleLayer", type); } catch {}
    // BUG-V172-1 fix: guard — Google Maps script must be loaded before creating GoogleMutant
    if (!(window as any).google?.maps) {
      // Script not loaded yet — switchTileLayer will create the requested type.
      return;
    }
    const map = leafletMapRef.current;
    try {
      const mutant = new (GoogleMutantClass as any)({ type, maxZoom: 22 });
      // Construct first. If GoogleMutant throws, keep the working map mounted.
      if (requestGeneration !== googleSubLayerSwitchGenerationRef.current || tileLayer !== "google") return;
      const previousMutant = googleMutantRef.current;
      if (previousMutant) {
        try { map.removeLayer(previousMutant); } catch {}
      }
      googleMutantRef.current = mutant;
      tileLayerRef.current = mutant;
      mutant.addTo(map);
    } catch(e) {
      console.error("[v172] switchGoogleSubLayer error:", e);
      sonnerToast.error("ໂຫຼດ Google layer ບໍ່ສຳເລັດ — ຮັກສາແຜນທີ່ເດີມໄວ້");
    }
  }, [tileLayer]);

  // H2: Toggle segment distance labels on/off
  const toggleSegmentLabels = useCallback(() => {
    const mgr = segmentLabelMgrRef.current;
    if (!mgr) return;
    const next = !showSegmentLabelsRef.current;
    setShowSegmentLabels(next);
    showSegmentLabelsRef.current = next;
    if (next) {
      // Re-render labels for all polygons
      mgr.clearLabels();
      polygonLayersRef.current.forEach((layer) => {
        if (layer) mgr.appendForLayer(layer);
      });
    } else {
      mgr.clearLabels();
    }
  }, []);

  // ============================================================
  // Lifecycle
  // ============================================================

  useEffect(() => {
    if (!open) {
      // v377: UI surfaces are never persisted with a collateral or its geometry.
      setActiveSurface('none');
      initCalledRef.current = false;
      // Sprint v131: reset client-side visibility state on modal close
      setHiddenPolygons(new Set());
      setColorPickerIndex(null);
      setEditingLabelIndex(null);
      // D27 v31: clear auto-save timer on modal close to prevent post-unmount saveBoundary() call
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      // Sprint v100 B9: clear draw measure interval if modal closes during draw
      if (_drawMeasureIntervalRef.current) {
        clearInterval(_drawMeasureIntervalRef.current);
        _drawMeasureIntervalRef.current = null;
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (segmentLabelMgrRef.current) {
        segmentLabelMgrRef.current.destroy();
        segmentLabelMgrRef.current = null;
      }
      if (offsetDragRef.current) {
        offsetDragRef.current.destroy();
        offsetDragRef.current = null;
      }
      // V65 + Sprint v161: cleanup device orientation listeners (now dual-listener object)
      if (deviceOrientationListenerRef.current) {
        const ref = deviceOrientationListenerRef.current as any;
        if (ref.absolute) {
          window.removeEventListener('deviceorientationabsolute', ref.absolute);
          window.removeEventListener('deviceorientation', ref.relative);
        } else {
          // Legacy single-listener fallback
          window.removeEventListener('deviceorientationabsolute', ref);
          window.removeEventListener('deviceorientation', ref);
        }
        deviceOrientationListenerRef.current = null;
        setFollowDeviceCompass(false);
      }
      if (leafletMapRef.current) {
        // Sprint v93 B7: remove touchstart/touchend listeners before destroying map
        try {
          const b7c = (leafletMapRef.current as any).__b7TouchContainer;
          const b7s = (leafletMapRef.current as any).__b7OnEditTouchStart;
          const b7e = (leafletMapRef.current as any).__b7OnEditTouchEnd;
          if (b7c && b7s) b7c.removeEventListener('touchstart', b7s, true);
          if (b7c && b7e) { b7c.removeEventListener('touchend', b7e, true); b7c.removeEventListener('touchcancel', b7e, true); }
        } catch {}
        // Sprint v95: disconnect MutationObserver (fallback for when monkey-patch is skipped)
        try { (leafletMapRef.current as any).__midpointObserver?.disconnect(); } catch {}
        // Sprint v110 A2: flush any orphaned draw-session DOM listeners (modal closed mid-draw)
        try {
          const dsc = (leafletMapRef.current as any).__drawSessionCleanups;
          if (Array.isArray(dsc)) {
            while (dsc.length) { try { dsc.pop()(); } catch {} }
          }
          (leafletMapRef.current as any).__drawSessionCleanups = null;
        } catch {}
        // Sprint v114 Fix I: ensure draw cleanup on mid-draw modal close
        // pm:drawend once-listeners (interval, _syncHintMarker restore) never fire if modal closes mid-draw.
        // Explicitly clear interval and disableDraw so Geoman cleans up its own state.
        try {
          if (_drawMeasureIntervalRef.current) {
            clearInterval(_drawMeasureIntervalRef.current);
            _drawMeasureIntervalRef.current = null;
          }
        } catch {}
        try {
          if (isDrawingRef.current && leafletMapRef.current.pm) {
            leafletMapRef.current.pm.disableDraw(); // fires pm:drawend → restores _syncHintMarker
          }
        } catch {}
        // A16 v28: Remove ALL map event listeners before destroying the map
        // Prevents duplicate listeners accumulating on modal re-open
        try { leafletMapRef.current.off(); } catch {}
        try { if (leafletMapRef.current.pm) leafletMapRef.current.pm.setGlobalOptions({}); } catch {};
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        polygonLayerRef.current = null;
        // G6 v34: clear polygonLayersRef array on modal close
        // Without this: re-open on collateral with no saved boundary leaves stale layer refs
        // (loadSavedPolygon clears it when geoBoundaryJson exists, but not when null)
        polygonLayersRef.current = [];
        tileLayerRef.current = null;
        labelLayerRef.current = null;
        measureLayerRef.current = null;
        if (tempMarkerRef.current) { tempMarkerRef.current = null; }
        setMapReady(false);
        setHoverCoords(null);
        setIsDrawing(false);
        setLiveArea(0);
        setLivePerimeter(0);
        setSavedPerimeter(0);
        if (measureDragFrameRef.current !== null) cancelAnimationFrame(measureDragFrameRef.current);
        measureDragFrameRef.current = null;
        measureActiveDragIndexRef.current = null;
        setMeasureMode(false);
        setMeasureStage('idle');
        setMeasureDistance(null);
        setMeasurePointCount(0);
        measurePointsRef.current = [];
        // Sprint v109 Fix 4: reset mapMode and drawMode on close
        // Without this: re-open after editing leaves blue edit strip over a fresh map
        setMapMode('view');
        setDrawMode(null);
        isDrawingRef.current = false;
      }
      return;
    }

    const timer = setTimeout(() => { initMap(); }, 250);
    return () => clearTimeout(timer);
  }, [open, initMap]);

  useEffect(() => {
    if (mapReady && !isLoading) {
      loadSavedPolygon();
    }
  }, [mapReady, isLoading, loadSavedPolygon]);

  // v377: Desktop Street View reserves canvas width. Ask Leaflet to measure once
  // after React applies the pane layout, without panning, rebuilding geometry, or
  // touching the draft state.
  useEffect(() => {
    if (!open || isMobile || !leafletMapRef.current) return;
    const frame = window.requestAnimationFrame(() => {
      try { leafletMapRef.current?.invalidateSize?.({ pan: false, animate: false }); } catch {}
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isMobile, open, showStreetView]);

  // NAV-1 (Sprint v168.9): Navigate map to new collateral GPS when gpsLatitude/gpsLongitude changes
  // (i.e. when user switches collateral). Also re-adds the stored GPS pin for the new collateral.
  // This fires AFTER gpsLatRef.current is updated (line ~325) and after mapReady.
  const prevGpsLatRef = useRef<string | number | null | undefined>(null);
  const prevGpsLngRef = useRef<string | number | null | undefined>(null);
  useEffect(() => {
    if (!mapReady || !leafletMapRef.current || !L) return;
    // Only fire when gpsLatitude/gpsLongitude actually changes (collateral switch)
    if (gpsLatitude === prevGpsLatRef.current && gpsLongitude === prevGpsLngRef.current) return;
    prevGpsLatRef.current = gpsLatitude;
    prevGpsLngRef.current = gpsLongitude;
    const map = leafletMapRef.current;
    // Step 1: Remove old stored GPS pin
    if (storedGpsPinRef.current) {
      try { map.removeLayer(storedGpsPinRef.current); } catch {}
      storedGpsPinRef.current = null;
    }
    // Step 2: Navigate to new collateral GPS
    if (gpsLatitude && gpsLongitude) {
      const lat = Number(gpsLatitude);
      const lng = Number(gpsLongitude);
      // Step 3: Re-add stored GPS pin for new collateral
      const gpsIcon = L.divIcon({
        html: `<div style="width:14px;height:14px;background:#002975;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.5);"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
        className: "crs-map-stored-gps-pin",
      });
      storedGpsPinRef.current = L.marker([lat, lng], { icon: gpsIcon })
        .bindTooltip("\uD83D\uDCCD GPS \u0E88\u0EB8\u0E94", { permanent: false, direction: "top" })
        .addTo(map);
      // Step 4: Fly to new collateral GPS (only if no polygon — loadSavedPolygon handles fitBounds when polygon exists)
      if (!polygonLayerRef.current) {
        map.flyTo([lat, lng], COLLATERAL_MAP.DEFAULT_ZOOM, { animate: true, duration: 0.6 });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsLatitude, gpsLongitude, mapReady]);

  // G4: Live GPS dot + accuracy circle on map
  // Sprint v107 Fix 1: Hide GPS dot during draw/edit mode — it clutters the polygon editing UI
  // and can be mistaken for a vertex marker. Only show in view mode.
  useEffect(() => {
    if (!leafletMapRef.current || !L || !mapReady) return;
    const map = leafletMapRef.current;
    // Remove old markers
    if (liveGpsMarkerRef.current) { map.removeLayer(liveGpsMarkerRef.current); liveGpsMarkerRef.current = null; }
    if (liveGpsCircleRef.current) { map.removeLayer(liveGpsCircleRef.current); liveGpsCircleRef.current = null; }
    // Sprint v107: Do NOT render GPS dot/circle during draw or edit mode
    if (!liveGps || mapMode !== 'view') return;
    // Accuracy circle (semi-transparent blue)
    liveGpsCircleRef.current = L.circle([liveGps.lat, liveGps.lng], {
      radius: liveGps.accuracy,
      color: "#3b82f6",
      fillColor: "#3b82f6",
      fillOpacity: 0.10,
      weight: 1.5,
    }).addTo(map);
    // Blue dot with white border — use pointer-events:none so it never intercepts Geoman taps
    const dotIcon = L.divIcon({
      html: `<div style="width:14px;height:14px;background:#3b82f6;border:2.5px solid white;border-radius:50%;box-shadow:0 0 6px rgba(59,130,246,0.7);pointer-events:none;"></div>`,
      iconSize: [14, 14], iconAnchor: [7, 7], className: "gps-live-dot",
    });
    liveGpsMarkerRef.current = L.marker([liveGps.lat, liveGps.lng], { icon: dotIcon, zIndexOffset: 500, interactive: false }).addTo(map);
  }, [liveGps, mapReady, mapMode]);

  // G4: Live GPS tracking — start watchPosition when modal opens, stop on close
  useEffect(() => {
    if (!open || !navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setLiveGps({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => {
        console.warn("[GPS] watchPosition error:", err.message);
        // Permission denied or unavailable — silently fall back to stored GPS
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
    gpsWatchIdRef.current = watchId;
    return () => {
      navigator.geolocation.clearWatch(watchId);
      gpsWatchIdRef.current = null;
      setLiveGps(null);
    };
  }, [open]);

  // ============================================================
  // Handlers
  // ============================================================

  const handleClose = useCallback(() => {
    setPendingCollateralSwitchId(null);
    if (hasUnsavedChanges) setShowUnsavedDialog(true);
    else onClose();
  }, [hasUnsavedChanges, onClose]);

  const completeCollateralSwitch = useCallback((nextCollateralId: number) => {
    if (nextCollateralId !== collateralId) onSwitchCollateral?.(nextCollateralId);
  }, [collateralId, onSwitchCollateral]);

  const requestCollateralSwitch = useCallback((nextCollateralId: number | null) => {
    if (!nextCollateralId || nextCollateralId === collateralId) return;
    if (hasUnsavedChanges) {
      setPendingCollateralSwitchId(nextCollateralId);
      setShowUnsavedDialog(true);
      return;
    }
    completeCollateralSwitch(nextCollateralId);
  }, [collateralId, completeCollateralSwitch, hasUnsavedChanges]);

  const handleConfirmDiscard = useCallback(() => {
    setShowUnsavedDialog(false);
    const nextCollateralId = pendingCollateralSwitchId;
    setPendingCollateralSwitchId(null);
    if (nextCollateralId) completeCollateralSwitch(nextCollateralId);
    else onClose();
  }, [completeCollateralSwitch, onClose, pendingCollateralSwitchId]);

  // G10: Area change warning helper—returns true if user confirms or no warning needed
  const checkAreaChangeWarning = useCallback((onConfirmedSave?: () => Promise<void>): boolean => {
    if (!draftGeoJson || !areaSqM || areaSqM <= 0) return true; // first save or no saved area
    // G9 FIX: use totalAreaSqM for FeatureCollection, fallback to areaSqM for single polygon
    const draftArea = (draftGeoJson as any)?.properties?.totalAreaSqM ?? (draftGeoJson as any)?.properties?.areaSqM ?? 0;
    if (draftArea <= 0) return true;
    const delta = Math.abs(draftArea - areaSqM) / areaSqM;
    if (delta > 0.20) {
      const sign = draftArea > areaSqM ? "+" : "-";
      const pct = (delta * 100).toFixed(1);
      const fromArea = formatAreaByUnit(areaSqM, "rai");
      const toArea = formatAreaByUnit(draftArea, "rai");
      // Sprint v115 Fix #4: use branded dialog instead of window.confirm
      // Non-blocking: show dialog and return false; actual save triggered from dialog onConfirm
      setConfirmState({
        title: `ເນື້ອທີ່ປ່ຽນແປງ ${sign}${pct}%`,
        message: `ຈາກ: ${fromArea} → ${toArea}\n\nຕ້ອງການ save ຫື່ອບໍ້?`,
        confirmLabel: 'ບັນທຶກ',
        cancelLabel: 'ຍົກເລີກ',
        icon: 'warning',
        onConfirm: async () => {
          if (onConfirmedSave) {
            await onConfirmedSave();
            return;
          }
          // BUG-STALE-2 FIX (Sprint v178): reset liveArea/livePerimeter after save
          // via area-change warning dialog (3rd save path — missed in original plan).
          const ok = await saveBoundary();
          if (ok) {
            setLiveArea(0);
            setLivePerimeter(0);
          }
        },
      });
      return false; // dialog handles save asynchronously
    }
    return true;
  }, [draftGeoJson, areaSqM, saveBoundary]);

  const handleSave = useCallback(async () => {
    if (!checkAreaChangeWarning()) return;
    // BUG-STALE-2 FIX (Sprint v178): capture return value and reset liveArea/livePerimeter
    // so desktop info bar + mobile area pill show areaSqM from server (not stale edit value).
    const ok = await saveBoundary();
    if (ok) {
      setLiveArea(0);
      setLivePerimeter(0);
    }
  }, [saveBoundary, checkAreaChangeWarning]);

  // BUG-D FIX: Save-and-Close handler
  const handleSaveAndExit = useCallback(async () => {
    const exitAfterSave = async () => {
      const ok = await saveBoundary();
      if (!ok) return;
      setLiveArea(0);
      setLivePerimeter(0);
      const nextCollateralId = pendingCollateralSwitchId;
      setPendingCollateralSwitchId(null);
      setShowUnsavedDialog(false);
      if (nextCollateralId) completeCollateralSwitch(nextCollateralId);
      else onClose();
    };
    setShowUnsavedDialog(false);
    if (!checkAreaChangeWarning(exitAfterSave)) return;
    await exitAfterSave();
  }, [checkAreaChangeWarning, completeCollateralSwitch, onClose, pendingCollateralSwitchId, saveBoundary]);

  const handleClear = useCallback(async () => {
    // Sprint v115 Fix #4: use branded dialog instead of confirm()
    setConfirmState({
      title: 'ລຶບຂອບເຂດທັງໝອດ',
      message: 'ຕ້ອງການລຶບຂອບເຂດທັງໝອດ ຫລືອບໍ້? ການກະທຳນີ້ບໍ່ສາມາຖຖອນຄືນໄດ້.',
      confirmLabel: 'ລຶບຂອບເຂດ',
      cancelLabel: 'ຍົກເລີກ',
      destructive: true,
      icon: 'delete',
      onConfirm: async () => {
        const clearedDraft = buildFeatureCollection([], []);
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
        }
        isBoundaryDeletionRef.current = true;
        draftGeoJsonRef.current = clearedDraft;
        setDraftGeoJson(clearedDraft as any);
        polygonLayersRef.current.forEach((layer: any) => {
          try { layer.pm?.disable(); layer.off(); leafletMapRef.current?.removeLayer(layer); } catch {}
        });
        polygonLayersRef.current = [];
        polygonLayerRef.current = null;
        segmentLabelMgrRef.current?.clearLabels();
        renderCenterLabels();
        const ok = await clearBoundary();
        isBoundaryDeletionRef.current = false;
        if (ok) {
          if (leafletMapRef.current) {
          setSavedPerimeter(0);
          setLiveArea(0);
          setLivePerimeter(0);
          setActivePolygonIndex(0);
          // Sprint v132 Bug3: reset hiddenPolygons when all layers are cleared
          setHiddenPolygons(new Set());
          if (segmentLabelMgrRef.current) segmentLabelMgrRef.current.clearLabels();
          // [BUG-CLEAR-CENTER FIX v252r] Also clear center labels after clearBoundary
          // clearLabels() only removes edge labels; center labels (name+area at centroid) were left on map
          renderCenterLabels();
          }
        } else {
          draftGeoJsonRef.current = geoBoundaryJson as any;
          hydrateSavedGeoJson(geoBoundaryJson as any);
          setTimeout(() => loadSavedPolygon(), 0);
        }
      },
    });
  }, [clearBoundary, geoBoundaryJson, hydrateSavedGeoJson, loadSavedPolygon, renderCenterLabels, setConfirmState, setDraftGeoJson]);

  // v274: Both desktop and mobile manager rows must persist their resulting
  // FeatureCollection. The former local-only delete let `geoBoundaryJson` survive
  // in MySQL, so delayed label timers and reopening the modal could resurrect it.
  const handleManagedPolygonDelete = useCallback(async (index: number) => {
    if (isBoundaryDeletionRef.current) return;
    const current = managedPolygonGeoJson as any;
    const features = current?.features;
    if (!Array.isArray(features) || !features[index]) return;

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    const nextDraft = removeManagedPolygon(current, index);
    const nextFeatures = nextDraft.features;

    isBoundaryDeletionRef.current = true;
    draftGeoJsonRef.current = nextDraft;
    setDraftGeoJson(nextDraft as any);

    const map = leafletMapRef.current;
    const deletedLayer = polygonLayersRef.current[index];
    try { deletedLayer?.pm?.disable?.(); deletedLayer?.off?.(); map?.removeLayer?.(deletedLayer); } catch {}
    polygonLayersRef.current.splice(index, 1);
    polygonLayerRef.current = polygonLayersRef.current[polygonLayersRef.current.length - 1] || null;
    setActivePolygonIndex(Math.max(0, Math.min(index, polygonLayersRef.current.length - 1)));
    setHiddenPolygons(prev => {
      const next = new Set<number>();
      prev.forEach(hiddenIndex => { if (hiddenIndex < index) next.add(hiddenIndex); else if (hiddenIndex > index) next.add(hiddenIndex - 1); });
      return next;
    });
    segmentLabelMgrRef.current?.clearLabels();
    if (showSegmentLabelsRef.current) {
      polygonLayersRef.current.forEach((layer: any) => segmentLabelMgrRef.current?.appendForLayer(layer));
    }
    if (polygonLayersRef.current.length === 0) {
      setSavedPerimeter(0);
      setShowMobilePolySheet(false);
    }
    renderCenterLabels();

    const ok = nextFeatures.length === 0 ? await clearBoundary() : await saveBoundary();
    isBoundaryDeletionRef.current = false;
    if (ok) return;

    // The DB rejected the write, so restore the last confirmed boundary rather
    // than leaving the user with a visually deleted but unsaved polygon.
    draftGeoJsonRef.current = geoBoundaryJson as any;
    hydrateSavedGeoJson(geoBoundaryJson as any);
    setTimeout(() => loadSavedPolygon(), 0);
  }, [clearBoundary, geoBoundaryJson, hydrateSavedGeoJson, loadSavedPolygon, managedPolygonGeoJson, renderCenterLabels, saveBoundary, setDraftGeoJson]);

  const handleSelectManagedPolygon = useCallback((index: number) => {
    const layer = polygonLayersRef.current[index];
    const map = leafletMapRef.current;
    if (!layer || !map) return;
    setActivePolygonIndex(index);
    try {
      const bounds = layer.getBounds?.();
      if (bounds?.isValid?.()) map.fitBounds(bounds, { padding: [48, 48], maxZoom: 18, animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches });
    } catch {}
  }, []);

  const handleStageManagedPolygonDelete = useCallback((index: number) => {
    const current = managedPolygonGeoJson as any;
    const features = current?.features;
    if (!Array.isArray(features) || !features[index]) return;

    // The final polygon is intentionally routed through the existing whole-boundary
    // confirmation. `saveBoundary` requires at least one polygon, and this keeps
    // the irreversible server clear explicit rather than silently persisting it.
    if (features.length <= 1) {
      setPendingManagedPolygonDeleteIndex(null);
      setShowMobilePolySheet(false);
      setShowMobileDeleteConfirm(true);
      return;
    }

    const nextDraft = removeManagedPolygon(current, index);
    draftGeoJsonRef.current = nextDraft;
    setDraftGeoJson(nextDraft as any);

    const map = leafletMapRef.current;
    const deletedLayer = polygonLayersRef.current[index];
    try { deletedLayer?.pm?.disable?.(); deletedLayer?.off?.(); map?.removeLayer?.(deletedLayer); } catch {}
    polygonLayersRef.current.splice(index, 1);
    polygonLayerRef.current = polygonLayersRef.current[polygonLayersRef.current.length - 1] || null;
    setActivePolygonIndex(Math.max(0, Math.min(index, polygonLayersRef.current.length - 1)));
    setHiddenPolygons(previous => {
      const next = new Set<number>();
      previous.forEach(hiddenIndex => {
        if (hiddenIndex < index) next.add(hiddenIndex);
        else if (hiddenIndex > index) next.add(hiddenIndex - 1);
      });
      return next;
    });
    segmentLabelMgrRef.current?.clearLabels();
    if (showSegmentLabelsRef.current) {
      polygonLayersRef.current.forEach((layer: any) => segmentLabelMgrRef.current?.appendForLayer(layer));
    }
    renderCenterLabels();
    setPendingManagedPolygonDeleteIndex(null);
  }, [managedPolygonGeoJson, removeManagedPolygon, renderCenterLabels, setDraftGeoJson]);

  const handleResetView = useCallback(() => {
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    if (polygonLayerRef.current) {
      try { map.fitBounds(polygonLayerRef.current.getBounds(), { padding: [40, 40] }); } catch {}
    } else if (gpsLatitude && gpsLongitude) {
      map.setView([Number(gpsLatitude), Number(gpsLongitude)], COLLATERAL_MAP.DEFAULT_ZOOM);
    }
  }, [gpsLatitude, gpsLongitude]);

  const handleZoomToGPS = useCallback(() => {
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    // G4: Prefer live device GPS, fall back to stored GPS coords
    if (liveGps) {
      map.setView([liveGps.lat, liveGps.lng], 18, { animate: true });
    } else if (gpsLatitude && gpsLongitude) {
      map.setView([Number(gpsLatitude), Number(gpsLongitude)], 18, { animate: true });
    } else {
      // Request one-shot GPS if no stored coords
      navigator.geolocation?.getCurrentPosition(
        (pos) => {
          map.setView([pos.coords.latitude, pos.coords.longitude], 18, { animate: true });
        },
        (err) => console.warn("[GPS] getCurrentPosition error:", err.message),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  }, [liveGps, gpsLatitude, gpsLongitude]);

  // ─── Custom drawing toolbar handlers ───────────────────────────────────────

  // GAP-5: Reset to saved boundary
  // Sprint v115 Fix #4: extracted reset logic into doResetToSaved helper (called from branded dialog)
  const doResetToSaved = useCallback(() => {
    if (!leafletMapRef.current || !L) return;
    const map = leafletMapRef.current;
    // Disable all Geoman modes first (prevent state corruption)
    try {
      map.pm.disableGlobalEditMode();
      map.pm.disableDraw();
      map.pm.disableGlobalDragMode();
      map.pm.disableGlobalRemovalMode();
    } catch {}
    setDrawMode(null);
    // F6 v33: remove ALL polygon layers
    polygonLayersRef.current.forEach((layer: any) => {
      try { layer.pm?.disable(); layer.off(); map.removeLayer(layer); } catch {}
    });
    polygonLayersRef.current = [];
    polygonLayerRef.current = null;
    if (segmentLabelMgrRef.current) segmentLabelMgrRef.current.clearLabels();
    setLiveArea(0);
    setLivePerimeter(0);
    setSavedPerimeter(0);
    setActivePolygonIndex(0);
    // Sprint v132 Bug4: reset hiddenPolygons on reset-to-saved (layers will be rebuilt by loadSavedPolygon)
    setHiddenPolygons(new Set());
    if (geoBoundaryJson) {
      setTimeout(() => loadSavedPolygon(), 50);
    }
  }, [geoBoundaryJson, loadSavedPolygon]);

  const handleResetToSaved = useCallback(() => {
    if (!leafletMapRef.current || !L) return;
    // Sprint v115 Fix #4: use branded dialog instead of confirm()
    setConfirmState({
      title: 'Reset ກັບ Boundary ທີ່ Save ແລ້ວ',
      message: 'ຕ້ອງການ reset ກັບ boundary ທີ່ save ແລ້ວ ຫລືອບໍ້? ການປ່ຽນແປງທີ່ຍັງບໍ່ໄດ້ບັນທຶກຈະຖືກຍົກເລີກ.',
      confirmLabel: 'Reset',
      cancelLabel: 'ຍົກເລີກ',
      icon: 'reset',
      onConfirm: () => doResetToSaved(),
    });
  }, [doResetToSaved, setConfirmState]);

  // GAP-7: Go to coordinates + temp marker
  const LAO_BOUNDS = { latMin: 13.9, latMax: 22.5, lonMin: 100.1, lonMax: 107.6 };
  const handleGoToCoord = useCallback(() => {
    const lat = parseFloat(coordLat.trim());
    const lng = parseFloat(coordLng.trim());
    if (isNaN(lat) || isNaN(lng)) {
      setCoordError("ພິກັດບໍ່ຖືກຕ້ອງ — ໃສ່ຕົວເລກ");
      return;
    }
    if (lat < LAO_BOUNDS.latMin || lat > LAO_BOUNDS.latMax) {
      setCoordError(`Latitude ${lat.toFixed(4)}° ຢູ່ນອກ ສປປ ລາວ (13.9–22.5)`);
      return;
    }
    if (lng < LAO_BOUNDS.lonMin || lng > LAO_BOUNDS.lonMax) {
      setCoordError(`Longitude ${lng.toFixed(4)}° ຢູ່ນອກ ສປປ ລາວ (100.1–107.6)`);
      return;
    }
    if (!leafletMapRef.current || !L) return;
    const map = leafletMapRef.current;
    setCoordError("");
    // Pan + zoom to coordinates
    map.setView([lat, lng], 18, { animate: true });
    // Remove previous temp marker
    if (tempMarkerRef.current) {
      try { map.removeLayer(tempMarkerRef.current); } catch {}
      tempMarkerRef.current = null;
    }
    // Add pulsing temp marker
    const tempIcon = L.divIcon({
      html: `<div style="position:relative;width:20px;height:20px">
        <div style="position:absolute;inset:0;border-radius:50%;background:#f59e0b;opacity:0.4;animation:pulse-ring 1.5s ease-out infinite"></div>
        <div style="position:absolute;top:5px;left:5px;width:10px;height:10px;background:#f59e0b;border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.5)"></div>
      </div>`,
      iconSize: [20, 20], iconAnchor: [10, 10], className: "crs-map-coordinate-pin",
    });
    const marker = L.marker([lat, lng], { icon: tempIcon }).addTo(map);
    marker.bindTooltip(`📍 ${lat.toFixed(6)}, ${lng.toFixed(6)}`, { permanent: false, direction: "top" });
    tempMarkerRef.current = marker;
    // Remove marker when draw starts
    map.once("pm:drawstart", () => {
      if (tempMarkerRef.current) {
        try { map.removeLayer(tempMarkerRef.current); } catch {}
        tempMarkerRef.current = null;
      }
    });
    // Auto-remove after 30s
    setTimeout(() => {
      if (tempMarkerRef.current) {
        try { map.removeLayer(tempMarkerRef.current); } catch {}
        tempMarkerRef.current = null;
      }
    }, 30000);
    setShowCoordPanel(false);
  }, [coordLat, coordLng]);

  const renderSmartMeasureSession = useCallback((points: [number, number][]) => {
    const group = measureLayerRef.current;
    if (!group || !L) return;
    if (measureDragFrameRef.current !== null) {
      cancelAnimationFrame(measureDragFrameRef.current);
      measureDragFrameRef.current = null;
    }
    measurePolylineRef.current = null;
    measureSegmentLabelRefs.current = [];
    group.clearLayers();
    setMeasurePointCount(points.length);

    points.forEach((point, index) => {
      const isLast = index === points.length - 1;
      const icon = L.divIcon({
        className: 'crs-smart-measure-point',
        html: `<div style="width:26px;height:26px;border-radius:999px;background:${isLast ? '#f97316' : '#002975'};border:3px solid #fff;color:#fff;display:flex;align-items:center;justify-content:center;font:700 11px system-ui;box-shadow:0 2px 8px rgba(0,0,0,.35)">${index + 1}</div>`,
        iconSize: [26, 26], iconAnchor: [13, 13],
      });
      // V391: these are temporary measurement handles, not collateral geometry.
      // Dragging or keyboard nudging must update only the isolated measure ref and
      // must never enter the GeoJSON/dirty/save lifecycle.
      const marker = L.marker(point, {
        icon,
        interactive: measureStageRef.current === 'measuring',
        draggable: measureStageRef.current === 'measuring',
        keyboard: true,
        title: `ຈຸດວັດ ${index + 1}`,
        alt: `ຈຸດວັດ ${index + 1}`,
      }).addTo(group);
      let restoreMapDragging = false;
      marker.on('dragstart', () => {
        measureActiveDragIndexRef.current = index;
        const map = leafletMapRef.current;
        restoreMapDragging = Boolean(map?.dragging?.enabled?.());
        if (restoreMapDragging) map.dragging.disable();
      });
      marker.on('dragend', (event: any) => {
        const map = leafletMapRef.current;
        if (restoreMapDragging && map?.dragging) map.dragging.enable();
        const { lat, lng } = event.target.getLatLng();
        const nextPoints = measurePointsRef.current.map((candidate, candidateIndex) =>
          candidateIndex === index ? [lat, lng] as [number, number] : candidate,
        );
        measurePointsRef.current = nextPoints;
        measureActiveDragIndexRef.current = null;
        renderSmartMeasureSessionRef.current(nextPoints);
      });
      marker.on('drag', (event: any) => {
        const { lat, lng } = event.target.getLatLng();
        const nextPoints = measurePointsRef.current.map((candidate, candidateIndex) =>
          candidateIndex === index ? [lat, lng] as [number, number] : candidate,
        );
        measurePointsRef.current = nextPoints;
        if (measureDragFrameRef.current !== null) return;
        measureDragFrameRef.current = requestAnimationFrame(() => {
          measureDragFrameRef.current = null;
          const framePoints = measurePointsRef.current;
          measurePolylineRef.current?.setLatLngs?.(framePoints);
          let total = 0;
          const segmentCount = Math.max(0, framePoints.length - 1);
          measureSegmentLabelRefs.current.forEach((segmentLabel, fallbackSegmentIndex) => {
            const segmentIndex = Number((segmentLabel as any).__smartMeasureSegmentIndex ?? fallbackSegmentIndex);
            const previous = framePoints[segmentIndex];
            const point = framePoints[segmentIndex + 1];
            if (!previous || !point) return;
            const segment = L.latLng(previous[0], previous[1]).distanceTo(L.latLng(point[0], point[1]));
            total += segment;
            segmentLabel.setLatLng(L.latLng((previous[0] + point[0]) / 2, (previous[1] + point[1]) / 2));
            segmentLabel.setIcon(createSmartMeasureSegmentIcon(segment, segmentIndex, shouldShowSmartMeasureSegmentLabel(segmentIndex, segmentCount, measureActiveDragIndexRef.current)));
          });
          setMeasureDistance(total || null);
        });
      });
      const markerElement = marker.getElement();
      if (markerElement) {
        markerElement.setAttribute('role', 'button');
        markerElement.setAttribute('aria-label', `ຈຸດວັດ ${index + 1}; ລາກເພື່ອປັບ ຫຼື ໃຊ້ປຸ່ມລູກສອນ`);
        markerElement.addEventListener('keydown', (event: KeyboardEvent) => {
          const step = event.shiftKey ? 0.0005 : 0.00005;
          const offsets: Record<string, [number, number]> = {
            ArrowUp: [step, 0], ArrowDown: [-step, 0], ArrowLeft: [0, -step], ArrowRight: [0, step],
          };
          const offset = offsets[event.key];
          if (!offset) return;
          event.preventDefault();
          event.stopPropagation();
          const nextPoints = measurePointsRef.current.map((candidate, candidateIndex) =>
            candidateIndex === index ? [candidate[0] + offset[0], candidate[1] + offset[1]] as [number, number] : candidate,
          );
          measurePointsRef.current = nextPoints;
          renderSmartMeasureSessionRef.current(nextPoints);
        });
      }
    });

    if (points.length < 2) {
      setMeasureDistance(null);
      return;
    }

    measurePolylineRef.current = L.polyline(points, { className: 'crs-smart-measure-route', color: '#f59e0b', weight: 4, dashArray: '7 7', opacity: 0.95, interactive: false, renderer: L.svg() }).addTo(group);
    requestAnimationFrame(() => {
      measurePolylineRef.current?.getElement?.()?.setAttribute('data-smart-measure-route', 'true');
    });
    const total = points.slice(1).reduce((sum, point, index) => {
      const previous = points[index];
      const segment = L.latLng(previous[0], previous[1]).distanceTo(L.latLng(point[0], point[1]));
      const midpoint = L.latLng((previous[0] + point[0]) / 2, (previous[1] + point[1]) / 2);
      const segmentCount = Math.max(0, points.length - 1);
      const segmentLabel = L.marker(midpoint, {
        interactive: false,
        icon: createSmartMeasureSegmentIcon(segment, index, shouldShowSmartMeasureSegmentLabel(index, segmentCount, measureActiveDragIndexRef.current)),
      }).addTo(group);
      (segmentLabel as any).__smartMeasureSegmentIndex = index;
      measureSegmentLabelRefs.current.push(segmentLabel);
      return sum + segment;
    }, 0);
    setMeasureDistance(total);
  }, []);
  const renderSmartMeasureSessionRef = useRef<(points: [number, number][]) => void>(() => {});
  useEffect(() => { renderSmartMeasureSessionRef.current = renderSmartMeasureSession; }, [renderSmartMeasureSession]);
  useEffect(() => {
    const finalizeInterruptedMeasureDrag = () => {
      if (measureActiveDragIndexRef.current === null) return;
      if (measureDragFrameRef.current !== null) {
        cancelAnimationFrame(measureDragFrameRef.current);
        measureDragFrameRef.current = null;
      }
      measureActiveDragIndexRef.current = null;
      const finalSnapshot = [...measurePointsRef.current] as [number, number][];
      if (finalSnapshot.length && measureLayerRef.current) renderSmartMeasureSessionRef.current(finalSnapshot);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) finalizeInterruptedMeasureDrag();
    };
    window.addEventListener('blur', finalizeInterruptedMeasureDrag);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('blur', finalizeInterruptedMeasureDrag);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const resetSmartMeasureSession = useCallback(() => {
    const map = leafletMapRef.current;
    if (map) {
      (map as any)._measureModeActive = false;
      map.getContainer().style.cursor = '';
    }
    measurePointsRef.current = [];
    if (measureDragFrameRef.current !== null) {
      cancelAnimationFrame(measureDragFrameRef.current);
      measureDragFrameRef.current = null;
    }
    measurePolylineRef.current = null;
    measureSegmentLabelRefs.current = [];
    measureActiveDragIndexRef.current = null;
    measureLayerRef.current?.clearLayers?.();
    measureStageRef.current = 'idle';
    setMeasureMode(false);
    setMeasureStage('idle');
    setMeasureDistance(null);
    setMeasurePointCount(0);
    setShowMeasureManualEntry(false);
    setMeasureManualLat('');
    setMeasureManualLng('');
    setMeasureManualError('');
  }, []);

  // Clear removes only the temporary route. Unlike Finish, it deliberately
  // retains the measuring workspace, crosshair, and map-tap ownership so the
  // field user can begin a new route immediately.
  const handleClearMeasurePoints = useCallback(() => {
    measurePointsRef.current = [];
    if (measureDragFrameRef.current !== null) {
      cancelAnimationFrame(measureDragFrameRef.current);
      measureDragFrameRef.current = null;
    }
    measurePolylineRef.current = null;
    measureSegmentLabelRefs.current = [];
    measureActiveDragIndexRef.current = null;
    measureLayerRef.current?.clearLayers?.();
    setMeasureDistance(null);
    setMeasurePointCount(0);
    setMeasureManualLat('');
    setMeasureManualLng('');
    setMeasureManualError('');
  }, []);

  // V407: Close is non-destructive when points exist. It releases map-tap
  // ownership but deliberately retains the temporary route for review/reopen.
  const handleMinimizeMeasure = useCallback(() => {
    const map = leafletMapRef.current;
    if (!measurePointsRef.current.length) {
      resetSmartMeasureSession();
      setActiveSurface('none');
      return;
    }
    if (map) {
      (map as any)._measureModeActive = false;
      map.getContainer().style.cursor = '';
    }
    if (measureDragFrameRef.current !== null) {
      cancelAnimationFrame(measureDragFrameRef.current);
      measureDragFrameRef.current = null;
    }
    measureActiveDragIndexRef.current = null;
    measureStageRef.current = 'review';
    renderSmartMeasureSessionRef.current([...measurePointsRef.current]);
    setMeasureMode(false);
    setMeasureStage('review');
    setActiveSurface('none');
  }, [resetSmartMeasureSession]);

  const handleResumeMeasure = useCallback(() => {
    const map = leafletMapRef.current;
    if (!map) return;
    map.pm.disableDraw();
    map.pm.disableGlobalEditMode();
    map.pm.disableGlobalDragMode();
    map.pm.disableGlobalRemovalMode();
    setDrawMode(null);
    setIsDrawing(false);
    (map as any)._measureModeActive = true;
    map.getContainer().style.cursor = 'crosshair';
    measureStageRef.current = 'measuring';
    renderSmartMeasureSessionRef.current([...measurePointsRef.current]);
    setMeasureMode(true);
    setMeasureStage('measuring');
    setActiveSurface('measure');
  }, []);

  const handleUndoMeasurePoint = useCallback(() => {
    if (!measurePointsRef.current.length) return;
    const nextPoints = measurePointsRef.current.slice(0, -1);
    measurePointsRef.current = nextPoints;
    renderSmartMeasureSession(nextPoints);
  }, [renderSmartMeasureSession]);

  const handleAddMeasureManualPoint = useCallback(() => {
    const lat = Number(measureManualLat);
    const lng = Number(measureManualLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < 13.9 || lat > 22.5 || lng < 100.1 || lng > 107.7) {
      setMeasureManualError('ໃສ່ພິກັດໃນປະເທດລາວໃຫ້ຖືກຕ້ອງ');
      return;
    }
    if (measurePointsRef.current.length >= MAX_SMART_MEASURE_POINTS) {
      setMeasureManualError(`ວັດແທກໄດ້ສູງສຸດ ${MAX_SMART_MEASURE_POINTS} ຈຸດ`);
      return;
    }
    const previous = measurePointsRef.current.at(-1);
    if (previous && L.latLng(previous[0], previous[1]).distanceTo(L.latLng(lat, lng)) < MIN_SMART_MEASURE_POINT_METERS) {
      setMeasureManualError('ຈຸດໃໝ່ໃກ້ຈຸດກ່ອນເກີນໄປ');
      return;
    }
    const nextPoints = [...measurePointsRef.current, [lat, lng] as [number, number]];
    measurePointsRef.current = nextPoints;
    renderSmartMeasureSession(nextPoints);
    setMeasureManualLat('');
    setMeasureManualLng('');
    setMeasureManualError('');
  }, [measureManualLat, measureManualLng, renderSmartMeasureSession]);

  const handleStartMeasureFromCollateral = useCallback((rawIndex: string | null) => {
    if (!measureMode) return;
    if (measurePointsRef.current.length > 0) {
      sonnerToast.message('ເລີ່ມຈາກຫຼັກຊັບໄດ້ກ່ອນເພີ່ມຈຸດວັດເທົ່ານັ້ນ');
      return;
    }
    const polygonIndex = Number(rawIndex ?? activePolygonIndexRef.current);
    const origin = getManagedPolygonOrigin((managedPolygonGeoJson as any)?.features?.[polygonIndex]);
    if (!origin) {
      sonnerToast.error('ບໍ່ສາມາດຫາຈຸດເລີ່ມຈາກຫຼັກຊັບໄດ້');
      return;
    }
    const nextPoints: [number, number][] = [origin];
    measurePointsRef.current = nextPoints;
    renderSmartMeasureSession(nextPoints);
    sonnerToast.success('ເລີ່ມວັດຈາກຫຼັກຊັບແລ້ວ');
  }, [managedPolygonGeoJson, measureMode, renderSmartMeasureSession]);

  // V407: leaving active measuring never silently destroys a non-empty route.
  // The route becomes pointer-safe review; full map close and collateral changes
  // remain the session boundaries that clear it.
  useEffect(() => {
    if (activeSurface !== 'measure' && measureMode && measureStage === 'measuring') handleMinimizeMeasure();
  }, [activeSurface, measureMode, measureStage, handleMinimizeMeasure]);

  // V390: Toggle Smart Field Measure. It owns map taps only while active.
  const handleToggleMeasure = useCallback(() => {
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    if (measureMode || activeSurface === 'measure') {
      handleMinimizeMeasure();
    } else if (measureStage === 'review') {
      handleResumeMeasure();
    } else {
      map.pm.disableDraw();
      map.pm.disableGlobalEditMode();
      map.pm.disableGlobalDragMode();
      map.pm.disableGlobalRemovalMode();
      setDrawMode(null);
      setIsDrawing(false);
      resetSmartMeasureSession();
      (map as any)._measureModeActive = true;
      map.getContainer().style.cursor = 'crosshair';
      measureStageRef.current = 'measuring';
      setMeasureMode(true);
      setMeasureStage('measuring');
      setActiveSurface('measure');
    }
  }, [measureMode, activeSurface, measureStage, handleMinimizeMeasure, handleResumeMeasure, resetSmartMeasureSession]);

  // W1 v49 FIX: Moved enablePerLayerRotate + disablePerLayerRotate BEFORE all callbacks that
  // reference them. JavaScript `const` does NOT hoist — referencing a const before its declaration
  // causes ReferenceError: Cannot access 'X' before initialization (TDZ crash).
  // U1 v47: Helper — enable per-layer rotate on all polygon layers (avoids Geoman global rotate bug)
  // Geoman 2.20.0 bug: handleLayerAdditionInGlobalRotateMode uses _isRelevantForRemoval (not
  // _isRelevantForRotate), so it calls enableRotate() on L.Marker (segment labels) which has no
  // getLatLngs() → crash. Fix: use per-layer layer.pm.enableRotate() only on actual polygon layers.
  const enablePerLayerRotate = useCallback(() => {
    polygonLayersRef.current.forEach((l: any) => {
      try {
        // Get the actual polygon sub-layer (handles both L.Polygon and L.geoJSON wrapper)
        const actualLayer = (typeof l.getLatLngs === "function") ? l
          : (typeof l.getLayers === "function" ? l.getLayers()[0] : null);
        if (actualLayer && actualLayer.pm && typeof actualLayer.pm.enableRotate === "function") {
          actualLayer.pm.enableRotate();
        }
      } catch {}
    });
  }, []);

  const disablePerLayerRotate = useCallback(() => {
    polygonLayersRef.current.forEach((l: any) => {
      try {
        const actualLayer = (typeof l.getLatLngs === "function") ? l
          : (typeof l.getLayers === "function" ? l.getLayers()[0] : null);
        if (actualLayer && actualLayer.pm && typeof actualLayer.pm.disableRotate === "function") {
          actualLayer.pm.disableRotate();
        }
      } catch {}
    });
    // Also try global disable as fallback cleanup
    if (leafletMapRef.current) {
      try { leafletMapRef.current.pm.disableGlobalRotateMode(); } catch {}
    }
  }, []);

  // Sprint v110.2 D1 FIX: handleEscapeMapMode placed HERE (after disablePerLayerRotate at line 2703).
  // v110.1 had it 22 lines BEFORE disablePerLayerRotate — `const` TDZ crash on every render.
  // W1 v49 comment above is the exact same class of bug; this placement follows that rule.
  const handleEscapeMapMode = useCallback(() => {
    const map = leafletMapRef.current;
    if (!map) return;
    // Priority 1: draw mode (isDrawingRef is the authoritative flag)
    if (isDrawingRef.current) {
      try { map.pm.disableDraw(); } catch {}
      return;
    }
    // Priority 2: measure mode
    if ((map as any)._measureModeActive) { handleToggleMeasure(); return; }
    // Priority 3: rotate mode
    if (isRotating) { disablePerLayerRotate(); setIsRotating(false); setMapMode('view'); return; }
    // Priority 4: edit/drag/delete via drawModeRef (stable ref, no stale closure)
    switch (drawModeRef.current) {
      case 'edit':
        try { map.pm.disableGlobalEditMode(); } catch {}
        if (isMobile) map.getContainer().style.touchAction = '';
        setDrawMode(null); setMapMode('view');
        break;
      case 'drag':
        try { map.pm.disableGlobalDragMode(); } catch {}
        setDrawMode(null); setMapMode('view');
        break;
      case 'delete':
        try { map.pm.disableGlobalRemovalMode(); } catch {}
        setDrawMode(null); setMapMode('view');
        break;
    }
  }, [isRotating, disablePerLayerRotate, handleToggleMeasure]);

  // Sprint v73 STEP C: attachMobileEditHandler — tap polygon on mobile to enter edit mode
  // Called from: (1) load path per subLayer, (2) pm:create path per new layer
  // NOTE: declared as useCallback here but ALSO stored in a ref so the map useEffect can call it
  const attachMobileEditHandler = useCallback((layer: any) => {
    if (!isMobile) return; // desktop: no tap-to-edit (uses toolbar)
    layer.on('click', () => {
      if (!canEditRef.current) return;
      if (mapModeRef.current !== 'view') return; // only enter edit from view mode
      if (!leafletMapRef.current) return;
      try {
        // Enable edit on this specific layer
        if (layer.pm) layer.pm.enable({ allowSelfIntersection: false });
        // Also enable global edit mode so Geoman tracks it
        leafletMapRef.current.pm.enableGlobalEditMode(_editModeOpts());
        leafletMapRef.current.getContainer().style.touchAction = 'none';
        setDrawMode('edit');
        setMapMode('edit');
      } catch {}
    });
  }, [isMobile]);
  // Sync attachMobileEditHandlerRef with latest callback
  useEffect(() => { attachMobileEditHandlerRef.current = attachMobileEditHandler; }, [attachMobileEditHandler]);

  // Sprint v73 STEP H: attachLiveMeasure — rAF-throttled live measurement during vertex drag
  // R10: DOM ref during drag (zero React re-renders), setState on release (1 re-render total)
  // R8: updateForLayerDrag mutates textContent + setLatLng (no DOM destroy during drag)
  // R9: use e.indexPath[last] to get dragged vertex index
  const attachLiveMeasure = useCallback((layer: any) => {
    let raf: number | null = null;

    layer.on('pm:markerdrag', (e: any) => {
      const idx = Array.isArray(e.indexPath) ? e.indexPath[e.indexPath.length - 1] : 0;
      // [BUG-CENTER-LIVE-V2 FIX v252s] capture e.layer before rAF — use current dragged layer
      // not closure 'layer' which may be stale after refetch or pm re-enable
      const _dragLayer = e.layer ?? layer;
      if (raf) return; // already scheduled — skip this frame
      raf = requestAnimationFrame(() => {
        raf = null;
        // Update segment labels (2-label DOM mutation, no full re-render)
        if (showSegmentLabelsRef.current) {
          segmentLabelMgrRef.current?.updateForLayerDrag(_dragLayer, idx);
        }
        // Update live measurement text via DOM ref (zero React re-renders during drag)
        try {
          const latLngs = extractOuterRing(_dragLayer);
          if (latLngs.length >= 3) {
            const { areaM2, perimeter } = computeAreaPerimeter(latLngs);
            const areaText = formatAreaByUnit(areaM2, areaUnitRef.current);
            const perimText = formatPerimeter(perimeter);
            // Mobile edit strip
            if (liveMeasureRef.current) {
              liveMeasureRef.current.textContent = `${areaText} · ${perimText}`;
            }
            // Sprint v96: Desktop info bar — update area + perimeter DOM refs directly
            if (desktopAreaRef.current) {
              desktopAreaRef.current.textContent = areaText;
            }
            if (desktopPerimRef.current) {
              desktopPerimRef.current.textContent = perimText;
            }
          }
        } catch {}
        // [BUG-CENTER-NOT-LIVE FIX v252m / BUG-CENTER-LIVE-V2 FIX v252s / BUG-CENTER-LIVE-V3 FIX v252t]
        // live center label position update during vertex drag
        // V3 FIX: Use DOM manipulation (same pattern as segment labels) instead of setLatLng alone
        // Root cause: iconAnchor:[0,0] + CSS transform:translate(-50%,-50%) in divIcon conflicts
        // with Leaflet's CSS transform update from setLatLng in some browsers
        // Fix: call setLatLng (updates internal LatLng) + directly set DOM element transform
        try {
          const _lls = extractOuterRing(_dragLayer);
          if (_lls.length >= 3) {
            const _centroid = calcPolygonCentroid(_lls);
            const _layerIdx = polygonLayersRef.current.indexOf(_dragLayer);
            // [BUG-J1 FIX v252o] Skip if layer not found in ref — don't move wrong center label (index 0)
            if (_layerIdx < 0) return;
            const _cm = centerLabelMarkersRef.current[_layerIdx];
            if (_cm) {
              const _newLatLng = L.latLng(_centroid.lat, _centroid.lng);
              // Step 1: update Leaflet internal LatLng
              _cm.setLatLng(_newLatLng);
              // Step 2: force DOM position update via map pixel coordinates
              // This bypasses any CSS transform conflict from iconAnchor:[0,0] + divIcon CSS
              const _map = leafletMapRef.current;
              if (_map) {
                try {
                  const _pt = _map.latLngToLayerPoint(_newLatLng);
                  const _el = _cm.getElement?.();
                  if (_el) {
                    L.DomUtil.setTransform(_el, _pt, 1);
                  }
                } catch {}
              }
            }
          }
        } catch {}
      });
    });

    layer.on('pm:markerdragend', () => {
      if (raf) { cancelAnimationFrame(raf); raf = null; }
      // NO updateForLayer here — map-level pm:markerdragend handles full rebuild via setTimeout(0)
      // Commit React state ONLY on release — 1 re-render total
      try {
        const latLngs = extractOuterRing(layer);
        if (latLngs.length >= 3) {
          const { areaM2, perimeter } = computeAreaPerimeter(latLngs);
          setLiveArea(areaM2);
          setLivePerimeter(perimeter);
        }
      } catch {}
    });
  }, []);
  // Sync attachLiveMeasureRef with latest callback
  useEffect(() => { attachLiveMeasureRef.current = attachLiveMeasure; }, [attachLiveMeasure]);

  const handleDrawPolygon = useCallback(() => {
    if (!canEditRef.current) return;
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    // Polygon drawing owns pointer input; retain an existing route as review.
    if (measureMode) {
      handleMinimizeMeasure();
    }
    // G11: Exit rotate mode if active (U1 v47: use per-layer disable)
    if (isRotating) { disablePerLayerRotate(); setIsRotating(false); }
    if (drawMode === "polygon") {
      map.pm.disableDraw();
      setDrawMode(null);
      setMapMode('view');
    } else {
      // Disable other modes first
      map.pm.disableGlobalEditMode();
      map.pm.disableGlobalDragMode();
      map.pm.disableGlobalRemovalMode();
      // Sprint v108 Fix 2: Use mobile-aware snapDistance (40px mobile / 15px desktop)
      // Root cause: setGlobalOptions sets mobile snapDistance=40 but enableDraw() was
      // hardcoding 15, overriding the global setting. Close-shape snap was too tight on mobile.
      const _snapDist = window.matchMedia('(max-width:767px)').matches ? 40 : 15;
      // Sprint v114 Fix M: wrap Geoman API in try/catch to prevent stuck draw mode on API error
      try {
        map.pm.enableDraw("Polygon", {
          snappable: true,
          snapDistance: _snapDist,
          allowSelfIntersection: false,
          tooltips: false, // BUG-V3 FIX: disable English tooltips in draw mode
          // BUG-2 FIX: Pass pathOptions directly to prevent red flash on draw start
          pathOptions: { color: "#ffffff", fillColor: "#22c55e", fillOpacity: 0.35, weight: 3 },
          templineStyle: { color: "#22c55e", weight: 3 },
          hintlineStyle: { color: "#22c55e", weight: 2, dashArray: "5,5" },
          // GAP-2: Enter key finishes polygon drawing
          finishOn: "enter",
          // Sprint v118 Bug1 Fix: hide cursor-marker dot (ghost marker on Samsung)
          // The hintline (dashed line to map center) still works via Sprint v79 _syncHintMarker override.
          cursorMarker: false,
        });
        setDrawMode("polygon");
        setMapMode('draw');
      } catch (err) {
        // Sprint v114 Fix M: Geoman API threw — reset state to prevent stuck draw mode
        console.error('[Sprint v114 Fix M] enableDraw(Polygon) failed:', err);
        setDrawMode(null);
        setIsDrawing(false);
        isDrawingRef.current = false;
      }
    }
  }, [drawMode, isRotating, disablePerLayerRotate, measureMode, handleMinimizeMeasure]);
  const handleDrawRectangle = useCallback(() => {
    if (!canEditRef.current) return;
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    // Rectangle drawing owns pointer input; retain an existing route as review.
    if (measureMode) handleMinimizeMeasure();
    // G11: Exit rotate mode if active (U1 v47: use per-layer disable)
    if (isRotating) { disablePerLayerRotate(); setIsRotating(false); }
    if (drawMode === "rectangle") {
      map.pm.disableDraw();
      setDrawMode(null);
      setMapMode('view');
    } else {
      map.pm.disableGlobalEditMode();
      map.pm.disableGlobalDragMode();
      map.pm.disableGlobalRemovalMode();
      // Sprint v108 Fix 2: Use mobile-aware snapDistance (same fix as polygon draw)
      const _snapDistRect = window.matchMedia('(max-width:767px)').matches ? 40 : 15;
      // Sprint v114 Fix M: wrap Geoman API in try/catch to prevent stuck draw mode on API error
      try {
        map.pm.enableDraw("Rectangle", {
          snappable: true,
          snapDistance: _snapDistRect,
          tooltips: false, // BUG-V3 FIX: disable English tooltips in draw mode
          // BUG-2 FIX: Pass pathOptions directly to prevent red flash on draw start
          pathOptions: { color: "#ffffff", fillColor: "#22c55e", fillOpacity: 0.35, weight: 3 },
          templineStyle: { color: "#22c55e", weight: 3 },
          hintlineStyle: { color: "#22c55e", weight: 2, dashArray: "5,5" },
          // GAP-2: Enter key finishes rectangle drawing
          finishOn: "enter",
          // Sprint v118 Bug1 Fix: hide cursor-marker dot (ghost marker on Samsung)
          cursorMarker: false,
        });
        setDrawMode("rectangle");
        setMapMode('draw');
      } catch (err) {
        // Sprint v114 Fix M: Geoman API threw — reset state to prevent stuck draw mode
        console.error('[Sprint v114 Fix M] enableDraw(Rectangle) failed:', err);
        setDrawMode(null);
        setIsDrawing(false);
        isDrawingRef.current = false;
      }
    }
  }, [drawMode, isRotating, disablePerLayerRotate, measureMode, handleMinimizeMeasure]);
  const handleEditMode = useCallback(() => {
    if (!canEditRef.current) return;
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    // G11: Exit rotate mode if active (U1 v47: use per-layer disable)
    if (isRotating) { disablePerLayerRotate(); setIsRotating(false); }
    if (drawMode === "edit") {
      map.pm.disableGlobalEditMode();
      if (isMobile) map.getContainer().style.touchAction = '';
      setDrawMode(null);
      // Sprint v108 Fix 3: sync mapMode state so GPS dot + B7 touchstart guard work correctly
      setMapMode('view');
    } else {
      map.pm.disableDraw();
      map.pm.disableGlobalDragMode();
      map.pm.disableGlobalRemovalMode();
      if ((map as any)._measureModeActive) handleMinimizeMeasure();
      map.pm.enableGlobalEditMode(_editModeOpts());
      if (isMobile) map.getContainer().style.touchAction = 'none';
      setDrawMode("edit");
      // Sprint v108 Fix 3: sync mapMode so Sprint v107 GPS hide + Sprint v93 B7 drag guard fire
      setMapMode('edit');
    }
  }, [drawMode, isRotating, disablePerLayerRotate, setMapMode, handleMinimizeMeasure]);
  const handleDragMode = useCallback(() => {
    if (!canEditRef.current) return;
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    // G11: Exit rotate mode if active (U1 v47: use per-layer disable)
    if (isRotating) { disablePerLayerRotate(); setIsRotating(false); }
    if (drawMode === "drag") {
      map.pm.disableGlobalDragMode();
      setDrawMode(null);
      setMapMode('view');
    } else {
      map.pm.disableDraw();
      map.pm.disableGlobalEditMode();
      map.pm.disableGlobalRemovalMode();
      if ((map as any)._measureModeActive) handleMinimizeMeasure();
      map.pm.enableGlobalDragMode();
      setDrawMode("drag");
      setMapMode('edit');
    }
  }, [drawMode, isRotating, disablePerLayerRotate, handleMinimizeMeasure]);
  const handleDeleteMode = useCallback(() => {
    if (!canEditRef.current) return;
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    // G11: Exit rotate mode if active (U1 v47: use per-layer disable)
    if (isRotating) { disablePerLayerRotate(); setIsRotating(false); }
    if (drawMode === "delete") {
      map.pm.disableGlobalRemovalMode();
      setDrawMode(null);
      setMapMode('view');
    } else {
      map.pm.disableDraw();
      map.pm.disableGlobalEditMode();
      map.pm.disableGlobalDragMode();
      if ((map as any)._measureModeActive) handleMinimizeMeasure();
      map.pm.enableGlobalRemovalMode();
      setDrawMode("delete");
      setMapMode('edit');
    }
  }, [drawMode, isRotating, disablePerLayerRotate, handleMinimizeMeasure]);

  // G11: Toggle polygon rotation mode (mutual exclusion with all other modes)
  const handleToggleRotate = useCallback(() => {
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    if (isRotating) {
      // Exit rotate mode — use per-layer disable
      disablePerLayerRotate();
      setIsRotating(false);
      setMapMode('view');
    } else {
      // Enter rotate mode — disable all other Geoman modes first
      map.pm.disableDraw();
      map.pm.disableGlobalEditMode();
      map.pm.disableGlobalDragMode();
      map.pm.disableGlobalRemovalMode();
      // Rotation owns pointer input; keep a temporary route available in review.
      if ((map as any)._measureModeActive) handleMinimizeMeasure();
      setDrawMode(null);
      setIsDrawing(false);
      // U1 v47: use per-layer enableRotate (NOT enableGlobalRotateMode) to avoid Geoman bug
      enablePerLayerRotate();
      setIsRotating(true);
      setMapMode('edit');
    }
  }, [isRotating, enablePerLayerRotate, disablePerLayerRotate, handleMinimizeMeasure]);

  // V65: ±15° map rotation buttons
  const handleRotateBy = useCallback((deg: number) => {
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    try {
      const current = map.getBearing ? map.getBearing() : 0;
      if (map.setBearing) map.setBearing(current + deg);
    } catch {}
  }, []);

  // V65: Toggle device compass follow mode (mobile)
  const handleToggleDeviceCompass = useCallback(async () => {
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;

    // If already following — disable (Sprint v161: handle dual-listener object)
    if (followDeviceCompass) {
      if (deviceOrientationListenerRef.current) {
        const ref = deviceOrientationListenerRef.current as any;
        if (ref.absolute) {
          window.removeEventListener('deviceorientationabsolute', ref.absolute);
          window.removeEventListener('deviceorientation', ref.relative);
        } else {
          window.removeEventListener('deviceorientationabsolute', ref);
          window.removeEventListener('deviceorientation', ref);
        }
        deviceOrientationListenerRef.current = null;
      }
      setFollowDeviceCompass(false);
      return;
    }

    // iOS 13+ requires permission request from user gesture
    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      try {
        const res = await (DeviceOrientationEvent as any).requestPermission();
        if (res !== 'granted') {
          sonnerToast.error('ບໍ່ໄດ້ຮັບອະນຸຍາດເຂັມທິດ', {
            description: 'ກະລຸນາອະນຸຍາດ Compass ໃນ Settings ແລ້ວລອງໃໝ່',
          });
          return;
        }
      } catch {
        sonnerToast.error('ບໍ່ສາມາດເປີດເຂັມທິດໄດ້', {
          description: 'ກະລຸນາກວດສອບການອະນຸຍາດ Compass ແລ້ວລອງໃໝ່',
        });
        return;
      }
    }

    // Sprint v161 B1+B2 Fix: track which event source is active to prevent dual-fire
    let absoluteEventFired = false;

    const listenerAbsolute = (e: DeviceOrientationEvent) => {
      if (!leafletMapRef.current) return;
      try {
        absoluteEventFired = true;
        // For absolute events: webkitCompassHeading (iOS) or (360 - alpha) for true north
        const heading = (e as any).webkitCompassHeading
          ?? (e.alpha != null ? ((360 - e.alpha) % 360) : null);
        if (heading != null && map.setBearing) {
          map.setBearing(heading);
        }
      } catch {}
    };

    const listenerRelative = (e: DeviceOrientationEvent) => {
      // Sprint v161 B1 Fix: skip if absolute event already fired (prevent dual-fire on Android)
      if (absoluteEventFired) return;
      if (!leafletMapRef.current) return;
      try {
        // For relative events: only webkitCompassHeading is reliable (iOS Safari)
        // Do NOT use alpha here — relative alpha is not compass heading
        const heading = (e as any).webkitCompassHeading ?? null;
        if (heading != null && map.setBearing) {
          map.setBearing(heading);
        }
      } catch {}
    };

    // Store both listeners for cleanup
    (deviceOrientationListenerRef.current as any) = { absolute: listenerAbsolute, relative: listenerRelative };
    window.addEventListener('deviceorientationabsolute', listenerAbsolute as any);
    window.addEventListener('deviceorientation', listenerRelative as any);
    setFollowDeviceCompass(true);
  }, [followDeviceCompass]);

  // Reset drawMode when drawing ends
  const handleDrawModeReset = useCallback(() => {
    setDrawMode(null);
  }, []);

  const handleUndoVertex = useCallback(() => {
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    if (map.pm && map.pm.Draw) {
      // Sprint v114 Fix G: use drawModeRef to correctly pick shape (Rectangle undo was broken
      // because Polygon was checked first — Rectangle._removeLastVertex was never called)
      const _undoShape = drawModeRef.current === 'rectangle' ? 'Rectangle' : 'Polygon';
      const activeShape = (map.pm.Draw as any)[_undoShape] || (map.pm.Draw as any).Polygon || (map.pm.Draw as any).Rectangle;
      if (activeShape && typeof activeShape._removeLastVertex === "function") {
        // v281: a queued pan/zoom settle must never re-anchor labels from the
        // pre-undo drawing snapshot after this geometry mutation.
        try { (map as any).__cancelDrawingLabelReanchor?.(); } catch {}
        activeShape._removeLastVertex();
        drawingVerticesRef.current.pop();
        setCanUndoVertex(drawingVerticesRef.current.length >= 1);
        setDrawVertexCount(drawingVerticesRef.current.length); // Sprint v110 J1: sync drawVertexCount on undo
        const area = calcAreaFromLatLngs(drawingVerticesRef.current);
        const perim = calcPerimeterFromLatLngs(drawingVerticesRef.current);
        setLiveArea(area);
        setLivePerimeter(perim);
        if (segmentLabelMgrRef.current) {
          segmentLabelMgrRef.current.updateForDrawingVertices(drawingVerticesRef.current);
        }
        // Sprint v120: rebuild draw-mode midpoints after undo via component-scope ref
        if (drawMidLayerRef.current && typeof buildDrawMidpointsRef.current === 'function') {
          const snap = [...drawingVerticesRef.current];
          setTimeout(() => { buildDrawMidpointsRef.current(snap); }, 0);
        }
      }
    }
  }, []);

  // G7+G9: Undo — restore previous FeatureCollection state
  const handleUndo = useCallback(() => {
    if (!leafletMapRef.current || undoStackRef.current.length === 0) return;
    const map = leafletMapRef.current;
    // Push current state to redo stack
    const currentSnapshot = draftGeoJsonRef.current;
    if (currentSnapshot) {
      redoStackRef.current.push(JSON.parse(JSON.stringify(currentSnapshot)));
    }
    const prev = undoStackRef.current.pop()!;
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
    // Restore all polygon layers from FeatureCollection snapshot
    // Sprint v93 B5-ext: use module-level L (not window.L which may be null before async import)
    try {
      if (!L) return; // guard: L not yet loaded
      // Sprint v115 Fix #1: uses module-level POLYGON_COLORS (single source of truth)
      // Remove all existing polygon layers
      polygonLayersRef.current.forEach(l => { try { map.removeLayer(l); } catch {} });
      polygonLayersRef.current = [];
      polygonLayerRef.current = null;
      // Restore from FeatureCollection
      const features = (prev as any)?.features || [];
      features.forEach((feat: any, i: number) => {
        // Sprint v131: use saved custom color if present
        const savedColor = feat?.properties?.color as string | null;
        const color = savedColor || POLYGON_COLORS[i % POLYGON_COLORS.length];
        const restored = L.geoJSON(feat, {
          style: { color: "#ffffff", fillColor: color, fillOpacity: 0.35, weight: 3 },
        });
        restored.addTo(map);
        // Sprint v109 Fix 5: re-wire mobile edit handler and live measure after undo restore
        // Without this: tap-to-edit and live drag measurement stop working after undo
        attachMobileEditHandlerRef.current(restored);
        attachLiveMeasureRef.current(restored);
        // Sprint v179 BUG-DRIFT-2: re-wire pm:dragend after undo restore
        restored.on("pm:dragend", () => {
          if (segmentLabelMgrRef.current && showSegmentLabelsRef.current) {
            segmentLabelMgrRef.current.clearLabels();
            polygonLayersRef.current.forEach((l: any) => { segmentLabelMgrRef.current!.appendForLayer(l); });
          }
          renderCenterLabels();
        });
        polygonLayersRef.current.push(restored);
        if (i === features.length - 1) polygonLayerRef.current = restored;
      });
      setDraftGeoJson(prev as any);
      // Sprint v132 Bug2: reset hiddenPolygons after undo — layers are fully rebuilt, old indices invalid
      setHiddenPolygons(new Set());
      // Recalculate total area
      let totalArea = 0;
      polygonLayersRef.current.forEach(l => {
        const flat2 = getSafeLatLngsModule(l);
        totalArea += calcAreaFromLatLngs(flat2);
      });
      if (totalArea > 0) setLiveArea(totalArea);
            // C22 v30: render labels for ALL polygons after undo (not just active)
      // updateForLayer(activeLayer) only shows labels for the active polygon;
      // other polygons lose labels after undo with multi-polygon boundaries.
      if (segmentLabelMgrRef.current) {
        setTimeout(() => {
          if (!segmentLabelMgrRef.current) return;
          // J1 v37: respect segment label toggle
          if (!showSegmentLabelsRef.current) return;
          segmentLabelMgrRef.current.clearLabels();
          polygonLayersRef.current.forEach((l: any) => {
            segmentLabelMgrRef.current!.appendForLayer(l);
          });
        }, 100);
      }
    } catch {}
  }, [setDraftGeoJson, activePolygonIndex]);
  // G7+G9: Redo — restore next FeatureCollection state
  const handleRedo = useCallback(() => {
    if (!leafletMapRef.current || redoStackRef.current.length === 0) return;
    const map = leafletMapRef.current;
    // Push current state to undo stack
    const currentSnapshot = draftGeoJsonRef.current;
    if (currentSnapshot) {
      undoStackRef.current.push(JSON.parse(JSON.stringify(currentSnapshot)));
    }
    const next = redoStackRef.current.pop()!;
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
    // Sprint v93 B5-ext: use module-level L (not window.L which may be null before async import)
    try {
      if (!L) return; // guard: L not yet loaded
      // Sprint v115 Fix #1: uses module-level POLYGON_COLORS (single source of truth)
      // Remove all existing polygon layers
      polygonLayersRef.current.forEach(l => { try { map.removeLayer(l); } catch {} });
      polygonLayersRef.current = [];
      polygonLayerRef.current = null;
      // Restore from FeatureCollection
      const features = (next as any)?.features || [];
      features.forEach((feat: any, i: number) => {
        // Sprint v131: use saved custom color if present
        const savedColor = feat?.properties?.color as string | null;
        const color = savedColor || POLYGON_COLORS[i % POLYGON_COLORS.length];
        const restored = L.geoJSON(feat, {
          style: { color: "#ffffff", fillColor: color, fillOpacity: 0.35, weight: 3 },
        });
        restored.addTo(map);
        // Sprint v109 Fix 5b: re-wire mobile edit handler and live measure after redo restore
        attachMobileEditHandlerRef.current(restored);
        attachLiveMeasureRef.current(restored);
        // Sprint v179 BUG-DRIFT-2: re-wire pm:dragend after redo restore
        restored.on("pm:dragend", () => {
          if (segmentLabelMgrRef.current && showSegmentLabelsRef.current) {
            segmentLabelMgrRef.current.clearLabels();
            polygonLayersRef.current.forEach((l: any) => { segmentLabelMgrRef.current!.appendForLayer(l); });
          }
          renderCenterLabels();
        });
        polygonLayersRef.current.push(restored);
        if (i === features.length - 1) polygonLayerRef.current = restored;
      });
      setDraftGeoJson(next as any);
      // Sprint v132 Bug2b: reset hiddenPolygons after redo — layers are fully rebuilt
      setHiddenPolygons(new Set());
      // Recalculate total area
      let totalArea = 0;
      polygonLayersRef.current.forEach(l => {
        const flat2 = getSafeLatLngsModule(l);
        totalArea += calcAreaFromLatLngs(flat2);
      });
      if (totalArea > 0) setLiveArea(totalArea);
            // C22 v30: render labels for ALL polygons after redo (not just active)
      if (segmentLabelMgrRef.current) {
        setTimeout(() => {
          if (!segmentLabelMgrRef.current) return;
          // J1 v37: respect segment label toggle
          if (!showSegmentLabelsRef.current) return;
          segmentLabelMgrRef.current.clearLabels();
          polygonLayersRef.current.forEach((l: any) => {
            segmentLabelMgrRef.current!.appendForLayer(l);
          });
        }, 100);
      }
    } catch {}
  }, [setDraftGeoJson, activePolygonIndex]);
  // ============================================================
  // SPRINT v84 Fix: Sync drawLiveMeasureRef after React re-render
  // Claude diagnosis: useLayoutEffect was overwriting pan values with committed vertex area.
  // Fix: EARLY RETURN if projectedMeasureRef has a non-default value (pan is active).
  // projectedMeasureRef is set by _updateDrawHeaderLiveMeasure on every Leaflet 'move' event.
  // useLayoutEffect only falls back to state values when no pan has occurred yet.
  // ============================================================
  useLayoutEffect(() => {
    if (!isDrawing || !drawLiveMeasureRef.current) return;
    // CRITICAL: If projectedMeasureRef has a real measure value (not default/empty),
    // ALWAYS use it — this is the freshest pan-projected value.
    // Do NOT overwrite with state values (which are committed vertex area, not projected).
    const defaultText = 'ກຳລັງແຕ້ມ... ແຕະ ✓ ເພື່ອຈົບ';
    if (projectedMeasureRef.current && projectedMeasureRef.current !== defaultText) {
      // Pan has occurred — restore the projected value and return early
      drawLiveMeasureRef.current.textContent = projectedMeasureRef.current;
      return;
    }
    // No pan yet — fall back to state values
    if (liveArea > 0) {
      const text = `${formatAreaByUnit(liveArea, areaUnit)} · ${formatPerimeter(livePerimeter)}`;
      drawLiveMeasureRef.current.textContent = text;
      projectedMeasureRef.current = text; // Sync ref with state
    } else {
      drawLiveMeasureRef.current.textContent = defaultText;
    }
  }, [isDrawing, liveArea, livePerimeter, areaUnit]);

  // Sprint v103: Force map.invalidateSize() whenever mapMode or isDrawing changes
  // Root cause: Mobile peek bar is a fixed portal — it overlays the map without resizing the
  // map container, so ResizeObserver never fires. When peek bar appears (after pm:create sets
  // mapMode='view'), Leaflet's pixel→LatLng cache is stale → markers float off polygon edges.
  // Fix: call invalidateSize() at 0ms + 150ms + 350ms after every mode transition.
  useEffect(() => {
    if (!leafletMapRef.current) return;
    const map = leafletMapRef.current;
    // Immediate + two delayed calls to cover CSS transition duration of peek bar slide-up
    const t1 = setTimeout(() => { try { map.invalidateSize(true); } catch {} }, 0);
    const t2 = setTimeout(() => { try { map.invalidateSize(true); } catch {} }, 150);
    const t3 = setTimeout(() => { try { map.invalidateSize(true); } catch {} }, 350);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [mapMode, isDrawing]);

  // G7: Keyboard shortcuts Ctrl+Z / Ctrl+Y
  // Sprint v110 D1: Escape cancels draw; Sprint v110.1 D1: moved to onEscapeKeyDown (Radix-safe)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Sprint v110.1 D1: Escape is now handled by onEscapeKeyDown on DialogContent.
      // Radix @radix-ui/react-dismissable-layer@1.1.11 captures Escape on document with capture:true,
      // so onEscapeKeyDown fires BEFORE this window bubble listener. No double-fire needed.
      if (e.key === 'Escape') return;
      // Sprint v110.1 D1: INPUT/TEXTAREA guard — do not hijack Ctrl+Z when typing in coord panel
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        if (isDrawing) { handleUndoVertex(); }
        else { handleUndo(); }
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) {
        e.preventDefault();
        if (!isDrawing) handleRedo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isDrawing, handleUndo, handleRedo, handleUndoVertex]);

  // ============================================================
  // Render helpers
  // ============================================================

  const tileLabels: Record<MapTileLayer, string> = {
    streets: "ຖະໜົນ",
    satellite: "ດາວທຽມ",
    hybrid: "ປະສົມ",
    maptiler: "HD ສູງ",
    google: "Google",
  };

  // Live area: show from first vertex (drawing status), then real value from 3+ vertices
  // Saved area: format by selected unit (not always Lao rai)
  const displayArea = isDrawing
    ? liveArea > 0
      ? `✏️ ${formatAreaByUnit(liveArea, areaUnit)}`
      : `✏️ ກຳລັງແຕ້ມ...`
    : areaSqM > 0
    ? formatAreaByUnit(areaSqM, areaUnit)
    : "ຍັງບໍ່ໄດ້ວັດ";

  const displayPerimeter = isDrawing && livePerimeter > 0
    ? formatPerimeter(livePerimeter)
    : savedPerimeter > 0
    ? formatPerimeter(savedPerimeter)
    : null;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
        {/* BUG-E FIX: desktop expands to min(1400px, 96vw); mobile unchanged */}
        <DialogContent
          ref={setMapDialogPortalElement}
          className={`!fixed !inset-0 !max-w-none !w-screen !h-[100dvh] !translate-x-0 !translate-y-0 !top-0 !left-0 !rounded-none !p-0 !border-0 overflow-hidden ${isMobile ? '!bg-transparent relative' : 'flex flex-col gap-0'}`}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            // Sprint v110.1 D1: intercept Escape BEFORE Radix dismisses the dialog.
            // @radix-ui/react-dismissable-layer@1.1.11 captures Escape on document with capture:true
            // — fires before window bubble listener. onEscapeKeyDown is the only reliable hook.
            if (isDrawingRef.current || drawModeRef.current !== null || isRotating || measureMode) {
              e.preventDefault();
              handleEscapeMapMode();
            }
          }}
          showCloseButton={false}
        >
          {/* ─── Header ─────────────────────────────────── */}
          {/* Sprint v140: Minimal translucent header on mobile, solid on desktop */}
          {/* Sprint v174: Smart header — merges edit mode strip INTO header (0 extra px), draw mode stays as overlay pill */}
          {/* Sprint v175: Glassmorphism — mobile header is absolute overlay, desktop stays flex-col */}
          <DialogHeader className={`px-3 ${
            isMobile
              ? `absolute top-0 left-0 right-0 z-[1010] transition-all duration-200 ${
                  mapMode === 'edit'
                    ? 'py-1 bg-blue-600/90 backdrop-blur-md border-b border-blue-500/50 shadow-md'
                    : 'opacity-0 pointer-events-none h-0 overflow-hidden py-0'
                }`
              : 'flex-shrink-0 py-2.5 border-b bg-[#002975] text-white'
          }`}>
            <div ref={mobileMapHeaderRef} className="flex items-center justify-between gap-2">
              {/* Sprint v174: Edit mode — show live measure + Done button IN header (replaces separate 48px strip) */}
              {isMobile && mapMode === 'edit' ? (
                <>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    <span
                      ref={liveMeasureRef}
                      className="text-xs font-semibold text-white truncate flex-1"
                    >
                      {liveArea > 0
                        ? `${formatAreaByUnit(liveArea, areaUnit)} · ${formatPerimeter(livePerimeter)}`
                        : areaSqM > 0
                        ? `${formatAreaByUnit(areaSqM, areaUnit)} · ${savedPerimeter > 0 ? formatPerimeter(savedPerimeter) : '...'}`
                        : 'ກຳລັງແກ້ໄຂ...'}
                    </span>
                  </div>
                  {/* Sprint v110 M3: Edit strip done button — Sprint v174: merged into header */}
                  {/* Sprint v129 Bug2: sync drawMode state (mirrors handleEditMode toggle-off) */}
                  <button
                    onClick={() => {
                      if (!leafletMapRef.current) return;
                      const map = leafletMapRef.current;
                      map.pm.disableGlobalEditMode();
                      // v74: cleanup any lingering .am-dragging
                      document.querySelectorAll('.am-dragging').forEach(el => el.classList.remove('am-dragging'));
                      setMapMode('view');
                      setDrawMode(null);
                    }}
                    className="flex items-center gap-1 px-3 h-7 rounded-lg bg-white text-blue-700 text-xs font-bold active:bg-blue-50 flex-shrink-0"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    ສຳເລັດ
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <MapPin className={`h-3.5 w-3.5 flex-shrink-0 ${isMobile ? 'text-white/80' : 'text-white'}`} />
                    {/* Sprint v163: Collateral Switcher — replaces static title when multiple collaterals */}
                    {allCollaterals && allCollaterals.length > 1 && onSwitchCollateral ? (
                      <AnchoredSelectPopover
                        value={collateralId}
                        options={collateralSwitchOptions}
                        onChange={requestCollateralSwitch}
                        ariaLabel="ເລືອກຫຼັກຊັບເພື່ອເບິ່ງແຜນທີ່"
                        placeholder="ເລືອກຫຼັກຊັບ"
                        searchable
                        searchPlaceholder="ຄົ້ນຫາເລກໃບຕາດິນ, ເອກະສານ ຫຼື ລະຫັດ..."
                        testId="collateral-map-header-switcher"
                        className="min-w-0 max-w-[230px]"
                        triggerClassName={`min-h-8 border-white/30 px-2 py-1 text-xs font-semibold text-white shadow-none ${isMobile ? 'bg-black/20 backdrop-blur-sm' : 'bg-white/15 hover:bg-white/20'}`}
                        menuClassName="z-[10050]"
                        portalContainer={mapDialogPortalElement}
                      />
                    ) : (
                      <DialogTitle className={`font-semibold truncate ${isMobile ? 'text-xs text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]' : 'text-sm text-white'}`}>
                        {isMobile
                          ? `${activeCollateralReference.primaryLabel} · #${collateralId}`
                          : `ແຜນທີ່ຫຼັກຊັບ: ${activeCollateralReference.primaryLabel}`}
                        {!isMobile && collateralType && (
                          <span className="ml-1.5 text-blue-200 text-xs font-normal">
                            — {collateralTypeMap[collateralType] || collateralType} · #{collateralId}
                          </span>
                        )}
                      </DialogTitle>
                    )}
                    {!isMobile && !canEditMapBoundary && (
                      <span className="hidden sm:inline-flex max-w-[170px] truncate rounded-md bg-amber-50/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-100" title={mapTypePolicy.guidance}>
                        {mapTypePolicy.label}
                      </span>
                    )}
                  </div>
                  {!isMobile && canEditMapBoundary && (
                    <button
                      type="button"
                      onClick={() => {
                        if (activeSurface === 'edit-tools') { handleEscapeMapMode(); setActiveSurface('none'); return; }
                        if (mapMode === 'view') handleEditMode();
                        setActiveSurface('edit-tools');
                      }}
                      aria-label={activeSurface === 'edit-tools' ? 'ປິດເຄື່ອງມືແກ້ໄຂ' : 'ເປີດເຄື່ອງມືແກ້ໄຂແຜນທີ່'}
                      className={`inline-flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${activeSurface === 'edit-tools' ? 'bg-white text-[#002975]' : 'bg-white/15 text-white hover:bg-white/25'}`}
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      ແກ້ໄຂແຜນທີ່
                    </button>
                  )}
                  {!isMobile && !canEditMapBoundary && (
                    <span
                      data-testid="desktop-map-drawing-access-status"
                      title={mobileMapEditAvailabilityMessage}
                      className="inline-flex max-w-[250px] items-center rounded-md bg-amber-50/15 px-2 py-1 text-[10px] font-semibold leading-tight text-amber-100"
                    >
                      {desktopMapEditAvailabilityLabel}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-7 w-7 flex-shrink-0 rounded-full ${isMobile ? 'text-white/90 hover:bg-white/20 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]' : 'text-white hover:bg-white/20'}`}
                    onClick={handleClose}
                    aria-label="ປິດແຜນທີ່"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          </DialogHeader>

          {/* ─── Info Bar: Area + Perimeter (reference app style) ─── */}
          {/* Sprint v97: also show during draw mode (isDrawing) so desktopAreaRef/desktopPerimRef are mounted in DOM */}
          {!isMobile && (areaSqM > 0 || liveArea > 0 || isDrawing) && (
            <div className="flex items-center justify-between px-3 py-1.5 bg-black/80 text-white text-xs flex-shrink-0">
              <div className="flex items-center gap-1">
                <span className="text-gray-400">ເນື້ອທີ່:</span>
                {/* Sprint v96/v97: desktopAreaRef for live DOM update during vertex drag/draw (zero re-renders) */}
                <span ref={desktopAreaRef} className="font-bold text-sm">
                  {liveArea > 0
                    ? formatAreaByUnit(liveArea, areaUnit)
                    : isDrawing
                    ? 'ກຳລັງແຕ້ມ...'
                    : formatAreaByUnit(
                        (draftGeoJson as any)?.properties?.totalAreaSqM ?? areaSqM,
                        areaUnit
                      )}
                </span>
                {/* G9: polygon count badge */}
                {((draftGeoJson as any)?.features?.length ?? 0) > 1 && (
                  <span className="ml-1 bg-emerald-500 text-white text-[9px] rounded-full px-1.5 py-0.5 font-bold">
                    {(draftGeoJson as any).features.length} ຜືນ
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Unit selector inline */}
                <div className="flex items-center gap-0.5">
                  {(["sqm", "rai", "hectare"] as AreaUnit[]).map((u) => (
                    <button
                      key={u}
                      onClick={() => setAreaUnit(u)}
                      className={`px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${
                        areaUnit === u
                          ? "bg-white text-black"
                          : "text-gray-400 hover:text-white"
                      }`}
                    >
                      {u === "sqm" ? "m²" : u === "rai" ? "ໄຮ" : "ha"}
                    </button>
                  ))}
                </div>
                {/* Sprint v96: always render perimeter span so desktopPerimRef is always mounted in DOM */}
                {(displayPerimeter || savedPerimeter > 0) && (
                  <span className="text-gray-400">
                    {/* Sprint v96: desktopPerimRef for live DOM update during vertex drag */}
                    ເສັ້ນຮອບ: <span ref={desktopPerimRef} className="text-white font-medium">
                      {displayPerimeter || formatPerimeter(savedPerimeter)}
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ─── Map area ────────────────────────────────── */}
          {/* Sprint v175: Glassmorphism — mobile map fills full 100dvh */}
          <div className={isMobile ? "absolute inset-0" : "flex-1 relative"} style={isMobile ? {} : { minHeight: 0 }}>
            {/* CRITICAL: mapRef div MUST always be mounted (never conditional) so initMap can find it */}
            {/* Sprint v110.1 M2+S1: map bottom shrinks by --peek-bar-h so OSM attribution is visible */}
            <div
              ref={mapRef}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                // Desktop Street View owns a dedicated side pane. Reserving this
                // width at the map canvas level keeps Leaflet controls and map
                // content out of its hit region instead of layering surfaces.
                right: !isMobile && showStreetView ? '396px' : 0,
                bottom: 'var(--peek-bar-h, 0px)',
              }}
              className="z-0"
            />
            {/* Skeleton overlay shown on top while loading — does NOT unmount mapRef */}
            {isLoading && !mapReady && (
              <div style={{ position: "absolute", inset: 0, zIndex: 10 }}>
                <MapLoadingSkeleton height={400} />
              </div>
            )}
            {osmTilesUnavailable && (
              <div
                data-pdf-hide
                role="alert"
                aria-live="polite"
                className="absolute bottom-4 left-4 z-[1005] max-w-[min(420px,calc(100%-32px))] rounded-xl border border-amber-300 bg-amber-50/95 p-3 text-sm text-amber-950 shadow-lg backdrop-blur-sm"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
                  <div>
                    <p className="font-semibold">ໂຫຼດແຜນທີ່ OpenStreetMap ບໍ່ສຳເລັດ</p>
                    <p className="mt-1 text-xs leading-5 text-amber-900">
                      ພິກັດ GPS, ຂອບເຂດ ແລະຂໍ້ມູນຫຼັກຊັບບໍ່ຖືກປ່ຽນແປງ. ສະຫຼັບເປັນດາວທຽມ ຫຼື ເປີດ Google Maps.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => switchTileLayer("satellite")}
                        className="border-amber-400 bg-white text-amber-950 hover:bg-amber-100"
                      >
                        ສະຫຼັບເປັນດາວທຽມ
                      </Button>
                      {mapFallbackGoogleUrl && (
                        <a
                          href={mapFallbackGoogleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex min-h-9 items-center rounded-md border border-amber-400 bg-white px-3 text-xs font-semibold text-blue-800 hover:bg-amber-100"
                        >
                          ເປີດ Google Maps
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sprint v73 STEP E: Draw mode strip (mobile only) */}
            {/* Sprint v155: Draw mode — minimal 2-element layout */}
            {/* TOP: auto-width pill anchored top-left — does not block Geoman tooltips */}
            {isMobile && mapMode === 'draw' && (
              <div
                data-pdf-hide
                className="absolute top-3 left-3 z-[1002] flex items-center gap-2 px-3 bg-gray-900/82 backdrop-blur-md text-white rounded-full shadow-lg"
                style={{ height: '36px', maxWidth: 'calc(100% - 80px)' }}
              >
                <svg className="w-3.5 h-3.5 flex-shrink-0 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                {/* Sprint v82 Fix B: span content managed ONLY via DOM ref (drawLiveMeasureRef.textContent) */}
                <span
                  ref={drawLiveMeasureRef}
                  className="text-sm font-semibold truncate"
                  suppressHydrationWarning
                />
                {/* Sprint v153: Unit switcher badge — tap to cycle sqm → rai → hectare */}
                <button
                  onClick={() => setAreaUnit(u => u === 'sqm' ? 'rai' : u === 'rai' ? 'hectare' : 'sqm')}
                  title="ກດເພີ່ມຫົ່ວຍ"
                  className="flex-shrink-0 flex items-center gap-1 px-2 h-6 rounded-full bg-emerald-500/30 text-emerald-300 text-[11px] font-bold active:bg-emerald-500/50 border border-emerald-500/40"
                >
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                  {areaUnit === 'sqm' ? 'm²' : areaUnit === 'rai' ? 'ໄຮ' : 'ha'}
                </button>
              </div>
            )}
            {/* BOTTOM: 2-button row — Cancel pill (left) + Undo FAB circle (right) — Sprint v155 */}
            {isMobile && mapMode === 'draw' && (
              <div
                data-pdf-hide
                className="absolute left-3 right-3 z-[1002] flex items-center justify-between"
                style={{ bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}
              >
                {/* Sprint v110.1 D1-mobile: cancel button — mobile has no Escape key */}
                {/* Sprint v114 Fix K: minimum vertex guard — v155 removed finish button from mobile; guard preserved in desktop finish handler */}
                {/* _minVerts: drawMode === 'rectangle' ? 2 : 3 — kept in desktop path */}
                <button
                  onClick={handleEscapeMapMode}
                  title="ຍົກເລີກ"
                  className="flex items-center gap-2 px-5 h-11 rounded-full bg-gray-900/80 backdrop-blur-sm text-white text-sm font-medium active:bg-gray-900/95 shadow-xl border border-white/10"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  ຍົກເລີກ
                </button>
                {/* Undo FAB circle — professional, Google Maps style */}
                {/* Sprint v90 Bug1 Fix: undo vertex (replaces ເພີ່ມຈຸດ in v155 minimal layout) */}
                <button
                  onClick={handleUndoVertex}
                  disabled={!canUndoVertex}
                  title="Undo"
                  className="w-12 h-12 rounded-full bg-white shadow-xl flex items-center justify-center text-gray-700 disabled:opacity-30 active:bg-gray-100 border border-gray-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                </button>
              </div>
            )}

            {/* Sprint v141: Edit mode floating pill bar — Sprint v174: merged into header (saves 48px) */}

            {/* BUG-v276: one coordinate-feedback owner only. The former generic
                top-right overlay plus a draw-specific overlay rendered the same
                coordinate twice on Android. Drawing reserves space beneath its
                header and away from the right-side map-control rail. */}
            {hoverCoords && (
              <div
                data-testid="map-coordinate-feedback"
                className={isMobile && mapMode === 'draw'
                  ? "absolute left-3 z-[1001] max-w-[calc(100%_-_6rem)] truncate rounded-full bg-black/60 px-2 py-1 font-mono text-[10px] text-white/90 pointer-events-none backdrop-blur-sm"
                  : "absolute top-2 right-2 z-[1000] flex items-center gap-1 rounded bg-black/60 px-2 py-1 font-mono text-xs text-white pointer-events-none"}
                style={isMobile && mapMode === 'draw' ? { top: '56px' } : undefined}
              >
                {!(isMobile && mapMode === 'draw') && <Crosshair className="h-3 w-3" />}
                {formatCoords(hoverCoords.lat, hoverCoords.lng)}
              </div>
            )}

            {/* Sprint v160: 2-layer compass overlay — rotating needle + static labels */}
            {isMobile && showCompassOverlay && (
              <div
                className="absolute pointer-events-none z-[998]"
                style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 160, height: 160 }}
              >
              {/* Layer 1: Rotating needle + rings (rotates with map bearing) */}
              <div style={{ position: 'absolute', inset: 0, transform: `rotate(${-mapBearing}deg)`, transition: 'transform 0.2s ease' }}>
                <svg width="160" height="160" viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg">
                  {/* Dark semi-transparent background circle — makes compass visible on any map */}
                  <circle cx="80" cy="80" r="76" fill="rgba(15,20,30,0.45)"/>

                  {/* Outer ring */}
                  <circle cx="80" cy="80" r="74" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" fill="none"/>
                  {/* Inner ring */}
                  <circle cx="80" cy="80" r="18" stroke="rgba(255,255,255,0.25)" strokeWidth="1" fill="none"/>

                  {/* 8 cardinal tick marks (N/NE/E/SE/S/SW/W/NW) */}
                  {[0,45,90,135,180,225,270,315].map((deg, i) => {
                    const rad = (deg * Math.PI) / 180;
                    const isCardinal = deg % 90 === 0;
                    const r1 = 74;
                    const r2 = isCardinal ? 58 : 64;
                    const x1 = 80 + r1 * Math.sin(rad);
                    const y1 = 80 - r1 * Math.cos(rad);
                    const x2 = 80 + r2 * Math.sin(rad);
                    const y2 = 80 - r2 * Math.cos(rad);
                    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                      stroke={isCardinal ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)'}
                      strokeWidth={isCardinal ? 2.5 : 1.5} />;
                  })}

                  {/* 16 minor tick marks every 22.5° (skip the 8 major ones) */}
                  {Array.from({length: 16}).map((_, i) => {
                    const deg = i * 22.5;
                    if (deg % 45 === 0) return null;
                    const rad = (deg * Math.PI) / 180;
                    const x1 = 80 + 74 * Math.sin(rad);
                    const y1 = 80 - 74 * Math.cos(rad);
                    const x2 = 80 + 68 * Math.sin(rad);
                    const y2 = 80 - 68 * Math.cos(rad);
                    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                      stroke="rgba(255,255,255,0.2)" strokeWidth="1" />;
                  })}

                  {/* Crosshair lines N-S and E-W */}
                  <line x1="80" y1="6" x2="80" y2="62" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>
                  <line x1="80" y1="98" x2="80" y2="154" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>
                  <line x1="6" y1="80" x2="62" y2="80" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>
                  <line x1="98" y1="80" x2="154" y2="80" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>

                  {/* NE/NW/SE/SW diagonal lines */}
                  <line x1="27" y1="27" x2="42" y2="42" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
                  <line x1="133" y1="27" x2="118" y2="42" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
                  <line x1="27" y1="133" x2="42" y2="118" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
                  <line x1="133" y1="133" x2="118" y2="118" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>

                  {/* === N needle — bold red kite/diamond === */}
                  {/* Main red blade pointing north */}
                  <polygon points="80,8 86,76 80,68 74,76" fill="rgba(220,38,38,0.95)"/>
                  {/* White highlight edge on N needle */}
                  <polygon points="80,8 86,76 80,68 74,76" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.8"/>
                  {/* Inner shading for 3D effect */}
                  <polygon points="80,8 80,68 74,76" fill="rgba(180,20,20,0.6)"/>

                  {/* === S needle — dark gray kite/diamond === */}
                  <polygon points="80,152 86,84 80,92 74,84" fill="rgba(100,110,120,0.7)"/>
                  <polygon points="80,152 86,84 80,92 74,84" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.8"/>
                  <polygon points="80,152 80,92 74,84" fill="rgba(60,70,80,0.5)"/>

                  {/* === NE/NW/SE/SW small diamond tips === */}
                  <polygon points="118,42 114,50 110,42 114,34" fill="rgba(255,255,255,0.3)"/>
                  <polygon points="42,42 46,50 50,42 46,34" fill="rgba(255,255,255,0.3)"/>
                  <polygon points="118,118 114,110 110,118 114,126" fill="rgba(255,255,255,0.3)"/>
                  <polygon points="42,118 46,110 50,118 46,126" fill="rgba(255,255,255,0.3)"/>

                  {/* Center pivot dot */}
                  <circle cx="80" cy="80" r="6" fill="rgba(20,25,35,0.9)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
                  <circle cx="80" cy="80" r="3" fill="rgba(220,38,38,0.95)"/>

                </svg>
              </div>

              {/* Layer 2: ROTATING labels — rotate WITH needle so N points to true North on map */}
              {/* Sprint v177 Fix: labels must be in rotating layer, not static — static N always top is WRONG */}
              <svg
                width="160" height="160" viewBox="0 0 160 160"
                xmlns="http://www.w3.org/2000/svg"
                style={{ position: 'absolute', inset: 0, pointerEvents: 'none', transform: `rotate(${-mapBearing}deg)`, transition: 'transform 0.2s ease' }}
              >
                {/* N — large bold red with dark shadow for contrast */}
                <text x="80" y="18" textAnchor="middle" dominantBaseline="middle"
                  fill="white" fontSize="15" fontWeight="bold"
                  fontFamily="system-ui,-apple-system,sans-serif"
                  style={{ filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.9)) drop-shadow(0 0 6px rgba(0,0,0,0.7))' }}>N</text>
                <text x="80" y="18" textAnchor="middle" dominantBaseline="middle"
                  fill="rgba(255,70,70,1)" fontSize="15" fontWeight="bold"
                  fontFamily="system-ui,-apple-system,sans-serif">N</text>

                {/* S */}
                <text x="80" y="142" textAnchor="middle" dominantBaseline="middle"
                  fill="white" fontSize="13" fontWeight="700"
                  fontFamily="system-ui,-apple-system,sans-serif"
                  style={{ filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.9))' }}>S</text>
                <text x="80" y="142" textAnchor="middle" dominantBaseline="middle"
                  fill="rgba(220,230,240,1)" fontSize="13" fontWeight="700"
                  fontFamily="system-ui,-apple-system,sans-serif">S</text>

                {/* W */}
                <text x="18" y="80" textAnchor="middle" dominantBaseline="middle"
                  fill="white" fontSize="13" fontWeight="700"
                  fontFamily="system-ui,-apple-system,sans-serif"
                  style={{ filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.9))' }}>W</text>
                <text x="18" y="80" textAnchor="middle" dominantBaseline="middle"
                  fill="rgba(220,230,240,1)" fontSize="13" fontWeight="700"
                  fontFamily="system-ui,-apple-system,sans-serif">W</text>

                {/* E */}
                <text x="142" y="80" textAnchor="middle" dominantBaseline="middle"
                  fill="white" fontSize="13" fontWeight="700"
                  fontFamily="system-ui,-apple-system,sans-serif"
                  style={{ filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.9))' }}>E</text>
                <text x="142" y="80" textAnchor="middle" dominantBaseline="middle"
                  fill="rgba(220,230,240,1)" fontSize="13" fontWeight="700"
                  fontFamily="system-ui,-apple-system,sans-serif">E</text>

                {/* NE */}
                <text x="128" y="32" textAnchor="middle" dominantBaseline="middle"
                  fill="white" fontSize="9" fontWeight="600"
                  fontFamily="system-ui,-apple-system,sans-serif"
                  style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.9))' }}>NE</text>
                <text x="128" y="32" textAnchor="middle" dominantBaseline="middle"
                  fill="rgba(200,215,230,0.95)" fontSize="9" fontWeight="600"
                  fontFamily="system-ui,-apple-system,sans-serif">NE</text>

                {/* NW */}
                <text x="32" y="32" textAnchor="middle" dominantBaseline="middle"
                  fill="white" fontSize="9" fontWeight="600"
                  fontFamily="system-ui,-apple-system,sans-serif"
                  style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.9))' }}>NW</text>
                <text x="32" y="32" textAnchor="middle" dominantBaseline="middle"
                  fill="rgba(200,215,230,0.95)" fontSize="9" fontWeight="600"
                  fontFamily="system-ui,-apple-system,sans-serif">NW</text>

                {/* SE */}
                <text x="128" y="128" textAnchor="middle" dominantBaseline="middle"
                  fill="white" fontSize="9" fontWeight="600"
                  fontFamily="system-ui,-apple-system,sans-serif"
                  style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.9))' }}>SE</text>
                <text x="128" y="128" textAnchor="middle" dominantBaseline="middle"
                  fill="rgba(200,215,230,0.95)" fontSize="9" fontWeight="600"
                  fontFamily="system-ui,-apple-system,sans-serif">SE</text>

                {/* SW */}
                <text x="32" y="128" textAnchor="middle" dominantBaseline="middle"
                  fill="white" fontSize="9" fontWeight="600"
                  fontFamily="system-ui,-apple-system,sans-serif"
                  style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.9))' }}>SW</text>
                <text x="32" y="128" textAnchor="middle" dominantBaseline="middle"
                  fill="rgba(200,215,230,0.95)" fontSize="9" fontWeight="600"
                  fontFamily="system-ui,-apple-system,sans-serif">SW</text>
              </svg>

              {/* Sprint v162: Layer 3 — static heading text at compass center (cardinal + degrees) */}
              {(() => {
                const bearing = ((mapBearing % 360) + 360) % 360;
                const deg = Math.round(bearing);
                const cardinals = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
                const cardinal = cardinals[Math.round(bearing / 22.5) % 16];
                const isNorth = cardinal === 'N' || cardinal === 'NNE' || cardinal === 'NNW';
                return (
                  <div
                    style={{
                      position: 'absolute', inset: 0, pointerEvents: 'none',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 0,
                    }}
                  >
                    {/* Cardinal direction */}
                    <span style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
                      color: isNorth ? 'rgba(255,80,80,1)' : 'rgba(200,215,230,0.95)',
                      textShadow: '0 0 4px rgba(0,0,0,1), 0 0 8px rgba(0,0,0,0.9)',
                      lineHeight: 1.1, fontFamily: 'system-ui,-apple-system,sans-serif',
                      marginBottom: 1,
                    }}>{cardinal}</span>
                    {/* Degree value */}
                    <span style={{
                      fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em',
                      color: 'rgba(255,255,255,0.97)',
                      textShadow: '0 0 6px rgba(0,0,0,1), 0 0 12px rgba(0,0,0,0.9)',
                      lineHeight: 1, fontFamily: 'system-ui,-apple-system,sans-serif',
                    }}>{deg}°</span>
                  </div>
                );
              })()}
              </div>
            )}

            {/* v377: Contextual Edit Dock. It is deliberately absent in read-only
                state and only appears after the authorized user explicitly enters
                an edit surface from the desktop header or mobile action cluster. */}
            {!isMobile && canEditMapBoundary && activeSurface === 'edit-tools' && mapMode !== 'view' && (
              <aside data-pdf-hide className="pointer-events-none absolute right-4 top-1/2 z-[1002] -translate-y-1/2" aria-label="ເຄື່ອງມືແກ້ໄຂແຜນທີ່">
                <div className="pointer-events-auto w-[116px] rounded-2xl border border-slate-200 bg-white/95 p-2 shadow-xl backdrop-blur-sm">
                  <div className="mb-2 flex items-center justify-between px-1">
                    <span className="text-[11px] font-semibold text-slate-700">ແກ້ໄຂແຜນທີ່</span>
                    <button type="button" onClick={() => { handleEscapeMapMode(); setActiveSurface('none'); }} aria-label="ປິດເຄື່ອງມືແກ້ໄຂ" className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"><X className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={handleDrawPolygon} aria-label="ແຕ້ມຂອບເຂດ" title="ແຕ້ມຂອບເຂດ" className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${drawMode === 'polygon' ? 'bg-[#002975] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><Hexagon className="h-4 w-4" /></button>
                    <button type="button" onClick={handleDrawRectangle} aria-label="ແຕ້ມສີ່ຫຼ່ຽມ" title="ແຕ້ມສີ່ຫຼ່ຽມ" className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${drawMode === 'rectangle' ? 'bg-[#002975] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><Maximize className="h-4 w-4" /></button>
                    {/* Sprint v230: Edit group remains hidden during active drawing so tools cannot overlap the draw interaction. */}
                    {!isDrawing && <>
                      <button type="button" onClick={handleEditMode} aria-label="ແກ້ໄຂຈຸດ" title="ແກ້ໄຂຈຸດ" className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${drawMode === 'edit' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><Pencil className="h-4 w-4" /></button>
                      <button type="button" onClick={handleDragMode} aria-label="ລາກຍ້າຍຮູບຊົງ" title="ລາກຍ້າຍ" className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${drawMode === 'drag' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><Hand className="h-4 w-4" /></button>
                      <button type="button" onClick={handleDeleteMode} aria-label="ລຶບຮູບຊົງ" title="ລຶບ" className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${drawMode === 'delete' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><Eraser className="h-4 w-4" /></button>
                      <button type="button" onClick={handleToggleRotate} disabled={!geoBoundaryJson && !draftGeoJson} aria-label="ໝູນຮູບຊົງ" title="ໝູນຮູບຊົງ" className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] disabled:opacity-35 ${isRotating ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><RotateCw className="h-4 w-4" /></button>
                      <button type="button" onClick={handleUndo} disabled={!canUndo} aria-label="ຖອນຄືນການແກ້ໄຂ" title="ຖອນຄືນ" className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] disabled:opacity-35"><Undo2 className="h-4 w-4" /></button>
                      <button type="button" onClick={handleRedo} disabled={!canRedo} aria-label="ເຮັດການແກ້ໄຂຄືນ" title="ເຮັດຄືນ" className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] disabled:opacity-35"><Redo2 className="h-4 w-4" /></button>
                      <button type="button" onClick={() => setShowPolygonList(v => !v)} aria-label="ຈັດການລາຍການ polygon" title="ລາຍການ polygon" className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${showPolygonList ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><List className="h-4 w-4" /></button>
                      <button type="button" onClick={handleResetToSaved} disabled={!geoBoundaryJson && !draftGeoJson} aria-label="ກັບຄືນຂອບເຂດທີ່ບັນທຶກ" title="ກັບຄືນທີ່ບັນທຶກ" className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-amber-700 transition-colors hover:bg-amber-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] disabled:opacity-35"><RotateCcw className="h-4 w-4" /></button>
                      {/* Sprint v230: hidden during active drawing (BUG-DRAW-3) */}
                      <button type="button" onClick={handleClear} disabled={isSaving || (!geoBoundaryJson && !draftGeoJson)} aria-label="ລຶບຂອບເຂດທັງໝົດ" title="ລຶບຂອບເຂດທັງໝົດ" className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-700 transition-colors hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] disabled:opacity-35"><Trash2 className="h-4 w-4" /></button>
                    </>}
                  </div>
                </div>
              </aside>
            )}

            {/* ─── Drawing guide tooltip (top-center, desktop only — Sprint v156: mobile hides this) ─── */}
            {isDrawing && !isMobile && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 z-[1001]">
                <div className="bg-black/75 text-white text-xs px-3 py-1.5 rounded-full pointer-events-none shadow-lg max-w-[85vw] text-center flex items-center gap-1.5 flex-wrap justify-center">
                  <span className="text-green-400">●</span>
                  {/* GAP-3: vertex count in drawing guide */}
                  {drawingVerticesRef.current.length === 0
                    ? "ກົດ map ເພື່ອເພີ່ມຈຸດທຳອິດ"
                    : drawingVerticesRef.current.length === 1
                    ? "ຈຸດທີ 1 — ກົດຕໍ່ (ຕ້ອງການ 3 ຈຸດຂຶ້ນໄປ)"
                    : drawingVerticesRef.current.length === 2
                    ? "ຈຸດທີ 2 — ກົດຕໍ່ (ຕ້ອງການ 1 ຈຸດອີກ)"
                    : isMobile
                    ? `ຈຸດທີ ${drawingVerticesRef.current.length} — ແຕະຈຸດທຳອິດ ຫຼື ກົດ ✓ ເພື່ອຈົບ`
                    : `ຈຸດທີ ${drawingVerticesRef.current.length} — ຄລິກຈຸດທຳອິດ, double-click, ຫຼື Enter ເພື່ອຈົບ`
                  }
                </div>
                {/* G8: Snap-to-angle toggle (visible during drawing only) */}
                <button
                  onClick={() => setSnapAngle(v => !v)}
                  title={snapAngle ? "ປິດ Snap 45°" : "ເປິດ Snap 45°/90°"}
                  className={`pointer-events-auto text-xs px-2.5 py-1 rounded-full shadow-lg flex items-center gap-1 transition-all ${
                    snapAngle
                      ? "bg-cyan-600 text-white ring-1 ring-cyan-300"
                      : "bg-black/60 text-white/70 hover:bg-black/80"
                  }`}
                >
                  <span className="font-mono font-bold">45°</span>
                  {snapAngle ? "✓ Snap ON" : "Snap OFF"}
                </button>
                {/* Sprint v115 Bug1b: desktop finish button — mobile uses the draw strip's ✓.
                     Desktop had NO finish affordance and click-first-vertex was intercepted,
                     leaving users stuck. Enabled at min vertices (polygon 3 / rectangle 2). */}
                {!isMobile && (
                  <button
                    onClick={() => {
                      const map = leafletMapRef.current;
                      if (!map) return;
                      const _min = drawModeRef.current === 'rectangle' ? 2 : 3;
                      if (drawingVerticesRef.current.length < _min) return;
                      const di = (map.pm?.Draw as any)?.Polygon || (map.pm?.Draw as any)?.Rectangle;
                      if (di && typeof di._finishShape === 'function') { try { di._finishShape(); } catch {} }
                    }}
                    disabled={drawVertexCount < (drawMode === 'rectangle' ? 2 : 3)}
                    className="pointer-events-auto flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-bold shadow-lg hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    ສຳເລັດ (ຫຼື Enter)
                  </button>
                )}
              </div>
            )}

            {/* GAP-7: Coordinate navigation panel */}
            {showCoordPanel && (
              <div className="absolute left-[76px] top-4 z-[1003] w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-blue-600" /> ໄປຫາພິກັດ
                  </span>
                  <button onClick={() => setShowCoordPanel(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                {/* Google Maps link paste */}
                <div className="mb-2">
                  <label className="text-xs text-gray-500 mb-1 block">Paste Google Maps Link</label>
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={coordMapsLink}
                      onChange={(e) => setCoordMapsLink(e.target.value)}
                      placeholder="https://maps.app.goo.gl/..."
                      className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                    <button
                      onClick={() => {
                        if (coordMapsLink.trim()) {
                          setCoordError("");
                          resolveLinkMutation.mutate({ url: coordMapsLink.trim() });
                        }
                      }}
                      disabled={resolveLinkMutation.isPending}
                      className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {resolveLinkMutation.isPending ? "..." : "ສົກັດ"}
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-400 text-center mb-2">— ຫຼື ໃສ່ພິກັດໂດຍກົງ —</div>
                {/* Manual lat/lng inputs — type=text + inputMode=decimal for Samsung keyboard */}
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  <div>
                    <label className="text-xs text-gray-500 mb-0.5 block">Latitude</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      value={coordLat}
                      onChange={(e) => { setCoordLat(e.target.value); setCoordError(""); }}
                      placeholder="17.975700"
                      className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-0.5 block">Longitude</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      value={coordLng}
                      onChange={(e) => { setCoordLng(e.target.value); setCoordError(""); }}
                      placeholder="102.633300"
                      className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                    />
                  </div>
                </div>
                {coordError && (
                  <div className="text-xs text-red-500 mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {coordError}
                  </div>
                )}
                <button
                  onClick={handleGoToCoord}
                  disabled={!coordLat || !coordLng}
                  className="w-full py-1.5 bg-[#002975] text-white text-xs rounded hover:bg-[#003d99] disabled:opacity-40 flex items-center justify-center gap-1"
                >
                  <Navigation className="h-3 w-3" /> ໄປ (Pan + Zoom)
                </button>
                <p className="text-xs text-gray-400 mt-1.5 text-center">Marker ຈະຫາຍ ເມື່ອເລີ່ມແຕ້ມ ຫຼື ຫຼັງ 30 ວິ</p>
              </div>
            )}

            {/* G5: Boundary history panel */}
            {!isMobile && showHistoryPanel && (
              <div className="absolute left-[76px] top-4 z-[1003] w-72 overflow-hidden rounded-xl border border-gray-200 bg-white/95 shadow-2xl backdrop-blur-sm">
                <div className="flex items-center justify-between px-3 py-2 bg-indigo-600 text-white">
                  <div className="flex items-center gap-1.5 text-sm font-semibold">
                    <History className="h-4 w-4" />
                    ປະຫວັດ Boundary
                  </div>
                  <button onClick={() => setShowHistoryPanel(false)} className="text-white/80 hover:text-white">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {isLoadingHistory ? (
                    <div className="flex items-center justify-center py-8 text-gray-400 text-xs gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> ກຳລັງໂຫຼດ...
                    </div>
                  ) : boundaryHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-xs">ຍັງບໍ່ມີປະຫວັດ</div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {boundaryHistory.map((entry: any, idx: number) => (
                        <div key={entry.id} className="px-3 py-2 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                              <Clock className="h-3 w-3 text-indigo-500" />
                              {idx === 0 ? (
                                <span className="text-indigo-600">ຫຼ້າສຸດ</span>
                              ) : (
                                `ຄັ້ງທີ ${boundaryHistory.length - idx}`
                              )}
                            </span>
                            <span className="text-xs text-gray-400 font-mono">
                              {new Date(entry.savedAt).toLocaleDateString("lo-LA", { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>📐 {entry.areaSqM > 0 ? `${Math.round(entry.areaSqM).toLocaleString("en-US")} m²` : "-"}</span>
                            <span>📍 {entry.vertexCount > 0 ? `${entry.vertexCount - 1} ດ້ານ` : "-"}</span>
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {new Date(entry.savedAt).toLocaleTimeString("lo-LA", { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="px-3 py-2 border-t bg-gray-50 text-xs text-gray-400 text-center">
                  ສະແດງ {Math.min(boundaryHistory.length, 20)} ຄັ້ງຫຼ້າສຸດ
                </div>
              </div>
            )}

            {/* G9 + Sprint v131: Polygon list panel with visibility, color picker, rename */}
            {!isMobile && showPolygonList && canEdit && (
              <div className="absolute left-[76px] top-4 z-[1004] w-64 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
                <div className="flex items-center justify-between px-3 py-2 bg-emerald-600 text-white">
                  <span className="text-xs font-bold">ຈັດການ Polygon</span>
                  <button onClick={() => { setShowPolygonList(false); setColorPickerIndex(null); setEditingLabelIndex(null); }} className="text-white/80 hover:text-white"><X className="h-3.5 w-3.5" /></button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {(managedPolygonGeoJson as any)?.features?.length > 0 ? (
                    (managedPolygonGeoJson as any).features.map((feat: any, i: number) => {
                      const isHidden = hiddenPolygons.has(i);
                      const savedColor = feat.properties?.color as string | null;
                      const dotColor = savedColor || POLYGON_COLORS[i % POLYGON_COLORS.length];
                      const isEditingLabel = editingLabelIndex === i;
                      const isPickingColor = colorPickerIndex === i;
                      return (
                        <div key={i} className={`border-b last:border-0 ${isHidden ? 'opacity-50' : ''} ${activePolygonIndex === i ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}>
                          {/* Main row */}
                          <div className="flex items-center gap-1.5 px-2 py-2">
                            {/* Color dot — click to open color picker */}
                            <button
                              onClick={(e) => { e.stopPropagation(); setColorPickerIndex(isPickingColor ? null : i); setEditingLabelIndex(null); }}
                              className="w-4 h-4 rounded-full flex-shrink-0 border-2 border-white shadow-sm ring-1 ring-gray-300 hover:ring-gray-500 transition-all"
                              style={{ background: dotColor }}
                              title="ເລືອກສີ"
                            />
                            {/* Label — click to edit */}
                            {isEditingLabel ? (
                              <input
                                autoFocus
                                className="text-xs flex-1 border border-emerald-400 rounded px-1 py-0.5 outline-none"
                                value={editingLabelValue}
                                onChange={(e) => setEditingLabelValue(e.target.value)}
                                onBlur={() => {
                                  // Sprint v136 Bug A: use draftGeoJsonRef.current (not functional updater — setDraftGeoJson takes a value, not a function)
                                  const trimmed = editingLabelValue.trim() || `ຜືນທີ ${i + 1}`;
                                  const cur = draftGeoJsonRef.current as any;
                                  if (cur?.features) {
                                    const features = cur.features.map((f: any, fi: number) =>
                                      fi === i ? { ...f, properties: { ...f.properties, label: trimmed } } : f
                                    );
                                    setDraftGeoJson({ ...cur, features } as any);
                                  }
                                  setEditingLabelIndex(null);
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingLabelIndex(null); }}
                              />
                            ) : (
                              <button
                                className="text-xs flex-1 text-left text-gray-700 hover:text-emerald-700 truncate"
                                onClick={(e) => { e.stopPropagation(); setEditingLabelIndex(i); setEditingLabelValue(feat.properties?.label || `ຜືນທີ ${i + 1}`); setColorPickerIndex(null); }}
                                title="ກົດເພື່ອປ່ຽນຊື່"
                              >
                                {feat.properties?.label || `ຜືນທີ ${i + 1}`}
                              </button>
                            )}
                            <span className="text-[10px] text-gray-400 flex-shrink-0">{feat.properties?.areaSqM ? `${Math.round(feat.properties.areaSqM).toLocaleString()} m²` : ""}</span>
                            {/* Visibility toggle */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const layer = polygonLayersRef.current[i];
                                const map = leafletMapRef.current;
                                if (!layer || !map) return;
                                setHiddenPolygons(prev => {
                                  const next = new Set(prev);
                                  if (next.has(i)) {
                                    next.delete(i);
                                    try { layer.addTo(map); } catch {}
                                    // [BUG-HIDE-CENTER FIX v252r] Re-render center labels when polygon is shown
                                    setTimeout(() => renderCenterLabels(next), 0);
                                  } else {
                                    next.add(i);
                                    try { map.removeLayer(layer); } catch {}
                                    // [BUG-HIDE-CENTER FIX v252r] Re-render center labels skipping hidden polygon
                                    // renderCenterLabels clears all first then re-renders for visible polygons only
                                    setTimeout(() => renderCenterLabels(next), 0);
                                  }
                                  refreshVisibleSegmentLabels(next);
                                  return next;
                                });
                              }}
                              className={`flex-shrink-0 ${isHidden ? 'text-gray-300 hover:text-gray-500' : 'text-gray-400 hover:text-emerald-600'}`}
                              title={isHidden ? 'ສະແດງ' : 'ຊ່ອນ'}
                            >
                              {isHidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </button>
                            {/* Delete */}
                            <button
                              disabled={isSaving}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleManagedPolygonDelete(i);
                              }}
                              className="flex-shrink-0 text-red-400 hover:text-red-600 disabled:opacity-40"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                          {/* Color picker palette */}
                          {isPickingColor && (
                            <div className="px-2 pb-2 flex flex-wrap gap-1.5">
                              {POLYGON_COLOR_PALETTE.map((c) => (
                                <button
                                  key={c}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Apply color to map layer
                                    const layer = polygonLayersRef.current[i];
                                    if (layer) {
                                      try { layer.setStyle({ fillColor: c }); } catch {}
                                      layer.eachLayer?.((sub: any) => { try { sub.setStyle({ fillColor: c }); } catch {}; });
                                    }
                                    // Sprint v136 Bug A: use draftGeoJsonRef.current (not functional updater)
                                    const cur = draftGeoJsonRef.current as any;
                                    if (cur?.features) {
                                      const features = cur.features.map((f: any, fi: number) =>
                                        fi === i ? { ...f, properties: { ...f.properties, color: c } } : f
                                      );
                                      setDraftGeoJson({ ...cur, features } as any);
                                    }
                                    setColorPickerIndex(null);
                                  }}
                                  className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${
                                    dotColor === c ? 'border-gray-700 scale-110' : 'border-white shadow-sm ring-1 ring-gray-200'
                                  }`}
                                  style={{ background: c }}
                                  title={c}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-3 py-4 text-xs text-gray-400 text-center">ຍັງບໍ່ມີ polygon</div>
                  )}
                </div>
                <div className="px-3 py-2 border-t bg-gray-50">
                  <button
                    onClick={() => { setShowPolygonList(false); setColorPickerIndex(null); handleDrawPolygon(); }}
                    className="w-full flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg py-1.5 font-medium"
                  >
                    <Plus className="h-3.5 w-3.5" /> ເພີ່ມ polygon ໃໝ່
                  </button>
                </div>
              </div>
            )}
            {/* V390: brief pointer-safe measure mode guide; the actionable session rail is below. */}
            {measureMode && measureStage === 'measuring' && (
              <div data-smart-measure-status className={`absolute z-[1001] bg-amber-700/95 text-white text-xs px-3 py-1.5 rounded-full pointer-events-none shadow-lg whitespace-nowrap flex items-center gap-1.5 font-semibold ${isMobile ? 'top-[52px] left-3 max-w-[calc(100%-5rem)]' : 'top-2 left-1/2 -translate-x-1/2'}`}>
                <Ruler className="h-4 w-4" />
                {measurePointCount === 0 ? 'ແຕະແຜນທີ່ ຫຼື ເລີ່ມຈາກຫຼັກຊັບ' : `ກຳລັງວັດ · ${measurePointCount} ຈຸດ`}
              </div>
            )}

            {measureStage === 'review' && measurePointCount > 0 && (
              <div data-smart-measure-review data-testid="smart-measure-review" className={`absolute z-[1001] pointer-events-auto ${isMobile ? 'top-[52px] left-3 max-w-[calc(100%-5rem)]' : 'top-3 left-1/2 -translate-x-1/2'}`}>
                <div className="flex items-center gap-2 rounded-full border border-amber-200 bg-white/95 py-1.5 pl-3 pr-1.5 shadow-lg backdrop-blur-sm">
                  <Ruler className="h-4 w-4 text-amber-700" />
                  <span className="text-xs font-bold tabular-nums text-slate-900">{measureDistance === null ? '—' : formatMeasureDistance(measureDistance)}</span>
                  <span className="text-xs font-semibold text-slate-500">{measurePointCount} ຈຸດ</span>
                  <button type="button" onClick={handleResumeMeasure} aria-label="ເປີດການວັດຕໍ່" className="min-h-11 rounded-full bg-amber-600 px-3 text-xs font-bold text-white hover:bg-amber-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700">ເປີດຕໍ່</button>
                  <button type="button" onClick={() => { handleClearMeasurePoints(); setMeasureStage('idle'); }} aria-label="ລ້າງເສັ້ນວັດ" className="flex h-11 w-11 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"><X className="h-4 w-4" /></button>
                </div>
              </div>
            )}

            {measureMode && measureStage === 'measuring' && (
              <section
                data-pdf-hide
                data-testid="mobile-smart-measure-workspace"
                aria-label="Smart Field Measure"
                className={`absolute z-[1003] pointer-events-auto rounded-2xl border border-amber-200 bg-white/95 shadow-xl backdrop-blur-sm ${isMobile ? `left-3 right-3 bottom-[calc(env(safe-area-inset-bottom)+1rem)] ${measurePointCount === 0 ? 'max-h-[min(20dvh,10.5rem)]' : 'max-h-[min(31dvh,18rem)]'} overflow-y-auto overscroll-contain` : 'left-20 top-4 w-[328px]'}`}
              >
                <div className="flex items-center justify-between gap-3 border-b border-amber-100 bg-amber-50/70 px-3 py-2.5 sm:px-4 sm:py-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-sm font-bold text-slate-900 sm:text-[15px]"><Ruler className="h-4 w-4 text-amber-700" /> ວັດໄລຍະຕາມເສັ້ນ</p>
                    {(!isMobile || measurePointCount > 0) && <p className="mt-0.5 text-xs font-medium text-slate-600">ຊົ່ວຄາວ · ບໍ່ໄດ້ບັນທຶກ</p>}
                  </div>
                  <button type="button" onClick={handleMinimizeMeasure} aria-label={measurePointCount ? 'ຫຍໍ້ Smart Field Measure' : 'ປິດ Smart Field Measure'} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"><X className="h-4 w-4" /></button>
                </div>
                <div className="px-3 py-3 sm:px-4 sm:py-4">
                  {measurePointCount > 0 && <div className="flex items-end justify-between gap-3 rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2">
                    <div><p className="text-xs font-semibold text-slate-600">ລວມໄລຍະຕາມເສັ້ນທີ່ວາງ (ປະມານ)</p><p className="mt-0.5 text-xl font-extrabold tabular-nums text-[#002975] sm:text-2xl" aria-live="off">{measureDistance === null ? '—' : formatMeasureDistance(measureDistance)}</p></div>
                    <p className="rounded-full bg-white px-2.5 py-1.5 text-xs font-bold text-amber-900 ring-1 ring-amber-200">{measurePointCount} ຈຸດ</p>
                  </div>}
                  <p className={`mt-2 leading-4 text-slate-700 ${measurePointCount === 0 && isMobile ? 'text-[13px] font-medium' : 'text-xs'}`}>{measurePointCount === 0 ? 'ແຕະແຜນທີ່ ຫຼືເລີ່ມຈາກຫຼັກຊັບ.' : 'ແຕະວາງຈຸດຕາມເສັ້ນທາງ; label ບອກໄລຍະແຕ່ລະຊ່ວງ. ຄ່າແມ່ນປະມານ ບໍ່ແມ່ນ route ອັດຕະໂນມັດ.'}</p>
                  <div data-testid="measure-zero-point-composer" className={measurePointCount === 0 ? 'mt-2 grid grid-cols-[minmax(0,1fr)_2.75rem] items-center gap-2' : 'contents'}>
                    {measurePointCount === 0 && measureOriginOptions.length > 0 && (
                      measureOriginOptions.length === 1 ? (
                        <button type="button" onClick={() => handleStartMeasureFromCollateral(measureOriginOptions[0].value)} className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#002975] px-3 text-xs font-bold text-white hover:bg-[#001f59] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"><MapPinned className="h-4 w-4" />ເລີ່ມຈາກຫຼັກຊັບ</button>
                      ) : (
                        <AnchoredSelectPopover value={null} options={measureOriginOptions} onChange={handleStartMeasureFromCollateral} ariaLabel="ເລືອກ Polygon ເພື່ອເລີ່ມວັດ" placeholder="ເລືອກຫຼັກຊັບເພື່ອເລີ່ມ" testId="measure-origin-picker" triggerClassName="min-h-11 w-full border-0 bg-[#002975] text-left text-xs font-bold text-white hover:bg-[#001f59]" menuClassName="z-[10050]" portalContainer={mapDialogPortalElement} />
                      )
                    )}
                  <div data-testid="measure-zero-point-actions" className={`items-center ${measurePointCount === 0 ? 'flex justify-end' : 'mt-2 flex justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1.5'}`}>
                    {measurePointCount > 0 && <p className="min-w-0 text-[11px] leading-4 text-slate-600">ປ່ຽນພື້ນແຜນທີ່ໄດ້ ໂດຍບໍ່ລ້າງຈຸດ</p>}
                    <MapLayerSelectorPopover<MapLayerChooserValue>
                      value={tileLayer === 'google' && googleSubLayer !== 'satellite' ? `google:${googleSubLayer}` : tileLayer}
                      ariaLabel="ປ່ຽນພື້ນແຜນທີ່ຂະນະວັດ"
                      testId="measure-map-base-layer"
                      disabled={false}
                      onTrace={traceMapLayer}
                      portalContainer={mapDialogPortalElement}
                      open={measureTilePickerOpen}
                      onOpenChange={setMeasureTilePickerOpen}
                      triggerClassName={`h-11 min-h-11 w-11 shrink-0 rounded-lg border-0 px-0 ${measureTilePickerOpen ? 'bg-[#002975] text-white' : 'bg-white text-[#002975] ring-1 ring-slate-200'}`}
                      renderTrigger={() => measurePointCount === 0 ? <Layers className="h-4 w-4" aria-hidden="true" /> : <span className="flex items-center gap-1 text-xs font-bold"><Layers className="h-3.5 w-3.5" /> ພື້ນແຜນທີ່</span>}
                      options={[
                        { value: 'streets', label: tileLabels.streets, description: 'ຖະໜົນ ແລະສະຖານທີ່', icon: <Layers className="h-4 w-4" /> },
                        { value: 'satellite', label: tileLabels.satellite, description: 'ພາບດາວທຽມ', icon: <Layers className="h-4 w-4" /> },
                        { value: 'hybrid', label: tileLabels.hybrid, description: 'ດາວທຽມພ້ອມຊື່', icon: <Layers className="h-4 w-4" /> },
                        { value: 'maptiler', label: tileLabels.maptiler, description: 'ພາບ HD ຈາກ MapTiler', icon: <Layers className="h-4 w-4" /> },
                        { value: 'google', label: 'Google Maps', description: 'Street View ແລະຊູມເລິກ', icon: <Layers className="h-4 w-4" /> },
                        { value: 'google:roadmap', label: 'Google · ຖະໜົນ', description: 'ແຜນທີ່ຖະໜົນ Google', icon: <Layers className="h-4 w-4" /> },
                        { value: 'google:hybrid', label: 'Google · ຜະສົມ', description: 'ດາວທຽມພ້ອມຊື່ Google', icon: <Layers className="h-4 w-4" /> },
                        { value: 'google:terrain', label: 'Google · ພູມສັນຖານ', description: 'ພູ ແລະເສັ້ນຊັ້ນຄວາມສູງ', icon: <Layers className="h-4 w-4" /> },
                      ]}
                      onChange={(selection) => {
                        if (selection.startsWith('google:')) {
                          const googleType = selection.slice('google:'.length) as 'roadmap' | 'hybrid' | 'terrain';
                          switchGoogleSubLayer(googleType);
                          if (tileLayer !== 'google') switchTileLayer('google');
                        } else {
                          switchTileLayer(selection);
                        }
                        setMeasureTilePickerOpen(false);
                      }}
                    />
                  </div>
                  </div>
                  {measurePointCount > 0 && <div className="sticky bottom-0 -mx-3 mt-2 grid grid-cols-3 gap-2 border-t border-amber-100 bg-white/95 px-3 py-2 backdrop-blur-sm sm:-mx-4 sm:px-4">
                    <button type="button" disabled={!measurePointCount} onClick={handleUndoMeasurePoint} className="min-h-11 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 disabled:opacity-40 hover:bg-slate-50">ຖອຍ 1 ຈຸດ</button>
                    <button type="button" disabled={!measurePointCount} onClick={handleClearMeasurePoints} className="min-h-11 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 disabled:opacity-40 hover:bg-slate-50">ລ້າງເສັ້ນວັດ</button>
                    <button type="button" onClick={handleMinimizeMeasure} className="min-h-11 rounded-xl bg-[#002975] text-xs font-semibold text-white hover:bg-[#001f59]">ຈົບ · ເບິ່ງຄືນ</button>
                  </div>}
                  <button type="button" onClick={() => setShowMeasureManualEntry(value => !value)} className={`text-xs font-semibold text-[#002975] underline underline-offset-4 ${measurePointCount === 0 ? 'mt-2' : 'mt-3'}`}>{showMeasureManualEntry ? 'ປິດການໃສ່ພິກັດ' : 'ເພີ່ມຈຸດດ້ວຍພິກັດ'}</button>
                  {showMeasureManualEntry && <div className="mt-2 grid grid-cols-2 gap-2"><input value={measureManualLat} onChange={(event) => setMeasureManualLat(event.target.value)} inputMode="decimal" placeholder="Latitude" aria-label="Latitude ສຳລັບຈຸດວັດ" className="min-h-11 rounded-xl border border-slate-200 px-3 text-sm" /><input value={measureManualLng} onChange={(event) => setMeasureManualLng(event.target.value)} inputMode="decimal" placeholder="Longitude" aria-label="Longitude ສຳລັບຈຸດວັດ" className="min-h-11 rounded-xl border border-slate-200 px-3 text-sm" /><button type="button" onClick={handleAddMeasureManualPoint} className="col-span-2 min-h-11 rounded-xl border border-[#002975] text-xs font-semibold text-[#002975] hover:bg-slate-50">ເພີ່ມຈຸດວັດ</button>{measureManualError && <p className="col-span-2 text-xs text-red-600" role="alert">{measureManualError}</p>}</div>}
                </div>
              </section>
            )}

            {/* BUG-A UX: edit hint after polygon creation */}
            {showEditHint && !isDrawing && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-green-700/90 text-white text-xs px-3 py-1.5 rounded-full pointer-events-none z-[1001] shadow-lg whitespace-nowrap flex items-center gap-1.5">
                <span>✏️</span>
                ລາກຈຸດ ເພື່ອປັບຕຳແໜ່ງ — ກົດ ✓ ບັນທຶກ
              </div>
            )}

            {/* V65 / v379: navigation controls yield the mobile canvas while an
                active draw gesture owns map touches; edit/view navigation stays available. */}
            {/* Sprint v137: Floating map controls — production-grade circular buttons */}
            {/* Sprint v115.7 Bug3: compass top-4 on desktop (preserved for regression) */}
            {mapReady && !(isMobile && isDrawing) && (
              <div
                ref={mobileNavigationDockRef}
                data-pdf-hide
                className={`absolute right-4 flex flex-col gap-2 z-[1002] ${
                  isMobile
                    ? mapMode === 'draw' ? 'top-[52px]'  /* Sprint v156: show compass in draw mode, below top pill */
                    : mapMode !== 'view' ? 'top-[60px]'
                    : 'top-[60px]'  /* Sprint v169 FAB-1: was top-4 — caused overlap with 56px dropdown header */
                    : 'right-2 top-4'
                }`}
              >
                {/* Sprint v157: Mobile — 1 merged smart compass button (tap=toggle device compass, long-press=reset north) */}
                {/* Sprint v144: Device compass follow button — mobile + desktop (merged into 1 on mobile in v157) */}
                {/* Desktop — keep original 2 buttons */}
                {isMobile ? (
                  <button
                    onClick={handleToggleDeviceCompass}
                    onPointerDown={() => {
                      debugCompassLongPressRef.current = setTimeout(() => {
                        if (leafletMapRef.current?.setBearing) { leafletMapRef.current.setBearing(0); setMapBearing(0); }
                        dbg('\ud83e\uddad Reset North via long-press (mobile merged compass)');
                      }, 1000);
                    }}
                    onPointerUp={() => { if (debugCompassLongPressRef.current) { clearTimeout(debugCompassLongPressRef.current); debugCompassLongPressRef.current = null; } }}
                    onPointerLeave={() => { if (debugCompassLongPressRef.current) { clearTimeout(debugCompassLongPressRef.current); debugCompassLongPressRef.current = null; } }}
                    title={followDeviceCompass ? '\u0e9b\u0eb4\u0e94\u0ec0\u0e82\u0eb1\u0e9a\u0e97\u0eb4\u0e94 | Long-press = Reset North' : '\u0e95\u0eb4\u0e94\u0e95\u0eb2\u0ea1\u0e97\u0eb4\u0e94\u0ec0\u0e84\u0eb7\u0ec8\u0ead\u0e87 | Long-press = Reset North'}
                    className={`w-12 h-12 flex flex-col items-center justify-center rounded-full shadow-lg border active:scale-95 transition-all ${
                      followDeviceCompass
                        ? 'bg-blue-600 text-white border-blue-400 ring-2 ring-blue-300'
                        : 'bg-white text-gray-500 border-gray-100'
                    }`}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" style={{ transform: `rotate(${-mapBearing}deg)`, transition: 'transform 0.15s ease' }}>
                      <polygon points="12,2 15.5,11 12,9.5 8.5,11" fill={followDeviceCompass ? '#fff' : '#e74c3c'} />
                      <polygon points="12,22 8.5,13 12,14.5 15.5,13" fill={followDeviceCompass ? 'rgba(255,255,255,0.5)' : '#95a5a6'} />
                      <circle cx="12" cy="12" r="1.5" fill={followDeviceCompass ? '#fff' : '#555'} />
                    </svg>
                    <span className={`text-[7px] font-bold leading-none mt-0.5 ${followDeviceCompass ? 'text-blue-100' : 'text-gray-500'}`}>
                      {followDeviceCompass ? 'ຕິດ' : `${Math.round(((mapBearing % 360) + 360) % 360)}°`}
                    </span>
                  </button>
                ) : null}
                {/* Sprint v158: Compass overlay toggle button — mobile only, below compass button */}
                {/* Sprint v168.9: GPS FAB is now the 3rd button in this flex-col (no overlap) */}
                {isMobile && (
                  <button
                    onClick={() => {
                      setShowCompassOverlay(v => {
                        const next = !v;
                        try { localStorage.setItem('crs-compass-overlay', next ? '1' : '0'); } catch {}
                        return next;
                      });
                    }}
                    title={showCompassOverlay ? '\u0e8a\u0ec8\u0ead\u0e99 Compass Overlay' : '\u0eaa\u0eb0\u0ec1\u0e94\u0e87 Compass Overlay'}
                    className={`w-12 h-12 flex flex-col items-center justify-center rounded-full shadow-lg border active:scale-95 transition-all ${
                      showCompassOverlay
                        ? 'bg-emerald-600 text-white border-emerald-400 ring-2 ring-emerald-300'
                        : 'bg-white/80 text-gray-400 border-gray-200'
                    }`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" stroke={showCompassOverlay ? 'rgba(255,255,255,0.5)' : '#d1d5db'} strokeWidth="1.5"/>
                      <circle cx="12" cy="12" r="2" fill={showCompassOverlay ? '#fff' : '#9ca3af'}/>
                      <line x1="12" y1="3" x2="12" y2="8" stroke={showCompassOverlay ? '#fca5a5' : '#d1d5db'} strokeWidth="2" strokeLinecap="round"/>
                      <line x1="12" y1="16" x2="12" y2="21" stroke={showCompassOverlay ? 'rgba(255,255,255,0.4)' : '#d1d5db'} strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="3" y1="12" x2="8" y2="12" stroke={showCompassOverlay ? 'rgba(255,255,255,0.4)' : '#d1d5db'} strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="16" y1="12" x2="21" y2="12" stroke={showCompassOverlay ? 'rgba(255,255,255,0.4)' : '#d1d5db'} strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span className={`text-[7px] font-bold leading-none mt-0.5 ${showCompassOverlay ? 'text-emerald-100' : 'text-gray-400'}`}>
                      {showCompassOverlay ? 'ON' : 'OFF'}
                    </span>
                  </button>
                )}
                {/* Sprint v170 F1: GPS FAB always visible (removed gpsLatitude && gpsLongitude guard) */}
                {/* Sprint v168.9: GPS FAB — 3rd button in mobile flex-col stack (no overlap) */}
                {isMobile && mapReady && (
                  <button
                    data-pdf-hide
                    className="w-12 h-12 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center active:scale-95 transition-transform"
                    onClick={handleZoomToGPS}
                    title="Zoom \u0ec4\u0e9b GPS"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
                    </svg>
                  </button>
                )}
                {/* V393: base-layer choice belongs in the always-visible navigation dock,
                    not the width-constrained collateral action row. This keeps it
                    reachable in view-only and edit-capable sessions alike. */}
                {isMobile && mapReady && (
                  <MapLayerSelectorPopover<MapLayerChooserValue>
                    value={tileLayer === 'google' && googleSubLayer !== 'satellite' ? `google:${googleSubLayer}` : tileLayer}
                    ariaLabel="ເລືອກຮູບແບບແຜນທີ່"
                    testId="mobile-map-base-layer"
                    disabled={isMapControlInteractionLocked}
                    onTrace={traceMapLayer}
                    portalContainer={mapDialogPortalElement}
                    getAvoidRects={getMobileLayerPopoverAvoidRects}
                    open={showTilePanel}
                    onOpenChange={(next) => setActiveSurface(next ? 'layers' : 'none')}
                    triggerClassName={`h-12 w-12 min-h-12 justify-center rounded-full border ${showTilePanel ? 'border-[#002975] bg-[#002975] text-white' : 'border-gray-100 bg-white text-slate-700 active:bg-slate-100'} shadow-lg`}
                    renderTrigger={() => <Layers className="h-5 w-5" aria-hidden="true" />}
                    options={[
                      { value: 'streets', label: tileLabels.streets, description: 'ຖະໜົນ ແລະສະຖານທີ່', icon: <Layers className="h-4 w-4" /> },
                      { value: 'satellite', label: tileLabels.satellite, description: 'ພາບດາວທຽມ', icon: <Layers className="h-4 w-4" /> },
                      { value: 'hybrid', label: tileLabels.hybrid, description: 'ດາວທຽມພ້ອມຊື່', icon: <Layers className="h-4 w-4" /> },
                      { value: 'maptiler', label: tileLabels.maptiler, description: 'ພາບ HD ຈາກ MapTiler', icon: <Layers className="h-4 w-4" /> },
                      { value: 'google', label: 'Google Maps', description: 'Street View ແລະຊູມເລິກ', icon: <Layers className="h-4 w-4" /> },
                      { value: 'google:roadmap', label: 'Google · ຖະໜົນ', description: 'ແຜນທີ່ຖະໜົນ Google', icon: <Layers className="h-4 w-4" /> },
                      { value: 'google:hybrid', label: 'Google · ຜະສົມ', description: 'ດາວທຽມພ້ອມຊື່ Google', icon: <Layers className="h-4 w-4" /> },
                      { value: 'google:terrain', label: 'Google · ພູມສັນຖານ', description: 'ພູ ແລະເສັ້ນຊັ້ນຄວາມສູງ', icon: <Layers className="h-4 w-4" /> },
                    ]}
                    onChange={(selection) => {
                      traceMapLayer({ phase: 'parent-on-change', option: selection, detail: `active-before=${tileLayer}` });
                      if (selection.startsWith('google:')) {
                        const googleType = selection.slice('google:'.length) as 'roadmap' | 'hybrid' | 'terrain';
                        switchGoogleSubLayer(googleType);
                        if (tileLayer !== 'google') switchTileLayer('google');
                      } else {
                        switchTileLayer(selection);
                      }
                      setActiveSurface('none');
                    }}
                  />
                )}
                {isMobile && mapReady && (
                  <div className="flex flex-col overflow-hidden rounded-full border border-gray-100 bg-white shadow-lg" aria-label="ຄວບຄຸມການຊູມແຜນທີ່">
                    <button
                      type="button"
                      onClick={() => leafletMapRef.current?.zoomIn()}
                      aria-label="ຊູມເຂົ້າແຜນທີ່"
                      title="ຊູມເຂົ້າ"
                      className="flex h-11 w-12 items-center justify-center border-b border-slate-100 text-slate-700 active:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"
                    >
                      <ZoomIn className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => leafletMapRef.current?.zoomOut()}
                      aria-label="ຊູມອອກແຜນທີ່"
                      title="ຊູມອອກ"
                      className="flex h-11 w-12 items-center justify-center text-slate-700 active:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"
                    >
                      <ZoomOut className="h-5 w-5" />
                    </button>
                  </div>
                )}
                {isMobile && (gpsLatitude || liveGps) && mapMode === 'view' && (
                  <button
                    type="button"
                    ref={streetViewLaunchButtonRef}
                    data-testid="mobile-street-view-trigger"
                    onClick={() => { if (showStreetView) closeMobileStreetView(); else void openMobileStreetView(); }}
                    aria-label={showStreetView ? 'ປິດ Street View' : 'ເປີດ Street View'}
                    title={showStreetView ? 'ປິດ Street View' : 'ເປີດ Street View'}
                    className={`flex h-12 w-12 items-center justify-center rounded-full border text-xs font-bold shadow-lg transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${showStreetView ? 'border-emerald-400 bg-emerald-600 text-white' : 'border-slate-200 bg-white text-slate-700 active:bg-slate-100'}`}
                  >SV</button>
                )}
                {/* Sprint v170 F2: Save device GPS to DB — only when liveGps active AND canEdit */}
                {isMobile && mapReady && liveGps && canEdit && (
                  <button
                    data-pdf-hide
                    disabled={saveGpsMutation.isPending}
                    className="w-12 h-12 rounded-full bg-green-500 shadow-lg border border-green-400 flex items-center justify-center active:scale-95 transition-transform disabled:opacity-60"
                    onClick={async () => {
                      if (!liveGps) return;
                      try {
                        await saveGpsMutation.mutateAsync({
                          id: collateralId,
                          gpsLatitude: String(liveGps.lat),
                          gpsLongitude: String(liveGps.lng),
                        });
                        sonnerToast.success("ບັນທຶກ GPS ສຳເລັດ", { description: `${liveGps.lat.toFixed(6)}, ${liveGps.lng.toFixed(6)}` });
                      } catch (err: any) {
                        sonnerToast.error("ບັນທຶກ GPS ບໍ່ສຳເລັດ", { description: err?.message || "ເກີດຂໍ້ຜິດພາດ" });
                      }
                    }}
                    title="ບັນທຶກ GPS ຕຳແໜ່ງນີ້"
                  >
                    {saveGpsMutation.isPending ? (
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                        <path d="M12 2a10 10 0 0 1 10 10" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17 21 17 13 7 13 7 21" />
                        <polyline points="7 3 7 8 15 8" />
                      </svg>
                    )}
                  </button>
                )}
                {/* Sprint v170 F3: GPS accuracy badge — color-coded by accuracy */}
                {isMobile && mapReady && liveGps && (
                  <span
                    data-pdf-hide
                    className={`text-[10px] font-mono font-bold text-center leading-tight px-1 py-0.5 rounded ${
                      liveGps.accuracy <= 10 ? "text-green-600 bg-green-50" :
                      liveGps.accuracy <= 30 ? "text-yellow-600 bg-yellow-50" : "text-orange-500 bg-orange-50"
                    }`}
                  >
                    ±{Math.round(liveGps.accuracy)}m
                  </span>
                )}
                {false && !isMobile && (
                  <>
                    {/* Desktop: original reset-north button */}
                    <button
                      onClick={() => { if (leafletMapRef.current?.setBearing) { leafletMapRef.current.setBearing(0); setMapBearing(0); } }}
                      onPointerDown={() => {
                        debugCompassLongPressRef.current = setTimeout(() => {
                          setDebugPanelOpen(v => !v);
                          dbg(`\ud83d\udd27 Debug panel ${debugPanelOpen ? 'OFF' : 'ON'}`);
                        }, 1000);
                      }}
                      onPointerUp={() => { if (debugCompassLongPressRef.current) { clearTimeout(debugCompassLongPressRef.current); debugCompassLongPressRef.current = null; } }}
                      onPointerLeave={() => { if (debugCompassLongPressRef.current) { clearTimeout(debugCompassLongPressRef.current); debugCompassLongPressRef.current = null; } }}
                      title="\u0e81\u0eb1\u0e9a\u0e84\u0eb7\u0e99\u0e97\u0eb4\u0e94\u0ec0\u0edc\u0eb7\u0ead (Reset North) | Long-press 1s = Debug"
                      className="w-12 h-12 flex flex-col items-center justify-center rounded-full bg-white shadow-lg border border-gray-100 active:scale-95 transition-transform"
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" style={{ transform: `rotate(${-mapBearing}deg)`, transition: 'transform 0.15s ease' }}>
                        <polygon points="12,2 15.5,11 12,9.5 8.5,11" fill="#e74c3c" />
                        <polygon points="12,22 8.5,13 12,14.5 15.5,13" fill="#95a5a6" />
                        <circle cx="12" cy="12" r="1.5" fill="#555" />
                      </svg>
                      <span className="text-[7px] font-bold text-gray-500 leading-none mt-0.5">{Math.round(((mapBearing % 360) + 360) % 360)}°</span>
                    </button>
                    {/* Desktop: \u00b115\u00b0 rotate buttons */}
                    <button onClick={() => handleRotateBy(-15)} title="\u0edd\u0eb8\u0e99\u0e8a\u0ec9\u0eb2\u0e8d 15\u00b0" className="w-11 h-11 hidden md:flex items-center justify-center rounded-full bg-white shadow-lg border border-gray-100 text-gray-600 hover:bg-gray-50 active:scale-95 transition-all text-sm font-bold">
                      ↺
                    </button>
                    <button onClick={() => handleRotateBy(15)} title="\u0edd\u0eb8\u0e99\u0e82\u0ea7\u0eb2 15\u00b0" className="w-11 h-11 hidden md:flex items-center justify-center rounded-full bg-white shadow-lg border border-gray-100 text-gray-600 hover:bg-gray-50 active:scale-95 transition-all text-sm font-bold">
                      ↻
                    </button>
                    {/* Desktop: device compass follow mode */}
                    <button
                      onClick={handleToggleDeviceCompass}
                      title={followDeviceCompass ? '\u0e9b\u0eb4\u0e94\u0ec0\u0e82\u0eb1\u0e9a\u0e97\u0eb4\u0e94\u0ec0\u0e84\u0eb7\u0ec8\u0ead\u0e87' : '\u0e95\u0eb4\u0e94\u0e95\u0eb2\u0ea1\u0e97\u0eb4\u0e94\u0ec0\u0e84\u0eb7\u0ec8\u0ead\u0e87'}
                      className={`w-11 h-11 flex items-center justify-center rounded-full shadow-lg border active:scale-95 transition-all ${
                        followDeviceCompass
                          ? 'bg-blue-600 text-white border-blue-400 ring-2 ring-blue-300'
                          : 'bg-white text-gray-500 border-gray-100'
                      }`}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="9" stroke={followDeviceCompass ? 'rgba(255,255,255,0.4)' : '#d1d5db'} strokeWidth="1.5"/>
                        <polygon points="12,4 14,11 12,10 10,11" fill={followDeviceCompass ? '#fff' : '#e74c3c'}/>
                        <polygon points="12,20 10,13 12,14 14,13" fill={followDeviceCompass ? 'rgba(255,255,255,0.5)' : '#95a5a6'}/>
                        <circle cx="12" cy="12" r="1.5" fill={followDeviceCompass ? '#fff' : '#555'}/>
                      </svg>
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Sprint v168.9: GPS FAB moved inside compass flex-col container (see below) — removed from here to prevent overlap */}

            {/* Sprint v115.8 / v117 Phase 4: DBG toggle button — DEV only (gated behind import.meta.env.DEV) */}
            {/* Sprint v115.7 GHOST-DEBUG: DEV gate restored in v117 Phase 4 (Samsung diagnosis complete) */}
            {import.meta.env.DEV && mapReady && (
              <button
                data-pdf-hide
                onPointerDown={() => { setDebugPanelOpen(v => !v); dbg(`🔧 DBG button tapped (Sprint v115.8)`); }}
                style={{
                  position: 'absolute',
                  top: isMobile && mapMode !== 'view'
                    ? 'calc(64px + 48px + 8px + 48px + 8px)'  /* below draw strip + compass + GPS */
                    : 'calc(16px + 48px + 8px + 48px + 8px)', /* below compass + GPS */
                  right: 16,
                  zIndex: 10003,
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  border: 'none',
                  padding: '5px 10px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 1,
                  pointerEvents: 'all',
                  opacity: 0.75,
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                }}
                title="Toggle Debug Panel (Sprint v115.8)"
              >DBG</button>
            )}

            {/* v377: Navigation Dock. It is separate from the contextual Edit Dock
                so routine navigation never competes with geometry actions. */}
            {!isMobile && mapReady && (
              <aside data-pdf-hide className="pointer-events-none absolute right-4 top-4 z-[1002]" aria-label="ເຄື່ອງມືນຳທາງແຜນທີ່">
                <div className="pointer-events-auto flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white/95 p-2 shadow-xl backdrop-blur-sm">
                  <div className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white" aria-label="ຄວບຄຸມການຊູມແຜນທີ່">
                    <button type="button" onClick={() => leafletMapRef.current?.zoomIn()} aria-label="ຊູມເຂົ້າແຜນທີ່" title="ຊູມເຂົ້າ" className="flex h-11 w-11 items-center justify-center border-b border-slate-200 text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"><ZoomIn className="h-4 w-4" /></button>
                    <button type="button" onClick={() => leafletMapRef.current?.zoomOut()} aria-label="ຊູມອອກແຜນທີ່" title="ຊູມອອກ" className="flex h-11 w-11 items-center justify-center text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"><ZoomOut className="h-4 w-4" /></button>
                  </div>
                  <button type="button" onClick={() => { if (leafletMapRef.current?.setBearing) { leafletMapRef.current.setBearing(0); setMapBearing(0); } }} aria-label="ກັບຄືນທິດເໜືອ" title="ກັບຄືນທິດເໜືອ" className="flex h-11 w-11 flex-col items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]">
                    <Navigation className="h-4 w-4 text-red-600" style={{ transform: `rotate(${-mapBearing}deg)` }} />
                    <span className="mt-0.5 text-[9px] font-semibold">{Math.round(((mapBearing % 360) + 360) % 360)}°</span>
                  </button>
                  <button type="button" onClick={() => { const target = mapRef.current; if (!target) return; if (document.fullscreenElement) void document.exitFullscreen?.(); else void target.requestFullscreen?.(); }} aria-label="ຂະຫຍາຍແຜນທີ່ເຕັມຈໍ" title="ຂະຫຍາຍແຜນທີ່ເຕັມຈໍ" className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"><Maximize className="h-4 w-4" /></button>
                  {tileLayer === 'google' && (gpsLatitude || liveGps) && mapMode === 'view' && <button type="button" onClick={() => { if (showStreetView) closeMobileStreetView(); else void openMobileStreetView(); }} aria-label={showStreetView ? 'ປິດ Street View' : 'ເປີດ Street View'} title={showStreetView ? 'ປິດ Street View' : 'ເປີດ Street View'} className={`flex h-11 w-11 items-center justify-center rounded-xl text-xs font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${showStreetView ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>SV</button>}
                </div>
              </aside>
            )}

            {/* v377: View Dock. Only the dock islands receive pointer events; the
                rest of the map remains available for pan and geometry work. */}
            {!isMobile && (
              <aside data-pdf-hide className="pointer-events-none absolute left-4 top-4 z-[1002] flex flex-col gap-2" aria-label="ເຄື່ອງມືເບິ່ງແຜນທີ່">
                <div className="pointer-events-auto flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white/95 p-2 shadow-xl backdrop-blur-sm">
                  <button type="button" onClick={() => setActiveSurface(activeSurface === 'landmarks' ? 'none' : 'landmarks')} aria-label="ເປີດຈຸດສຳຄັນຮອບຫຼັກຊັບ" title="ຈຸດສຳຄັນຮອບຫຼັກຊັບ" className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${activeSurface === 'landmarks' ? 'bg-[#002975] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><MapPinned className="h-4 w-4" /></button>
                  <MapLayerSelectorPopover<MapLayerChooserValue>
                    value={tileLayer === 'google' && googleSubLayer !== 'satellite' ? `google:${googleSubLayer}` : tileLayer}
                    ariaLabel="ເລືອກຮູບແບບແຜນທີ່"
                    testId="desktop-map-base-layer"
                    disabled={isMapControlInteractionLocked}
                    onTrace={traceMapLayer}
                    portalContainer={mapDialogPortalElement}
                    open={showTilePanel}
                    onOpenChange={(next) => setActiveSurface(next ? 'layers' : 'none')}
                    triggerClassName={`h-11 w-11 min-h-11 justify-center rounded-xl border-0 p-0 ${showTilePanel ? 'bg-[#002975] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                    renderTrigger={() => <Layers className="h-4 w-4" aria-hidden="true" />}
                    options={[
                      { value: 'streets', label: tileLabels.streets, description: 'ຖະໜົນ ແລະສະຖານທີ່', icon: <Layers className="h-4 w-4" /> },
                      { value: 'satellite', label: tileLabels.satellite, description: 'ພາບດາວທຽມ', icon: <Layers className="h-4 w-4" /> },
                      { value: 'hybrid', label: tileLabels.hybrid, description: 'ດາວທຽມພ້ອມຊື່', icon: <Layers className="h-4 w-4" /> },
                      { value: 'maptiler', label: tileLabels.maptiler, description: 'ພາບ HD ຈາກ MapTiler', icon: <Layers className="h-4 w-4" /> },
                      { value: 'google', label: 'Google Maps', description: 'Street View ແລະຊູມເລິກ', icon: <Layers className="h-4 w-4" /> },
                      { value: 'google:roadmap', label: 'Google · ຖະໜົນ', description: 'ແຜນທີ່ຖະໜົນ Google', icon: <Layers className="h-4 w-4" /> },
                      { value: 'google:hybrid', label: 'Google · ຜະສົມ', description: 'ດາວທຽມພ້ອມຊື່ Google', icon: <Layers className="h-4 w-4" /> },
                      { value: 'google:terrain', label: 'Google · ພູມສັນຖານ', description: 'ພູ ແລະເສັ້ນຊັ້ນຄວາມສູງ', icon: <Layers className="h-4 w-4" /> },
                    ]}
                    onChange={(selection) => {
                      traceMapLayer({ phase: 'parent-on-change', option: selection, detail: `active-before=${tileLayer}` });
                      if (selection.startsWith('google:')) {
                        const googleType = selection.slice('google:'.length) as 'roadmap' | 'hybrid' | 'terrain';
                        switchGoogleSubLayer(googleType);
                        if (tileLayer !== 'google') switchTileLayer('google');
                      } else {
                        switchTileLayer(selection);
                      }
                      setActiveSurface('none');
                    }}
                  />
                  {(tileLayer === 'satellite' || tileLayer === 'hybrid' || tileLayer === 'maptiler') && <button type="button" onClick={toggleLabelOverlay} aria-label={showLabelOverlay ? 'ປິດຊື່ສະຖານທີ່' : 'ສະແດງຊື່ສະຖານທີ່'} title={showLabelOverlay ? 'ປິດຊື່ສະຖານທີ່' : 'ສະແດງຊື່ສະຖານທີ່'} className={`flex h-11 w-11 items-center justify-center rounded-xl text-sm font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${showLabelOverlay ? 'bg-[#002975] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>A</button>}
                  {mapReady && <button type="button" onClick={handleZoomToGPS} aria-label="ນຳແຜນທີ່ໄປຈຸດ GPS" title="ນຳແຜນທີ່ໄປຈຸດ GPS" className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"><Navigation className="h-4 w-4" /></button>}
                  {(draftGeoJson || geoBoundaryJson) && <button type="button" onClick={toggleSegmentLabels} aria-label={showSegmentLabels ? 'ປິດປ້າຍໄລຍະຫ່າງ' : 'ສະແດງປ້າຍໄລຍະຫ່າງ'} title={showSegmentLabels ? 'ປິດໄລຍະຫ່າງ' : 'ສະແດງໄລຍະຫ່າງ'} className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${showSegmentLabels ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><Tag className="h-4 w-4" /></button>}
                  <div className="h-px bg-slate-200" />
                  <button type="button" onClick={() => { setActiveSurface('none'); setShowCoordPanel(v => !v); }} aria-label="ໄປຫາພິກັດ" title="ໄປຫາພິກັດ" className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${showCoordPanel ? 'bg-[#002975] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><Crosshair className="h-4 w-4" /></button>
                  <button type="button" onClick={handleToggleMeasure} aria-label={measureMode ? 'ປິດ Smart Field Measure' : 'ເປີດ Smart Field Measure'} title={measureMode ? 'ປິດ Smart Field Measure' : 'Smart Field Measure'} className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${measureMode ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><Ruler className="h-4 w-4" /></button>
                  <button type="button" onClick={handleResetView} aria-label="ກັບໄປມຸມມອງເດີມ" title="ກັບໄປມຸມມອງເດີມ" className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"><RotateCcw className="h-4 w-4" /></button>
                  {(geoBoundaryJson || boundaryHistory.length > 0) && <button type="button" onClick={() => { setActiveSurface('none'); setShowHistoryPanel(v => !v); if (!showHistoryPanel) refetchHistory(); }} aria-label="ເບິ່ງປະຫວັດ boundary" title="ປະຫວັດ boundary" className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975] ${showHistoryPanel ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}><History className="h-4 w-4" /></button>}
                </div>
              </aside>
            )}

            {mapLayerTraceEnabled && landmarkTraceSummary && (
              <div
                data-pdf-hide
                data-testid="landmark-render-trace"
                aria-live="polite"
                className="pointer-events-none absolute bottom-4 left-4 z-[1003] max-w-[min(520px,calc(100%-32px))] rounded-lg border border-amber-300 bg-slate-950/95 px-3 py-2 font-mono text-[10px] leading-4 text-amber-100 shadow-lg"
                style={{ pointerEvents: 'none' }}
              >
                <span className="mr-2 font-bold text-amber-300">LM TRACE</span>
                {landmarkTraceSummary}
              </div>
            )}

            {/* ─── Bottom-center Undo FAB (desktop only — Sprint v156: mobile uses FAB circle in draw strip) ─── */}
            {!isMobile && isDrawing && canUndoVertex && (
              <button
                onClick={handleUndoVertex}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/80 text-white rounded-full w-14 h-14 flex items-center justify-center z-[1002] shadow-xl active:scale-95 transition-transform border-2 border-white/30"
                aria-label="Undo last vertex"
              >
                <Undo2 className="h-6 w-6" />
              </button>
            )}

            {/* Desktop Street View is a dedicated pane, never a floating map overlay.
                The canvas reserves its width above so selector, layer picker, and
                Leaflet controls retain predictable pointer targets. */}
            {showStreetView && !isMobile && (
              <section
                className="absolute right-2 top-2 bottom-2 z-[1004] flex w-[380px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
                aria-label="Street View ຈຸດ GPS ຫຼັກຊັບ"
              >
                <div className="flex min-h-11 items-center justify-between border-b border-slate-200 bg-slate-50 px-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800">Street View ຈຸດ GPS ຫຼັກຊັບ</p>
                    <p className="text-[11px] text-slate-500">ກວດສອບຮູບຈາກ Google ແບບອ່ານຢ່າງດຽວ</p>
                  </div>
                  <StreetViewOverlayCloseButton
                    onClose={closeMobileStreetView}
                    ariaLabel="ປິດ Street View"
                    className="ml-3 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#002975]"
                  >
                    <X className="h-4 w-4" />
                  </StreetViewOverlayCloseButton>
                </div>

                <div className="relative flex min-h-0 flex-1 bg-slate-100" aria-live="polite">
                  {streetViewSessionState === 'checking' && (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-slate-600">
                      <Loader2 className="h-6 w-6 animate-spin text-[#002975]" />
                      <p className="text-sm font-medium">ກຳລັງກວດຫາ Street View...</p>
                    </div>
                  )}
                  {streetViewSessionState === 'no-coverage' && (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-7 text-center text-slate-600">
                      <MapPin className="h-7 w-7 text-amber-600" />
                      <p className="text-sm font-semibold text-slate-800">ບໍ່ມີ Street View ໃນບໍລິເວນນີ້</p>
                      <p className="text-xs leading-5">Google ຍັງບໍ່ມີຮູບພາບຖະໜົນໃກ້ພິກັດນີ້. ແຜນທີ່ ແລະການເລືອກຫຼັກຊັບຍັງໃຊ້ງານໄດ້ຕາມປົກກະຕິ.</p>
                    </div>
                  )}
                  {streetViewSessionState === 'error' && (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-7 text-center text-slate-600">
                      <AlertTriangle className="h-7 w-7 text-amber-600" />
                      <p className="text-sm font-semibold text-slate-800">ບໍ່ສາມາດເປີດ Street View ໄດ້ຕອນນີ້</p>
                      <p className="text-xs leading-5">ກະລຸນາປິດ panel ແລະລອງໃໝ່. ຂໍ້ມູນຫຼັກຊັບບໍ່ຖືກປ່ຽນແປງ.</p>
                    </div>
                  )}
                  {streetViewSessionState === 'available' && stableStreetViewUrl && (
                    <iframe
                      src={stableStreetViewUrl}
                      className="h-full w-full border-0"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Google Street View"
                    />
                  )}
                </div>
              </section>
            )}

            {/* Disclaimer — desktop only (mobile: shown in full sheet) */}
            {!isMobile && (
            <div className="absolute bottom-2 left-2 right-12 sm:right-auto sm:max-w-xs bg-amber-50/90 border border-amber-200 rounded text-xs text-amber-700 px-2 py-1 pointer-events-none z-[1000]">
              ⚠️ ຂໍ້ມູນຂົບເຂດນີ້ ເປີນການຄາດຄະເນ ສຳລັບການວິເຄາະສິນເຊື່ອເທົ່ານັ້ນ
            </div>
            )}
          </div>

          {/* ─── Bottom Action Bar (desktop only) ─── */}
          {!isMobile && <div className="px-3 py-2 border-t bg-white flex items-center gap-2 flex-shrink-0" style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
            {/* GPS info + G3 Zoom badge + G4 Live GPS accuracy (left) */}
            <div className="flex-1 min-w-0 flex items-center gap-2">
              {/* G4: Live GPS indicator (when available) */}
              {liveGps ? (
                <span className={`text-xs flex items-center gap-1 truncate font-mono ${
                  liveGps.accuracy <= 10 ? "text-green-600" :
                  liveGps.accuracy <= 30 ? "text-yellow-600" : "text-orange-500"
                }`}>
                  ● {liveGps.lat.toFixed(5)}, {liveGps.lng.toFixed(5)}
                  <span className="text-gray-400">±{Math.round(liveGps.accuracy)}m</span>
                </span>
              ) : gpsLatitude && gpsLongitude ? (
                <span className="text-xs text-gray-400 flex items-center gap-1 truncate">
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  {Number(gpsLatitude).toFixed(5)}, {Number(gpsLongitude).toFixed(5)}
                </span>
              ) : updatedAt ? (
                <span className="text-xs text-gray-400 truncate">
                  {new Date(updatedAt).toLocaleDateString("lo-LA", { year: "numeric", month: "short", day: "numeric" })}
                </span>
              ) : null}
              {/* G3: Zoom level badge — color-coded by accuracy */}
              <span className={`text-xs font-mono font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${
                currentZoom >= 18
                  ? "bg-green-600/20 text-green-700"
                  : currentZoom >= 16
                  ? "bg-yellow-500/20 text-yellow-700"
                  : "bg-amber-500/20 text-amber-700"
              }`}>
                Z{Math.round(currentZoom)}
              </span>
            </div>

            {/* Unsaved badge */}
            {hasUnsavedChanges && (
              <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-700 gap-1 text-xs h-6 px-1.5 flex-shrink-0">
                <AlertTriangle className="h-3 w-3" />
                <span>ບໍ່ເທື່ອບັນທຶກ</span>
              </Badge>
            )}

	            {/* Export PDF */}
	            <Button
	              variant="outline"
	              size="sm"
	              onClick={handleExportCollateralAssessmentReport}
	              disabled={isAssessmentReportExporting || hasUnsavedChanges}
	              className="h-9 px-3 gap-1.5 border-emerald-300 text-emerald-800 hover:bg-emerald-50 flex-shrink-0"
	              title="ບົດປະເມີນຫຼັກຊັບ PDF — ໃຊ້ຂໍ້ມູນທີ່ບັນທຶກແລ້ວເທົ່ານັ້ນ"
	              data-testid="collateral-assessment-report-export"
	            >
	              {isAssessmentReportExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
	              <span className="text-xs">ບົດປະເມີນ PDF</span>
	            </Button>
	            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                // E5 fix: Extract segment lengths from ALL polygons (not just last drawn)
                let segmentLengthsM: number[] | undefined;
                try {
                  const allSegs: number[] = [];
                  const layers = polygonLayersRef.current.length > 0
                    ? polygonLayersRef.current
                    : (polygonLayerRef.current ? [polygonLayerRef.current] : []);
                  for (const layer of layers) {
                    if (!layer) continue;
                    const lls = layer.getLatLngs ? layer.getLatLngs() : [];
                    const flat: any[] = Array.isArray(lls[0]) ? lls[0] : lls;
                    if (flat.length >= 3) {
                      flat.forEach((_: any, i: number) => {
                        const a = flat[i];
                        const b = flat[(i + 1) % flat.length];
                        allSegs.push(typeof a.distanceTo === "function" ? a.distanceTo(b) : 0);
                      });
                    }
                  }
                  if (allSegs.length > 0) segmentLengthsM = allSegs;
                } catch {
                  // segment extraction failed
                }

                // G6: Improved map screenshot — scale:2 retina, hide UI overlays during capture
                let mapImageBase64: string | undefined;
                const captureToast = sonnerToast.loading("ກຳລັງຖ່າຍພາບແຜນທີ່...");
                try {
                  const mapContainer = mapRef.current;
                  if (mapContainer) {
                    // X2 v51: Keep segment-label-icon VISIBLE during map capture so labels appear in PDF
                    // Only hide FAB buttons and [data-pdf-hide] elements
                    const hideEls = mapContainer.querySelectorAll<HTMLElement>(
                      "[data-pdf-hide]"
                    );
                    const fabEls = mapContainer.querySelectorAll<HTMLElement>(
                      "button, [role='button']"
                    );
                    hideEls.forEach((el) => { el.style.visibility = "hidden"; });
                    fabEls.forEach((el) => { el.style.opacity = "0"; });
                    // Wait 2 animation frames for DOM to settle
                    await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
                    try {
                      // Sprint v58 B1: Replace dom-to-image with html2canvas.
                      // html2canvas renders each tile individually without seam artifacts
                      // because it does not use SVG foreignObject (which causes blend-mode seams).
                      // useCORS:true allows cross-origin map tiles to be captured.
                      const captureWithTimeout = (container: HTMLElement) =>
                        Promise.race([
                          html2canvas(container, {
                            useCORS: true,
                            // Sprint v60 C1: allowTaint:true so cross-origin MapTiler tiles
                            // are captured. Without this, CORS-blocked tiles render blank.
                            allowTaint: true,
                            backgroundColor: "#0f172a",
                            scale: window.devicePixelRatio >= 2 ? 2 : 1.5,
                            width: container.offsetWidth,
                            height: container.offsetHeight,
                            ignoreElements: (el: Element) =>
                              el.classList?.contains("leaflet-control-container") ||
                              el.tagName === "BUTTON" ||
                              el.getAttribute("role") === "button",
                            logging: false,
                          }).then((canvas) => canvas.toDataURL("image/png")),
                          new Promise<never>((_, reject) =>
                            setTimeout(() => reject(new Error("screenshot timeout")), 8000)
                          ),
                        ]);
                      mapImageBase64 = await captureWithTimeout(mapContainer);
                    } catch {
                      // Fallback: Leaflet canvas.toDataURL (works when WebGL renderer is used)
                      const canvas = mapContainer.querySelector("canvas");
                      if (canvas) {
                        try { mapImageBase64 = (canvas as HTMLCanvasElement).toDataURL("image/png"); } catch { /* tainted canvas */ }
                      }
                    }
                    // Restore visibility
                    hideEls.forEach((el: Element) => ((el as HTMLElement).style.visibility = ""));
                    fabEls.forEach((el: Element) => ((el as HTMLElement).style.opacity = ""));
                  }
                } catch {
                  // Screenshot failed entirely — PDF will show placeholder
                }
                sonnerToast.dismiss(captureToast);

                const info: CollateralMapPDFInfo = {
                  collateralId,
                  collateralType,
                  ownerName: undefined,
                  areaSqM,
                  perimeterM: savedPerimeter,
                  gpsLatitude,
                  gpsLongitude,
                  updatedAt,
                  mapImageBase64,
                  segmentLengthsM,
                };
                // O1 v42: Update template props with latest info, then wait 1 frame for React to re-render
                setPdfInfo(info);
                await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
                exportPDF(info, pdfTemplateRef);
              }}
              disabled={isExporting || !geoBoundaryJson}
              className="h-9 px-3 gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50 flex-shrink-0"
            >
              {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              <span className="text-xs">ດາວໂຫລດ PDF</span>
            </Button>

            {/* Save button */}
            {canEdit && (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
                className="h-9 px-4 gap-1.5 bg-[#002975] hover:bg-[#003d99] flex-shrink-0"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span className="text-xs">{isSaving ? "ບັນທຶກ..." : "ບັນທຶກ"}</span>
              </Button>
            )}
          </div>}{/* end bottom action bar (desktop only) */}
          {/* BUG-v296: Keep the mobile Street View layer inside DialogContent. Radix
              disables pointer events outside its dismissable layer; a body portal
              could remain visible but make both recovery close controls inert. */}
          {isMobile && showStreetView && (
            <div
              data-pdf-hide
              role="dialog"
              aria-modal="true"
              aria-label="Street View ຂອງຈຸດ GPS ຫຼັກຊັບ"
              className="fixed inset-0 z-[10001] flex min-h-dvh flex-col bg-slate-950 text-white"
              style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              <header className="flex min-h-14 shrink-0 items-center justify-between border-b border-white/10 bg-slate-950/95 px-3 backdrop-blur-sm">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10" aria-hidden="true">
                    <Eye className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold leading-tight">Street View</p>
                    <p className="truncate text-xs text-slate-300">
                      {streetViewSessionState === 'available' && streetViewOffsetMeters && streetViewOffsetMeters > 0
                        ? `ພາບຖະໜົນທີ່ໃກ້ສຸດ ຫ່າງປະມານ ${streetViewOffsetMeters} ແມັດ`
                        : 'ກວດເບິ່ງບໍລິເວນ GPS ຫຼັກຊັບ'}
                    </p>
                  </div>
                </div>
                <StreetViewOverlayCloseButton
                  onClose={closeMobileStreetView}
                  ariaLabel="ປິດ Street View"
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white transition-colors active:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  <X className="h-6 w-6" />
                </StreetViewOverlayCloseButton>
              </header>

              <div className="relative min-h-0 flex-1 bg-slate-900">
                {streetViewSessionState === 'checking' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
                    <Loader2 className="h-9 w-9 animate-spin text-white" aria-hidden="true" />
                    <div>
                      <p className="text-base font-semibold">ກຳລັງກວດ Street View...</p>
                      <p className="mt-1 text-sm text-slate-300">ກຳລັງກວດວ່າບໍລິເວນນີ້ມີພາບຫຼືບໍ່</p>
                    </div>
                  </div>
                )}

                {streetViewSessionState === 'available' && (
                  <div ref={streetViewPanoramaElementRef} className="absolute inset-0" aria-label="Google Street View panorama" />
                )}

                {streetViewSessionState === 'no-coverage' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-7 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10" aria-hidden="true">
                      <MapPin className="h-7 w-7 text-slate-100" />
                    </div>
                    <div className="max-w-sm">
                      <h2 className="text-lg font-semibold">ບໍ່ມີ Street View ບໍລິເວນນີ້</h2>
                      <p className="mt-2 text-sm leading-6 text-slate-300">Google ບໍ່ພົບພາບ Street View ຢູ່ໃນລັດສະໝີ 500 ແມັດ ຈາກ GPS ນີ້. ທ່ານສາມາດກັບໄປກວດເບິ່ງຈາກແຜນທີ່ໄດ້.</p>
                    </div>
                    <StreetViewOverlayCloseButton
                      onClose={closeMobileStreetView}
                      className="min-h-11 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-950 shadow-sm transition-colors active:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    >
                      ກັບໄປເບິ່ງແຜນທີ່
                    </StreetViewOverlayCloseButton>
                  </div>
                )}

                {streetViewSessionState === 'error' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-7 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400/15" aria-hidden="true">
                      <AlertTriangle className="h-7 w-7 text-amber-300" />
                    </div>
                    <div className="max-w-sm">
                      <h2 className="text-lg font-semibold">ບໍ່ສາມາດເປີດ Street View ໄດ້</h2>
                      <p className="mt-2 text-sm leading-6 text-slate-300">ກະລຸນາກວດສອບອິນເຕີເນັດ ແລ້ວລອງໃໝ່ ຫຼືກັບໄປເບິ່ງແຜນທີ່.</p>
                    </div>
                    <div className="flex w-full max-w-sm gap-3">
                      <StreetViewOverlayCloseButton
                        onClose={closeMobileStreetView}
                        className="min-h-11 flex-1 rounded-xl border border-white/20 px-4 py-3 text-sm font-semibold text-white active:bg-white/10"
                      >
                        ກັບໄປແຜນທີ່
                      </StreetViewOverlayCloseButton>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New-384/V386: Mobile owns Landmark through its bottom-sheet portal;
          desktop must mount the same responsive panel inside the dialog-owned
          map layer. Without this branch the desktop control changed state but
          rendered no panel at all. */}
      {!isMobile && open && mapDialogPortalElement && activeSurface === 'landmarks' && createPortal(
        <LandmarkProximityPanel
          collateralId={collateralId}
          canEdit={canEdit}
          onClose={() => {
            handleCancelLandmarkMapPick();
            setLandmarkPickedCoordinate(null);
            landmarkEvidenceLayerRef.current?.clearLayers?.();
            landmarkPreviewLayerRef.current?.clearLayers?.();
            setActiveSurface('none');
          }}
          isPickingOnMap={landmarkCaptureMode}
          pickedCoordinate={landmarkPickedCoordinate}
          onRequestMapPick={handleStartLandmarkMapPick}
          onCancelMapPick={() => {
            handleCancelLandmarkMapPick();
            setLandmarkPickedCoordinate(null);
            landmarkPreviewLayerRef.current?.clearLayers?.();
          }}
          onSaved={() => {
            setLandmarkPickedCoordinate(null);
            landmarkPreviewLayerRef.current?.clearLayers?.();
          }}
          onFocusLandmark={focusLandmarkOnMap}
          focusedLandmarkId={focusedLandmarkId}
        />,
        mapDialogPortalElement,
      )}

      {/* V52: Hidden PDF template — mounted via portal to document.body to escape Dialog CSS context.
           Uses position:absolute top:-9999px (same as LoanPDFTemplate) so dom-to-image captures clean.
           opacity:0 is safe here because we use buildLoanPDF which reads wrapper.scrollWidth/scrollHeight. */}
      {createPortal(
      <div style={{ position: "absolute", top: "-9999px", left: 0, opacity: 0, pointerEvents: "none", zIndex: 0 }}>
        <CollateralMapPDFTemplate
          ref={pdfTemplateRef}
          collateralId={pdfInfo?.collateralId ?? collateralId}
          collateralType={pdfInfo?.collateralType ?? collateralType}
          areaSqM={pdfInfo?.areaSqM ?? areaSqM}
          perimeterM={pdfInfo?.perimeterM ?? savedPerimeter}
          gpsLatitude={pdfInfo?.gpsLatitude ?? gpsLatitude}
          gpsLongitude={pdfInfo?.gpsLongitude ?? gpsLongitude}
          updatedAt={pdfInfo?.updatedAt ?? updatedAt}
          mapImageBase64={pdfInfo?.mapImageBase64}
          segmentLengthsM={pdfInfo?.segmentLengthsM}
          mapPolicyDisclosure={activeMapEvidence.pdfDisclosure}
        />
      </div>
      , document.body)}
      {isMobile && focusedLandmarkId !== null && activeSurface === 'none' && (() => {
        const focusedItem = (savedLandmarkEvidence as any[]).find((item) => Number(item.id) === focusedLandmarkId);
        if (!focusedItem) return null;
        return <div data-pdf-hide data-testid="mobile-landmark-focus-status" role="status" aria-live="polite" className="pointer-events-none absolute bottom-[calc(env(safe-area-inset-bottom)+9rem)] left-3 right-20 z-[1001] rounded-xl border border-blue-200 bg-white/95 px-3 py-2 text-xs font-semibold text-[#002975] shadow-md backdrop-blur-sm">
          <span className="mr-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-[#002975]">{(savedLandmarkEvidence as any[]).length} ຈຸດ</span>
          ກຳລັງເບິ່ງ: <span className="font-bold">{focusedItem.name || 'ຈຸດສຳຄັນ'}</span> · ກົດໄອຄອນຈຸດໝາຍເພື່ອກັບຫາລາຍການ
        </div>;
      })()}
      {/* Sprint v138: Mobile Contextual Floating Pill — production-grade bottom bar */}
      {/* FIX v73.1: wrap in createPortal so it renders above Radix Dialog overlay (z-50) */}
      {/* Sprint v115 Fix #3: Disclaimer compliance preserved in More sheet */}
      {isMobile && open && !(measureMode && measureStage === 'measuring') && createPortal((
        <div
          ref={peekBarRef}
          data-pdf-hide
          data-testid="mobile-collateral-dock"
          className="fixed bottom-0 left-0 right-0 z-[9999] flex flex-col items-center pointer-events-none"
          style={{ paddingBottom: 'calc(8px + env(safe-area-inset-bottom))' }}
        >
          {/* Sprint v173: Compact single-row Google Maps-style bottom bar */}
          {/* Sprint v176: Option X — collateral info merged into bottom card (Google Maps place card style) */}
          {/* Sprint v86 Fix B: hide peek during draw (preserved for regression) */}
          {activeSurface === 'landmarks' && <LandmarkProximityPanel collateralId={collateralId} canEdit={canEdit} onClose={() => { handleCancelLandmarkMapPick(); setLandmarkPickedCoordinate(null); landmarkEvidenceLayerRef.current?.clearLayers?.(); landmarkPreviewLayerRef.current?.clearLayers?.(); setFocusedLandmarkId(null); setActiveSurface('none'); }} isPickingOnMap={landmarkCaptureMode} pickedCoordinate={landmarkPickedCoordinate} onRequestMapPick={handleStartLandmarkMapPick} onCancelMapPick={() => { handleCancelLandmarkMapPick(); setLandmarkPickedCoordinate(null); landmarkPreviewLayerRef.current?.clearLayers?.(); }} onSaved={() => { setLandmarkPickedCoordinate(null); landmarkPreviewLayerRef.current?.clearLayers?.(); }} onFocusLandmark={focusLandmarkOnMap} focusedLandmarkId={focusedLandmarkId} />}
          {!isDrawing && (
          <div className="pointer-events-auto w-[calc(100%-16px)] max-w-md bg-black/55 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/15 px-3 pt-2.5 pb-2">
            {/* Row 1: Collateral ID + type + close button — Google Maps place card style */}
            {!showTilePanel && mapMode !== 'edit' && (
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <MapPin className="h-3 w-3 text-white/60 flex-shrink-0" />
                {allCollaterals && allCollaterals.length > 1 && onSwitchCollateral ? (
                  <AnchoredSelectPopover
                    value={collateralId}
                    options={collateralSwitchOptions}
                    onChange={requestCollateralSwitch}
                    ariaLabel="ເລືອກຫຼັກຊັບໃນແຜນທີ່"
                    placeholder="ເລືອກຫຼັກຊັບ"
                    searchable
                    searchPlaceholder="ຄົ້ນຫາເລກໃບຕາດິນ, ເອກະສານ ຫຼື ລະຫັດ..."
                    testId="collateral-map-mobile-switcher"
                    className="min-w-0 max-w-[220px]"
                    triggerClassName="min-h-8 border-white/20 bg-white/10 px-2 py-1 text-xs font-semibold text-white shadow-none"
                    menuClassName="z-[10050]"
                    // v378: the mobile bottom bar is portalled above the dialog, but its
                    // menu must remain dialog-owned; otherwise Radix makes its body portal inert.
                    portalContainer={mapDialogPortalElement}
                  />
                ) : (
                  <span className="text-xs font-semibold text-white truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                    {activeCollateralReference.primaryLabel} · #{collateralId}
                  </span>
                )}
              </div>
              <button
                onClick={handleClose}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-white/15 text-white/80 active:bg-white/25 flex-shrink-0 ml-2"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            )}
            {/* Row 2: Area + action buttons */}
            <div className="flex items-center gap-2">
              {!showTilePanel && <>
              {/* Area pill — tap to cycle unit; flex-1 but min-w-0 so it shrinks before buttons */}
              <button
                className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden active:opacity-70"
                onClick={() => setAreaUnit(u => u === 'sqm' ? 'hectare' : u === 'hectare' ? 'rai' : 'sqm')}
                title="ກດເພື່ອປໍ່ນຫນ່ວ"
              >
                <span className="text-xs font-bold text-white whitespace-nowrap drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                  {liveArea > 0
                    ? formatAreaByUnit(liveArea, areaUnit)
                    : areaSqM > 0
                    ? formatAreaByUnit(areaSqM, areaUnit)
                    : '—'}
                </span>
                <span className="text-[10px] text-white/70 bg-white/15 rounded-full px-1.5 py-0.5 flex-shrink-0 font-medium">
                  {areaUnit === 'sqm' ? 'm²' : areaUnit === 'hectare' ? 'ha' : 'ໄຮ'}
                </span>
                {hasUnsavedChanges && (
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-500" title="ບໍ່ໄດ້ບັນທຶກ" />
                )}
              </button>
              {/* Divider */}
              <div className="w-px h-5 bg-white/20 flex-shrink-0" />
              {/* Draw/Edit button */}
              {canEditMapBoundary && (
                <button
                  type="button"
                  onClick={() => {
                    if (activeSurface === 'edit-tools') { handleEscapeMapMode(); setActiveSurface('none'); return; }
                    setActiveSurface('edit-tools');
                  }}
                  aria-label={activeSurface === 'edit-tools' ? 'ປິດເຄື່ອງມືແກ້ໄຂ' : 'ເປີດເຄື່ອງມືແກ້ໄຂແຜນທີ່'}
                  className={`w-12 h-11 flex flex-col items-center justify-center rounded-xl transition-colors flex-shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${activeSurface === 'edit-tools' ? 'bg-emerald-500 text-white' : 'bg-white/15 text-emerald-300 active:bg-white/25'}`}
                  title="ແຕ້ມ ແລະ ແກ້ໄຂແຜນທີ່"
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                  <span className="mt-0.5 text-[8px] font-bold leading-none">ແຕ້ມ</span>
                </button>
              )}
              {!canEditMapBoundary && (
                <span
                  data-testid="mobile-map-drawing-access-status"
                  className="max-w-28 rounded-lg bg-white/15 px-2 py-1 text-[10px] font-semibold leading-tight text-white/85"
                  title={mobileMapEditAvailabilityMessage}
                  aria-label={mobileMapEditAvailabilityMessage}
                >
                  {mobileMapEditAvailabilityLabel}
                </span>
              )}
              {/* Save button */}
              {canEdit && (
                <button
                  onClick={handleSave}
                  disabled={isSaving || !hasUnsavedChanges}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-blue-300 active:bg-white/25 disabled:opacity-30 transition-colors flex-shrink-0"
                  title="ບັນທຶກ"
                >
                  {isSaving
                    ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                    : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                  }
                </button>
              )}
	              {/* PDF button — FIX v73.3: use same full capture logic as desktop */}
	              <button
	                type="button"
	                onClick={handleExportCollateralAssessmentReport}
	                disabled={isAssessmentReportExporting || hasUnsavedChanges}
	                aria-label="ສ້າງບົດປະເມີນຫຼັກຊັບ PDF"
	                title="ບົດປະເມີນ PDF"
	                data-testid="collateral-assessment-report-export-mobile"
	                className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-200 active:bg-emerald-500/30 disabled:opacity-30 transition-colors flex-shrink-0"
	              >
	                {isAssessmentReportExporting
	                  ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
	                  : <FileText className="w-4 h-4" aria-hidden="true" />}
	              </button>
	              <button
                onClick={async () => {
                  if (!geoBoundaryJson) return;
                  const mapContainer = mapRef.current;
                  let mapImageBase64: string | undefined;
                  let segLengths: number[] | undefined;
                  if (polygonLayersRef.current.length > 0) {
                    const allSegs: number[] = [];
                    polygonLayersRef.current.forEach((layer: any) => {
                      if (!layer) return;
                      const lls = layer.getLatLngs?.();
                      if (!lls) return;
                      const ring: any[] = Array.isArray(lls[0]) ? lls[0] : lls;
                      for (let i = 0; i < ring.length; i++) {
                        const a = ring[i]; const b = ring[(i + 1) % ring.length];
                        if (a && b) allSegs.push(a.distanceTo(b));
                      }
                    });
                    if (allSegs.length > 0) segLengths = allSegs;
                  }
                  const captureToast = sonnerToast.loading('ກໍາລັງຖ່າຍພາບແຜນທີ່...');
                  if (mapContainer) {
                    const hideEls = mapContainer.querySelectorAll<HTMLElement>('[data-pdf-hide]');
                    const fabEls = mapContainer.querySelectorAll<HTMLElement>("button, [role='button']");
                    hideEls.forEach((el) => { el.style.visibility = 'hidden'; });
                    fabEls.forEach((el) => { el.style.opacity = '0'; });
                    await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
                    try {
                      const captureWithTimeout = (container: HTMLElement) =>
                        Promise.race([
                          html2canvas(container, {
                            useCORS: true, allowTaint: true,
                            backgroundColor: '#0f172a',
                            scale: window.devicePixelRatio >= 2 ? 2 : 1.5,
                            width: container.offsetWidth, height: container.offsetHeight,
                            ignoreElements: (el: Element) =>
                              el.classList?.contains('leaflet-control-container') ||
                              el.tagName === 'BUTTON' || el.getAttribute('role') === 'button',
                            logging: false,
                          }).then((canvas) => canvas.toDataURL('image/png')),
                          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('screenshot timeout')), 8000)),
                        ]);
                      mapImageBase64 = await captureWithTimeout(mapContainer);
                    } catch {
                      const canvas = mapContainer.querySelector('canvas');
                      if (canvas) { try { mapImageBase64 = (canvas as HTMLCanvasElement).toDataURL('image/png'); } catch { /* tainted */ } }
                    }
                    hideEls.forEach((el: Element) => ((el as HTMLElement).style.visibility = ''));
                    fabEls.forEach((el: Element) => ((el as HTMLElement).style.opacity = ''));
                  }
                  sonnerToast.dismiss(captureToast);
                  const info: CollateralMapPDFInfo = { collateralId, collateralType, ownerName: undefined, areaSqM, perimeterM: savedPerimeter, gpsLatitude, gpsLongitude, updatedAt, mapImageBase64, segmentLengthsM: segLengths };
                  setPdfInfo(info);
                  await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
                  exportPDF(info, pdfTemplateRef);
                }}
                disabled={isExporting || !geoBoundaryJson}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-white/80 active:bg-white/25 disabled:opacity-30 transition-colors flex-shrink-0"
                title="Export PDF"
              >
                {isExporting
                  ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                  : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                }
              </button>
              </>}
              <button
                type="button"
                onClick={handleToggleMeasure}
                aria-label={measureMode ? 'ປິດ Smart Field Measure' : 'ເປີດ Smart Field Measure'}
                title="Smart Field Measure"
                className={`h-11 w-11 min-h-11 flex items-center justify-center rounded-full transition-colors flex-shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${measureMode ? 'bg-amber-400 text-slate-900' : 'bg-white/15 text-white/80 active:bg-white/25'}`}
              >
                <Ruler className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                ref={mobileLandmarkTriggerRef}
                onClick={() => {
                  if (activeSurface === 'landmarks') {
                    setFocusedLandmarkId(null);
                    setActiveSurface('none');
                  } else {
                    setActiveSurface('landmarks');
                  }
                }}
                aria-label={activeSurface === 'landmarks' ? 'ປິດຈຸດສຳຄັນຮອບຫຼັກຊັບ' : 'ເປີດຈຸດສຳຄັນຮອບຫຼັກຊັບ'}
                className={`h-11 w-11 min-h-11 flex items-center justify-center rounded-full transition-colors flex-shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${activeSurface === 'landmarks' ? 'bg-white text-[#002975]' : 'bg-white/15 text-white/80 active:bg-white/25'}`}
                title="ຈຸດສຳຄັນຮອບຫຼັກຊັບ"
              >
                <MapPinned className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
          )}
        </div>
      ), mapDialogPortalElement ?? document.body)}

      {/* Sprint v73.4: Mobile More Sheet — full UX redesign with all toolbar features */}
      {isMobile && showMobileMoreSheet && createPortal((
        <div
          data-pdf-hide
          className="pointer-events-none fixed inset-x-3 bottom-3 z-[10000] flex flex-col justify-end"
        >
          <div
            className="pointer-events-auto relative flex max-h-[min(60dvh,520px)] min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            {/* Sheet title */}
            <div className="flex items-center justify-between px-5 pb-3">
              <p className="text-base font-bold text-gray-800">ເຄື່ອງມືແກ້ໄຂ</p>
              <button type="button" onClick={() => { handleEscapeMapMode(); setShowMobileMoreSheet(false); }} aria-label="ປິດເຄື່ອງມືແກ້ໄຂ" className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"><X className="h-4 w-4" /></button>
            </div>

            {canEditMapBoundary && (
              <div className="px-4 pb-3">
                <button
                  type="button"
                  onClick={() => { setShowMobileMoreSheet(false); handleDrawPolygon(); }}
                  aria-label="ເລີ່ມແຕ້ມ Polygon ໃໝ່"
                  className="flex min-h-12 w-full items-center gap-3 rounded-xl bg-emerald-600 px-4 py-3 text-left text-white shadow-sm active:bg-emerald-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#002975]"
                >
                  <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  <span className="text-sm font-semibold">ເພີ່ມ Polygon ໃໝ່</span>
                  <span className="ml-auto text-xs text-emerald-100">ເລີ່ມແຕ້ມ</span>
                </button>
              </div>
            )}

            {canEditMapBoundary && (managedPolygonGeoJson as any)?.features?.length > 1 && (
              <section
                data-testid="mobile-polygon-inventory-rail"
                aria-label="ລາຍການ Polygon ໃນແຜນທີ່"
                className="border-y border-slate-100 bg-slate-50 px-4 py-3"
              >
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold text-slate-800">Polygon ໃນແຜນທີ່</p>
                    <p className="text-[11px] text-slate-500">{(managedPolygonGeoJson as any).features.length} ຜືນ · ເລືອກເພື່ອ focus ແລະແກ້ໄຂ</p>
                  </div>
                  <button
                    type="button"
                    data-testid="mobile-polygon-inventory-manage"
                    onClick={() => { setShowMobileMoreSheet(false); setShowMobilePolySheet(true); }}
                    className="min-h-11 shrink-0 rounded-xl border border-indigo-200 bg-white px-3 text-xs font-semibold text-indigo-700 active:bg-indigo-50"
                  >
                    ຈັດການ
                  </button>
                </div>
                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none]" aria-label="ເລືອກ Polygon">
                  {(managedPolygonGeoJson as any).features.map((feature: any, index: number) => {
                    const area = Number(feature?.properties?.areaSqM);
                    const selected = activePolygonIndex === index;
                    return (
                      <button
                        key={`${feature?.properties?.id ?? 'polygon'}-${index}`}
                        type="button"
                        data-testid={`mobile-polygon-rail-${index}`}
                        aria-pressed={selected}
                        aria-label={`ເລືອກ Polygon ຜືນທີ ${index + 1}${Number.isFinite(area) ? `, ${Math.round(area).toLocaleString()} ຕາແມັດ` : ''}`}
                        onClick={() => {
                          handleSelectManagedPolygon(index);
                          setShowMobileMoreSheet(false);
                          handleEditMode();
                        }}
                        className={`min-h-11 min-w-[8.5rem] rounded-xl border px-3 py-2 text-left active:scale-[0.98] ${selected ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-200 bg-white text-slate-700 active:bg-slate-100'}`}
                      >
                        <span className="block text-xs font-bold">Polygon {index + 1}</span>
                        <span className={`block text-[11px] ${selected ? 'text-emerald-100' : 'text-slate-500'}`}>{Number.isFinite(area) ? `${Math.round(area).toLocaleString()} m²` : 'ກຳລັງຄຳນວນ'}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            <div
              data-testid="mobile-more-sheet-scroll-region"
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-1 touch-pan-y [-webkit-overflow-scrolling:touch]"
              style={{ touchAction: 'pan-y' }}
              onTouchStartCapture={(event) => event.stopPropagation()}
              onTouchMoveCapture={(event) => event.stopPropagation()}
              onTouchEndCapture={(event) => event.stopPropagation()}
              onPointerDownCapture={(event) => event.stopPropagation()}
              onPointerMoveCapture={(event) => event.stopPropagation()}
              onPointerUpCapture={(event) => event.stopPropagation()}
            >
              <div className="flex flex-col gap-1 pb-1">

              {/* ─── Section: Map view utilities (layer chooser lives in bottom action cluster) ─── */}
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">ແຜນທີ່</p>

              {/* BUG-v287: Google is a base layer; Street View remains an explicit,
                  read-only action and returns to this drawing tool surface. */}
              {(gpsLatitude || liveGps) && (
                <button
                  ref={streetViewLaunchButtonRef}
                  onClick={openStreetViewFromMobileTools}
                  aria-label="ເປີດ Street View ສຳລັບຈຸດ GPS ຫຼັກຊັບ"
                  disabled={isMapControlInteractionLocked || streetViewSessionState === 'checking'}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl border ${
                    streetViewSessionState === 'checking' || isMapControlInteractionLocked ? 'bg-slate-100 text-slate-500' : 'bg-gray-50 text-gray-700'
                  } active:opacity-80 disabled:cursor-not-allowed disabled:opacity-60 mb-1`}
                >
                  <Eye className="h-5 w-5" aria-hidden="true" />
                  <span className="text-sm font-medium">ເປີດ Street View</span>
                  {isDrawing && <span className="ml-auto text-[11px] text-slate-500">ກັບມາແຕ້ມຕໍ່ໄດ້</span>}
                </button>
              )}
              {/* Label overlay toggle */}
              {(tileLayer === 'satellite' || tileLayer === 'hybrid' || tileLayer === 'maptiler') && (
                <button
                  onClick={toggleLabelOverlay}
                  disabled={isMapControlInteractionLocked}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl ${
                    showLabelOverlay ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-700'
                  } active:opacity-80 disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <span className="w-5 h-5 flex items-center justify-center text-base font-bold">A</span>
                  <span className="text-sm">{showLabelOverlay ? 'ປິດຊື່ສະຖານທີ່' : 'ສະແດງຊື່ສະຖານທີ່'}</span>
                  {showLabelOverlay && <span className="ml-auto text-xs text-blue-500">ON</span>}
                </button>
              )}

              {/* Segment labels toggle */}
              {(draftGeoJson || geoBoundaryJson) && (
                <button
                  onClick={toggleSegmentLabels}
                  disabled={isMapControlInteractionLocked}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl ${
                    showSegmentLabels ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-500'
                  } active:opacity-80 disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                  <span className="text-sm">{showSegmentLabels ? 'ປິດໄລຍະຫ່າງ' : 'ສະແດງໄລຍະຫ່າງ'}</span>
                  {showSegmentLabels && <span className="ml-auto text-xs text-emerald-500">ON</span>}
                </button>
              )}

              {/* GPS zoom */}
              {gpsLatitude && gpsLongitude && (
                <button
                  onClick={() => { handleZoomToGPS(); setShowMobileMoreSheet(false); }}
                  aria-label="ນຳແຜນທີ່ໄປຈຸດ GPS"
                  disabled={isMapControlInteractionLocked}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-700 active:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Navigation className="h-5 w-5" aria-hidden="true" />
                  <span className="text-sm">Zoom ໄປ GPS</span>
                </button>
              )}

              {/* ─── Section: ແກ້ໄຂ ─── */}
              {canEdit && (
                <>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-3 mb-1">ແກ້ໄຂ</p>

                  {/* Sprint v129 Bug4: Edit Vertices + Drag Polygon buttons (mobile) */}
                  {(draftGeoJson || geoBoundaryJson) && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => { handleEditMode(); setShowMobileMoreSheet(false); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl ${
                          drawMode === 'edit' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'
                        } active:opacity-80`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        <span className="text-sm">ແກ້ໄຂຈຸດ</span>
                        {drawMode === 'edit' && <span className="text-xs text-green-500 font-medium">ON</span>}
                      </button>
                      <button
                        onClick={() => { handleDragMode(); setShowMobileMoreSheet(false); }}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl ${
                          drawMode === 'drag' ? 'bg-orange-50 text-orange-700' : 'bg-gray-50 text-gray-700'
                        } active:opacity-80`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" /></svg>
                        <span className="text-sm">ລາກຍ້າຍ</span>
                        {drawMode === 'drag' && <span className="text-xs text-orange-500 font-medium">ON</span>}
                      </button>
                    </div>
                  )}

                  {/* Sprint v135: Manage Polygons button — opens mobile polygon management bottom sheet */}
                  {(draftGeoJson || geoBoundaryJson) && (
                    <button
                      onClick={() => { setShowMobileMoreSheet(false); setShowMobilePolySheet(true); }}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-indigo-50 text-indigo-700 active:bg-indigo-100"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                      <span className="text-sm">ຈັດການ Polygon</span>
                    </button>
                  )}

                  {/* Undo / Redo row */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => { handleUndo(); setShowMobileMoreSheet(false); }}
                      disabled={!canUndo}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-50 text-gray-700 disabled:opacity-40 active:bg-gray-100"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6M3 10l6-6" /></svg>
                      <span className="text-sm">Undo</span>
                    </button>
                    <button
                      onClick={() => { handleRedo(); setShowMobileMoreSheet(false); }}
                      disabled={!canRedo}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-50 text-gray-700 disabled:opacity-40 active:bg-gray-100"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6M21 10l-6-6" /></svg>
                      <span className="text-sm">Redo</span>
                    </button>
                  </div>

                  {/* Reset to saved */}
                  {geoBoundaryJson && (
                    <button
                      onClick={() => { handleResetToSaved(); setShowMobileMoreSheet(false); }}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-amber-50 text-amber-700 active:bg-amber-100"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                      <span className="text-sm">ຄືນສູ່ທີ່ບັນທຶກ</span>
                    </button>
                  )}

                  {/* V395: route mobile removal through the per-polygon manager so
                      a single polygon cannot be confused with the whole boundary. */}
                  {(geoBoundaryJson || draftGeoJson) && (
                    <button
                      onClick={() => { setShowMobileMoreSheet(false); setShowMobilePolySheet(true); }}
                      data-testid="mobile-manage-polygon-trigger"
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-indigo-700 bg-indigo-50 active:bg-indigo-100"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      <span className="text-sm font-medium">ເລືອກ ຫຼື ລຶບ Polygon</span>
                    </button>
                  )}
                </>
              )}

              {/* ─── Close button ─── */}
              <button
                onClick={() => setShowMobileMoreSheet(false)}
                className="flex items-center justify-center w-full px-4 py-3.5 mt-3 rounded-2xl text-gray-500 bg-gray-50 active:bg-gray-100 border border-gray-100"
              >
                <span className="text-sm font-medium">ປິດ</span>
              </button>
              {/* STEP0-R23: Version stamp for build verification */}
              <div className="text-center text-[10px] text-gray-400 mt-1 pb-1">
                build: {import.meta.env.VITE_BUILD_COMMIT ?? 'dev'}
              </div>
              </div>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Sprint v73 STEP D: Mobile Delete Confirm */}
      {/* FIX v73.1: createPortal so it renders above Radix Dialog overlay */}
      {isMobile && showMobileDeleteConfirm && createPortal((
        <div
          data-pdf-hide
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
          style={{ pointerEvents: 'all' }}
        >
          <div className="bg-white rounded-2xl shadow-2xl p-6 mx-4 w-full max-w-sm">
            <h3 className="text-base font-bold text-gray-800 mb-2">ລຶບ Polygon?</h3>
            <p className="text-sm text-gray-500 mb-6">ການລຶບຈະບໍ່ສາມາດຄືນກັບໄດ້</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowMobileDeleteConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium active:bg-gray-200"
              >
                ຍົກເລີກ
              </button>
              <button
                onClick={() => {
                  setShowMobileDeleteConfirm(false);
                  // v74: cleanup any lingering .am-dragging before mode change
                  document.querySelectorAll('.am-dragging').forEach(el => el.classList.remove('am-dragging'));
                  // Direct delete: remove all polygons (P3 v74.1: off() before remove())
                  polygonLayersRef.current.forEach((l: any) => { try { l?.pm?.disable(); l?.off(); l?.remove(); } catch {} });
                  polygonLayersRef.current = [];
                  polygonLayerRef.current = null;
                  if (segmentLabelMgrRef.current) segmentLabelMgrRef.current.clearLabels();
                  setDraftGeoJson(null);
                  setLiveArea(0);
                  setLivePerimeter(0);
                  setSavedPerimeter(0); // Sprint v86 Fix A: reset savedPerimeter so peek bar shows empty after delete
                  setMapMode('view');
                  // Sprint v87 Fix A: call clearBoundary() so server refetch resets areaSqM → peek bar truly clears
                  clearBoundary().catch(() => {});
                }}
                className="flex-1 py-3 rounded-xl bg-red-600 text-white text-sm font-medium active:bg-red-700"
              >
                ລຶບ
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Sprint v135: Mobile Polygon Management Bottom Sheet */}
      {isMobile && showMobilePolySheet && createPortal((
        <div
          data-pdf-hide
          className="fixed inset-0 z-[10002] flex flex-col justify-end"
          style={{ pointerEvents: 'all' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setShowMobilePolySheet(false); setColorPickerIndex(null); setEditingLabelIndex(null); } }}
        >
          {/* Sprint v142: Polished Polygon Management sheet */}
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => { setShowMobilePolySheet(false); setColorPickerIndex(null); setEditingLabelIndex(null); }} />
          {/* Sheet */}
          <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[80vh] flex flex-col" style={{ pointerEvents: 'all' }}>
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-2">
              <span className="text-base font-bold text-gray-800">ຈັດການ Polygon</span>
              <button
                onClick={() => { setShowMobilePolySheet(false); setColorPickerIndex(null); setEditingLabelIndex(null); }}
                className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* Polygon list */}
            <div className="overflow-y-auto flex-1">
              {(managedPolygonGeoJson as any)?.features?.length > 0 ? (
                (managedPolygonGeoJson as any).features.map((feat: any, i: number) => {
                  const isHidden = hiddenPolygons.has(i);
                  const savedColor = feat.properties?.color as string | null;
                  const dotColor = savedColor || POLYGON_COLORS[i % POLYGON_COLORS.length];
                  const isEditingLabel = editingLabelIndex === i;
                  const isPickingColor = colorPickerIndex === i;
                  return (
                    <div key={i} className={`border-b last:border-0 ${isHidden ? 'opacity-50' : ''} ${activePolygonIndex === i ? 'bg-emerald-50' : ''}`}>
                      {/* Main row */}
                      <div className="flex items-center gap-3 px-4 py-3">
                        {/* Color dot — tap to open color picker */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setColorPickerIndex(isPickingColor ? null : i); setEditingLabelIndex(null); }}
                          className="w-7 h-7 rounded-full flex-shrink-0 border-2 border-white shadow ring-2 ring-gray-300 active:ring-gray-500"
                          style={{ background: dotColor }}
                        />
                        <button
                          type="button"
                          data-testid={`mobile-polygon-select-${i}`}
                          onClick={(e) => { e.stopPropagation(); handleSelectManagedPolygon(i); setColorPickerIndex(null); setEditingLabelIndex(null); }}
                          aria-label={`ເລືອກ Polygon ຜືນທີ ${i + 1}`}
                          className={`min-h-11 shrink-0 rounded-lg px-2 text-xs font-semibold ${activePolygonIndex === i ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 active:bg-slate-200'}`}
                        >
                          ເລືອກ
                        </button>
                        {/* Sprint v145: Label — tap to edit; mobile keyboard fix: readOnly→false after mount + setTimeout focus */}
                        {isEditingLabel ? (
                          <input
                            ref={(el) => { if (el) { el.readOnly = true; setTimeout(() => { el.readOnly = false; el.focus(); el.select(); }, 50); } }}
                            className="text-sm flex-1 border border-indigo-400 rounded-lg px-2 py-1.5 outline-none bg-white"
                            inputMode="text"
                            enterKeyHint="done"
                            value={editingLabelValue}
                            onChange={(e) => setEditingLabelValue(e.target.value)}
                            onBlur={() => {
                              // Sprint v136 Bug A: use draftGeoJsonRef.current (not functional updater)
                              const trimmed = editingLabelValue.trim() || `ຜືນທີ ${i + 1}`;
                              const cur = draftGeoJsonRef.current as any;
                              if (cur?.features) {
                                const features = cur.features.map((f: any, fi: number) =>
                                  fi === i ? { ...f, properties: { ...f.properties, label: trimmed } } : f
                                );
                                setDraftGeoJson({ ...cur, features } as any);
                              }
                              setEditingLabelIndex(null);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingLabelIndex(null); }}
                          />
                        ) : (
                          <button
                            className="text-sm flex-1 text-left text-gray-800 font-medium truncate active:text-indigo-700"
                            onClick={(e) => { e.stopPropagation(); setEditingLabelIndex(i); setEditingLabelValue(feat.properties?.label || `ຜືນທີ ${i + 1}`); setColorPickerIndex(null); }}
                          >
                            {feat.properties?.label || `ຜືນທີ ${i + 1}`}
                          </button>
                        )}
                        {/* Area */}
                        <span className="text-xs text-gray-400 flex-shrink-0">{feat.properties?.areaSqM ? `${Math.round(feat.properties.areaSqM).toLocaleString()} m²` : ''}</span>
                        {/* Visibility toggle */}
                        <button
                          data-testid={`polygon-visibility-${i}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const layer = polygonLayersRef.current[i];
                            const map = leafletMapRef.current;
                            if (!layer || !map) return;
                            setHiddenPolygons(prev => {
                              const next = new Set(prev);
                              if (next.has(i)) {
                                next.delete(i);
                                try { layer.addTo(map); } catch {}
                              } else {
                                next.add(i);
                                try { map.removeLayer(layer); } catch {}
                              }
                              refreshVisibleSegmentLabels(next);
                              // Pass the same next visibility state used for the
                                // Leaflet layer so a hidden polygon cannot retain
                                // a centre label during React's async state commit.
                                setTimeout(() => renderCenterLabels(next), 0);
                                return next;
                            });
                          }}
                          className={`flex-shrink-0 p-1.5 rounded-full ${isHidden ? 'text-gray-300 bg-gray-100' : 'text-indigo-500 bg-indigo-50'}`}
                        >
                          {isHidden ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                        {/* Delete */}
                        <button
                          disabled={isSaving}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingManagedPolygonDeleteIndex(i);
                          }}
                          aria-label={`ລຶບ Polygon ຜືນທີ ${i + 1}`}
                          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-red-500 bg-red-50 active:bg-red-100 disabled:opacity-40"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                      {/* Color picker palette */}
                      {isPickingColor && (
                        <div className="px-4 pb-3 flex flex-wrap gap-3">
                          {POLYGON_COLOR_PALETTE.map((c) => (
                            <button
                              key={c}
                              onClick={(e) => {
                                e.stopPropagation();
                                const layer = polygonLayersRef.current[i];
                                if (layer) {
                                  try { layer.setStyle({ fillColor: c }); } catch {}
                                  layer.eachLayer?.((sub: any) => { try { sub.setStyle({ fillColor: c }); } catch {}; });
                                }
                                // Sprint v136 Bug A: use draftGeoJsonRef.current (not functional updater)
                                const cur = draftGeoJsonRef.current as any;
                                if (cur?.features) {
                                  const features = cur.features.map((f: any, fi: number) =>
                                    fi === i ? { ...f, properties: { ...f.properties, color: c } } : f
                                  );
                                  setDraftGeoJson({ ...cur, features } as any);
                                }
                                setColorPickerIndex(null);
                              }}
                              className={`w-9 h-9 rounded-full border-4 transition-transform active:scale-95 ${
                                dotColor === c ? 'border-gray-700 scale-110' : 'border-white shadow ring-1 ring-gray-200'
                              }`}
                              style={{ background: c }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-sm text-gray-400 text-center">ຍັງບໍ່ມີ polygon</div>
              )}
            </div>
            {pendingManagedPolygonDeleteIndex !== null && (
              <div className="border-t border-red-100 bg-red-50 px-4 py-3" role="alert" data-testid="mobile-polygon-delete-confirm">
                <p className="text-sm font-semibold text-red-800">ລຶບ Polygon ຜືນນີ້?</p>
                <p className="mt-1 text-xs leading-5 text-red-700">ຈຸດນີ້ຈະຫາຍອອກຈາກແຜນທີ່ຊົ່ວຄາວ. ກົດ ບັນທຶກ ຢູ່ແຜນທີ່ເພື່ອຢືນຢັນການປ່ຽນແປງ.</p>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => setPendingManagedPolygonDeleteIndex(null)} className="min-h-11 flex-1 rounded-xl border border-red-200 bg-white px-3 text-sm font-semibold text-red-700 active:bg-red-100">ຍົກເລີກ</button>
                  <button type="button" onClick={() => handleStageManagedPolygonDelete(pendingManagedPolygonDeleteIndex)} className="min-h-11 flex-1 rounded-xl bg-red-600 px-3 text-sm font-semibold text-white active:bg-red-700">ລຶບຈາກແຜນທີ່</button>
                </div>
              </div>
            )}
            {/* Footer — Add new polygon */}
            <div className="px-4 py-3 border-t bg-gray-50">
              <button
                onClick={() => { setShowMobilePolySheet(false); setColorPickerIndex(null); setEditingLabelIndex(null); handleDrawPolygon(); }}
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 active:bg-emerald-700 text-white text-sm rounded-xl py-3 font-medium"
              >
                <Plus className="h-4 w-4" /> ເພີ່ມ Polygon ໃໝ່
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Sprint v89: Debug Overlay Panel — activated by long-pressing compass 1s */}
      {/* Sprint v117 Phase 4: DEV gate restored — Samsung ghost-marker diagnosis complete */}
      {/* DEBUG LOG (Sprint v89) */}
      {import.meta.env.DEV && debugPanelOpen && createPortal(
        <div
          data-pdf-hide
          style={{
            position: 'fixed',
            bottom: 80,
            left: 8,
            right: 8,
            maxHeight: '40vh',
            zIndex: 99999,
            background: 'rgba(0,0,0,0.88)',
            borderRadius: 10,
            padding: '8px 10px',
            fontFamily: 'monospace',
            fontSize: 11,
            color: '#00ff88',
            overflowY: 'auto',
            pointerEvents: 'auto',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontWeight: 'bold', color: '#fff', fontSize: 12 }}>🔧 GHOST DEBUG (v115.7)</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => { debugLogsRef.current = []; setDebugLogs([]); }}
                style={{ background: '#444', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
              >CLR</button>
              <button
                onClick={() => setDebugPanelOpen(false)}
                style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
              >✕</button>
            </div>
          </div>
          {/* State summary row */}
          <div style={{ color: '#ffcc00', marginBottom: 4, fontSize: 10 }}>
            mode={mapMode} | draw={drawMode ?? 'null'} | verts={drawVertexCount} | area={liveArea > 0 ? (liveArea/1600).toFixed(2)+'rai' : areaSqM > 0 ? (areaSqM/1600).toFixed(2)+'rai' : '0'} | zoom={currentZoom} | polys={polygonLayersRef.current.length}
          </div>
          {/* Sprint v120: live sync badge — shows 4-array sync state during draw-mode midpoint drag */}
          {isDrawingRef.current && (() => {
            try {
              const map = leafletMapRef.current;
              const di = (map?.pm?.Draw as any)?.[drawModeRef.current === 'rectangle' ? 'Rectangle' : 'Polygon'];
              if (!di) return null;
              const nV = drawingVerticesRef.current?.length ?? 0;
              const lls = di._layer?.getLatLngs?.() ?? [];
              const ring = Array.isArray(lls[0]) ? lls[0] : lls;
              const nR = ring.length;
              const nM = Array.isArray(di._markers) ? di._markers.length : '?';
              const nI = Array.isArray(di._layer?._latlngInfo) ? di._layer._latlngInfo.length : '?';
              const ok = nV === nR && nR === nM && nM === nI;
              return (
                <div style={{ background: ok ? '#1a3a1a' : '#3a1a1a', color: ok ? '#4ade80' : '#f87171', padding: '2px 6px', borderRadius: 3, fontSize: 11, marginTop: 2 }}>
                  {ok ? '✅' : '🔴'} SYNC v={nV} ring={nR} mk={String(nM)} info={String(nI)}
                </div>
              );
            } catch { return null; }
          })()}
          {debugLogs.length === 0 && <div style={{ color: '#888' }}>ຍັງບໍ່ມີ events — ລອງແຕ້ມ polygon</div>}
          {debugLogs.map((line, i) => (
            <div key={i} style={{ borderBottom: '1px solid #222', padding: '1px 0', lineHeight: 1.4 }}>{line}</div>
          ))}
        </div>,
        document.body
      )}
      {/* BUG-D FIX: pass onSaveAndClose for primary action */}
      <MapUnsavedDialog
        open={showUnsavedDialog}
        onConfirmDiscard={handleConfirmDiscard}
        onCancel={() => {
          setShowUnsavedDialog(false);
          setPendingCollateralSwitchId(null);
        }}
        onSaveAndClose={handleSaveAndExit}
        description={pendingCollateralSwitchId
          ? 'ມີການແຕ້ມຂອບເຂດທີ່ຍັງບໍ່ໄດ້ບັນທຶກ. ກະລຸນາເລືອກການດຳເນີນການກ່ອນປ່ຽນຫຼັກຊັບ.'
          : undefined}
        saveLabel={pendingCollateralSwitchId ? 'ບັນທຶກ ແລ້ວ ປ່ຽນຫຼັກຊັບ' : undefined}
        discardLabel={pendingCollateralSwitchId ? 'ບໍ່ບັນທຶກ ແລ້ວ ປ່ຽນຫຼັກຊັບ' : undefined}
        cancelLabel={pendingCollateralSwitchId ? 'ຢູ່ຫຼັກຊັບເດີມ' : undefined}
      />
      {/* Sprint v115 Fix #4: branded confirm dialog (replaces window.confirm) */}
      <MapConfirmDialog
        state={confirmState}
        onClose={() => setConfirmState(null)}
      />
    </>
  );
}
